import { equalReportProps } from "./reportRender";
import { lyComparisonLabel, normalizeLyComparison, type LyComparison } from "./LyComparisonControl";
import CompactPeriodToolbar from "./CompactPeriodToolbar";
import GroupedActiveFilters from "./GroupedActiveFilters";
import ProductFiltersToolbar from "./ProductFiltersToolbar";
import LoginBrandStrip from "./LoginBrandStrip";
import "./loginScreen.css";
import "./identityBrand.css";
import useBrowserTitle from "./useBrowserTitle";
import { createContext, FormEvent, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import CampaignReports from "./CampaignReports";
import { NavigationFlights, emptyCampaignNavigation, emptyShowroomNavigation, navigationPath, navigationReportKey, readNavigation, writeNavigation, type CampaignNavigation, type NavigationState, type ShowroomNavigation } from "./navigationState";
import BotReportHistory from "./BotReportHistory";
import StoreBreakdownSection from "./StoreBreakdownSection";
import MonthBreakdownSection from "./MonthBreakdownSection";
import {useMonthGrouping} from "./monthGrouping";
import {readMonthViews} from "./monthBreakdownExport";
import { readStoreBreakdownExports } from "./storeBreakdownExport";
import StoreOverviewTable from "./FocusedStoreOverviewTable";
import ExecutiveOverviewTable from "./ExecutiveOverviewTable";
import FocusedExecutiveOverviewTable from "./FocusedExecutiveOverviewTable";
import ReportSection from "./ReportSection";
import ActionStoresPresentation from "./ActionStoresPresentation";
import {useExecutiveWeekdays} from "./useExecutiveWeekdays";
import "./executiveTableArrangement.css";
import HourlySalesTable from "./HourlySalesTable";
import { hourlySalesColumns } from "./hourlySalesPresentation";
import { withoutUzsLabel } from "./currencyLabels";
import { isScreenMetric, screenMetricColumnLabel, screenMetricColumns, type ScreenMetric } from "./metricScreenHeadings";
import { executiveOverviewColumns } from "./executiveOverviewPresentation";
import AdviserOverviewTable from "./AdviserOverviewTable";
import { adviserOverviewColumns, formatAdviserDailyRequired } from "./adviserOverviewPresentation";
import CategoryOverviewTable from "./CategoryOverviewTable";
import FocusedStoreAnalysisTable from "./FocusedStoreAnalysisTable";
import { StoreAnalysisPresentationProvider } from "./storeAnalysisPresentation";
import { resetReportScroll } from "./reportUsability";
import "./reportUsability.css";
import { ExecutiveKpiPresentation, ExecutiveKpiPreviousLabel, ExecutiveKpiChange, executiveKpiTitle, useExecutiveKpiPresentation } from "./executiveKpiPresentation";
import { expandGenderFilterValues, groupedGenderFilterOptions } from "./genderFilterGroups";
import { categoryOverviewColumns } from "./categoryOverviewPresentation";
import { readStoreCategoryExport, registerStoreCategoryExport, storeCategoryRowId, useStoreCategoryRows, type StoreCategoryScope } from "./storeCategoryMode";
import { desktopColumnLabel, desktopDetailHiddenColumns, desktopInformationView, desktopKpiTitle, desktopMetricHelp, useDesktopInformation } from "./desktopInformation";
import { storeOverviewColumns, storeOverviewCopy, storeOverviewDefaultHiddenColumns, storeOverviewMenuColumns, storeOverviewOptionalKeys, storeOverviewToggleKeys,storeTargetRemainder } from "./storeOverviewPresentation";
import {focusedStoreColumns,focusedChangeLabel} from "./focusedStoreOverview";
import {focusedStoreBasketDisplay} from "./focusedStoreTotals";
import { storeOverviewOptionalDisplay, storeOverviewOptionalTotals, storeOverviewOptionalValues } from "./storeOverviewOptionalLy";
import TableColumnPicker from "./TableColumnPicker";
import TableScopeInfo from "./TableScopeInfo";
import TableFullText from "./TableFullText";
import DesktopTableNavigation, { DesktopShowroomLabel } from "./DesktopTableNavigation";
import { repeatedSubtotalScope, singleIdentityLabel, weekdayLyUptTotals } from "./tableCompletion";
import "./tableCompletion.css";
import { optionalLyKeys, showroomDefaultHidden, overviewSummaryVisibility, standardCopy, standardMenu, standardReportOrder, standardStack, standardToggle } from "./tableStandard";
import {TelegramCodeCell,TelegramCodeEditor,useTelegramCodes} from "./TelegramCodes";
import { localeCopy, localizeRuntimeText, localizedCalendarLabel, useGridLocale } from "./languagePolish";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry, themeQuartz, type ColDef, type GridApi, type GridReadyEvent, type PostSortRowsParams } from "ag-grid-community";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  AlertTriangle,
  Boxes,
  Broom,
  BuildingComplex,
  Check,
  CheckCircle2,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Database,
  Download,
  FileSpreadsheet,
  Maximize2,
  LayoutDashboard,
  Languages,
  LogOut,
  PackageCheck,
  PackageSearch,
  ReceiptText,
  Search,
  Settings,
  ShieldCheck,
  ShoppingBasket,
  SlidersHorizontal,
  Store,
  Tag,
  Table2,
  Target,
  TrendingUp,
  UsersRound,
  X,
  XCircle,
} from "lucide-react";
import { downloadWorkbook, type WorkbookCell, type WorkbookRowKind, type WorkbookSheet } from "./excel";
import { collectExportPages, exportVisibleColumns, tableExcelSheet, type ExportColumn } from "./tableExcel";
import {
  calculateTargetGapSimulation,
  calculateTargetPlanOperationalGuidance,
  TARGET_PLAN_SAFETY_RATE,
  type SimulatorFocus,
} from "./targetSimulator";
import { nextMobileSort, sortMobileRows, type MobileSortState } from "./mobileSorting";
import { averageSessionDuration, filterSessions, type SessionStatusFilter } from "./sessionFilters";
import { productAnalysisRange, type ProductPeriodMode, type ProductDateRange } from "./productPeriod";
import { withProductImportDates } from "./productImportDates";
import ProductAnalysisWorkspace from "./ProductAnalysisWorkspace";
import ProductSellthroughWorkspace from "./ProductSellthroughWorkspace";
import { canOverlapFilterCatalog, canReuseStoreTrend, ProductPagePrefetch, type InitialProductPage } from "./pageLoading";
import { parseProductSkuList, productExportBlocked, productSellthrough, type ProductVariantDetail } from "./productAnalysisModel";
import { productColorDisplayLabel } from "./productColorTable";

ModuleRegistry.registerModules([AllCommunityModule]);

const srrGridTheme = themeQuartz.withParams({
  accentColor: "#0f8c83",
  backgroundColor: "#ffffff",
  borderColor: "#d5e1e7",
  borderRadius: 12,
  browserColorScheme: "light",
  cellHorizontalPadding: 10,
  columnBorder: true,
  fontFamily: 'Inter, "Segoe UI Variable Text", "Segoe UI", sans-serif',
  fontSize: 12,
  foregroundColor: "#244657",
  headerBackgroundColor: "#143f56",
  headerFontSize: 11,
  headerFontWeight: 700,
  headerTextColor: "#ffffff",
  oddRowBackgroundColor: "#f8fafb",
  rowBorder: true,
  rowHoverColor: "#edf8f6",
  selectedRowBackgroundColor: "#e7f6f3",
  spacing: 7,
});

type PlatformStatus = {
  name: string;
  api_version: string;
  release_id: string;
  production_data_enabled: boolean;
};
type AuthUser = {
  username: string;
  display_name: string;
  role: "admin" | "viewer";
  allowed_brands: string[];
  showroom_access: boolean;
  demo_mode: boolean;
};
type AuthResponse = { user: AuthUser };
type KpiValues = {
  net_sales_uzs: number;
  net_sales_usd: number;
  net_units: number;
  receipts: number;
  upt: number;
  average_basket_uzs: number;
  average_unit_price_uzs: number;
  visitors: number;
  conversion_rate_percent: number | null;
};
type SeasonSellthrough = {
  import_units: number;
  sales_units: number;
  stock_units: number;
  sellthrough_percent: number | null;
};
type Filters = {
  brands: string[];
  stores: string[];
  malls: string[];
  main_categories: string[];
  subcategories: string[];
  genders: string[];
  seasons: string[];
  skus: string[];
};
type FilterCatalog = Filters & {
  mode: string;
  release_id: string;
  min_date: string;
  max_date: string;
  production_data_enabled: boolean;
};
type MetricRow = {
  label: string;
  store?: string;
  brand?: string;
  net_sales_uzs: number;
  net_units: number;
  receipts: number;
  upt: number;
  average_basket_uzs: number;
  average_unit_price_uzs: number;
  revenue_share_percent: number;
  previous_year_sales_uzs: number;
  previous_year_units: number;
  previous_year_receipts?: number;
  revenue_change_percent: number | null;
  previous_year_upt: number;
  upt_change_percent: number | null;
  target_uzs: number | null;
  target_achievement_percent: number | null;
  target_progress_percent?: number | null;
  visitors: number | null;
  conversion_rate_percent: number | null;
  net_sales_usd: number;
  stock_units: number | null;
  stock_share_percent: number | null;
  day_count: number | null;
  average_sales_uzs: number | null;
};
type StoreActionReasonKind = "company-cr" | "company-upt" | "company-upt-drop" | "company-sales-drop" | "company-sales";
type StoreActionReason = { kind: StoreActionReasonKind; rank?: number };
type StoreActionRow = MetricRow & {
  brand: string;
  reasons: StoreActionReason[];
};
type KpiTrendPoint = {
  label: string;
  value: number;
};
type ExecutiveTables = {
  ly_comparison?: LyComparison;
  breakdown_scope?: string;
  request_scope?: string;
  mode: string;
  release_id: string;
  period: { start: string; end: string };
  stores: MetricRow[];
  brands: MetricRow[];
  malls: MetricRow[];
  daily: MetricRow[];
  hourly: MetricRow[];
  monthly: MetricRow[];
  production_data_enabled: boolean;
};
type AdviserRow = {
  store: string;
  name: string;
  target_uzs: number | null;
  net_sales_uzs: number;
  target_achievement_percent: number | null;
  target_progress_percent: number | null;
  remaining_target_uzs: number | null;
  revenue_share_percent: number;
  receipts: number;
  net_units: number;
  upt: number;
  average_basket_uzs: number;
  average_unit_price_uzs: number;
};
type StoreReport = {
  ly_comparison?: LyComparison;
  mode: string;
  release_id: string;
  period: { start: string; end: string };
  categories: MetricRow[];
  genders: MetricRow[];
  weekdays: MetricRow[];
  subcategories: MetricRow[];
  advisers: AdviserRow[];
  production_data_enabled: boolean;
  category_scope?: string;
};
type ProductPerformanceRow = {
  label: string;
  category: string;
  subcategory?: string;
  color_name?: string;
  season: string;
  import_units: number;
  first_import: string | null;
  last_import: string | null;
  sales_units: number;
  stock_units: number;
  sellthrough_percent: number | null;
  price_uzs: number;
  average_unit_price_uzs: number;
  discount_percent: number | null;
  daily_sales: number;
  stock_days: number | null;
  first_sale: string | null;
  last_sale: string | null;
  days_without_sale: number | null;
  status: string;
  total_rows: number;
  overall_import_units: number;
  overall_sales_units: number;
  overall_stock_units: number;
  overall_revenue_uzs: number;
  overall_gross_uzs: number;
  overall_discount_revenue_uzs?: number;
  overall_discount_reference_uzs?: number;
};
type ProductDimensionRow = {
  label: string;
  color_name: string;
  import_units: number;
  sales_units: number;
  stock_units: number;
  total_rows: number;
  overall_import_units: number;
  overall_sales_units: number;
  overall_stock_units: number;
};
type ProductReport = {
  mode: string;
  release_id: string;
  period: { start: string; end: string };
  products: ProductPerformanceRow[];
  sku_colors: ProductPerformanceRow[];
  sizes: ProductDimensionRow[];
  colors: ProductDimensionRow[];
  inventory_scope_supported: boolean;
  production_data_enabled: boolean;
};
type ProductTablePage = {
  rows: ProductPerformanceRow[];
  total_rows: number;
};
type ShowroomLeafRow = {
  brand: string;
  store: string;
  main_category: string;
  subcategory: string;
  gender: string;
  sku: string;
  product_name: string;
  color_code: string;
  color_name: string;
  size: string;
  month: string;
  quantity: number;
  first_import: string;
  last_import: string;
  current_stock: number;
  first_sale: string;
  last_sale: string;
};
type ShowroomReport = {
  mode: string;
  release_id: string;
  period: { start: string; end: string };
  summary: {
    sales_days: number;
    active_skus: number;
    total_quantity: number;
    first_sale: string;
    last_sale: string;
  };
  rows: ShowroomLeafRow[];
  production_data_enabled: boolean;
};
type BrandedStoreMetric = {
  brand: string;
  label: string;
  target_uzs: number;
  net_sales_uzs: number;
  visitors: number;
  receipts: number;
  net_units: number;
  previous_year_sales_uzs: number;
  previous_year_units: number;
  previous_year_receipts: number;
};
type StorePerformanceReport = {
  ly_comparison?: LyComparison;
  mode: string;
  release_id: string;
  period: { start: string; end: string };
  previous_year_period: { start: string; end: string };
  rows: BrandedStoreMetric[];
  production_data_enabled: boolean;
};
type PrivateWorkspaceSection = "reports" | "showroom";
type StorePerformanceView = "current" | "comparison" | "summary";
type MobileTableView = "summary" | "detail";
type AdminSection = "health" | "users" | "sessions" | "bot-report-history";
type AdminUser = {
  username: string;
  display_name: string;
  role: string;
  allowed_brands: string[];
  active: boolean;
  showroom_access: boolean;
};
type AdminUsersResponse = { users: AdminUser[]; writes_enabled: boolean };
type AdminSession = {
  username: string;
  display_name: string;
  login_at: string;
  last_seen_at: string;
  logout_at: string | null;
  duration_seconds: number;
  online: boolean;
};
type AdminSessionsResponse = {
  sessions: AdminSession[];
  retention_days: number;
  active_count: number;
  unique_users: number;
};
type AdminUserDraft = {
  username: string;
  display_name: string;
  role: "admin" | "viewer";
  allowed_brands: string[];
  pin: string;
  active: boolean;
};
type AdminNightSummary = {
  sales_date: string;
  net_sales_uzs: number;
  all_store_sales_uzs?: number;
  sales_excluding_karshi_outlet_uzs?: number;
  avva_karshi_outlet_sales_uzs?: number;
  receipts: number;
  receipts_excluding_karshi_outlet?: number;
  net_units: number;
  net_units_excluding_karshi_outlet?: number;
  stock: number;
  postpone: number;
  transfer_out: number;
  stock_total: number;
  visitors_date: string;
  visitors: number;
  import_date: string;
};
type AdminHealth = {
  status: string;
  api_release_id: string;
  uptime_seconds: number;
  data_release_id: string;
  source_created_at: string;
  loaded_at: string;
  release_status: string;
  rows: Record<string, unknown> | null;
  services: { name: string; status: string; detail: string }[];
  expected_data_date?: string;
  available_summary_dates?: string[];
  night_summary: AdminNightSummary | null;
};
type AdminNightHistory = {
  selected_data_date: string;
  available_dates: string[];
  release_id: string;
  source_created_at: string;
  summary: AdminNightSummary;
};
type ExecutiveSummary = {
  ly_comparison?: LyComparison;
  mode: string;
  release_id: string;
  release_created_at: string;
  period: { start: string; end: string };
  previous_year_period: { start: string; end: string };
  full_month_target: boolean;
  filtered_rows: number;
  current: KpiValues;
  previous_year: KpiValues;
  target_uzs: number;
  target_achievement_percent: number | null;
  target_to_date_uzs: number;
  target_progress_percent: number | null;
  remaining_target_uzs: number;
  season_sellthrough: { summer: SeasonSellthrough; winter: SeasonSellthrough };
  current_stock: number;
  source_freshness: {
    sales: string;
    stock: string;
    import: string;
    visitors: string;
  };
  applied_filters: Filters;
  target_and_visitor_scope_supported: boolean;
  production_data_enabled: boolean;
};
type FilterKey = Exclude<keyof Filters, "skus">;
type PeriodId = "yesterday" | "week" | "month" | "lastMonth" | "year" | "all" | "custom";
type ShowroomPeriodId = "month" | "lastMonth" | "last3" | "last6" | "year" | "lastYear" | "all" | "custom" | "monthRange";
type PageId = "executive" | "store" | "product" | "showroom" | "admin";
type LanguageCode = "tr" | "en" | "ru" | "uz";

const languageLabels: Record<LanguageCode, string> = {
  tr: "TR Türkçe",
  en: "EN English",
  ru: "RU Русский",
  uz: "UZ O‘zbekcha",
};
const uiTranslations: Record<string, Partial<Record<Exclude<LanguageCode, "tr">, string>>> = {
  "İlk importtan bugüne": { en: "From first import to today", ru: "С первого импорта по сегодня", uz: "Birinchi importdan bugungacha" },
  "Seçili tarihi kullan": { en: "Use selected dates", ru: "Использовать выбранные даты", uz: "Tanlangan sanalarni ishlatish" },
  "Ürün dönemi": { en: "Product period", ru: "Период товара", uz: "Mahsulot davri" },
  "Seçili tarih": { en: "Selected dates", ru: "Выбранные даты", uz: "Tanlangan sana" },
  "Tekrar hoş geldiniz": {
    en: "Welcome back",
    ru: "С возвращением",
    uz: "Xush kelibsiz",
  },
  "Hoş geldiniz": { en: "Welcome", ru: "Добро пожаловать", uz: "Xush kelibsiz" },
  "SRR360 hesabınızla çalışma alanınıza devam edin.": {
    en: "Continue to your workspace with your SRR360 account.",
    ru: "Продолжите работу в своём пространстве с учётной записью SRR360.",
    uz: "SRR360 hisobingiz bilan ish maydoningizga davom eting.",
  },
  "Kullanıcı adı": {
    en: "Username",
    ru: "Имя пользователя",
    uz: "Foydalanuvchi nomi",
  },
  "Beni hatırla": {
    en: "Remember me",
    ru: "Запомнить меня",
    uz: "Meni eslab qol",
  },
  "Bu cihazda 30 gün açık kal": {
    en: "Stay signed in on this device for 30 days",
    ru: "Оставаться в системе на этом устройстве 30 дней",
    uz: "Bu qurilmada 30 kun tizimda qolish",
  },
  "Giriş Yap": { en: "Sign In", ru: "Войти", uz: "Kirish" },
  "Giriş yapılıyor…": {
    en: "Signing in…",
    ru: "Выполняется вход…",
    uz: "Kirilmoqda…",
  },
  "SRR360 güvenli oturumu kontrol ediliyor…": {
    en: "Checking the secure SRR360 session…",
    ru: "Проверка защищённого сеанса SRR360…",
    uz: "SRR360 xavfsiz sessiyasi tekshirilmoqda…",
  },
  "Çıkış yap": { en: "Sign out", ru: "Выйти", uz: "Chiqish" },
  Yetki: { en: "Access", ru: "Доступ", uz: "Ruxsat" },
  "Tüm markalar": { en: "All brands", ru: "Все бренды", uz: "Barcha brendlar" },
  SAYFALAR: { en: "PAGES", ru: "СТРАНИЦЫ", uz: "SAHIFALAR" },
  FİLTRELER: { en: "FILTERS", ru: "ФИЛЬТРЫ", uz: "FILTRLAR" },
  "Yönetici özeti": {
    en: "Executive summary",
    ru: "Сводка руководителя",
    uz: "Rahbar xulosasi",
  },
  "Mağaza analizi": {
    en: "Store analysis",
    ru: "Анализ магазинов",
    uz: "Do‘kon tahlili",
  },
  "Ürün analizi": {
    en: "Product analysis",
    ru: "Анализ товаров",
    uz: "Mahsulot tahlili",
  },
  "Yönetici Özeti": {
    en: "Executive Summary",
    ru: "Сводка руководителя",
    uz: "Rahbar xulosasi",
  },
  "Mağaza Analizi": {
    en: "Store Analysis",
    ru: "Анализ магазинов",
    uz: "Do‘kon tahlili",
  },
  "Ürün Analizi": {
    en: "Product Analysis",
    ru: "Анализ товаров",
    uz: "Mahsulot tahlili",
  },
  "Showroom Ürün Planlama": {
    en: "Showroom Product Planning",
    ru: "Планирование шоурума",
    uz: "Showroom mahsulot rejalashtirish",
  },
  "Tanıtım hesabı": {
    en: "Demonstration account",
    ru: "Демонстрационная учетная запись",
    uz: "Namoyish hisobi",
  },
  "Bu oturum yalnızca sentetik veriler kullanır; gerçek şirket bilgileriyle bağlantısı yoktur.": {
    en: "This session uses synthetic data only and is not connected to real company information.",
    ru: "В этом сеансе используются только синтетические данные, не связанные с реальной информацией компании.",
    uz: "Bu sessiya faqat sun’iy ma’lumotlardan foydalanadi va haqiqiy kompaniya ma’lumotlariga ulanmagan.",
  },
  "İzole demo verisi": {
    en: "Isolated demo data",
    ru: "Изолированные демо-данные",
    uz: "Ajratilgan demo ma’lumoti",
  },
  Yönetim: { en: "Administration", ru: "Управление", uz: "Boshqaruv" },
  "Yönetim Platformu": {
    en: "Management Platform",
    ru: "Платформа управления",
    uz: "Boshqaruv platformasi",
  },
  Dönem: { en: "Period", ru: "Период", uz: "Davr" },
  Dün: { en: "Yesterday", ru: "Вчера", uz: "Kecha" },
  "Bu Hafta": { en: "This Week", ru: "Эта неделя", uz: "Bu hafta" },
  "Bu Ay": { en: "This Month", ru: "Этот месяц", uz: "Bu oy" },
  "Geçen Ay": { en: "Last Month", ru: "Прошлый месяц", uz: "O‘tgan oy" },
  "Bu Yıl": { en: "This Year", ru: "Этот год", uz: "Bu yil" },
  "Tüm Dönem": { en: "All Time", ru: "Весь период", uz: "Barcha davr" },
  "Özel Tarih": { en: "Custom Range", ru: "Свой период", uz: "Maxsus sana" },
  "Son 3 Ay": {
    en: "Last 3 Months",
    ru: "Последние 3 месяца",
    uz: "So‘nggi 3 oy",
  },
  "Son 6 Ay": {
    en: "Last 6 Months",
    ru: "Последние 6 месяцев",
    uz: "So‘nggi 6 oy",
  },
  "Geçen Yıl": { en: "Last Year", ru: "Прошлый год", uz: "O‘tgan yil" },
  "TÜM FİLTRELERİ TEMİZLE": {
    en: "CLEAR ALL FILTERS",
    ru: "СБРОСИТЬ ФИЛЬТРЫ",
    uz: "BARCHA FILTRLARNI TOZALASH",
  },
  Marka: { en: "Brand", ru: "Бренд", uz: "Brend" },
  Mağaza: { en: "Store", ru: "Магазин", uz: "Do‘kon" },
  "Alışveriş Merkezi": {
    en: "Shopping Mall",
    ru: "Торговый центр",
    uz: "Savdo markazi",
  },
  AVM: { en: "Mall", ru: "ТЦ", uz: "Savdo markazi" },
  "Ana Kategori": {
    en: "Main Category",
    ru: "Основная категория",
    uz: "Asosiy kategoriya",
  },
  "Alt Kategori": {
    en: "Subcategory",
    ru: "Подкатегория",
    uz: "Quyi kategoriya",
  },
  Cinsiyet: { en: "Gender", ru: "Пол", uz: "Jins" },
  Sezon: { en: "Season", ru: "Сезон", uz: "Mavsum" },
  Satış: { en: "Sales", ru: "Продажи", uz: "Savdo" },
  Stok: { en: "Stock", ru: "Остаток", uz: "Qoldiq" },
  İmport: { en: "Import", ru: "Импорт", uz: "Import" },
  Ziyaretçi: { en: "Visitors", ru: "Посетители", uz: "Tashrifchilar" },
  "Tüm Ay Hedefini Gör": {
    en: "Show Full-Month Target",
    ru: "Показать цель за весь месяц",
    uz: "To‘liq oy rejasini ko‘rish",
  },
  Başlangıç: { en: "Start", ru: "Начало", uz: "Boshlanish" },
  Bitiş: { en: "End", ru: "Окончание", uz: "Tugash" },
  Uygula: { en: "Apply", ru: "Применить", uz: "Qo‘llash" },
  "Aktif Filtreler": {
    en: "Active Filters",
    ru: "Активные фильтры",
    uz: "Faol filtrlar",
  },
  "Tümünü Temizle": {
    en: "Clear All",
    ru: "Очистить всё",
    uz: "Barchasini tozalash",
  },
  "Tümünü Seç": {
    en: "Select All",
    ru: "Выбрать всё",
    uz: "Barchasini tanlash",
  },
  Temizle: { en: "Clear", ru: "Очистить", uz: "Tozalash" },
  "Satılan Adet": {
    en: "Units Sold",
    ru: "Продано единиц",
    uz: "Sotilgan dona",
  },
  "İmport Adet": {
    en: "Import Units",
    ru: "Количество импорта",
    uz: "Import miqdori",
  },
  "Sell-through": {
    en: "Sell-through",
    ru: "Реализация",
    uz: "Sotilish",
  },
  "Fiş Sayısı": { en: "Receipts", ru: "Количество чеков", uz: "Cheklar soni" },
  "Sepet Ortalaması": {
    en: "Average Basket",
    ru: "Средний чек",
    uz: "O‘rtacha savat",
  },
  "Ortalama Birim Fiyat": {
    en: "Average Unit Price",
    ru: "Средняя цена",
    uz: "O‘rtacha birlik narxi",
  },
  "Ortalama Fiyat": { en: "Average Price", ru: "Средняя цена", uz: "O‘rtacha narx" },
  "Hedef Durumu": { en: "Target Status", ru: "Статус выполнения цели", uz: "Reja holati" },
  "Satışı Ne Değiştirdi?": { en: "What Changed Sales?", ru: "Что изменило продажи?", uz: "Savdoni nima o‘zgartirdi?" },
  "Hedefe Ulaşma Planı": { en: "Target Achievement Plan", ru: "План достижения цели", uz: "Rejaga erishish rejasi" },
  Odak: { en: "Focus", ru: "Фокус", uz: "Yo‘nalish" },
  "Eşit Ağırlık": { en: "Equal Weight", ru: "Равный вес", uz: "Teng vazn" },
  "Bugüne Kadarki Açık": { en: "Shortfall to Date", ru: "Отставание на текущую дату", uz: "Bugungacha yetishmagan" },
  "Planlanan Ek Satış": { en: "Planned Additional Sales", ru: "Запланированные дополнительные продажи", uz: "Rejalashtirilgan qo‘shimcha savdo" },
  "hedefin üzerinde": { en: "above target", ru: "выше цели", uz: "rejadan yuqori" },
  "hedefin altında": { en: "below target", ru: "ниже цели", uz: "rejadan past" },
  "ürün/fiş": { en: "units/receipt", ru: "товара/чек", uz: "mahsulot/chek" },
  "Her {count} ziyaretçide yaklaşık 1 ek fiş": { en: "About 1 extra receipt per {count} visitors", ru: "Примерно 1 дополнительный чек на {count} посетителей", uz: "Har {count} tashrifchiga taxminan 1 ta qo‘shimcha chek" },
  "Her {count} fişte 1 ek ürün": { en: "1 extra unit per {count} receipts", ru: "1 дополнительный товар на {count} чеков", uz: "Har {count} chekda 1 ta qo‘shimcha mahsulot" },
  "Bu odakta değişmez": { en: "No change for this focus", ru: "Без изменений для этого фокуса", uz: "Bu yo‘nalishda o‘zgarmaydi" },
  "Artış gerekmiyor": { en: "No increase required", ru: "Рост не требуется", uz: "O‘sish talab qilinmaydi" },
  "{gap} açık + %{rate} plan payı": { en: "{gap} shortfall + {rate}% plan margin", ru: "Отставание {gap} + запас плана {rate}%", uz: "{gap} kamomad + {rate}% reja zaxirasi" },
  "Simülasyon Bu Ay döneminde kullanılabilir": { en: "The simulation is available for This Month", ru: "Моделирование доступно для периода «Этот месяц»", uz: "Simulyatsiya “Bu oy” davrida mavjud" },
  "Simülasyon için hedef ve ziyaretçi verisi gerekli": { en: "Target and visitor data are required for the simulation", ru: "Для моделирования нужны данные о цели и посетителях", uz: "Simulyatsiya uchun reja va tashrifchi ma’lumotlari kerak" },
  "Gerçekleşen": { en: "Achieved", ru: "Выполнено", uz: "Bajarilgan" },
  "Olması gereken": { en: "Expected pace", ru: "Ожидаемый темп", uz: "Kutilgan sur’at" },
  "Bugüne Kadar Eksik": { en: "Shortfall to Date", ru: "Отставание на текущую дату", uz: "Bugungacha yetishmagan" },
  "Bugüne Kadar Fazla": { en: "Ahead to Date", ru: "Опережение на текущую дату", uz: "Bugungacha ortiqcha" },
  "Günlük gereken": { en: "Daily required", ru: "Необходимо в день", uz: "Kunlik kerak" },
  "Tempo farkı": { en: "Pace gap", ru: "Отклонение темпа", uz: "Sur’at farqi" },
  "Aylık hedef": { en: "Monthly target", ru: "Месячная цель", uz: "Oylik reja" },
  "Dönem hedefi": { en: "Period target", ru: "Цель периода", uz: "Davr rejasi" },
  "Hedef verisi yok": { en: "No target data", ru: "Нет данных о цели", uz: "Reja ma’lumoti yo‘q" },
  "Temponun önünde": { en: "Ahead of pace", ru: "Опережает темп", uz: "Sur’atdan oldinda" },
  "Temponun gerisinde": { en: "Behind pace", ru: "Отстаёт от темпа", uz: "Sur’atdan ortda" },
  önde: { en: "ahead", ru: "впереди", uz: "oldinda" },
  geride: { en: "behind", ru: "позади", uz: "ortda" },
  "Geçen yıl verisi bulunamadı": { en: "Last-year data is unavailable", ru: "Нет данных за прошлый год", uz: "O‘tgan yil ma’lumoti mavjud emas" },
  "Ciro etkisi": { en: "Revenue impact", ru: "Влияние на выручку", uz: "Tushumga ta’sir" },
  "Net Satış Değişimi": { en: "Net Sales Change", ru: "Чистое изменение продаж", uz: "Sof savdo o‘zgarishi" },
  "Ciroyu Arttıranlar": { en: "Revenue Drivers", ru: "Факторы роста выручки", uz: "Tushumni oshirganlar" },
  "Ciroyu Azaltanlar": { en: "Revenue Drags", ru: "Факторы снижения выручки", uz: "Tushumni kamaytirganlar" },
  "Değişim": { en: "Change", ru: "Изменение", uz: "O‘zgarish" },
  "Net değişim": { en: "Net change", ru: "Чистое изменение", uz: "Sof o‘zgarish" },
  "ATV sonucu": { en: "ATV outcome", ru: "Итог ATV", uz: "ATV natijasi" },
  "ciroyu destekledi": { en: "supported revenue", ru: "поддержал выручку", uz: "tushumni qo‘llab-quvvatladi" },
  "en büyük baskıyı oluşturdu": { en: "created the largest drag", ru: "оказал наибольшее давление", uz: "eng katta bosimni yaratdi" },
  "Ciro değişiminin tamamı pozitif katkılardan geldi": { en: "All revenue change came from positive contributions", ru: "Всё изменение выручки обеспечено положительными факторами", uz: "Tushumdagi o‘zgarish to‘liq ijobiy omillardan keldi" },
  "Ciro değişiminin tamamı negatif katkılardan geldi": { en: "All revenue change came from negative contributions", ru: "Всё изменение выручки обусловлено отрицательными факторами", uz: "Tushumdagi o‘zgarish to‘liq salbiy omillardan keldi" },
  "Önceki analiz": { en: "Previous analysis", ru: "Предыдущий анализ", uz: "Oldingi tahlil" },
  "Sonraki analiz": { en: "Next analysis", ru: "Следующий анализ", uz: "Keyingi tahlil" },
  "Analiz sayfaları": { en: "Analysis pages", ru: "Страницы анализа", uz: "Tahlil sahifalari" },
  puan: { en: "points", ru: "п.п.", uz: "punkt" },
  "Geçen yıl": { en: "Last year", ru: "Прошлый год", uz: "O‘tgan yil" },
  Hedef: { en: "Target", ru: "Цель", uz: "Reja" },
  "Karşılaştırma yok": {
    en: "No comparison",
    ru: "Нет сравнения",
    uz: "Taqqoslash yo‘q",
  },
  "Bilgi Yok": {
    en: "No data",
    ru: "Нет данных",
    uz: "Ma’lumot yo‘q",
  },
  "Kod veya değer ara...": {
    en: "Search code or value...",
    ru: "Поиск кода или значения...",
    uz: "Kod yoki qiymatni qidiring...",
  },
  "Sütunları Göster/Gizle": {
    en: "Show/Hide Columns",
    ru: "Показать/скрыть столбцы",
    uz: "Ustunlarni ko‘rsatish/yashirish",
  },
  "Sütunları Sığdır": {
    en: "Fit Columns",
    ru: "Подогнать столбцы",
    uz: "Ustunlarni moslash",
  },
  "Görünümü Sıfırla": {
    en: "Reset View",
    ru: "Сбросить вид",
    uz: "Ko‘rinishni tiklash",
  },
  "Tam Ekran": { en: "Full Screen", ru: "Полный экран", uz: "To‘liq ekran" },
  "Tam Ekrandan Çık": { en: "Exit Full Screen", ru: "Выйти из полноэкранного режима", uz: "To‘liq ekrandan chiqish" },
  "kayıt bulundu": {
    en: "records found",
    ru: "записей найдено",
    uz: "ta yozuv topildi",
  },
  "Gösterilecek sütunlar": {
    en: "Visible columns",
    ru: "Показываемые столбцы",
    uz: "Ko‘rsatiladigan ustunlar",
  },
  "Sistem Sağlığı": {
    en: "System Health",
    ru: "Состояние системы",
    uz: "Tizim salomatligi",
  },
  "Tüm veriler güncel": {
    en: "All data is up to date",
    ru: "Все данные актуальны",
    uz: "Barcha ma’lumotlar yangilangan",
  },
  "Veri güncellemesi kontrol edilmeli": {
    en: "Data refresh needs attention",
    ru: "Необходимо проверить обновление данных",
    uz: "Ma’lumotlar yangilanishini tekshirish kerak",
  },
  "Satış verisi güncel değil": {
    en: "Sales data is not current",
    ru: "Данные о продажах не актуальны",
    uz: "Savdo ma’lumotlari yangilanmagan",
  },
  "Stok ve fiyat verisi güncel değil": {
    en: "Stock and price data is not current",
    ru: "Данные об остатках и ценах не актуальны",
    uz: "Qoldiq va narx ma’lumotlari yangilanmagan",
  },
  "Ziyaretçi verisi eksik": {
    en: "Visitor data is missing",
    ru: "Отсутствуют данные о посетителях",
    uz: "Tashrifchilar ma’lumoti yetishmayapti",
  },
  "Hedefler aktarılmamış": {
    en: "Targets have not been transferred",
    ru: "Цели не перенесены",
    uz: "Rejalar o‘tkazilmagan",
  },
  "Platform yayını geride kalmış": {
    en: "Platform publication is behind",
    ru: "Публикация платформы отстаёт",
    uz: "Platforma nashri ortda qolgan",
  },
  "Sistem bağlantılarından biri kontrol edilmeli": {
    en: "A system connection needs attention",
    ru: "Необходимо проверить одно из системных подключений",
    uz: "Tizim ulanishlaridan birini tekshirish kerak",
  },
  "Kullanıcı Yönetimi": {
    en: "User Management",
    ru: "Управление пользователями",
    uz: "Foydalanuvchilar boshqaruvi",
  },
  "Kullanıcı Oturumları": {
    en: "User Sessions",
    ru: "Сеансы пользователей",
    uz: "Foydalanuvchi sessiyalari",
  },
  "Oturum geçmişi": { en: "Session history", ru: "История сеансов", uz: "Sessiyalar tarixi" },
  "Yalnızca giriş, son görülme, çıkış ve yaklaşık süre kaydedilir. Sayfa, filtre veya işlem ayrıntıları izlenmez.": {
    en: "Only sign-in, last seen, sign-out and approximate duration are recorded. Pages, filters and actions are not tracked.",
    ru: "Сохраняются только вход, последняя активность, выход и примерная длительность. Страницы, фильтры и действия не отслеживаются.",
    uz: "Faqat kirish, oxirgi faollik, chiqish va taxminiy davomiylik saqlanadi. Sahifalar, filtrlar va amallar kuzatilmaydi.",
  },
  "Çevrimiçi": { en: "Online", ru: "В сети", uz: "Onlayn" },
  "Oturum": { en: "Sessions", ru: "Сеансы", uz: "Sessiya" },
  "Farklı kullanıcı": { en: "Unique users", ru: "Уникальные пользователи", uz: "Turli foydalanuvchi" },
  "Aktif kullanıcı": { en: "Active users", ru: "Активные пользователи", uz: "Faol foydalanuvchi" },
  "Ortalama süre": { en: "Average duration", ru: "Средняя длительность", uz: "O‘rtacha davomiylik" },
  "Tüm durumlar": { en: "All statuses", ru: "Все статусы", uz: "Barcha holatlar" },
  "Aktif oturumlar": { en: "Active sessions", ru: "Активные сеансы", uz: "Faol sessiyalar" },
  "Tamamlanmış oturumlar": { en: "Completed sessions", ru: "Завершённые сеансы", uz: "Yakunlangan sessiyalar" },
  "Son 7 gün": { en: "Last 7 days", ru: "Последние 7 дней", uz: "So‘nggi 7 kun" },
  "Son 30 gün": { en: "Last 30 days", ru: "Последние 30 дней", uz: "So‘nggi 30 kun" },
  "Son 90 gün": { en: "Last 90 days", ru: "Последние 90 дней", uz: "So‘nggi 90 kun" },
  "Tüm kullanıcılar": { en: "All users", ru: "Все пользователи", uz: "Barcha foydalanuvchilar" },
  "Giriş zamanı": { en: "Sign-in time", ru: "Время входа", uz: "Kirish vaqti" },
  "Son görülme / Çıkış": { en: "Last seen / Sign-out", ru: "Последняя активность / Выход", uz: "Oxirgi faollik / Chiqish" },
  "Oturum süresi": { en: "Session duration", ru: "Длительность сеанса", uz: "Sessiya davomiyligi" },
  "Kullanıcı Oturumlarını Excel’e İndir": { en: "Download User Sessions Excel", ru: "Скачать сеансы пользователей в Excel", uz: "Foydalanuvchi sessiyalarini Excelga yuklash" },
  "Oturum kapandı": { en: "Signed out", ru: "Сеанс завершён", uz: "Sessiya yopildi" },
  "Bağlantı sona erdi": { en: "Connection ended", ru: "Соединение завершено", uz: "Ulanish tugadi" },
  "Henüz oturum kaydı yok": { en: "No session records yet", ru: "Записей сеансов пока нет", uz: "Hali sessiya yozuvlari yo‘q" },
  "Kayıtlar 90 gün saklanır.": { en: "Records are retained for 90 days.", ru: "Записи хранятся 90 дней.", uz: "Yozuvlar 90 kun saqlanadi." },
  Yenile: { en: "Refresh", ru: "Обновить", uz: "Yangilash" },
  Sağlıklı: { en: "Healthy", ru: "Исправно", uz: "Sog‘lom" },
  "Kontrol gerekli": {
    en: "Check required",
    ru: "Требуется проверка",
    uz: "Tekshiruv kerak",
  },
  "Hedef Yok": { en: "No Target", ru: "Нет цели", uz: "Reja yo‘q" },
  "Excel hazırlanıyor…": {
    en: "Preparing Excel…",
    ru: "Подготовка Excel…",
    uz: "Excel tayyorlanmoqda…",
  },
  "Yükleniyor…": { en: "Loading…", ru: "Загрузка…", uz: "Yuklanmoqda…" },
  "Yenileniyor…": { en: "Refreshing…", ru: "Обновление…", uz: "Yangilanmoqda…" },
  "Bekleniyor": { en: "Pending", ru: "Ожидание", uz: "Kutilmoqda" },
  "Tümü": { en: "All", ru: "Все", uz: "Barchasi" },
  "TOPLAM": { en: "TOTAL", ru: "ИТОГО", uz: "JAMI" },
  "Net Ciro UZS": { en: "Net Revenue UZS", ru: "Чистая выручка UZS", uz: "Sof tushum UZS" },
  "Net ciro": { en: "Net revenue", ru: "Чистая выручка", uz: "Sof tushum" },
  "Net Adet": { en: "Net Units", ru: "Чистое количество", uz: "Sof dona" },
  "Net ürün": { en: "Net units", ru: "Чистое количество", uz: "Sof dona" },
  "Hedef UZS": { en: "Target UZS", ru: "Цель UZS", uz: "Reja UZS" },
  "Hedef %": { en: "Target %", ru: "Выполнение %", uz: "Reja %" },
  "Dönüşüm Oranı %": { en: "Conversion Rate %", ru: "Конверсия %", uz: "Konversiya %" },
  "Sepet Ort.": { en: "Avg. Basket", ru: "Средний чек", uz: "O‘rtacha savat" },
  "Birim Fiyat": { en: "Unit Price", ru: "Цена за единицу", uz: "Birlik narxi" },
  "Ciro Payı": { en: "Revenue Share", ru: "Доля выручки", uz: "Tushum ulushi" },
  "Ciro Payı %": { en: "Revenue Share %", ru: "Доля выручки %", uz: "Tushum ulushi %" },
  "LY Ciro UZS": { en: "LY Revenue UZS", ru: "Выручка ПГ UZS", uz: "O‘tgan yil tushumi UZS" },
  "Ciro %": { en: "Revenue %", ru: "Изменение выручки %", uz: "Tushum o‘zgarishi %" },
  "Geçen Yıl Ciro UZS": { en: "Last Year Revenue UZS", ru: "Выручка прошлого года UZS", uz: "O‘tgan yil tushumi UZS" },
  "Ciro Değişimi %": { en: "Revenue Change %", ru: "Изменение выручки %", uz: "Tushum o‘zgarishi %" },
  "Kalan Hedef UZS": { en: "Remaining Target UZS", ru: "Остаток цели UZS", uz: "Qolgan reja UZS" },
  "Satış Danışmanı": { en: "Sales Adviser", ru: "Консультант", uz: "Savdo maslahatchisi" },
  "Satış Danışmanı Performansı": { en: "Sales Adviser Performance", ru: "Эффективность консультантов", uz: "Savdo maslahatchisi samaradorligi" },
  "Mağaza Özeti": { en: "Store Summary", ru: "Сводка по магазинам", uz: "Do‘konlar xulosasi" },
  "Marka Özeti": { en: "Brand Summary", ru: "Сводка по брендам", uz: "Brendlar xulosasi" },
  "Alışveriş Merkezi Özeti": { en: "Shopping Mall Summary", ru: "Сводка по ТЦ", uz: "Savdo markazlari xulosasi" },
  "Günlük Satış": { en: "Daily Sales", ru: "Продажи по дням", uz: "Kunlik savdo" },
  "Saatlik Satış": { en: "Hourly Sales", ru: "Продажи по часам", uz: "Soatlik savdo" },
  "Aylık Performans": { en: "Monthly Performance", ru: "Месячная динамика", uz: "Oylik samaradorlik" },
  "Tarih": { en: "Date", ru: "Дата", uz: "Sana" },
  "Saat": { en: "Hour", ru: "Час", uz: "Soat" },
  "Ay": { en: "Month", ru: "Месяц", uz: "Oy" },
  "Gün": { en: "Day", ru: "День", uz: "Kun" },
  "Kategori": { en: "Category", ru: "Категория", uz: "Kategoriya" },
  "Renk Kodu": { en: "Colour Code", ru: "Код цвета", uz: "Rang kodi" },
  "Renk Adı": { en: "Colour Name", ru: "Название цвета", uz: "Rang nomi" },
  Renk: { en: "Colour", ru: "Цвет", uz: "Rang" },
  "Beden": { en: "Size", ru: "Размер", uz: "O‘lcham" },
  "İndirim %": { en: "Discount %", ru: "Скидка %", uz: "Chegirma %" },
  "Günlük Satış Adedi": { en: "Daily Sales Units", ru: "Продажи в день", uz: "Kunlik savdo donasi" },
  "Stok Günü": { en: "Stock Days", ru: "Дни запаса", uz: "Qoldiq kunlari" },
  "İlk İmport": { en: "First Import", ru: "Первый импорт", uz: "Birinchi import" },
  "Son İmport": { en: "Last Import", ru: "Последний импорт", uz: "Oxirgi import" },
  "Güncel Stok": { en: "Current Stock", ru: "Текущий запас", uz: "Joriy qoldiq" },
  "İlk Satış": { en: "First Sale", ru: "Первая продажа", uz: "Birinchi savdo" },
  "Son Satış": { en: "Last Sale", ru: "Последняя продажа", uz: "Oxirgi savdo" },
  "Satışsız Gün": { en: "Days Without Sale", ru: "Дни без продаж", uz: "Savdosiz kun" },
  "Ürün Performansı": { en: "Product Performance", ru: "Эффективность товара", uz: "Mahsulot samaradorligi" },
  "SKU + Renk Performansı": { en: "SKU + Colour Performance", ru: "SKU + цвет", uz: "SKU + rang samaradorligi" },
  "Beden Performansı": { en: "Size Performance", ru: "Размеры", uz: "O‘lcham samaradorligi" },
  "Renk Performansı": { en: "Colour Performance", ru: "Цвета", uz: "Rang samaradorligi" },
  "Yönetici Özetini Excel’e İndir": { en: "Download Executive Summary Excel", ru: "Скачать сводку в Excel", uz: "Rahbar xulosasini Excelga yuklash" },
  "Paylaşılan görünüm": { en: "Shared view", ru: "Общий вид", uz: "Ulashilgan ko‘rinish" },
  "Raporları Excel’e İndir": { en: "Download Reports Excel", ru: "Скачать отчёты Excel", uz: "Hisobotlarni Excelga yuklash" },
  "Excel’e İndir Hazırla": { en: "Prepare Excel Download", ru: "Подготовить Excel", uz: "Excel yuklamasini tayyorlash" },
  "Ay Sonu Tahmini": { en: "Month-End Forecast", ru: "Прогноз на конец месяца", uz: "Oy yakuni prognozi" },
  "Tahmin": { en: "Forecast", ru: "Прогноз", uz: "Prognoz" },
  "Veri son tarihine kadar günlük ortalama bazlı": { en: "Based on the daily average through the latest data date", ru: "На основе среднего за день до даты последних данных", uz: "Oxirgi ma’lumot sanasigacha bo‘lgan kunlik o‘rtacha asosida" },
  "Dönem trendi": { en: "Period trend", ru: "Тренд за период", uz: "Davr trendi" },
  "Aksiyon Merkezi": { en: "Action Centre", ru: "Центр действий", uz: "Harakatlar markazi" },
  "Seçili dönemde önce ilgilenilmesi gereken mağazalar": { en: "Stores that need attention first in the selected period", ru: "Магазины, требующие внимания в выбранном периоде", uz: "Tanlangan davrda birinchi navbatda e’tibor talab qiladigan do‘konlar" },
  "Öncelikli aksiyon": { en: "Priority action", ru: "Приоритетное действие", uz: "Ustuvor harakat" },
  "Aksiyon bulunmadı": { en: "No action found", ru: "Нет приоритетных действий", uz: "Ustuvor harakat topilmadi" },
  "Seçili dönemde belirlenen risk eşiklerini aşan mağaza bulunmuyor.": { en: "No store exceeds the defined risk thresholds for the selected period.", ru: "За выбранный период магазины не превышают установленные пороги риска.", uz: "Tanlangan davrda belgilangan xavf chegaralaridan oshgan do‘kon yo‘q." },
  "Hedef riski": { en: "Target risk", ru: "Риск по цели", uz: "Reja xavfi" },
  "Satış düşüşü": { en: "Sales decline", ru: "Снижение продаж", uz: "Savdo pasayishi" },
  "Dönüşüm fırsatı": { en: "Conversion opportunity", ru: "Возможность конверсии", uz: "Konversiya imkoniyati" },
  "Stok dengesi": { en: "Stock balance", ru: "Баланс запасов", uz: "Qoldiq muvozanati" },
  "Mağazayı incele": { en: "Review store", ru: "Открыть магазин", uz: "Do‘konni ko‘rish" },
  "Aksiyon Gerektiren Mağazalar": { en: "Stores Requiring Action", ru: "Магазины, требующие внимания", uz: "E’tibor talab qiladigan do‘konlar" },
  "Şirket genelinde CR, UPT ve satışı en düşük; LY’ye göre UPT ve satış değişimi en fazla düşen 3’er mağaza.": { en: "Company-wide bottom 3 stores by CR, UPT and sales, and the 3 largest UPT and sales percentage declines versus LY.", ru: "По 3 магазина компании с самыми низкими CR, UPT и продажами, а также с наибольшим процентным снижением UPT и продаж к прошлому году.", uz: "Kompaniya bo‘yicha CR, UPT va savdosi eng past hamda UPT va savdo foizi o‘tgan yilga nisbatan eng ko‘p pasaygan 3 tadan do‘kon." },
  "Aksiyon Mağazaları": { en: "Action Stores", ru: "Магазины для действий", uz: "Harakat talab qiladigan do‘konlar" },
  "Mağaza Adı": { en: "Store Name", ru: "Название магазина", uz: "Do‘kon nomi" },
  "Kriter": { en: "Criteria", ru: "Критерии", uz: "Mezon" },
  "LY Satış": { en: "LY Sales", ru: "Продажи ПГ", uz: "O‘tgan yil savdosi" },
  "vs LY Satış": { en: "Sales vs LY", ru: "Продажи к ПГ", uz: "Savdo o‘tgan yilga nisbatan" },
  "vs LY UPT": { en: "UPT vs LY", ru: "UPT к ПГ", uz: "UPT o‘tgan yilga nisbatan" },
  "Aksiyon Nedeni": { en: "Action Reason", ru: "Причина действия", uz: "Harakat sababi" },
  "Net satış; iade ve değişim etkileri düşüldükten sonra seçili dönemde oluşan cirodur.": { en: "Net sales is revenue in the selected period after returns and exchanges.", ru: "Чистые продажи — выручка за выбранный период после возвратов и обменов.", uz: "Sof savdo — qaytarish va almashtirishlardan keyingi tanlangan davr tushumi." },
  "UPT, net satılan ürün adedinin fiş sayısına bölünmesiyle hesaplanır.": { en: "UPT is calculated as net units sold divided by receipt count.", ru: "UPT рассчитывается как чистое количество проданных единиц, делённое на число чеков.", uz: "UPT sof sotilgan dona sonini cheklar soniga bo‘lish orqali hisoblanadi." },
  "CR, fiş sayısının ziyaretçi sayısına bölünmesiyle hesaplanır.": { en: "CR is calculated as receipt count divided by visitor count.", ru: "CR рассчитывается как число чеков, делённое на число посетителей.", uz: "CR cheklar sonini tashrifchilar soniga bo‘lish orqali hisoblanadi." },
  "Sellthrough, satılan adedin satılan adet ile güncel stok toplamına oranıdır.": { en: "Sellthrough is units sold divided by units sold plus current stock.", ru: "Sellthrough — это проданные единицы, делённые на сумму проданных единиц и текущего остатка.", uz: "Sellthrough sotilgan donani sotilgan dona va joriy qoldiq yig‘indisiga bo‘lish orqali hisoblanadi." },
  "Net satılan adet; iade ve değişim etkileri dahil ürün adedidir.": { en: "Net units sold includes the effects of returns and exchanges.", ru: "Чистое количество проданных единиц учитывает возвраты и обмены.", uz: "Sof sotilgan dona qaytarish va almashtirish ta’sirini hisobga oladi." },
  "Fiş sayısı, seçili dönemdeki benzersiz satış fişlerinin sayısıdır.": { en: "Receipt count is the number of unique sales receipts in the selected period.", ru: "Число чеков — количество уникальных чеков за выбранный период.", uz: "Cheklar soni tanlangan davrdagi noyob savdo cheklari sonidir." },
  "Sepet ortalaması, net cironun fiş sayısına bölünmesiyle hesaplanır.": { en: "Average basket is net sales divided by receipt count.", ru: "Средний чек рассчитывается как чистые продажи, делённые на число чеков.", uz: "O‘rtacha savat sof savdoni cheklar soniga bo‘lish orqali hisoblanadi." },
  "Ortalama birim fiyat, net cironun net satılan adede bölünmesiyle hesaplanır.": { en: "Average unit price is net sales divided by net units sold.", ru: "Средняя цена единицы рассчитывается как чистые продажи, делённые на чистое количество проданных единиц.", uz: "O‘rtacha birlik narxi sof savdoni sof sotilgan donaga bo‘lish orqali hisoblanadi." },
  "Şirket CR": { en: "Company CR", ru: "CR компании", uz: "Kompaniya CR" },
  "Şirket satış düşüşü": { en: "Company sales decline", ru: "Снижение продаж компании", uz: "Kompaniya savdo pasayishi" },
  "Şirket en düşük satış": { en: "Company lowest sales", ru: "Самые низкие продажи компании", uz: "Kompaniya eng past savdo" },
  "Marka CR": { en: "Brand CR", ru: "CR бренда", uz: "Brend CR" },
  "Şirket UPT": { en: "Company UPT", ru: "UPT компании", uz: "Kompaniya UPT" },
  "Marka UPT": { en: "Brand UPT", ru: "UPT бренда", uz: "Brend UPT" },
  "Şirket UPT düşüş": { en: "Company UPT decline", ru: "Снижение UPT компании", uz: "Kompaniya UPT pasayishi" },
  "Marka UPT düşüş": { en: "Brand UPT decline", ru: "Снижение UPT бренда", uz: "Brend UPT pasayishi" },
  "Acil": { en: "Urgent", ru: "Срочно", uz: "Shoshilinch" },
  "Öncelikli": { en: "Priority", ru: "Приоритет", uz: "Ustuvor" },
  "İzle": { en: "Monitor", ru: "Наблюдать", uz: "Kuzatish" },
  "mağaza": { en: "stores", ru: "магазинов", uz: "do‘kon" },
  "Aksiyon gerektiren mağaza bulunmuyor.": { en: "No store requiring action was found.", ru: "Магазины, требующие внимания, не найдены.", uz: "Harakat talab qiladigan do‘kon topilmadi." },
  "Hedef gerçekleşmesi": { en: "Target achievement", ru: "Выполнение цели", uz: "Reja bajarilishi" },
  "Kalan hedef": { en: "Remaining target", ru: "Остаток цели", uz: "Qolgan reja" },
  "Geçen yıla göre satış": { en: "Sales vs last year", ru: "Продажи к прошлому году", uz: "O‘tgan yilga nisbatan savdo" },
  "Yüksek ziyaretçiye rağmen dönüşüm": { en: "Conversion despite high traffic", ru: "Конверсия при высокой посещаемости", uz: "Yuqori tashrifga qaramay konversiya" },
  "Stok payı": { en: "Stock share", ru: "Доля запасов", uz: "Qoldiq ulushi" },
  "ciro payı": { en: "revenue share", ru: "доля выручки", uz: "tushum ulushi" },
  Yaz: { en: "Summer", ru: "Лето", uz: "Yoz" },
  Kış: { en: "Winter", ru: "Зима", uz: "Qish" },
  Önceki: { en: "Previous", ru: "Назад", uz: "Oldingi" },
  Sonraki: { en: "Next", ru: "Далее", uz: "Keyingi" },
  Ara: { en: "Search", ru: "Найти", uz: "Qidirish" },
  Sayfa: { en: "Page", ru: "Страница", uz: "Sahifa" },
  "Sezonda Biter": { en: "Sells Out This Season", ru: "Закончится в сезоне", uz: "Mavsumda tugaydi" },
  "Riskli": { en: "At Risk", ru: "Риск", uz: "Xavfli" },
  "Yeni": { en: "New", ru: "Новый", uz: "Yangi" },
  "Stok Bitti": { en: "Out of Stock", ru: "Нет остатка", uz: "Qoldiq tugagan" },
  "Durum, 2026 Kış sezon sonu 28.02.2027’ye göre hesaplanır.": { en: "Status is calculated against the 2026 Winter season end on 28 February 2027.", ru: "Статус рассчитывается относительно окончания зимнего сезона 2026 года — 28.02.2027.", uz: "Holat 2026 Qish mavsumi 28.02.2027 da tugashiga ko‘ra hisoblanadi." },
  "Durum Filtresi": { en: "Status Filter", ru: "Фильтр статуса", uz: "Holat filtri" },
  "Durumlar ne demek?": { en: "What do the statuses mean?", ru: "Что означают статусы?", uz: "Holatlar nimani anglatadi?" },
  "İlk importtan sonra henüz 7 gün dolmadı.": { en: "Fewer than 7 days have passed since the first import.", ru: "С первого импорта прошло менее 7 дней.", uz: "Birinchi importdan keyin hali 7 kun o‘tmadi." },
  "Bu satış hızıyla stok sezon bitmeden tükenir.": { en: "At this sales rate, stock will run out before the season ends.", ru: "При текущем темпе продаж запас закончится до конца сезона.", uz: "Bu savdo tezligida qoldiq mavsum tugashidan oldin tugaydi." },
  "Bu satış hızıyla 28.02.2027 tarihinde stok kalır.": { en: "At this sales rate, stock will remain on 28 February 2027.", ru: "При текущем темпе продаж на 28.02.2027 останется запас.", uz: "Bu savdo tezligida 28.02.2027 kuni qoldiq qoladi." },
  "Yeni ürün süresi geçti; stok var ancak satış hızı sıfır.": { en: "The new-product period has passed; stock remains but the sales rate is zero.", ru: "Период нового товара завершён; запас есть, но темп продаж равен нулю.", uz: "Yangi mahsulot davri o‘tdi; qoldiq bor, ammo savdo tezligi nol." },
  "Güncel stok sıfırdır.": { en: "Current stock is zero.", ru: "Текущий остаток равен нулю.", uz: "Joriy qoldiq nol." },
  "Satış Yok": { en: "No Sales", ru: "Нет продаж", uz: "Savdo yo‘q" },
  "Satışı olmayan ürünler indirim ortalamasına katılmaz.": { en: "Products without sales are excluded from the average discount.", ru: "Товары без продаж не включаются в среднюю скидку.", uz: "Savdosi bo‘lmagan mahsulotlar o‘rtacha chegirmaga kiritilmaydi." },
  "Sıfır satışlı ayları gizle": { en: "Hide months with zero sales", ru: "Скрыть месяцы без продаж", uz: "Savdosi nol bo‘lgan oylarni yashirish" },
  "Mağazanın Kategori Kırılımı": { en: "Store Category Breakdown", ru: "Категории магазина", uz: "Do‘kon kategoriya kesimi" },
  "Cinsiyet Dağılımı": { en: "Gender Distribution", ru: "Распределение по полу", uz: "Jins bo‘yicha taqsimot" },
  "Haftanın Günleri Performansı": { en: "Weekday Performance", ru: "Показатели по дням недели", uz: "Hafta kunlari samaradorligi" },
  "Mağazanın Alt Kategori Kırılımı": { en: "Store Subcategory Breakdown", ru: "Подкатегории магазина", uz: "Do‘kon quyi kategoriya kesimi" },
  "En az 2 karakter yazın": { en: "Enter at least 2 characters", ru: "Введите не менее 2 символов", uz: "Kamida 2 ta belgi kiriting" },
  gün: { en: "days", ru: "дн.", uz: "kun" },
  saat: { en: "hours", ru: "ч.", uz: "soat" },
  dk: { en: "min", ru: "мин.", uz: "daq." },
  "Veri son tarihi": { en: "Latest data date", ru: "Дата последних данных", uz: "Oxirgi ma’lumot sanasi" },
  "Seçili dönem": { en: "Selected period", ru: "Выбранный период", uz: "Tanlangan davr" },
  "Filtrelenen satır": { en: "Filtered rows", ru: "Отфильтровано строк", uz: "Filtrlangan qator" },
  "Temel Göstergeler": { en: "Key Metrics", ru: "Ключевые показатели", uz: "Asosiy ko‘rsatkichlar" },
  "Satış, hedef, stok ve ziyaretçi performansının tek görünümü.": { en: "A single view of sales, targets, stock and visitor performance.", ru: "Продажи, цели, остатки и посещаемость на одном экране.", uz: "Savdo, reja, qoldiq va tashriflar yagona ekranda." },
  "Seçili mağazaların satış, müşteri ve danışman performansı.": { en: "Sales, customer and adviser performance for selected stores.", ru: "Продажи, клиенты и консультанты выбранных магазинов.", uz: "Tanlangan do‘konlarning savdo, mijoz va maslahatchi samaradorligi." },
  "SKU, renk ve beden bazında satış, stok ve ürün yaşam döngüsü.": { en: "Sales, stock and product lifecycle by SKU, colour and size.", ru: "Продажи, остатки и жизненный цикл по SKU, цвету и размеру.", uz: "SKU, rang va o‘lcham bo‘yicha savdo, qoldiq va mahsulot hayot sikli." },
  "Ürün, renk, beden ve aylık adet inceleme modeli.": { en: "Product, colour, size and monthly unit planning.", ru: "Планирование по товарам, цветам, размерам и месяцам.", uz: "Mahsulot, rang, o‘lcham va oylik dona rejalashtirishi." },
  "Sistem sağlığı ve kullanıcı erişimlerinin güvenli yönetimi.": { en: "Secure management of system health and user access.", ru: "Безопасное управление состоянием системы и доступами.", uz: "Tizim holati va foydalanuvchi ruxsatlarini xavfsiz boshqarish." },
  "Rapor Bilgisi": { en: "Report Info", ru: "Информация", uz: "Hisobot ma’lumoti" },
  "Alan": { en: "Field", ru: "Поле", uz: "Maydon" },
  "Değer": { en: "Value", ru: "Значение", uz: "Qiymat" },
  "Rapor": { en: "Report", ru: "Отчёт", uz: "Hisobot" },
  "Geçen Yıl Dönemi": { en: "Last Year Period", ru: "Период прошлого года", uz: "O‘tgan yil davri" },
  "Tüm Ay Hedefi": { en: "Full-Month Target", ru: "Цель за весь месяц", uz: "To‘liq oy rejasi" },
  "Evet": { en: "Yes", ru: "Да", uz: "Ha" },
  "Hayır": { en: "No", ru: "Нет", uz: "Yo‘q" },
  "Satış Son Tarihi": { en: "Latest Sales Date", ru: "Дата последних продаж", uz: "Oxirgi savdo sanasi" },
  "Stok Son Tarihi": { en: "Latest Stock Date", ru: "Дата последних остатков", uz: "Oxirgi qoldiq sanasi" },
  "Import Son Tarihi": { en: "Latest Import Date", ru: "Дата последнего импорта", uz: "Oxirgi import sanasi" },
  "Ziyaretçi Son Tarihi": { en: "Latest Visitor Date", ru: "Дата последних посетителей", uz: "Oxirgi tashrif sanasi" },
  "Uygulanan Filtreler": { en: "Applied Filters", ru: "Применённые фильтры", uz: "Qo‘llangan filtrlar" },
  "Veri Paketi": { en: "Data Package", ru: "Пакет данных", uz: "Ma’lumot paketi" },
  "Filtre yok": { en: "No filters", ru: "Без фильтров", uz: "Filtr yo‘q" },
  "AVM Özeti": { en: "Shopping Mall Summary", ru: "Сводка по ТЦ", uz: "Savdo markazlari xulosasi" },
  "Seçili Markalar": { en: "Selected Brands", ru: "Выбранные бренды", uz: "Tanlangan brendlar" },
  "Markaları Değiştir": { en: "Change Brands", ru: "Изменить бренды", uz: "Brendlarni o‘zgartirish" },
  "Showroom filtreleri": { en: "Showroom filters", ru: "Фильтры шоурума", uz: "Showroom filtrlari" },
  "Ürün planlama kapsamı": { en: "Product planning scope", ru: "Область планирования", uz: "Mahsulot rejalashtirish doirasi" },
  "Filtreleri Temizle": { en: "Clear Filters", ru: "Сбросить фильтры", uz: "Filtrlarni tozalash" },
  "Toplam Adet": { en: "Total Units", ru: "Всего единиц", uz: "Jami dona" },
  "Aktif SKU": { en: "Active SKUs", ru: "Активные SKU", uz: "Faol SKU" },
  "Satış Yapılan Gün": { en: "Sales Days", ru: "Дней с продажами", uz: "Savdo qilingan kun" },
  "İlk / Son Satış": { en: "First / Last Sale", ru: "Первая / последняя продажа", uz: "Birinchi / oxirgi savdo" },
  "Ürün Hiyerarşisi": { en: "Product Hierarchy", ru: "Иерархия товаров", uz: "Mahsulot iyerarxiyasi" },
  "Ana Kategori → SKU → Renk → Beden seviyelerinde aylık adetler.": { en: "Monthly units by Main Category → SKU → Colour → Size.", ru: "Месячные количества по уровням Категория → SKU → Цвет → Размер.", uz: "Asosiy kategoriya → SKU → Rang → O‘lcham bo‘yicha oylik dona." },
  "kayıt": { en: "records", ru: "записей", uz: "yozuv" },
  "Showroom tablosunda ara…": { en: "Search the Showroom table…", ru: "Поиск в таблице шоурума…", uz: "Showroom jadvalidan qidiring…" },
  "Tümünü Aç": { en: "Expand All", ru: "Развернуть всё", uz: "Barchasini ochish" },
  "Tümünü Kapat": { en: "Collapse All", ru: "Свернуть всё", uz: "Barchasini yopish" },
  "Açık hiyerarşi": { en: "Open hierarchy", ru: "Открытая иерархия", uz: "Ochiq iyerarxiya" },
  "Henüz seçim yapılmadı": { en: "No selection yet", ru: "Пока ничего не выбрано", uz: "Hali tanlov qilinmagan" },
  "Hedef görünümü: seçili ayların tamamı": { en: "Target view: full selected months", ru: "Цель: выбранные месяцы целиком", uz: "Reja ko‘rinishi: tanlangan oylar to‘liq" },
  "Hedef görünümü: seçili gün sayısına göre": { en: "Target view: selected day count", ru: "Цель: по числу выбранных дней", uz: "Reja ko‘rinishi: tanlangan kunlar soni bo‘yicha" },
  "Mağaza seçimi": { en: "Store selection", ru: "Выбор магазина", uz: "Do‘kon tanlash" },
  "İncelenecek mağazaları seçin": { en: "Select stores to analyse", ru: "Выберите магазины для анализа", uz: "Tahlil qilinadigan do‘konlarni tanlang" },
  "Marka ve mağaza seçimleri soldaki tüm filtrelerle birlikte uygulanır.": { en: "Brand and store selections are applied together with all filters on the left.", ru: "Бренды и магазины применяются вместе со всеми фильтрами слева.", uz: "Brend va do‘kon tanlovi chapdagi barcha filtrlar bilan birga qo‘llanadi." },
  "Ürün filtreleri": { en: "Product filters", ru: "Фильтры товаров", uz: "Mahsulot filtrlari" },
  "SKU kodlarını seçin": { en: "Select SKU codes", ru: "Выберите коды SKU", uz: "SKU kodlarini tanlang" },
  "Yalnızca SKU kodu yazın. Birden fazla kodu satır, virgül veya boşlukla ayırabilirsiniz.": { en: "Enter SKU codes only. Separate multiple codes with lines, commas or spaces.", ru: "Вводите только SKU. Разделяйте коды строками, запятыми или пробелами.", uz: "Faqat SKU kodlarini kiriting. Bir nechta kodni satr, vergul yoki bo‘shliq bilan ajrating." },
  "SKU Filtresi": { en: "SKU Filter", ru: "Фильтр SKU", uz: "SKU filtri" },
  "SKU Filtresini Uygula": { en: "Apply SKU Filter", ru: "Применить фильтр SKU", uz: "SKU filtrini qo‘llash" },
  "Yönetim bilgileri açılamadı.": { en: "Administration data could not be loaded.", ru: "Не удалось загрузить данные управления.", uz: "Boshqaruv ma’lumotlari ochilmadi." },
  "Genel Durum": { en: "Overall Status", ru: "Общее состояние", uz: "Umumiy holat" },
  "Tüm temel servislerin anlık durumu": { en: "Current status of all core services", ru: "Текущее состояние основных сервисов", uz: "Barcha asosiy xizmatlarning joriy holati" },
  "Durum": { en: "Status", ru: "Состояние", uz: "Holat" },
  "Kaynak Oluşturma": { en: "Source Created", ru: "Создание источника", uz: "Manba yaratilishi" },
  "Doğrulanmış paketin kaynak zamanı": { en: "Source time of the verified package", ru: "Время источника проверенного пакета", uz: "Tasdiqlangan paket manba vaqti" },
  "API Çalışma Süresi": { en: "API Uptime", ru: "Время работы API", uz: "API ishlash vaqti" },
  "Sürüm": { en: "Release", ru: "Версия", uz: "Versiya" },
  "SERVİSLER": { en: "SERVICES", ru: "СЕРВИСЫ", uz: "XIZMATLAR" },
  "Bağlantı Durumu": { en: "Connection Status", ru: "Состояние подключений", uz: "Ulanish holati" },
  "Çalışıyor": { en: "Running", ru: "Работает", uz: "Ishlamoqda" },
  "Kontrol": { en: "Check", ru: "Проверить", uz: "Tekshirish" },
  "Hata": { en: "Error", ru: "Ошибка", uz: "Xato" },
  "Servis bilgileri yükleniyor…": { en: "Loading service status…", ru: "Загрузка состояния сервисов…", uz: "Xizmat holati yuklanmoqda…" },
  "VERİ PAKETİ": { en: "DATA PACKAGE", ru: "ПАКЕТ ДАННЫХ", uz: "MA’LUMOT PAKETI" },
  "Satır Kontrolü": { en: "Row Validation", ru: "Проверка строк", uz: "Qator tekshiruvi" },
  "Veri Durumu": { en: "Data Status", ru: "Состояние данных", uz: "Ma’lumot holati" },
  "Beklenen tarih": { en: "Expected date", ru: "Ожидаемая дата", uz: "Kutilgan sana" },
  "AVVA Karshi Outlet hariç satış": { en: "Sales excluding AVVA Karshi Outlet", ru: "Продажи без AVVA Karshi Outlet", uz: "AVVA Karshi Outletdan tashqari savdo" },
  "AVVA Karshi Outlet hariç": { en: "Excluding AVVA Karshi Outlet", ru: "Без AVVA Karshi Outlet", uz: "AVVA Karshi Outletdan tashqari" },
  "Tüm mağazalar": { en: "All stores", ru: "Все магазины", uz: "Barcha do‘konlar" },
  "Fark": { en: "Difference", ru: "Разница", uz: "Farq" },
  "Özet tarihi": { en: "Summary date", ru: "Дата сводки", uz: "Xulosa sanasi" },
  "Seçilen doğrulanmış gece paketi": { en: "Selected verified nightly package", ru: "Выбранный проверенный ночной пакет", uz: "Tanlangan tasdiqlangan tungi paket" },
  "Güncel doğrulanmış gece paketi": { en: "Current verified nightly package", ru: "Текущий проверенный ночной пакет", uz: "Joriy tasdiqlangan tungi paket" },
  "Geçmiş paket": { en: "Historical package", ru: "Архивный пакет", uz: "Tarixiy paket" },
  "Seçilen tarihin doğrulanmış paketi bulunamadı.": { en: "No verified package was found for the selected date.", ru: "Для выбранной даты не найден проверенный пакет.", uz: "Tanlangan sana uchun tasdiqlangan paket topilmadi." },
  "Geçmiş özet yükleniyor…": { en: "Loading historical summary…", ru: "Загрузка архивной сводки…", uz: "Tarixiy xulosa yuklanmoqda…" },
  "Stok ve fiyat": { en: "Stock and price", ru: "Остатки и цены", uz: "Qoldiq va narx" },
  "stok satırı": { en: "stock rows", ru: "строк остатков", uz: "qoldiq qatori" },
  "fiyat satırı": { en: "price rows", ru: "строк цен", uz: "narx qatori" },
  "Mağaza hedefleri": { en: "Store targets", ru: "Цели магазинов", uz: "Do‘kon rejalari" },
  "Doğrulanmış paket kayıtları": { en: "Verified package records", ru: "Записи проверенного пакета", uz: "Tasdiqlangan paket yozuvlari" },
  "Manuel import": { en: "Manual import", ru: "Ручной импорт", uz: "Qo‘lda import" },
  "Gece yenilemesini bekletmez": { en: "Does not block the nightly refresh", ru: "Не задерживает ночное обновление", uz: "Tungi yangilanishni kutib turmaydi" },
  "Teknik satır sayılarını göster": { en: "Show technical row counts", ru: "Показать технические строки", uz: "Texnik qator sonlarini ko‘rsatish" },
  "Paket satır bilgisi bekleniyor.": { en: "Waiting for package row details.", ru: "Ожидание данных о строках пакета.", uz: "Paket qator ma’lumotlari kutilmoqda." },
  "Canlı kullanıcılar korunuyor.": { en: "Live users are protected.", ru: "Пользователи рабочей системы защищены.", uz: "Jonli tizim foydalanuvchilari himoyalangan." },
  "Beta doğrulaması tamamlanana kadar kullanıcı ekleme, PIN değiştirme ve yetki düzenleme kapalıdır. Canlıya geçiş anında mevcut kullanıcılar ve marka yetkileri aynen taşınacaktır.": { en: "User creation, PIN changes and access edits remain disabled until beta validation is complete. Existing users and brand scopes will be preserved at cutover.", ru: "Создание пользователей, смена PIN и прав отключены до завершения проверки. При переходе пользователи и доступы сохранятся.", uz: "Beta tekshiruvi tugaguncha foydalanuvchi qo‘shish, PIN va ruxsatlarni o‘zgartirish yopiq. Jonli o‘tishda mavjud foydalanuvchilar va brend ruxsatlari saqlanadi." },
  "Kullanıcılar": { en: "Users", ru: "Пользователи", uz: "Foydalanuvchilar" },
  "kullanıcı ve erişim kapsamı": { en: "users and access scopes", ru: "пользователей и областей доступа", uz: "foydalanuvchi va ruxsat doirasi" },
  "Düzenleme açık": { en: "Editing enabled", ru: "Редактирование включено", uz: "Tahrirlash ochiq" },
  "Salt okunur": { en: "Read only", ru: "Только чтение", uz: "Faqat o‘qish" },
  "Kullanıcı": { en: "User", ru: "Пользователь", uz: "Foydalanuvchi" },
  "Ad Soyad": { en: "Full Name", ru: "Имя и фамилия", uz: "Ism familiya" },
  "Rol": { en: "Role", ru: "Роль", uz: "Rol" },
  "Marka Yetkisi": { en: "Brand Access", ru: "Доступ к брендам", uz: "Brend ruxsati" },
  "Yönetici": { en: "Administrator", ru: "Администратор", uz: "Administrator" },
  "Erişim var": { en: "Allowed", ru: "Доступ есть", uz: "Ruxsat bor" },
  "Erişim yok": { en: "Not allowed", ru: "Нет доступа", uz: "Ruxsat yo‘q" },
  "Aktif": { en: "Active", ru: "Активен", uz: "Faol" },
  "Pasif": { en: "Inactive", ru: "Неактивен", uz: "Nofaol" },
  "Showroom markaları": { en: "Showroom brands", ru: "Бренды шоурума", uz: "Showroom brendlari" },
  "Markalarınızı Seçin": { en: "Select Your Brands", ru: "Выберите бренды", uz: "Brendlarni tanlang" },
  "Bir veya birden fazla marka seçin. Ağır Showroom verisi yalnızca “Showroom’u Aç” düğmesine bastığınızda sunucudan alınır.": { en: "Select one or more brands. Heavy Showroom data is loaded only after you press “Open Showroom”.", ru: "Выберите один или несколько брендов. Тяжёлые данные загрузятся только после нажатия «Открыть шоурум».", uz: "Bir yoki bir nechta brendni tanlang. Og‘ir Showroom ma’lumotlari faqat “Showroomni ochish” tugmasidan keyin yuklanadi." },
  "Seçili markalar": { en: "Selected brands", ru: "Выбранные бренды", uz: "Tanlangan brendlar" },
  "Henüz marka seçilmedi": { en: "No brand selected yet", ru: "Бренд пока не выбран", uz: "Hali brend tanlanmagan" },
  "Hazırlanıyor…": { en: "Preparing…", ru: "Подготовка…", uz: "Tayyorlanmoqda…" },
  "Showroom’u Aç": { en: "Open Showroom", ru: "Открыть шоурум", uz: "Showroomni ochish" },
  "Showroom’u Excel’e İndir": { en: "Download Showroom Excel", ru: "Скачать Showroom в Excel", uz: "Showroomni Excelga yuklash" },
  "Seçili kapsamı Excel dosyası olarak indirin.": { en: "Download the selected scope as an Excel file.", ru: "Скачайте выбранный объём в формате Excel.", uz: "Tanlangan qamrovni Excel fayli sifatida yuklab oling." },
  "Seçili marka verileri hazırlanıyor…": { en: "Preparing selected brand data…", ru: "Подготовка данных выбранных брендов…", uz: "Tanlangan brend ma’lumotlari tayyorlanmoqda…" },
  "Hedefler OX’taki ilgili ayın kişisel hedeflerinden alınır; gerçekleşme dashboarddaki doğrulanmış net satışla hesaplanır. Ciro payı danışmanın kendi mağazasına göredir. Aynı fişte birden fazla danışman varsa fiş her danışmanda sayılır; toplamda tek sayılır.": { en: "Targets come from each adviser’s OX monthly target; achievement uses verified net sales. Revenue share is within the adviser’s store. A receipt shared by advisers counts for each adviser but once in the total.", ru: "Цели берутся из личных месячных целей OX; выполнение считается по проверенным чистым продажам. Доля выручки — в рамках магазина. Общий чек учитывается у каждого консультанта, но один раз в итоге.", uz: "Rejalar OXdagi oylik shaxsiy rejalardan olinadi; bajarilish tasdiqlangan sof savdo bilan hisoblanadi. Tushum ulushi maslahatchining do‘koni bo‘yicha. Bir chek bir nechta maslahatchida sanalsa ham umumiyda bir marta hisoblanadi." },
  "Ay aralığı": { en: "Month range", ru: "Диапазон месяцев", uz: "Oy oralig‘i" },
  "Bitiş ayını seçin": { en: "Select the end month", ru: "Выберите конечный месяц", uz: "Tugash oyini tanlang" },
  "Başlangıç ve bitiş ayını sırayla seçin": { en: "Select the start and end months", ru: "Выберите начальный и конечный месяцы", uz: "Boshlanish va tugash oylarini tanlang" },
  "SKU filtresi etkin.": { en: "SKU filter is active.", ru: "Фильтр SKU активен.", uz: "SKU filtri faol." },
  "Hedef ve ziyaretçi SKU bazında dağıtılamadığı için bu iki oran canlı SRR360 ile aynı şekilde gösterilmiyor.": { en: "Targets and visitors cannot be allocated by SKU, so those two rates are not shown at SKU scope.", ru: "Цели и посетители нельзя распределить по SKU, поэтому эти показатели не выводятся для SKU.", uz: "Reja va tashrifchilar SKU bo‘yicha taqsimlanmaydi, shu sababli bu ikki ko‘rsatkich SKU doirasida ko‘rsatilmaydi." },
  "Mağaza bazlı stok dağılımı hazırlanıyor.": { en: "Store-level stock allocation is being prepared.", ru: "Готовится распределение остатков по магазинам.", uz: "Do‘kon bo‘yicha qoldiq taqsimoti tayyorlanmoqda." },
  "Satış seçili mağazaya göre doğrudur; hatalı stok göstermemek için stok ve import bu kapsamda boş bırakılır.": { en: "Sales reflect the selected store; stock and import are left blank where allocation cannot be verified.", ru: "Продажи соответствуют выбранному магазину; остатки и импорт скрыты, если распределение нельзя подтвердить.", uz: "Savdo tanlangan do‘kon bo‘yicha to‘g‘ri; taqsimot tasdiqlanmasa qoldiq va import bo‘sh qoldiriladi." },
  "Veri açılamadı.": { en: "Data could not be loaded.", ru: "Не удалось загрузить данные.", uz: "Ma’lumot ochilmadi." },
  "Tekrar dene": { en: "Try Again", ru: "Повторить", uz: "Qayta urinish" },
  "Yeni kullanıcı": { en: "New User", ru: "Новый пользователь", uz: "Yangi foydalanuvchi" },
  "değiştirmek istemiyorsanız boş bırakın": { en: "leave blank to keep unchanged", ru: "оставьте пустым без изменения", uz: "o‘zgartirmaslik uchun bo‘sh qoldiring" },
  "İptal": { en: "Cancel", ru: "Отмена", uz: "Bekor qilish" },
  "Kaydet": { en: "Save", ru: "Сохранить", uz: "Saqlash" },
  "Kaydediliyor…": { en: "Saving…", ru: "Сохранение…", uz: "Saqlanmoqda…" },
  "İşlem": { en: "Action", ru: "Действие", uz: "Amal" },
  "Düzenle": { en: "Edit", ru: "Изменить", uz: "Tahrirlash" },
  "Adet %": { en: "Units %", ru: "Количество %", uz: "Dona %" },
  "Analiz edilecek marka veya markaları seçin.": { en: "Select one or more brands to analyse.", ru: "Выберите один или несколько брендов для анализа.", uz: "Tahlil qilinadigan bir yoki bir nechta brendni tanlang." },
  "Boş bırakırsanız seçili markanın tüm mağazaları kullanılır.": { en: "Leave blank to include every store for the selected brand.", ru: "Оставьте пустым, чтобы включить все магазины выбранного бренда.", uz: "Bo‘sh qoldirilsa, tanlangan brendning barcha do‘konlari olinadi." },
  "Ciro payı seçili döneme; stok payı güncel stok adedine aittir.": { en: "Revenue share uses the selected period; stock share uses current stock.", ru: "Доля выручки рассчитана за выбранный период, доля запаса — по текущему остатку.", uz: "Tushum ulushi tanlangan davrga, qoldiq ulushi joriy qoldiqqa tegishli." },
  "Excel’den SKU listesi ekle": { en: "Add an SKU list from Excel", ru: "Добавить список SKU из Excel", uz: "Exceldan SKU ro‘yxatini qo‘shish" },
  "Filtresi": { en: "Filter", ru: "Фильтр", uz: "filtri" },
  "Gün Sayısı": { en: "Day Count", ru: "Количество дней", uz: "Kunlar soni" },
  "Kayıt bulunamadı": { en: "No records found", ru: "Записи не найдены", uz: "Yozuv topilmadi" },
  "Liste Yapıştır": { en: "Paste List", ru: "Вставить список", uz: "Ro‘yxatni joylash" },
  "Listeyi Uygula": { en: "Apply List", ru: "Применить список", uz: "Ro‘yxatni qo‘llash" },
  "LY Adet": { en: "LY Units", ru: "Количество ПГ", uz: "O‘tgan yil donasi" },
  "Marka Seçimi": { en: "Brand Selection", ru: "Выбор бренда", uz: "Brend tanlovi" },
  "Net Ciro USD": { en: "Net Revenue USD", ru: "Чистая выручка USD", uz: "Sof tushum USD" },
  "Ortalama Ciro": { en: "Average Revenue", ru: "Средняя выручка", uz: "O‘rtacha tushum" },
  "seçili": { en: "selected", ru: "выбрано", uz: "tanlangan" },
  "seçenek": { en: "options", ru: "вариантов", uz: "variant" },
  "Marka seçimi AVM ve mağaza seçeneklerini daraltır.": { en: "Selecting a brand narrows the mall and store options.", ru: "Выбор бренда сужает список ТЦ и магазинов.", uz: "Brend tanlovi savdo markazi va do‘kon variantlarini qisqartiradi." },
  "Seçenekler seçili markaya göre gösteriliyor.": { en: "Options reflect the selected brand.", ru: "Варианты показаны для выбранного бренда.", uz: "Variantlar tanlangan brend bo‘yicha ko‘rsatilmoqda." },
  "Marka seçerseniz AVM listesi daralır.": { en: "Choose a brand to narrow the mall list.", ru: "Выберите бренд, чтобы сузить список ТЦ.", uz: "Savdo markazlari ro‘yxatini qisqartirish uchun brend tanlang." },
  "Seçenekler marka ve AVM seçimine göre gösteriliyor.": { en: "Options reflect the selected brand and mall.", ru: "Варианты показаны для выбранных бренда и ТЦ.", uz: "Variantlar tanlangan brend va savdo markazi bo‘yicha ko‘rsatilmoqda." },
  "Marka veya AVM seçerseniz mağaza listesi daralır.": { en: "Choose a brand or mall to narrow the store list.", ru: "Выберите бренд или ТЦ, чтобы сузить список магазинов.", uz: "Do‘konlar ro‘yxatini qisqartirish uchun brend yoki savdo markazini tanlang." },
  "SKU filtresinde yalnızca SKU kodları gösterilir.": { en: "Only SKU codes are shown in the SKU filter.", ru: "В фильтре SKU отображаются только коды SKU.", uz: "SKU filtrida faqat SKU kodlari ko‘rsatiladi." },
  "SKU sütununu kopyalayıp aşağıya yapıştırın. Tekrar eden kodlar otomatik ayıklanır.": { en: "Copy the SKU column and paste it below. Duplicate codes are removed automatically.", ru: "Скопируйте столбец SKU и вставьте ниже. Повторяющиеся коды удаляются автоматически.", uz: "SKU ustunini nusxalab quyiga joylang. Takroriy kodlar avtomatik olib tashlanadi." },
  "sonuç": { en: "results", ru: "результатов", uz: "natija" },
  "Stok Payı %": { en: "Stock Share %", ru: "Доля запаса %", uz: "Qoldiq ulushi %" },
  "Toplu SKU Ekle": { en: "Add SKUs in Bulk", ru: "Добавить SKU списком", uz: "SKUlarni ommaviy qo‘shish" },
  "Gezinme ve filtreler": { en: "Navigation and filters", ru: "Навигация и фильтры", uz: "Navigatsiya va filtrlar" },
  "Filtreler": { en: "Filters", ru: "Фильтры", uz: "Filtrlar" },
  "Hızlı filtreler": { en: "Quick filters", ru: "Быстрые фильтры", uz: "Tezkor filtrlar" },
  "Yönetici özeti bölümleri": { en: "Executive summary sections", ru: "Разделы сводки", uz: "Rahbar xulosasi bo‘limlari" },
  "Tablolar": { en: "Tables", ru: "Таблицы", uz: "Jadvallar" },
  "Diğer sütunlar için yatay kaydırın": { en: "Swipe sideways for more columns", ru: "Проведите в сторону, чтобы увидеть другие столбцы", uz: "Boshqa ustunlar uchun yon tomonga suring" },
  "Tüm bilgiler için satıra dokunun": { en: "Tap a row for all details", ru: "Нажмите строку, чтобы увидеть все данные", uz: "Barcha ma’lumotlar uchun qatorni bosing" },
  "Tüm veriler": { en: "All data", ru: "Все данные", uz: "Barcha ma’lumotlar" },
  "Kapat": { en: "Close", ru: "Закрыть", uz: "Yopish" },
  "Filtreleri aç": { en: "Open filters", ru: "Открыть фильтры", uz: "Filtrlarni ochish" },
  "Filtreleri kapat": { en: "Close filters", ru: "Закрыть фильтры", uz: "Filtrlarni yopish" },
  "Görünüm": { en: "View", ru: "Вид", uz: "Ko‘rinish" },
  "Ana sayfalar": { en: "Main pages", ru: "Основные страницы", uz: "Asosiy sahifalar" },
  "Özet": { en: "Overview", ru: "Обзор", uz: "Umumiy" },
  "Detay": { en: "Detail", ru: "Детали", uz: "Batafsil" },
  "Ürün": { en: "Product", ru: "Товар", uz: "Mahsulot" },
  "Showroom ve Raporlar": { en: "Showroom & Reports", ru: "Шоурум и отчёты", uz: "Showroom va hisobotlar" },
  Raporlar: { en: "Reports", ru: "Отчёты", uz: "Hisobotlar" },
  Güncel: { en: "Current", ru: "Текущий", uz: "Joriy" },
  "Rapor Filtreleri": { en: "Report Filters", ru: "Фильтры отчёта", uz: "Hisobot filtrlari" },
  "Marka, AVM, mağaza ve tarih seçiminize göre tüm toplamlar yeniden hesaplanır.": {
    en: "All totals are recalculated for the selected brand, mall, store and dates.",
    ru: "Все итоги пересчитываются по выбранным бренду, ТЦ, магазину и датам.",
    uz: "Barcha jami qiymatlar tanlangan brend, savdo markazi, do‘kon va sanalar bo‘yicha qayta hisoblanadi.",
  },
  "Raporu Güncelle": { en: "Update Report", ru: "Обновить отчёт", uz: "Hisobotni yangilash" },
  "Mağaza Hedef": { en: "Store Target", ru: "Цель магазина", uz: "Do‘kon rejasi" },
  Müşteri: { en: "Customers", ru: "Покупатели", uz: "Mijozlar" },
  "Satış Adet": { en: "Units Sold", ru: "Продано единиц", uz: "Sotilgan dona" },
  "Genel Toplam": { en: "Grand Total", ru: "Общий итог", uz: "Umumiy jami" },
  Toplam: { en: "Total", ru: "Итого", uz: "Jami" },
};
const LanguageContext = createContext<LanguageCode>("tr");
function translate(language: LanguageCode, text: string) {
  return withoutUzsLabel(language === "tr" ? text : ((language === "ru" || language === "uz" ? localeCopy[text]?.[language] : undefined) ?? uiTranslations[text]?.[language] ?? localizeRuntimeText(language,text) ?? text));
}
function useT() {
  const language = useContext(LanguageContext);
  return (text: string) => translate(language, text);
}

const emptyFilters: Filters = {
  brands: [],
  stores: [],
  malls: [],
  main_categories: [],
  subcategories: [],
  genders: [],
  seasons: [],
  skus: [],
};
const emptySeasonSellthrough: SeasonSellthrough = {
  import_units: 0,
  sales_units: 0,
  stock_units: 0,
  sellthrough_percent: null,
};
const filterDefinitions: { key: FilterKey; label: string; icon: string }[] = [
  { key: "brands", label: "Marka", icon: "🏷️" },
  { key: "stores", label: "Mağaza", icon: "🏬" },
  { key: "malls", label: "Alışveriş Merkezi", icon: "🏢" },
  { key: "main_categories", label: "Ana Kategori", icon: "📦" },
  { key: "subcategories", label: "Alt Kategori", icon: "📁" },
  { key: "genders", label: "Cinsiyet", icon: "👤" },
  { key: "seasons", label: "Sezon", icon: "🗓️" },
];
const periods: { id: PeriodId; label: string }[] = [
  { id: "yesterday", label: "Dün" },
  { id: "week", label: "Bu Hafta" },
  { id: "month", label: "Bu Ay" },
  { id: "lastMonth", label: "Geçen Ay" },
  { id: "year", label: "Bu Yıl" },
  { id: "all", label: "Tüm Dönem" },
  { id: "custom", label: "Özel Tarih" },
];
const showroomPeriods: { id: ShowroomPeriodId; label: string }[] = [
  { id: "month", label: "Bu Ay" },
  { id: "lastMonth", label: "Geçen Ay" },
  { id: "last3", label: "Son 3 Ay" },
  { id: "last6", label: "Son 6 Ay" },
  { id: "year", label: "Bu Yıl" },
  { id: "lastYear", label: "Geçen Yıl" },
  { id: "all", label: "Tüm Dönem" },
  { id: "custom", label: "Özel Tarih" },
];
const integer = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const compactNumber = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 });
const numericLabelCollator = new Intl.Collator("tr", { numeric: true });
const decimal = new Intl.NumberFormat("tr-TR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const oneDecimal = new Intl.NumberFormat("tr-TR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const turkishFilterCollator = new Intl.Collator("tr-TR", {
  sensitivity: "base",
  numeric: true,
});

function sortFilterOptions(options: string[]) {
  return [...options].sort((left, right) => turkishFilterCollator.compare(left.trim(), right.trim()));
}

function tashkentDate(date: Date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}
function fallbackPeriod() {
  const completedDay = new Date(Date.now() - 86400000);
  const end = tashkentDate(completedDay);
  return { start: `${end.slice(0, 8)}01`, end };
}
function displayDate(value?: string) {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${day}.${month}.${year}`;
}
function displaySessionDate(value: string, language: LanguageCode) {
  const locale = { tr: "tr-TR", en: "en-GB", ru: "ru-RU", uz: "uz-UZ" }[language];
  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
function displaySessionDuration(value: number, language: LanguageCode) {
  const totalMinutes = Math.max(0, Math.floor(value / 60));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [
    days ? `${days} ${translate(language, "gün")}` : "",
    hours ? `${hours} ${translate(language, "saat")}` : "",
    !days && minutes ? `${minutes} ${translate(language, "dk")}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : `<1 ${translate(language, "dk")}`;
}
function sessionRangeDate(daysAgo: number) {
  return tashkentDate(new Date(Date.now() - daysAgo * 86_400_000));
}
function displayMonth(value?: string, language: LanguageCode = "tr") {
  if (!value) return "—";
  const [year, month] = value.slice(0, 7).split("-").map(Number);
  if (!year || !month) return value;
  const localized = localizedCalendarLabel(language, "month", month - 1);
  if (localized) return `${localized} ${year}`;
  const locale = { tr: "tr-TR", en: "en-US", ru: "ru-RU", uz: "uz-UZ" }[language];
  const label = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(new Date(year, month - 1, 1, 12));
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}
function displayWeekday(value?: string, language: LanguageCode = "tr") {
  if (!value) return "—";
  const localized = localizedCalendarLabel(language, "weekday", dateAtNoon(value).getDay());
  if (localized) return localized;
  const locale = { tr: "tr-TR", en: "en-US", ru: "ru-RU", uz: "uz-UZ" }[language];
  const label = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(dateAtNoon(value));
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}
function freshnessClass(value?: string, reference?: string) {
  if (!value) return "is-missing";
  return reference && value.slice(0, 10) < reference.slice(0, 10) ? "is-stale" : "";
}
function ratio(current: number, previous: number) {
  return previous ? ((current - previous) / Math.abs(previous)) * 100 : null;
}
function changeText(value: number | null) {
  const formatted = value === null ? "" : oneDecimal.format(value);
  return value === null ? "Karşılaştırma yok" : `${value >= 0 ? "+" : ""}${formatted}% vs LY`;
}
function compactUzs(value: number) {
  const absolute = Math.abs(value);
  const compactDecimal = compactNumber;
  if (absolute >= 1_000_000_000) return `${compactDecimal.format(value / 1_000_000_000)} mlr`;
  if (absolute >= 1_000_000) return `${compactDecimal.format(value / 1_000_000)} mln`;
  return integer.format(value);
}
function simulatorUzs(value: number) {
  const absolute = Math.abs(value);
  if (absolute >= 1_000_000_000) {
    return `${decimal.format(absolute / 1_000_000_000)} mlr`;
  }
  if (absolute >= 1_000_000) {
    return `${compactNumber.format(absolute / 1_000_000)} mln`;
  }
  return `${integer.format(Math.round(absolute))}`;
}
function signedSimulatorUzs(value: number) {
  return `${value >= 0 ? "+" : "−"}${simulatorUzs(value)}`;
}
function projectedMonthEnd(value: number, start: string, end: string, period: PeriodId) {
  if (period !== "month" || !start || !end || start.slice(0, 7) !== end.slice(0, 7) || !start.endsWith("-01")) return null;
  const finalDay = Number(monthEnd(end.slice(0, 7)).slice(-2));
  const elapsed = Math.round((dateAtNoon(end).getTime() - dateAtNoon(start).getTime()) / 86400000) + 1;
  return elapsed > 0 && elapsed < finalDay ? (value / elapsed) * finalDay : null;
}
function dateAtNoon(value: string) {
  return new Date(`${value}T12:00:00`);
}
function isoDate(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}
function periodRange(period: PeriodId, min: string, max: string) {
  const end = dateAtNoon(max);
  let start = new Date(end);
  if (period === "week") start.setDate(end.getDate() - ((end.getDay() + 6) % 7));
  if (period === "month") start = new Date(end.getFullYear(), end.getMonth(), 1, 12);
  if (period === "lastMonth") {
    start = new Date(end.getFullYear(), end.getMonth() - 1, 1, 12);
    end.setDate(0);
  }
  if (period === "year") start = new Date(end.getFullYear(), 0, 1, 12);
  if (period === "all") return { start: min, end: max };
  return { start: isoDate(start), end: isoDate(end) };
}
function showroomPeriodRange(period: ShowroomPeriodId, min: string, max: string) {
  const end = dateAtNoon(max);
  let start = new Date(end);
  if (period === "month") start = new Date(end.getFullYear(), end.getMonth(), 1, 12);
  if (period === "lastMonth") {
    start = new Date(end.getFullYear(), end.getMonth() - 1, 1, 12);
    end.setDate(0);
  }
  if (period === "last3") start = new Date(end.getFullYear(), end.getMonth() - 2, 1, 12);
  if (period === "last6") start = new Date(end.getFullYear(), end.getMonth() - 5, 1, 12);
  if (period === "year") start = new Date(end.getFullYear(), 0, 1, 12);
  if (period === "lastYear") {
    start = new Date(end.getFullYear() - 1, 0, 1, 12);
    end.setFullYear(end.getFullYear() - 1, 11, 31);
  }
  if (period === "all") return { start: min, end: max };
  const nextStart = isoDate(start) < min ? min : isoDate(start);
  const nextEnd = isoDate(end) > max ? max : isoDate(end);
  return nextStart <= nextEnd ? { start: nextStart, end: nextEnd } : { start: min, end: max };
}
function monthKeysBetween(min: string, max: string) {
  const values: string[] = [];
  const cursor = new Date(`${min.slice(0, 7)}-01T12:00:00`);
  const finish = max.slice(0, 7);
  while (isoDate(cursor).slice(0, 7) <= finish) {
    values.push(isoDate(cursor).slice(0, 7));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return values;
}
function monthEnd(value: string) {
  const date = new Date(`${value}-01T12:00:00`);
  return isoDate(new Date(date.getFullYear(), date.getMonth() + 1, 0, 12));
}
function targetProgressBenchmark(asOfDate: string, fullMonthTarget: boolean) {
  if (!fullMonthTarget) return 100;
  const asOfDay = Number(asOfDate.slice(8, 10));
  const monthDayCount = Number(monthEnd(asOfDate.slice(0, 7)).slice(8, 10));
  return Number.isFinite(asOfDay) && Number.isFinite(monthDayCount) && monthDayCount > 0
    ? Math.min(100, Math.max(0, (asOfDay / monthDayCount) * 100))
    : 100;
}
function metricTargetProgressBenchmark(firstColumn: string, rowLabel: string, asOfDate: string, fullMonthTarget: boolean, authoritativeRate?: number | null) {
  if (authoritativeRate != null) return authoritativeRate;
  if (!fullMonthTarget) return 100;
  if (firstColumn !== "Ay") return targetProgressBenchmark(asOfDate, true);
  return rowLabel.slice(0, 7) === asOfDate.slice(0, 7) ? targetProgressBenchmark(asOfDate, true) : 100;
}
function expectedCompletedDataDate() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(Date.now() - 86_400_000));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}
function expectedNightPackageDate() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const currentDate = `${values.year}-${values.month}-${values.day}`;
  const currentMinute = Number(values.hour) * 60 + Number(values.minute);
  if (currentMinute >= 45) return currentDate;
  const previousDate = dateAtNoon(currentDate);
  previousDate.setDate(previousDate.getDate() - 1);
  return isoDate(previousDate);
}
function showroomColorKey(row: ShowroomLeafRow) {
  return `${row.sku} | ${row.color_code || "—"}${row.color_name ? ` - ${row.color_name}` : ""}`;
}
function selectedValues(element: HTMLSelectElement) {
  return Array.from(element.selectedOptions).map((option) => option.value);
}
function reportParams(start: string, end: string, fullMonthTarget: boolean, filters: Filters, lyComparison: LyComparison = "same_date") {
  const params = new URLSearchParams({
    start,
    end,
    full_month_target: String(fullMonthTarget),
    ly_comparison: lyComparison,
  });
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length) params.set(key, (key === "genders" ? expandGenderFilterValues(values) : values).join(","));
  });
  return params;
}
function filterCatalogParams(filters: Filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length) params.set(key, (key === "genders" ? expandGenderFilterValues(values) : values).join(","));
  });
  return params;
}

type SharedViewState = {
  page: "executive" | "store" | "product";
  period: PeriodId;
  start: string;
  end: string;
  fullMonthTarget: boolean;
  filters: Filters;
  productPeriodMode?: ProductPeriodMode;
};

const shareablePages: SharedViewState["page"][] = ["executive", "store", "product"];
const validPeriods: PeriodId[] = ["yesterday", "week", "month", "lastMonth", "year", "all", "custom"];

function sharedViewFromUrl(catalog: FilterCatalog): SharedViewState | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get("shared") !== "1") return null;
  const requestedPage = params.get("view") as SharedViewState["page"] | null;
  const requestedPeriod = params.get("period") as PeriodId | null;
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  const rawStart = params.get("start") ?? "";
  const rawEnd = params.get("end") ?? "";
  const fallback = periodRange("month", catalog.min_date, catalog.max_date);
  const boundedStart = datePattern.test(rawStart) ? (rawStart < catalog.min_date ? catalog.min_date : rawStart > catalog.max_date ? catalog.max_date : rawStart) : fallback.start;
  const boundedEnd = datePattern.test(rawEnd) ? (rawEnd < catalog.min_date ? catalog.min_date : rawEnd > catalog.max_date ? catalog.max_date : rawEnd) : fallback.end;
  const range = boundedStart <= boundedEnd ? { start: boundedStart, end: boundedEnd } : fallback;
  const filters = (Object.keys(emptyFilters) as (keyof Filters)[]).reduce<Filters>((result, key) => {
    const raw = params.get(`filter_${key}`);
    if (!raw) return result;
    try {
      const values = JSON.parse(raw);
      if (!Array.isArray(values)) return result;
      if (key === "skus") {
        result[key] = values.filter((value): value is string => typeof value === "string" && value.length > 0 && value.length <= 128).slice(0, 250);
        return result;
      }
      const available = new Set(catalog[key]);
      result[key] = values.filter((value): value is string => typeof value === "string" && available.has(value));
    } catch {
      result[key] = [];
    }
    return result;
  }, { ...emptyFilters });
  return {
    page: requestedPage && shareablePages.includes(requestedPage) ? requestedPage : "executive",
    period: requestedPeriod && validPeriods.includes(requestedPeriod) ? requestedPeriod : "custom",
    start: range.start,
    end: range.end,
    fullMonthTarget: params.get("full_month_target") !== "0",
    filters,
    productPeriodMode: params.get("product_period") === "lifecycle" ? "lifecycle" : "selected",
  };
}

function sharedViewUrl(state: SharedViewState) {
  const url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set("shared", "1");
  url.searchParams.set("view", state.page);
  url.searchParams.set("period", state.period);
  url.searchParams.set("start", state.start);
  url.searchParams.set("end", state.end);
  url.searchParams.set("full_month_target", state.fullMonthTarget ? "1" : "0");
  if (state.page === "product") url.searchParams.set("product_period", state.productPeriodMode ?? "lifecycle");
  (Object.entries(state.filters) as [keyof Filters, string[]][]).forEach(([key, values]) => {
    if (values.length) url.searchParams.set(`filter_${key}`, JSON.stringify(values));
  });
  return url.toString();
}

type KpiIconName = "sales" | "upt" | "conversion" | "sellthrough" | "units" | "receipts" | "basket" | "price";

function KpiSparkline({ points, formatValue }: { points: KpiTrendPoint[]; formatValue: (value: number) => string }) {
  const t = useT();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const clean = points.filter((point) => Number.isFinite(point.value));
  if (clean.length < 2) return null;
  const width = 150;
  const height = 30;
  const padding = 2;
  const minimum = Math.min(...clean.map((point) => point.value));
  const maximum = Math.max(...clean.map((point) => point.value));
  const range = maximum - minimum || 1;
  const coordinates = clean.map((point, index) => {
    const x = padding + (index / (clean.length - 1)) * (width - padding * 2);
    const y = height - padding - ((point.value - minimum) / range) * (height - padding * 2);
    return [x, y] as const;
  });
  const line = coordinates.map(([x, y], index) => `${index ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${coordinates[coordinates.length - 1][0].toFixed(1)},${height} L${coordinates[0][0].toFixed(1)},${height} Z`;
  const last = coordinates[coordinates.length - 1];
  const active = activeIndex == null ? null : clean[activeIndex];
  const activePoint = activeIndex == null ? null : coordinates[activeIndex];
  return (
    <div className="kpi-sparkline" aria-label={t("Dönem trendi")} onMouseLeave={() => setActiveIndex(null)}>
      <div className="kpi-sparkline__chart">
        {active && activePoint ? (
          <div
            className="kpi-sparkline__tooltip"
            style={{ left: `${Math.min(88, Math.max(12, (activePoint[0] / width) * 100))}%` }}
            role="status"
          >
            <span>{displayDate(active.label)}</span>
            <strong>{formatValue(active.value)}</strong>
          </div>
        ) : null}
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <path className="kpi-sparkline__area" d={area} />
          <path className="kpi-sparkline__line" d={line} />
          {coordinates.map(([x, y], index) => (
            <circle
              className="kpi-sparkline__day-point"
              cx={x}
              cy={y}
              r="1.2"
              key={`day-${clean[index].label}-${index}`}
            />
          ))}
          <circle className="kpi-sparkline__point" cx={activePoint?.[0] ?? last[0]} cy={activePoint?.[1] ?? last[1]} r="2.4" />
          {coordinates.map(([x, y], index) => (
            <circle
              className="kpi-sparkline__hit"
              cx={x}
              cy={y}
              r="7"
              key={`${clean[index].label}-${index}`}
              tabIndex={0}
              aria-label={`${displayDate(clean[index].label)}: ${formatValue(clean[index].value)}`}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onBlur={() => setActiveIndex(null)}
            />
          ))}
        </svg>
      </div>
    </div>
  );
}

function KpiIcon({ name }: { name: KpiIconName }) {
  const icons: Record<KpiIconName, string> = {
    sales: "/kpi-sales.svg",
    upt: "/kpi-upt.svg",
    conversion: "/kpi-conversion.svg",
    sellthrough: "/kpi-sellthrough.svg",
    units: "/kpi-units.svg",
    receipts: "/kpi-receipts.svg",
    basket: "/kpi-basket.svg",
    price: "/kpi-price.svg",
  };
  return (
    <span className={`kpi-icon kpi-icon--${name}`} aria-hidden="true">
      <img src={icons[name]} alt="" />
    </span>
  );
}

function KpiLabel({ label, description }: { label: string; description: string }) {
  const t = useT();
  const language = useContext(LanguageContext);
  const desktop = useDesktopInformation();
  const polished = useExecutiveKpiPresentation();
  const originalTitle = (desktop ? desktopKpiTitle(label, language) : undefined) ?? t(label);
  const title = executiveKpiTitle(label, originalTitle, t, polished);
  return (
    <span className="kpi-label" tabIndex={0} aria-label={`${title}. ${t(description)}`}>
      <span>{title}</span>
      <CircleHelp aria-hidden="true" size={13} strokeWidth={2} />
      <span className="kpi-label__tooltip" role="tooltip">{t(description)}</span>
    </span>
  );
}

function KpiCard({ label, description, value, detail, previous, icon, trend = [], trendValueFormatter = integer.format }: { label: string; description: string; value: string; detail: string; previous?: string; icon: KpiIconName; trend?: KpiTrendPoint[]; trendValueFormatter?: (value: number) => string }) {
  const t = useT();
  const detailTone = detail.trim().startsWith("+") ? " is-positive" : detail.trim().startsWith("-") ? " is-negative" : "";
  const TrendIcon = detail.trim().startsWith("+") ? ArrowUpRight : detail.trim().startsWith("-") ? ArrowDownRight : null;
  return (
    <article className={`kpi-card kpi-card--${icon}`}>
      <div className="kpi-card__head">
        <p className="kpi-card__title"><KpiLabel label={label} description={description} /></p>
        <KpiIcon name={icon} />
      </div>
      <strong>{value}</strong>
      <KpiSparkline points={trend} formatValue={trendValueFormatter} />
      <div className="kpi-card__meta">
        <span className={`kpi-change${detailTone}`}>{TrendIcon ? <TrendIcon size={13} strokeWidth={2.3} /> : null}{t(detail)}</span>
        {previous ? (
          <small className="kpi-card__previous">
            <span>{t("Geçen yıl")}</span>
            <b>{previous}</b>
          </small>
        ) : null}
      </div>
    </article>
  );
}

type CompositeKpiMetric = {
  label: string;
  description: string;
  value: string;
  detail: string;
  previous?: string;
  contextLabel?: string;
  contextValue?: string;
  icon: KpiIconName;
};

function CompositeKpiMetricView({ metric }: { metric: CompositeKpiMetric }) {
  const t = useT();
  const detailTone = metric.detail.trim().startsWith("+") ? " is-positive" : metric.detail.trim().startsWith("-") ? " is-negative" : "";
  const TrendIcon = metric.detail.trim().startsWith("+") ? ArrowUpRight : metric.detail.trim().startsWith("-") ? ArrowDownRight : null;
  return (
    <section className={`kpi-composite__metric kpi-composite__metric--${metric.icon}`}>
      <div className="kpi-composite__head">
        <p className="kpi-card__title"><KpiLabel label={metric.label} description={metric.description} /></p>
        <KpiIcon name={metric.icon} />
      </div>
      <div className="kpi-composite__primary">
        <strong>{metric.value}</strong>
        {metric.contextLabel && metric.contextValue ? (
          <span className="kpi-composite__context">
            <small>{t(metric.contextLabel)}</small>
            <b>{metric.contextValue}</b>
          </span>
        ) : null}
      </div>
      <div className="kpi-composite__meta">
        <span className={`kpi-change${detailTone}`}>{TrendIcon ? <TrendIcon size={13} strokeWidth={2.3} /> : null}<ExecutiveKpiChange original={metric.detail} translated={t(metric.detail)} /></span>
        {metric.previous ? (
          <small className="kpi-card__previous">
            <ExecutiveKpiPreviousLabel original={t("Geçen yıl")} />
            <b>{metric.previous}</b>
          </small>
        ) : null}
      </div>
    </section>
  );
}

function DualKpiCard({ className, left, right }: { className: string; left: CompositeKpiMetric; right: CompositeKpiMetric }) {
  return (
    <article className={`kpi-card kpi-composite-card kpi-dual-card ${className}`}>
      <CompositeKpiMetricView metric={left} />
      <CompositeKpiMetricView metric={right} />
    </article>
  );
}

function VerticalKpiCard({ top, bottom }: { top: CompositeKpiMetric; bottom: CompositeKpiMetric }) {
  return (
    <article className="kpi-card kpi-composite-card kpi-vertical-card">
      <CompositeKpiMetricView metric={top} />
      <CompositeKpiMetricView metric={bottom} />
    </article>
  );
}

type ExecutiveInsightView = "pace" | "drivers" | "simulator";

const executiveInsightViews: ExecutiveInsightView[] = ["pace", "simulator", "drivers"];

function ExecutiveInsightPanel({ summary, period }: { summary: ExecutiveSummary | null; period: PeriodId }) {
  const t = useT();
  const [view, setView] = useState<ExecutiveInsightView>("pace");
  const [simulatorFocus, setSimulatorFocus] = useState<SimulatorFocus>("balanced");
  const touchStartX = useRef<number | null>(null);
  const current = summary?.current;
  const previous = summary?.previous_year;
  const hasTarget = Boolean(summary?.target_and_visitor_scope_supported && summary.target_uzs > 0);
  const achievement = hasTarget && summary ? (summary.current.net_sales_uzs / summary.target_uzs) * 100 : null;
  const expected = hasTarget && summary ? summary.target_progress_percent : null;
  const remaining = hasTarget && summary ? Math.max(summary.target_uzs - summary.current.net_sales_uzs, 0) : null;
  const forecast = summary ? projectedMonthEnd(summary.current.net_sales_uzs, summary.period.start, summary.period.end, period) : null;
  const expectedSalesToDate = expected != null && summary ? summary.target_to_date_uzs : null;
  const paceAmountGap = expectedSalesToDate != null && current ? current.net_sales_uzs - expectedSalesToDate : null;
  const monthDayCount = summary ? Number(monthEnd(summary.period.end.slice(0, 7)).slice(-2)) : 0;
  const elapsedMonthDay = summary ? Number(summary.period.end.slice(8, 10)) : 0;
  const remainingTargetDays = summary?.full_month_target ? Math.max(monthDayCount - elapsedMonthDay, 0) : 0;
  const dailyRequired = remaining != null
    ? (remainingTargetDays > 0 ? remaining / remainingTargetDays : remaining)
    : null;
  const roundedPaceGap = achievement != null && expected != null
    ? Number(achievement.toFixed(1)) - Number(expected.toFixed(1))
    : null;
  const dateLabel = summary ? `${displayDate(summary.period.start)} – ${displayDate(summary.period.end)}` : "—";
  const previousDateLabel = summary ? `${displayDate(summary.previous_year_period.start)} – ${displayDate(summary.previous_year_period.end)}` : "—";
  const signedPercent = (value: number | null) => value == null ? "—" : `${value >= 0 ? "+" : "−"}${oneDecimal.format(Math.abs(value))}%`;
  const signedInteger = (value: number) => `${value >= 0 ? "+" : "−"}${integer.format(Math.abs(Math.round(value)))}`;

  const canExplain = Boolean(
    current && previous && previous.net_sales_uzs > 0 && previous.receipts > 0 && previous.net_units > 0,
  );
  const currentUpt = current && current.receipts > 0 ? current.net_units / current.receipts : 0;
  const previousUpt = previous && previous.receipts > 0 ? previous.net_units / previous.receipts : 0;
  const previousBasket = previous && previous.receipts > 0 ? previous.net_sales_uzs / previous.receipts : 0;
  const previousUnitPrice = previous && previous.net_units > 0 ? previous.net_sales_uzs / previous.net_units : 0;
  const currentUnitPrice = current && current.net_units > 0 ? current.net_sales_uzs / current.net_units : previousUnitPrice;
  const netChange = current && previous ? current.net_sales_uzs - previous.net_sales_uzs : 0;
  const receiptEffect = current && previous ? Math.trunc((current.receipts - previous.receipts) * previousBasket) : 0;
  const uptEffect = current && previous ? Math.round(current.receipts * (currentUpt - previousUpt) * previousUnitPrice) : 0;
  const priceEffect = netChange - receiptEffect - uptEffect;
  const driverRows = canExplain && current && previous ? [
    {
      label: "Fiş Sayısı",
      previous: integer.format(previous.receipts),
      current: integer.format(current.receipts),
      change: ratio(current.receipts, previous.receipts),
      effect: receiptEffect,
    },
    {
      label: "UPT",
      previous: decimal.format(previousUpt),
      current: decimal.format(currentUpt),
      change: ratio(currentUpt, previousUpt),
      effect: uptEffect,
    },
    {
      label: "Ortalama Fiyat",
      previous: integer.format(previousUnitPrice),
      current: integer.format(currentUnitPrice),
      change: ratio(currentUnitPrice, previousUnitPrice),
      effect: priceEffect,
    },
  ] : [];
  const maxEffect = Math.max(1, ...driverRows.map((row) => Math.abs(row.effect)));
  const positiveDrivers = driverRows.filter((row) => row.effect >= 0).sort((a, b) => b.effect - a.effect);
  const negativeDrivers = driverRows.filter((row) => row.effect < 0).sort((a, b) => a.effect - b.effect);
  const netChangePercent = current && previous ? ratio(current.net_sales_uzs, previous.net_sales_uzs) : null;
  const positiveDriver = [...driverRows].filter((row) => row.effect > 0).sort((a, b) => b.effect - a.effect)[0];
  const negativeDriver = [...driverRows].filter((row) => row.effect < 0).sort((a, b) => a.effect - b.effect)[0];
  const driverInsight = positiveDriver && negativeDriver
    ? `${t(positiveDriver.label)} ${t("ciroyu destekledi")}; ${t(negativeDriver.label)} ${t("en büyük baskıyı oluşturdu")}.`
    : positiveDriver
      ? `${t("Ciro değişiminin tamamı pozitif katkılardan geldi")}.`
      : negativeDriver
        ? `${t("Ciro değişiminin tamamı negatif katkılardan geldi")}.`
        : t("Geçen yıl verisi bulunamadı");
  const paceStatus = roundedPaceGap == null
    ? t("Hedef verisi yok")
    : `${oneDecimal.format(Math.abs(roundedPaceGap))} ${t("puan")} ${t(roundedPaceGap >= 0 ? "önde" : "geride")}`;

  const currentCr = current?.conversion_rate_percent ?? 0;
  const simulatorBaseline = current?.net_sales_uzs ?? 0;
  const simulatorGap = Math.max(0, -(paceAmountGap ?? 0));
  const simulatorTarget = simulatorBaseline + simulatorGap;
  const simulatorReady = Boolean(
    period === "month"
    && summary?.target_and_visitor_scope_supported
    && paceAmountGap != null
    && simulatorBaseline > 0
    && simulatorTarget > 0
    && currentCr > 0
    && currentUpt > 0,
  );
  const simulation = calculateTargetGapSimulation(simulatorBaseline, simulatorGap, currentCr, currentUpt, simulatorFocus);
  const { suggestedCr, suggestedUpt, crContribution, uptContribution, additionalSales } = simulation;
  const operationalGuidance = calculateTargetPlanOperationalGuidance(currentCr, suggestedCr, currentUpt, suggestedUpt);
  const planMarginLabel = integer.format(TARGET_PLAN_SAFETY_RATE * 100);
  const planCalculationLabel = t("{gap} açık + %{rate} plan payı")
    .replace("{gap}", t(compactUzs(simulatorGap)))
    .replace("{rate}", planMarginLabel);
  const currentViewIndex = executiveInsightViews.indexOf(view);
  const showPrevious = () => setView(executiveInsightViews[Math.max(0, currentViewIndex - 1)]);
  const showNext = () => setView(executiveInsightViews[Math.min(executiveInsightViews.length - 1, currentViewIndex + 1)]);
  const finishSwipe = (clientX: number) => {
    if (touchStartX.current == null) return;
    const distance = clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(distance) < 44) return;
    if (distance < 0) showNext();
    else showPrevious();
  };

  return (
    <article
      className={`executive-insight-panel is-${view}`}
      onTouchStart={(event) => { touchStartX.current = event.touches[0]?.clientX ?? null; }}
      onTouchEnd={(event) => finishSwipe(event.changedTouches[0]?.clientX ?? 0)}
    >
      <header className="executive-insight-panel__head">
        <span className="executive-insight-panel__icon" aria-hidden="true">
          {view === "pace" ? <Target size={19} strokeWidth={2.1} /> : view === "drivers" ? <TrendingUp size={19} strokeWidth={2.1} /> : <SlidersHorizontal size={19} strokeWidth={2.1} />}
        </span>
        <div>
          <h2>{t(view === "pace" ? "Hedef Durumu" : view === "simulator" ? "Hedefe Ulaşma Planı" : "Satışı Ne Değiştirdi?")}</h2>
          <p>{view === "drivers" ? `${dateLabel} • ${t("Geçen yıl")}: ${previousDateLabel}` : dateLabel}</p>
        </div>
        <nav className="executive-insight-panel__pager" aria-label={t("Analiz sayfaları")}>
          <button type="button" onClick={showPrevious} disabled={currentViewIndex === 0} aria-label={t("Önceki analiz")}>
            <ChevronLeft aria-hidden="true" size={16} />
          </button>
          <span><b>{currentViewIndex + 1}</b> / {executiveInsightViews.length}</span>
          <button type="button" onClick={showNext} disabled={currentViewIndex === executiveInsightViews.length - 1} aria-label={t("Sonraki analiz")}>
            <ChevronRight aria-hidden="true" size={16} />
          </button>
        </nav>
      </header>

      {view === "pace" ? (
        <div className="target-pace-panel executive-insight-panel__body" key="pace">
          {achievement == null || expected == null || summary == null ? (
            <div className="executive-insight-panel__empty">
              <Target aria-hidden="true" size={24} />
              <strong>{t("Hedef verisi yok")}</strong>
            </div>
          ) : (
            <>
              <div className="target-pace-panel__main">
                <div className="target-pace-panel__gauge-wrap">
                  <div
                    className="target-pace-panel__gauge"
                    style={{ background: `conic-gradient(from -90deg, #12aa9c 0deg ${Math.min(100, Math.max(0, achievement)) * 3.6}deg, #e4ecef ${Math.min(100, Math.max(0, achievement)) * 3.6}deg 360deg)` }}
                    role="img"
                    aria-label={`${t("Gerçekleşen")}: ${oneDecimal.format(achievement)}%`}
                  >
                    <div>
                      <strong>{oneDecimal.format(achievement)}%</strong>
                      <span>{t("Gerçekleşen")}</span>
                    </div>
                  </div>
                  <div className="target-pace-panel__expected">
                    <i aria-hidden="true" />
                    <strong>{oneDecimal.format(expected)}%</strong>
                    <span>{t("Olması gereken")}</span>
                  </div>
                </div>
                <div className="target-pace-panel__details">
                  <span className={`target-pace-panel__status ${(roundedPaceGap ?? 0) >= 0 ? "is-positive" : "is-negative"}`}>
                    <Target aria-hidden="true" size={14} />
                    {paceStatus}
                  </span>
                  <div className="target-pace-panel__detail">
                    <span>{t((paceAmountGap ?? 0) >= 0 ? "Bugüne Kadar Fazla" : "Bugüne Kadar Eksik")}</span>
                    <strong>{integer.format(Math.abs(Math.round(paceAmountGap ?? 0)))}</strong>
                  </div>
                  <div className="target-pace-panel__detail">
                    <span>{t("Kalan hedef")}</span>
                    <strong>{integer.format(Math.round(remaining ?? 0))}</strong>
                  </div>
                </div>
              </div>
              <div className="target-pace-panel__footer">
                <span className="is-monthly-target">
                  <i aria-hidden="true"><Target size={16} /></i>
                  <small>{t(summary.period.start.slice(0, 7) === summary.period.end.slice(0, 7) ? "Aylık hedef" : "Dönem hedefi")}</small>
                  <strong title={integer.format(Math.round(summary.target_uzs))}>{integer.format(Math.round(summary.target_uzs))}</strong>
                </span>
                <span>
                  <i aria-hidden="true"><CalendarDays size={16} /></i>
                  <small>{t("Günlük gereken")}</small>
                  <strong title={dailyRequired == null ? "—" : `${integer.format(Math.round(dailyRequired))}`}>{dailyRequired == null ? "—" : `${integer.format(Math.round(dailyRequired))}`}</strong>
                </span>
                <span>
                  <i aria-hidden="true"><TrendingUp size={16} /></i>
                  <small>{t("Ay Sonu Tahmini")}</small>
                  <strong>{forecast == null ? "—" : `≈ ${t(compactUzs(forecast))}`}</strong>
                </span>
              </div>
            </>
          )}
        </div>
      ) : view === "drivers" ? (
        <div className="revenue-drivers-panel executive-insight-panel__body" key="drivers">
          {driverRows.length === 0 || !current || !previous ? (
            <div className="executive-insight-panel__empty">
              <TrendingUp aria-hidden="true" size={24} />
              <strong>{t("Geçen yıl verisi bulunamadı")}</strong>
            </div>
          ) : (
            <>
              <div className="revenue-drivers-panel__net">
                <div>
                  <span>{t("Net Satış Değişimi")}</span>
                  <strong>{signedInteger(netChange)}</strong>
                </div>
                <b className={netChange >= 0 ? "is-positive" : "is-negative"}>{signedPercent(netChangePercent)}</b>
              </div>
              <div className="revenue-driver-groups">
                {[
                  { label: "Ciroyu Arttıranlar", tone: "positive", rows: positiveDrivers },
                  { label: "Ciroyu Azaltanlar", tone: "negative", rows: negativeDrivers },
                ].map((group) => (
                  <section className={`revenue-driver-group is-${group.tone}`} key={group.label}>
                    <h3>{t(group.label)}</h3>
                    <div className="revenue-driver-group__list">
                      {group.rows.length ? group.rows.map((row) => (
                        <div className="revenue-driver-item" key={row.label}>
                          <div className="revenue-driver-item__head">
                            <strong>{t(row.label)}</strong>
                            <b>{signedInteger(row.effect)}</b>
                          </div>
                          <div className="revenue-driver-item__meta">
                            <span>{row.previous} → {row.current}</span>
                            <em>{signedPercent(row.change)}</em>
                          </div>
                          <i aria-hidden="true"><span style={{ width: `${Math.max(8, (Math.abs(row.effect) / maxEffect) * 100)}%` }} /></i>
                        </div>
                      )) : <span className="revenue-driver-group__empty">—</span>}
                    </div>
                  </section>
                ))}
              </div>
              <p className="revenue-drivers-panel__insight">{driverInsight}</p>
            </>
          )}
        </div>
      ) : (
        <div className="target-simulator-panel executive-insight-panel__body" key="simulator">
          <div className="target-simulator-panel__focus" role="group" aria-label={t("Odak")}>
            <span>{t("Odak")}</span>
            {([
              ["balanced", "Eşit Ağırlık"],
              ["cr", "CR"],
              ["upt", "UPT"],
            ] as [SimulatorFocus, string][]).map(([focus, label]) => (
              <button
                type="button"
                className={simulatorFocus === focus ? "is-active" : ""}
                aria-pressed={simulatorFocus === focus}
                onClick={() => setSimulatorFocus(focus)}
                key={focus}
              >
                {t(label)}
              </button>
            ))}
          </div>
          {!simulatorReady ? (
            <div className="executive-insight-panel__empty">
              <SlidersHorizontal aria-hidden="true" size={24} />
              <strong>{t(period === "month" ? "Simülasyon için hedef ve ziyaretçi verisi gerekli" : "Simülasyon Bu Ay döneminde kullanılabilir")}</strong>
            </div>
          ) : (
            <>
              <div className="target-simulator-panel__main">
                <section className="target-simulator-panel__recommendations">
                  {[
                    {
                      key: "cr",
                      label: "CR",
                      current: `${oneDecimal.format(currentCr)}%`,
                      suggested: `${oneDecimal.format(suggestedCr)}%`,
                      contribution: crContribution,
                      increase: oneDecimal.format(operationalGuidance.crIncreasePoints),
                      increaseSuffix: "%",
                      operation: operationalGuidance.visitorsPerExtraReceipt == null
                        ? (simulatorFocus === "upt" ? t("Bu odakta değişmez") : t("Artış gerekmiyor"))
                        : t("Her {count} ziyaretçide yaklaşık 1 ek fiş").replace("{count}", integer.format(operationalGuidance.visitorsPerExtraReceipt)),
                      enabled: simulatorFocus !== "upt",
                    },
                    {
                      key: "upt",
                      label: "UPT",
                      current: decimal.format(currentUpt),
                      suggested: decimal.format(suggestedUpt),
                      contribution: uptContribution,
                      increase: decimal.format(operationalGuidance.uptIncreasePerReceipt),
                      increaseSuffix: ` ${t("ürün/fiş")}`,
                      operation: operationalGuidance.receiptsPerExtraUnit == null
                        ? (simulatorFocus === "cr" ? t("Bu odakta değişmez") : t("Artış gerekmiyor"))
                        : t("Her {count} fişte 1 ek ürün").replace("{count}", integer.format(operationalGuidance.receiptsPerExtraUnit)),
                      enabled: simulatorFocus !== "cr",
                    },
                  ].map((item) => (
                    <div className={`target-simulator-metric ${item.enabled ? "is-enabled" : "is-fixed"}`} key={item.key}>
                      <div className="target-simulator-metric__row">
                        <strong>{item.label}</strong>
                        <b>{item.current} <ArrowRight aria-hidden="true" size={14} /> <em>{item.suggested}</em></b>
                        <span>
                          <strong>{t(signedSimulatorUzs(item.contribution))}</strong>
                        </span>
                      </div>
                      <i aria-hidden="true"><span style={{ width: item.enabled ? (simulatorFocus === "balanced" ? "68%" : "84%") : "18%" }} /></i>
                      <div className="target-simulator-metric__calculation">
                        <span><strong>+{item.increase}{item.increaseSuffix}</strong></span>
                        <span className="target-simulator-metric__calculation-divider" aria-hidden="true" />
                        <span>{item.operation}</span>
                      </div>
                    </div>
                  ))}
                </section>
                <section className="target-simulator-panel__result">
                  <div className="target-simulator-result__metric">
                    <span>{t("Bugüne Kadarki Açık")}</span>
                    <strong>{t(compactUzs(simulatorGap))}</strong>
                  </div>
                  <div className="target-simulator-result__metric">
                    <span>{t("Planlanan Ek Satış")}</span>
                    <strong>{t(compactUzs(additionalSales))}</strong>
                    <small>{planCalculationLabel}</small>
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      )}
      <nav className="executive-insight-panel__dots" aria-label={t("Analiz sayfaları")}>
        {executiveInsightViews.map((insightView, index) => (
          <button
            type="button"
            key={insightView}
            className={view === insightView ? "is-active" : ""}
            aria-label={`${index + 1} / ${executiveInsightViews.length}`}
            aria-current={view === insightView ? "page" : undefined}
            onClick={() => setView(insightView)}
          />
        ))}
      </nav>
    </article>
  );
}

function SalesKpiCard({ current, previous, target, forecast, trend = [], targetDetailsInInsight = false }: { current: number; previous: number; target: number; forecast: number | null; trend?: KpiTrendPoint[]; targetDetailsInInsight?: boolean }) {
  const t = useT();
  const change = changeText(ratio(current, previous));
  const tone = change.startsWith("+") ? " is-positive" : change.startsWith("-") ? " is-negative" : "";
  const TrendIcon = change.startsWith("+") ? ArrowUpRight : change.startsWith("-") ? ArrowDownRight : null;
  const targetRate = target > 0 ? (current / target) * 100 : null;
  return (
    <article className={`kpi-card kpi-card--sales sales-kpi-card${forecast == null ? "" : " has-forecast"}${targetDetailsInInsight ? " target-details-in-insight" : ""}`}>
      <div className="kpi-card__head">
        <p className="kpi-card__title"><KpiLabel label="Satış" description="Net satış; iade ve değişim etkileri düşüldükten sonra seçili dönemde oluşan cirodur." /></p>
        <KpiIcon name="sales" />
      </div>
      <div className="sales-kpi-card__primary">
        <div className="sales-kpi-card__value">
          <strong>{integer.format(current)}</strong>
          {targetRate == null ? null : (
            <b
              className={`kpi-target-rate${targetRate >= 100 ? " is-positive" : " is-negative"}`}
              title={`${t("Hedef %")}: ${oneDecimal.format(targetRate)}%`}
            >
              {oneDecimal.format(targetRate)}%
            </b>
          )}
        </div>
        {forecast == null ? null : (
          <div className="kpi-forecast" title={t("Veri son tarihine kadar günlük ortalama bazlı")}>
            <span>{t("Tahmin").toLocaleUpperCase()}</span>
            <b>≈ {t(compactUzs(forecast))}</b>
          </div>
        )}
      </div>
      {targetRate == null ? null : (
        <div className="kpi-target">
          <span>{t("Hedef")}</span>
          <strong>{integer.format(target)}</strong>
        </div>
      )}
      <KpiSparkline points={trend} formatValue={(value) => `${integer.format(value)}`} />
      <div className="kpi-card__meta">
        <span className={`kpi-change${tone}`}>{TrendIcon ? <TrendIcon size={13} strokeWidth={2.3} /> : null}<ExecutiveKpiChange original={change} translated={t(change)} /></span>
        <small className="kpi-card__previous">
          <ExecutiveKpiPreviousLabel original={t("Geçen yıl")} />
          <b>{integer.format(previous)}</b>
        </small>
      </div>
    </article>
  );
}

function ConversionKpiCard({ current, previous, visitors, supported, trend = [] }: { current: number | null; previous: number | null; visitors: number; supported: boolean; trend?: KpiTrendPoint[] }) {
  const t = useT();
  const change = supported && current != null && previous != null ? changeText(ratio(current, previous)) : "Karşılaştırma yok";
  const tone = change.startsWith("+") ? " is-positive" : change.startsWith("-") ? " is-negative" : "";
  return (
    <article className="kpi-card kpi-card--conversion cr-card">
      <div className="kpi-card__head">
        <p className="kpi-card__title"><KpiLabel label="CR" description="CR, fiş sayısının ziyaretçi sayısına bölünmesiyle hesaplanır." /></p>
        <KpiIcon name="conversion" />
      </div>
      <div className="cr-kpi-main">
        <strong>{supported && current != null ? `${oneDecimal.format(current)}%` : "—"}</strong>
        <div>
          <span>{t("Ziyaretçi")}</span>
          <b>{integer.format(visitors)}</b>
        </div>
      </div>
      <KpiSparkline points={trend} formatValue={(value) => `${oneDecimal.format(value)}%`} />
      <div className="kpi-card__meta">
        <span className={`kpi-change${tone}`}>{t(change)}</span>
        {previous == null ? null : (
          <small className="kpi-card__previous">
            <span>{t("Geçen yıl")}</span>
            <b>{oneDecimal.format(previous)}%</b>
          </small>
        )}
      </div>
    </article>
  );
}

function SellthroughCard({ summer, winter }: { summer: SeasonSellthrough; winter: SeasonSellthrough }) {
  const t = useT();
  const polished = useExecutiveKpiPresentation();
  const block = (label: string, values: SeasonSellthrough) => (
    <div className="sellthrough-season">
      <span>{label}</span>
      <strong>{values.sellthrough_percent == null ? "—" : `${oneDecimal.format(values.sellthrough_percent)}%`}</strong>
      <div>
        <span>{t("İmport")}</span>
        <b>{integer.format(values.import_units)}</b>
      </div>
      <div>
        <span>{t(polished ? "Adet" : "Satış")}</span>
        <b>{integer.format(values.sales_units)}</b>
      </div>
      <div>
        <span>{t("Stok")}</span>
        <b>{integer.format(values.stock_units)}</b>
      </div>
    </div>
  );
  return (
    <article className="kpi-card kpi-card--sellthrough sellthrough-card">
      <div className="kpi-card__head">
        <p className="kpi-card__title"><KpiLabel label="Sellthrough" description="Sellthrough, satılan adedin satılan adet ile güncel stok toplamına oranıdır." /></p>
        <KpiIcon name="sellthrough" />
      </div>
      <div className="sellthrough-grid">
        {block(`2026 ${t("Yaz").toLocaleUpperCase()}`, summer)}
        {block(`2026 ${t("Kış").toLocaleUpperCase()}`, winter)}
      </div>
    </article>
  );
}

type FilterPopoverVariant = "sidebar" | "main";

function FloatingFilterPopover({
  open,
  onOpenChange,
  trigger,
  variant,
  side = "bottom",
  align = "start",
  title,
  status,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactElement;
  variant: FilterPopoverVariant;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  title: string;
  status: string;
  children: React.ReactNode;
}) {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={`filter-popover filter-popover--${variant}`}
          side={side}
          align={align}
          sideOffset={4}
          collisionPadding={8}
          sticky="partial"
          aria-label={`${title}, ${status}`}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function FilterSearch({ value, disabled, placeholder, onChange }: { value: string; disabled: boolean; placeholder: string; onChange: (value: string) => void }) {
  return (
    <div className="filter-popover__search-wrap">
      <Search aria-hidden="true" size={16} strokeWidth={2} />
      <Command.Input className="filter-popover__search" value={value} disabled={disabled} placeholder={placeholder} onValueChange={onChange} autoFocus />
    </div>
  );
}

function FilterOptionList({ options, values, disabled, emptyText, onToggle }: { options: string[]; values: string[]; disabled: boolean; emptyText: string; onToggle: (value: string) => void }) {
  return (
    <Command.List className="filter-popover__list">
      {!options.length ? <Command.Empty className="filter-popover__empty">{emptyText}</Command.Empty> : null}
      <Command.Group>
        {options.map((option) => {
          const selected = values.includes(option);
          return (
            <Command.Item className="filter-popover__option" value={option} key={option} disabled={disabled} data-selected-value={selected || undefined} onSelect={() => onToggle(option)}>
              <span className="filter-popover__check" aria-hidden="true">{selected ? <Check size={14} strokeWidth={2.4} /> : null}</span>
              <span>{option}</span>
            </Command.Item>
          );
        })}
      </Command.Group>
    </Command.List>
  );
}

type ExecutiveMobileQuickFilterId = "period" | "brands" | "stores" | "malls";

function ExecutiveMobileFilterTrigger({
  open,
  onOpenChange,
  icon,
  label,
  value,
  disabled = false,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: React.ReactNode;
  label: string;
  value: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("mobile-quick-filter-open");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("mobile-quick-filter-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onOpenChange, open]);

  return (
    <>
      <button type="button" className={open ? "is-open" : ""} aria-expanded={open} aria-haspopup="dialog" disabled={disabled} onClick={() => onOpenChange(!open)}>
        {icon}
        <span><small>{label}</small><strong>{value}</strong></span>
        <ChevronDown aria-hidden="true" size={14} />
      </button>
      {open ? (
        <div className="executive-mobile-filter-sheet-layer" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) onOpenChange(false);
        }}>
          <section className="executive-mobile-filter-popover" role="dialog" aria-modal="true" aria-label={label}>
            <div className="executive-mobile-filter-popover__handle" aria-hidden="true" />
          {children}
          </section>
        </div>
      ) : null}
    </>
  );
}

function ExecutiveMobileMultiFilter({
  filterId,
  open,
  onOpenChange,
  icon,
  label,
  summary,
  values,
  options,
  disabled,
  context,
  onApply,
}: {
  filterId: "brands" | "stores" | "malls";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: React.ReactNode;
  label: string;
  summary: string;
  values: string[];
  options: string[];
  disabled: boolean;
  context?: string;
  onApply: (values: string[]) => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<string[]>(values);
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (!open) return;
    setDraft(values);
    setQuery("");
  }, [open, values]);
  const normalizedQuery = query.trim().toLocaleLowerCase("tr");
  const filteredOptions = useMemo(
    () => sortFilterOptions(options).filter((option) => !normalizedQuery || option.toLocaleLowerCase("tr").includes(normalizedQuery)),
    [normalizedQuery, options],
  );
  const toggle = (option: string) => {
    setDraft((current) => current.includes(option)
      ? current.filter((value) => value !== option)
      : sortFilterOptions([...current, option]));
  };
  return (
    <ExecutiveMobileFilterTrigger open={open} onOpenChange={onOpenChange} icon={icon} label={label} value={summary} disabled={disabled}>
      <div className="executive-mobile-filter-popover__heading">
        <span><strong>{label}</strong><small>{draft.length ? `${draft.length} ${t("seçili")}` : t("Tümü")} · {integer.format(options.length)} {t("seçenek")}</small></span>
        <button type="button" aria-label={t("Kapat")} onClick={() => onOpenChange(false)}><X aria-hidden="true" size={17} /></button>
      </div>
      {context ? <p className="executive-mobile-filter-popover__context">{context}</p> : null}
      <label className="executive-mobile-filter-popover__search">
        <Search aria-hidden="true" size={16} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`${t("Ara")}: ${label}`} />
      </label>
      <div className="executive-mobile-filter-popover__options" role="group" aria-label={label}>
        <button type="button" className={!draft.length ? "is-selected" : ""} onClick={() => setDraft([])}>
          <span className="executive-mobile-filter-popover__check">{!draft.length ? <Check aria-hidden="true" size={14} /> : null}</span>
          <span>{t("Tümü")}</span>
        </button>
        {filteredOptions.map((option) => {
          const selected = draft.includes(option);
          return (
            <button type="button" key={`${filterId}-${option}`} className={selected ? "is-selected" : ""} role="checkbox" aria-checked={selected} onClick={() => toggle(option)}>
              <span className="executive-mobile-filter-popover__check">{selected ? <Check aria-hidden="true" size={14} /> : null}</span>
              <span>{option}</span>
            </button>
          );
        })}
        {!filteredOptions.length ? <p>{t("Kayıt bulunamadı")}</p> : null}
      </div>
      <div className="executive-mobile-filter-popover__actions">
        <button type="button" disabled={!draft.length} onClick={() => setDraft([])}>{t("Temizle")}</button>
        <button type="button" disabled={disabled} onClick={() => { onApply(draft); onOpenChange(false); }}>
          {disabled ? t("Yükleniyor…") : t("Uygula")}
        </button>
      </div>
    </ExecutiveMobileFilterTrigger>
  );
}

function ExecutiveMobilePeriodFilter({
  open,
  onOpenChange,
  value,
  period,
  start,
  end,
  minDate,
  maxDate,
  disabled,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  period: PeriodId;
  start: string;
  end: string;
  minDate?: string;
  maxDate?: string;
  disabled: boolean;
  onApply: (nextPeriod: PeriodId, nextStart: string, nextEnd: string) => void;
}) {
  const t = useT();
  const [draftPeriod, setDraftPeriod] = useState<PeriodId>(period);
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);
  useEffect(() => {
    if (!open) return;
    setDraftPeriod(period);
    setDraftStart(start);
    setDraftEnd(end);
  }, [end, open, period, start]);
  const invalidRange = draftPeriod === "custom" && (!draftStart || !draftEnd || draftStart > draftEnd);
  return (
    <ExecutiveMobileFilterTrigger
      open={open}
      onOpenChange={onOpenChange}
      icon={<CalendarDays aria-hidden="true" size={16} />}
      label={t("Dönem")}
      value={value}
      disabled={disabled}
    >
      <div className="executive-mobile-filter-popover__heading">
        <span><strong>{t("Dönem")}</strong><small>{draftPeriod === "custom" ? `${displayDate(draftStart)} – ${displayDate(draftEnd)}` : t(periods.find((choice) => choice.id === draftPeriod)?.label ?? "Bu Ay")}</small></span>
        <button type="button" aria-label={t("Kapat")} onClick={() => onOpenChange(false)}><X aria-hidden="true" size={17} /></button>
      </div>
      <div className="executive-mobile-filter-popover__periods" role="radiogroup" aria-label={t("Dönem")}>
        {periods.map((choice) => (
          <button type="button" key={choice.id} className={draftPeriod === choice.id ? "is-selected" : ""} role="radio" aria-checked={draftPeriod === choice.id} onClick={() => setDraftPeriod(choice.id)}>
            {t(choice.label)}
          </button>
        ))}
      </div>
      {draftPeriod === "custom" ? (
        <div className="executive-mobile-filter-popover__dates">
          <label>{t("Başlangıç")}<input type="date" value={draftStart} min={minDate} max={draftEnd || maxDate} onChange={(event) => setDraftStart(event.target.value)} /></label>
          <label>{t("Bitiş")}<input type="date" value={draftEnd} min={draftStart || minDate} max={maxDate} onChange={(event) => setDraftEnd(event.target.value)} /></label>
        </div>
      ) : null}
      <div className="executive-mobile-filter-popover__actions executive-mobile-filter-popover__actions--single">
        <button type="button" disabled={disabled || invalidRange} onClick={() => { onApply(draftPeriod, draftStart, draftEnd); onOpenChange(false); }}>
          {disabled ? t("Yükleniyor…") : t("Uygula")}
        </button>
      </div>
    </ExecutiveMobileFilterTrigger>
  );
}

function SidebarMultiFilter({ definition, values, options, disabled, onChange }: { definition: { key: FilterKey; label: string; icon: string }; values: string[]; options: string[]; disabled: boolean; onChange: (values: string[]) => void }) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const normalizedQuery = query.trim().toLocaleLowerCase("tr");
  const orderedOptions = useMemo(() => sortFilterOptions(options), [options]);
  const filteredOptions = orderedOptions.filter((option) => !normalizedQuery || option.toLocaleLowerCase("tr").includes(normalizedQuery));
  function toggle(value: string) {
    onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value].sort((a, b) => a.localeCompare(b, "tr")));
  }
  function selectVisible() {
    onChange([...new Set([...values, ...filteredOptions])].sort((a, b) => a.localeCompare(b, "tr")));
  }
  return (
    <div className="sidebar-filter sidebar-filter--compact">
      <span>
        {definition.icon} &nbsp; {t(definition.label)}
      </span>
      <div className="filter-select">
        <FloatingFilterPopover
          open={open}
          onOpenChange={(nextOpen) => { setOpen(nextOpen); if (!nextOpen) setQuery(""); }}
          variant="sidebar"
          title={t(definition.label)}
          status={`${values.length} ${t("seçili")} · ${filteredOptions.length} ${t("sonuç")}`}
          trigger={(
            <button type="button" disabled={disabled} className={`filter-select__trigger${values.length ? " has-selection" : ""}${open ? " is-open" : ""}`} aria-expanded={open}>
              <span>{values.length ? <><b>{values[0]}</b>{values.length > 1 ? <em>+{values.length - 1}</em> : null}</> : t("Tümü")}</span>
              <ChevronDown aria-hidden="true" size={16} strokeWidth={2} />
            </button>
          )}
        >
          <Command className="filter-command" shouldFilter={false}>
            {options.length > 6 ? <FilterSearch value={query} disabled={disabled} placeholder={`${t("Ara")}...`} onChange={setQuery} /> : null}
            <div className="filter-menu-actions">
              <button type="button" disabled={disabled || !filteredOptions.length || filteredOptions.every((option) => values.includes(option))} onClick={selectVisible}><Check aria-hidden="true" size={14} /> {t("Tümünü Seç")}</button>
              <button type="button" disabled={disabled || !values.length} onClick={() => onChange([])}><X aria-hidden="true" size={14} /> {t("Tümünü Temizle")}</button>
            </div>
            <FilterOptionList options={filteredOptions} values={values} disabled={disabled} emptyText={t("Kayıt bulunamadı")} onToggle={toggle} />
          </Command>
        </FloatingFilterPopover>
      </div>
    </div>
  );
}

function MainMultiSelect({
  label,
  values,
  options,
  disabled,
  groupName,
  productToolbar,
  countSelection,
  onChange,
}: {
  label: string;
  values: string[];
  options: string[];
  disabled: boolean;
  groupName?: string;
  productToolbar?: "primary" | "secondary";
  countSelection?: boolean;
  onChange: (values: string[]) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const normalizedQuery = query.trim().toLocaleLowerCase("tr");
  const orderedOptions = useMemo(() => sortFilterOptions(options), [options]);
  const filteredOptions = orderedOptions.filter((option) => !normalizedQuery || option.toLocaleLowerCase("tr").includes(normalizedQuery));
  const toggle = (value: string) => onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value].sort((a, b) => a.localeCompare(b, "tr")));
  const selectVisible = () => onChange([...new Set([...values, ...filteredOptions])].sort((a, b) => a.localeCompare(b, "tr")));
  return (
    <div className="main-multiselect">
      {productToolbar !== "secondary" ? <span className="main-multiselect__label">{t(label)}</span> : null}
      <div className="filter-select" data-filter-group={groupName}>
        <FloatingFilterPopover
          open={open}
          onOpenChange={(nextOpen) => { setOpen(nextOpen); if (!nextOpen) setQuery(""); }}
          variant="main"
          title={t(label)}
          status={`${values.length} ${t("seçili")} · ${filteredOptions.length} ${t("sonuç")}`}
          trigger={(
            <button type="button" disabled={disabled} className={`filter-select__trigger${values.length ? " has-selection" : ""}${open ? " is-open" : ""}`} aria-label={`${t(label)} ${t("Filtresi")}`} aria-expanded={open}>
              <span className="main-multiselect__summary">{productToolbar ? <>{productToolbar === "secondary" ? <strong>{t(label)}</strong> : null}<b>{values.length > 1 || (countSelection && values.length) ? `${values.length} ${t("seçili")}` : values[0] ?? t("Tümü")}</b></> : values.length ? <><b>{values[0]}</b>{values.length > 1 ? <em>+{values.length - 1}</em> : null}</> : t("Tümü")}</span>
              <ChevronDown aria-hidden="true" size={16} strokeWidth={2} />
            </button>
          )}
        >
          <Command className="filter-command" shouldFilter={false}>
            {options.length > 8 ? <FilterSearch value={query} disabled={disabled} placeholder={t("Kod veya değer ara...")} onChange={setQuery} /> : null}
            <div className="filter-menu-actions">
              <button type="button" disabled={disabled || !filteredOptions.length || filteredOptions.every((option) => values.includes(option))} onClick={selectVisible}><Check aria-hidden="true" size={14} /> {t("Tümünü Seç")}</button>
              <button type="button" disabled={disabled || !values.length} onClick={() => onChange([])}><X aria-hidden="true" size={14} /> {t("Tümünü Temizle")}</button>
            </div>
            <FilterOptionList options={filteredOptions} values={values} disabled={disabled} emptyText={t("Kayıt bulunamadı")} onToggle={toggle} />
          </Command>
        </FloatingFilterPopover>
        {productToolbar && values.length ? <button type="button" className="pft-selection-clear" disabled={disabled} aria-label={`${t(label)} · ${t("Temizle")}`} onClick={() => onChange([])}><X size={12} aria-hidden="true" /></button> : null}
      </div>
    </div>
  );
}

function MainSkuSelect({
  values,
  query,
  suggestions,
  disabled,
  groupName,
  productToolbar,
  onQuery,
  onAdd,
  onAddMany,
  onRemove,
  onClear,
}: {
  values: string[];
  query: string;
  suggestions: string[];
  disabled: boolean;
  groupName?: string;
  productToolbar?: boolean;
  onQuery: (value: string) => void;
  onAdd: (value: string) => void;
  onAddMany: (values: string[]) => void;
  onRemove: (value: string) => void;
  onClear: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const orderedSuggestions = useMemo(() => sortFilterOptions(suggestions), [suggestions]);
  return (
    <div className="main-multiselect main-sku-select">
      <span className="main-multiselect__label">{t(productToolbar ? "SKU" : "SKU Filtresi")}</span>
      <div className="filter-select" data-filter-group={groupName}>
        <FloatingFilterPopover
          open={open}
          onOpenChange={setOpen}
          variant="main"
          title={t("SKU Filtresi")}
          status={`${values.length} ${t("seçili")} · ${orderedSuggestions.length} ${t("sonuç")}`}
          trigger={(
            <button type="button" disabled={disabled} className={`filter-select__trigger${values.length ? " has-selection" : ""}${open ? " is-open" : ""}`} aria-label={t("SKU Filtresi")} aria-expanded={open}>
              <span className="main-multiselect__summary">{productToolbar && values.length ? <b>{values.length} SKU {t("seçili")}</b> : values.length ? <><b>{values[0]}</b>{values.length > 1 ? <em>+{values.length - 1}</em> : null}</> : t("Tümü")}</span>
              <ChevronDown aria-hidden="true" size={16} strokeWidth={2} />
            </button>
          )}
        >
          <Command className="filter-command" shouldFilter={false}>
            <FilterSearch value={query} disabled={disabled} placeholder={t("En az 2 karakter yazın")} onChange={onQuery} />
            <div className="filter-menu-actions">
              <button type="button" disabled={disabled || !orderedSuggestions.length} onClick={() => onAddMany(orderedSuggestions)}><Check aria-hidden="true" size={14} /> {t("Tümünü Seç")}</button>
              <button type="button" disabled={disabled || !values.length} onClick={onClear}><X aria-hidden="true" size={14} /> {t("Tümünü Temizle")}</button>
            </div>
            {values.length ? <div className="main-sku-select__selected">{values.map((sku) => <button type="button" key={sku} disabled={disabled} onClick={() => onRemove(sku)}>{sku}<X aria-hidden="true" size={12} /></button>)}</div> : null}
            <Command.List className="filter-popover__list main-sku-select__options">
              {query.trim().length < 2 ? <div className="filter-popover__empty">{t("En az 2 karakter yazın")}</div> : null}
              {orderedSuggestions.map((sku) => <Command.Item className="filter-popover__result" value={sku} key={sku} disabled={disabled} onSelect={() => onAdd(sku)}><span>{sku}</span><span className="filter-popover__add" aria-hidden="true">＋</span></Command.Item>)}
              {query.trim().length >= 2 && !orderedSuggestions.length ? <div className="filter-popover__empty">{t("Kayıt bulunamadı")}</div> : null}
            </Command.List>
          </Command>
        </FloatingFilterPopover>
        {productToolbar && values.length ? <button type="button" className="pft-selection-clear" disabled={disabled} aria-label={`SKU · ${t("Temizle")}`} onClick={onClear}><X size={12} aria-hidden="true" /></button> : null}
      </div>
    </div>
  );
}

function SidebarSkuFilter({
  values,
  query,
  suggestions,
  disabled,
  onQuery,
  onAdd,
  onAddMany,
  onRemove,
  onClear,
}: {
  values: string[];
  query: string;
  suggestions: string[];
  disabled: boolean;
  onQuery: (value: string) => void;
  onAdd: (value: string) => void | Promise<void>;
  onAddMany: (values: string[]) => void | Promise<void>;
  onRemove: (value: string) => void | Promise<void>;
  onClear: () => void | Promise<void>;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const orderedSuggestions = useMemo(() => sortFilterOptions(suggestions), [suggestions]);
  const updateQuery = (value: string) => {
    onQuery(value);
    setOpen(value.trim().length >= 2);
  };
  return (
    <div className="sidebar-filter global-sku-filter">
      <span># &nbsp; SKU</span>
      <FloatingFilterPopover
        open={open && query.trim().length >= 2}
        onOpenChange={(nextOpen) => setOpen(nextOpen && query.trim().length >= 2)}
        variant="sidebar"
        side="right"
        align="end"
        title="SKU"
        status={`${values.length} ${t("seçili")} · ${orderedSuggestions.length} ${t("sonuç")}`}
        trigger={<input type="search" value={query} disabled={disabled} placeholder={t("En az 2 karakter yazın")} onFocus={() => setOpen(query.trim().length >= 2)} onChange={(event) => updateQuery(event.target.value)} />}
      >
        <Command className="filter-command" shouldFilter={false}>
          <div className="filter-menu-actions">
            <button type="button" disabled={disabled || !orderedSuggestions.length} onClick={() => void onAddMany(orderedSuggestions)}><Check aria-hidden="true" size={14} /> {t("Tümünü Seç")}</button>
            <button type="button" disabled={disabled || !values.length} onClick={() => void onClear()}><X aria-hidden="true" size={14} /> {t("Tümünü Temizle")}</button>
          </div>
          <Command.List className="filter-popover__list">
            {orderedSuggestions.map((sku) => <Command.Item className="filter-popover__result" value={sku} key={sku} onSelect={() => void onAdd(sku)}><span>{sku}</span><span className="filter-popover__add" aria-hidden="true">＋</span></Command.Item>)}
            {!orderedSuggestions.length ? <div className="filter-popover__empty">{t("Kayıt bulunamadı")}</div> : null}
          </Command.List>
        </Command>
      </FloatingFilterPopover>
      {values.length ? (
        <div className="global-sku-selected">
          {values.map((sku) => (
            <button type="button" key={sku} disabled={disabled} onClick={() => void onRemove(sku)}>
              {sku} ×
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function LoginScreen({ busy, error, language, onLanguage, onLogin }: { busy: boolean; error: string | null; language: LanguageCode; onLanguage: (language: LanguageCode) => void; onLogin: (username: string, pin: string, rememberMe: boolean) => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const t = (text: string) => translate(language, text);

  return (
    <main className="login-screen login-screen--centered">
      <div className="login-backdrop" aria-hidden="true" />
      <header className="login-branding" aria-label="SRR360 · Silk Road Retail">
        <span className="login-branding__mark"><img src="/identity-20261104/system-lockup.png" alt="SRR360" width="175" height="58" /></span>
        <span className="login-branding__name">SILK ROAD RETAIL</span>
      </header>
      <section className="login-panel">
        <select className="login-language" value={language} onChange={(event) => onLanguage(event.target.value as LanguageCode)} aria-label={t("Dil")}>
          {(Object.keys(languageLabels) as LanguageCode[]).map((code) => (
            <option key={code} value={code}>
              {languageLabels[code]}
            </option>
          ))}
        </select>
        <form
          className="login-form"
          aria-busy={busy}
          onSubmit={(event) => {
            event.preventDefault();
            void onLogin(username, pin, rememberMe);
          }}
        >
          <div className="login-heading">
            <ShieldCheck aria-hidden="true" className="login-heading__icon" />
            <h1>{t("Hoş geldiniz")}</h1>
          </div>
          <label>
            {t("Kullanıcı adı")}
            <input autoComplete="username" value={username} disabled={busy} onChange={(event) => setUsername(event.target.value)} />
          </label>
          <label>
            PIN
            <input type="password" inputMode="numeric" autoComplete="current-password" maxLength={12} value={pin} disabled={busy} onChange={(event) => setPin(event.target.value.replace(/\D/g, ""))} />
          </label>
          <label className="login-remember">
            <input type="checkbox" checked={rememberMe} disabled={busy} onChange={(event) => setRememberMe(event.target.checked)} />
            <span>
              <strong>{t("Beni hatırla")}</strong>
            </span>
          </label>
          <button type="submit" disabled={busy || !username.trim() || pin.length < 4}>
            <span>{busy ? t("Giriş yapılıyor…") : t("Giriş Yap")}</span>
            {!busy ? <ArrowRight aria-hidden="true" size={18} strokeWidth={2.2} /> : null}
          </button>
          <div className={`login-feedback${error ? " is-visible" : ""}`} aria-live="polite" aria-atomic="true">
            {error ? <p className="login-error">{t(error)}</p> : null}
          </div>
        </form>
      </section>
      <LoginBrandStrip />
    </main>
  );
}

type ProfessionalGridRow = Record<string, string | number | null>;
type GridSortState = MobileSortState;
type StorePerformanceSorts = Record<StorePerformanceView, GridSortState>;
const gridSortRegistry = new Map<string, GridSortState>();
const gridVisibleRowsRegistry = new Map<string, string[]>();
const gridSearchRegistry = new Map<string, string>();
type ProfessionalGridColumn = {
  key: string;
  label: string;
  screenMetric?: ScreenMetric;
  minWidth?: number;
  flex?: number;
  align?: "left" | "right" | "center";
  tone?: boolean;
  sortType?: "number" | "date" | "text";
  dangerAtOrAbove?: number;
  statusTone?: boolean;
  progressComparison?: boolean;
  expectedHeader?: string;
  headerTooltip?: string;
  sortable?: boolean;
  band?: "label" | "previous" | "current" | "variance" | "context";
  wrap?: boolean;
  cellClassKey?: string;
  comparisonKey?: string;
  comparisonLabel?: string;
};

const mobileMetricPriority = [
  "sales", "targetRate", "achievement", "conversion", "cr", "upt", "stock", "sellthrough",
  "units", "receipts", "visitors", "atv", "avp", "basket", "unit", "discount", "daily",
  "share", "target", "remaining", "salesChange", "uptChange", "criteria", "reason",
];

const mobileMetricBaseByCompanion: Record<string, string> = {
  salesChange: "sales",
  revenueChange: "sales",
  lySales: "sales",
  previousSales: "sales",
  lyUpt: "upt",
  previousUpt: "upt",
  uptChange: "upt",
  lyUnits: "units",
  previousUnits: "units",
  unitsChange: "units",
  lyReceipts: "receipts",
  previousReceipts: "receipts",
  receiptsChange: "receipts",
  previousAtv: "atv",
  atvChange: "atv",
  previousAvp: "avp",
  avpChange: "avp",
  stockShare: "stock",
  sellthrough: "stock",
  status: "stock",
  stockDays: "stock",
  import: "stock",
  firstImport: "stock",
  lastImport: "stock",
  unit: "price",
  discount: "price",
};

const mobileMetricCompanions: Record<string, string[]> = {
  sales: ["salesChange", "revenueChange", "daily", "first", "last"],
  upt: ["lyUpt", "previousUpt", "uptChange"],
  cr: ["visitors", "receipts"],
  conversion: ["visitors", "receipts"],
  units: ["lyUnits", "previousUnits", "unitsChange"],
  receipts: ["lyReceipts", "previousReceipts", "receiptsChange"],
  atv: ["previousAtv", "atvChange"],
  avp: ["previousAvp", "avpChange"],
  stock: ["stockShare", "sellthrough", "status", "stockDays", "import", "firstImport", "lastImport"],
  price: ["unit", "discount"],
};

const mobileFeaturedFacts: Record<string, string[]> = {
  sales: ["target", "lySales", "previousSales", "targetRate", "achievement", "remaining", "share", "salesChange", "revenueChange"],
  stock: ["stockShare", "sellthrough", "status", "import", "firstImport", "lastImport"],
};

const mobileProductDetailOrder = [
  "basket", "average", "atv", "unitPrice", "avp", "unit",
  "units", "lyUnits", "previousUnits", "unitsChange",
  "receipts", "lyReceipts", "previousReceipts", "receiptsChange",
  "share",
];

const mobileProductMetricKeys = new Set(["average", "atv", "avp", "units", "receipts"]);

function MobileDataExplorer({
  columns,
  rows,
  totalRow,
  ariaLabel,
  view,
  onView,
  primaryKeys,
  metricKeys,
  sort,
  onSort,
  preserveGroups = false,
}: {
  columns: ProfessionalGridColumn[];
  rows: ProfessionalGridRow[];
  totalRow?: ProfessionalGridRow;
  ariaLabel: string;
  view: MobileTableView;
  onView: (view: MobileTableView) => void;
  primaryKeys?: string[];
  metricKeys?: string[];
  sort: GridSortState;
  onSort: (sort: GridSortState) => void;
  preserveGroups?: boolean;
}) {
  const t = useT();
  const explorerRef = useRef<HTMLDivElement>(null);
  const [selectedRow, setSelectedRow] = useState<ProfessionalGridRow | null>(null);
  const columnsByKey = useMemo(() => new Map(columns.map((column) => [column.key, column])), [columns]);
  const resolvedPrimaryKeys = useMemo(() => {
    const requested = (primaryKeys ?? []).filter((key) => columnsByKey.has(key));
    if (requested.length) return requested.slice(0, 2);
    const labels = columns.filter((column, index) => index === 0 || column.align === "left" || column.band === "label").map((column) => column.key);
    return Array.from(new Set(labels)).slice(0, 2);
  }, [columns, columnsByKey, primaryKeys]);
  const resolvedMetricKeys = useMemo(() => {
    const requested = (metricKeys ?? []).filter((key) => columnsByKey.has(key) && !resolvedPrimaryKeys.includes(key));
    const priority = mobileMetricPriority.filter((key) => columnsByKey.has(key) && !resolvedPrimaryKeys.includes(key));
    const remaining = columns.map((column) => column.key).filter((key) => !resolvedPrimaryKeys.includes(key));
    return Array.from(new Set([...requested, ...priority, ...remaining].map((key) => {
      const baseKey = mobileMetricBaseByCompanion[key];
      return baseKey && columnsByKey.has(baseKey) ? baseKey : key;
    }))).slice(0, 4);
  }, [columns, columnsByKey, metricKeys, resolvedPrimaryKeys]);
  const detailMetricKeys = useMemo(() => {
    const requested = (metricKeys ?? resolvedMetricKeys)
      .filter((key) => columnsByKey.has(key) && !resolvedPrimaryKeys.includes(key))
      .map((key) => {
        const baseKey = mobileMetricBaseByCompanion[key];
        return baseKey && columnsByKey.has(baseKey) ? baseKey : key;
      });
    return Array.from(new Set(requested));
  }, [columnsByKey, metricKeys, resolvedMetricKeys, resolvedPrimaryKeys]);
  const detailColumns = useMemo(() => {
    const orderedKeys = [
      ...detailMetricKeys,
      ...columns.map((column) => column.key),
    ];
    return Array.from(new Set(orderedKeys))
      .filter((key) => !resolvedPrimaryKeys.includes(key))
      .map((key) => columnsByKey.get(key))
      .filter((column): column is ProfessionalGridColumn => Boolean(column));
  }, [columns, columnsByKey, detailMetricKeys, resolvedPrimaryKeys]);
  const featuredMetricKey = detailMetricKeys[0] ?? resolvedMetricKeys[0] ?? detailColumns[0]?.key;
  const featuredExpectedKey = featuredMetricKey === "sales" && columnsByKey.has("targetRate") ? "targetRate" : null;
  const labelFor = (key: string) => columnsByKey.get(key)?.label ?? key;
  const shortLabelFor = (key: string) => {
    if (isScreenMetric(columnsByKey.get(key)?.screenMetric ?? key)) return labelFor(key);
    const compactLabels: Record<string, string> = {
      sales: t("Net Ciro"), targetRate: t("Hedef %"), achievement: t("Hedef %"), conversion: "CR", cr: "CR",
      upt: "UPT", stock: t("Stok"), sellthrough: t("Sellthrough"), units: t("Adet"), receipts: t("Fiş"),
      visitors: t("Ziyaretçi"), atv: "ATV", avp: "AVP", basket: t("Sepet"), unit: t("Birim Fiyat"),
      unitPrice: t("Birim Fiyat"), average: t("Ort. Ciro"), share: t("Pay %"), target: t("Hedef"),
      remaining: t("Kalan"), salesChange: t("vs LY"), uptChange: t("vs LY"), status: t("Durum"), import: t("İmport"),
      lySales: t("Geçen Yıl"), previousSales: t("Geçen Yıl"),
      lyUpt: t("Geçen Yıl"), previousUpt: t("Geçen Yıl"),
      lyUnits: t("Geçen Yıl"), previousUnits: t("Geçen Yıl"), unitsChange: t("vs LY"),
      lyReceipts: t("Geçen Yıl"), previousReceipts: t("Geçen Yıl"), receiptsChange: t("vs LY"),
      previousAtv: t("Geçen Yıl"), atvChange: t("vs LY"),
      previousAvp: t("Geçen Yıl"), avpChange: t("vs LY"),
    };
    return compactLabels[key] ?? labelFor(key).replace(/\s+UZS\b/gi, "").replace(/^Net\s+/i, "");
  };
  const valueFor = (row: ProfessionalGridRow, key: string) => String(row[key] ?? "—");
  const primaryFor = (row: ProfessionalGridRow) => resolvedPrimaryKeys.map((key) => valueFor(row, key)).filter((value) => value && value !== "—").join(" · ") || "—";
  const sortValueFor = useCallback((row: ProfessionalGridRow, key: string) => {
    const raw = row[`__sort_${key}`] ?? row[key];
    const column = columnsByKey.get(key);
    if (column?.sortType === "date") return parseGridDate(raw);
    if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
    const numeric = parseGridNumber(raw);
    if (column?.sortType === "number" || column?.align === "right") return numeric;
    return String(raw ?? "").trim() || null;
  }, [columnsByKey]);
  const sortedRows = useMemo(
    () => sortMobileRows(rows, sort, sortValueFor, preserveGroups),
    [preserveGroups, rows, sort, sortValueFor],
  );
  const primarySortKey = resolvedPrimaryKeys.at(-1) ?? resolvedPrimaryKeys[0];
  const toggleSort = (key: string | undefined) => {
    if (!key) return;
    onSort(nextMobileSort(sort, key));
  };
  const ariaSortFor = (key: string | undefined) => sort && sort.key === key ? (sort.direction === "desc" ? "descending" : "ascending") : "none";
  const toneFor = (row: ProfessionalGridRow, key: string) => {
    const customClassKey = columnsByKey.get(key)?.cellClassKey;
    if (customClassKey && row[customClassKey]) return ` ${String(row[customClassKey])}`;
    const value = valueFor(row, key).trim();
    const raw = parseGridNumber(row[`__sort_${key}`] ?? row[key]);
    const expected = parseGridNumber(row[`__expected_${key}`]);
    if (expected != null && raw != null) return raw >= expected ? " is-positive" : " is-negative";
    if (value.startsWith("+") || ((key.includes("Change") || key.includes("Rate")) && raw != null && raw > 0)) return " is-positive";
    if (value.startsWith("-") || ((key.includes("Change") || key.includes("Rate")) && raw != null && raw < 0)) return " is-negative";
    const status = String(row[`__status_${key}`] ?? "");
    if (status === "risk" || status === "no-sales") return " is-negative";
    if (status === "on-track" || status === "new") return " is-positive";
    return "";
  };
  const companionKeysFor = (key: string) => (mobileMetricCompanions[key] ?? []).filter((candidate) => columnsByKey.has(candidate));
  const comparisonCompanionFor = (key: string) => companionKeysFor(key).find((candidate) => candidate.toLowerCase().includes("change")) ?? null;
  const detailCardClassFor = (key: string, index: number) => {
    if (["upt", "units", "receipts", "stock"].includes(key)) return "executive-store-detail-card--upt";
    if (["cr", "conversion", "atv", "avp"].includes(key)) return "executive-store-detail-card--cr";
    return index === 0 ? "executive-store-detail-card--upt" : "executive-store-detail-card--cr";
  };
  const expectedFor = (row: ProfessionalGridRow, key: string) => {
    const expected = parseGridNumber(row[`__expected_${key}`]);
    return expected == null ? null : `${oneDecimal.format(expected)}%`;
  };
  const summaryContextFor = (row: ProfessionalGridRow, key: string) => {
    const expected = expectedFor(row, key);
    if (expected) return { label: t("Olması gereken"), value: expected, tone: "" };
    const comparisonKey = comparisonCompanionFor(key);
    const companionKey = comparisonKey && valueFor(row, comparisonKey) !== "—"
      ? comparisonKey
      : companionKeysFor(key).find((candidate) => valueFor(row, candidate) !== "—");
    return companionKey
      ? { label: shortLabelFor(companionKey), value: valueFor(row, companionKey), tone: toneFor(row, companionKey) }
      : null;
  };
  const featuredFactKeys = useMemo(() => {
    if (!featuredMetricKey) return [];
    const preferred = [
      ...(mobileFeaturedFacts[featuredMetricKey] ?? []),
      ...companionKeysFor(featuredMetricKey),
    ];
    const comparisonKey = comparisonCompanionFor(featuredMetricKey);
    return Array.from(new Set(preferred))
      .filter((key) => columnsByKey.has(key) && key !== featuredMetricKey && key !== comparisonKey)
      .slice(0, featuredExpectedKey ? 3 : 4);
  }, [columnsByKey, featuredExpectedKey, featuredMetricKey]);
  const secondaryMetricKeys = useMemo(() => detailMetricKeys
    .filter((key) => key !== featuredMetricKey && !featuredFactKeys.includes(key) && !mobileProductMetricKeys.has(key))
    , [detailMetricKeys, featuredFactKeys, featuredMetricKey]);
  const reservedFeaturedKeys = useMemo(() => new Set([
    ...(featuredMetricKey ? mobileFeaturedFacts[featuredMetricKey] ?? [] : []),
    ...(featuredMetricKey ? companionKeysFor(featuredMetricKey) : []),
  ]), [featuredMetricKey]);
  const featuredCompanionKeys = useMemo(() => new Set(featuredMetricKey ? companionKeysFor(featuredMetricKey) : []), [featuredMetricKey]);
  const secondaryCompanionKeys = useMemo(() => new Set(secondaryMetricKeys.flatMap((key) => companionKeysFor(key))), [secondaryMetricKeys]);
  const remainingDetailColumns = useMemo(() => {
    return detailColumns.filter((column) =>
      column.key !== featuredMetricKey
        && !featuredFactKeys.includes(column.key)
        && !reservedFeaturedKeys.has(column.key)
        && !featuredCompanionKeys.has(column.key)
        && !secondaryMetricKeys.includes(column.key)
        && !secondaryCompanionKeys.has(column.key),
    );
  }, [detailColumns, featuredCompanionKeys, featuredFactKeys, featuredMetricKey, reservedFeaturedKeys, secondaryCompanionKeys, secondaryMetricKeys]);
  const productDetailColumns = useMemo(() => remainingDetailColumns
    .filter((column) => mobileProductDetailOrder.includes(column.key))
    .sort((left, right) => mobileProductDetailOrder.indexOf(left.key) - mobileProductDetailOrder.indexOf(right.key)), [remainingDetailColumns]);
  const otherDetailColumns = useMemo(() => remainingDetailColumns
    .filter((column) => !mobileProductDetailOrder.includes(column.key)), [remainingDetailColumns]);

  const metricCell = (row: ProfessionalGridRow, key: string) => {
    const context = summaryContextFor(row, key);
    return (
      <span key={key} role="cell" className={`mobile-data-table__metric ${toneFor(row, key)}`}>
        <b>{valueFor(row, key)}</b>
        {context ? <small className={context.tone} title={context.label}>{context.value}</small> : null}
      </span>
    );
  };

  useEffect(() => {
    if (!selectedRow) return;
    document.body.classList.add("mobile-table-sheet-open");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedRow(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("mobile-table-sheet-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedRow]);

  useEffect(() => {
    if (view === "detail") setSelectedRow(null);
  }, [view]);

  const changeView = useCallback((nextView: MobileTableView) => {
    if (nextView === view) return;
    const beforeTop = explorerRef.current?.getBoundingClientRect().top;
    onView(nextView);
    if (beforeTop == null) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const afterTop = explorerRef.current?.getBoundingClientRect().top;
        if (afterTop != null) window.scrollBy({ top: afterTop - beforeTop, behavior: "auto" });
      });
    });
  }, [onView, view]);

  return (
    <div ref={explorerRef} className={`mobile-data-explorer${view === "detail" ? " is-detail" : " is-summary"}`}>
      <div className="mobile-data-explorer__bar">
        <div>
          <strong>{integer.format(sortedRows.length)} {t("kayıt bulundu")}</strong>
          <small>{view === "summary" ? t("Tüm bilgiler için satıra dokunun") : t("Diğer sütunlar için yatay kaydırın")}</small>
        </div>
        <div className="mobile-data-explorer__switch" role="group" aria-label={t("Görünüm")}>
          <button type="button" className={view === "summary" ? "is-active" : ""} aria-pressed={view === "summary"} onClick={() => changeView("summary")}>{t("Özet")}</button>
          <button type="button" className={view === "detail" ? "is-active" : ""} aria-pressed={view === "detail"} onClick={() => changeView("detail")}>{t("Detay")}</button>
        </div>
      </div>
      {view === "summary" ? (
        <div className="mobile-data-table" role="table" aria-label={`${ariaLabel} ${t("Özet")}`}>
          <div className="mobile-data-table__header" role="row">
            <span role="columnheader" aria-sort={ariaSortFor(primarySortKey)}>
              <button type="button" onClick={() => toggleSort(primarySortKey)}>{resolvedPrimaryKeys.map(labelFor).join(" / ")}</button>
            </span>
            {resolvedMetricKeys.map((key) => (
              <span key={key} role="columnheader" title={labelFor(key)} aria-sort={ariaSortFor(key)}>
                <button type="button" onClick={() => toggleSort(key)}>{shortLabelFor(key)}</button>
              </span>
            ))}
          </div>
          {totalRow ? (
            <div className="mobile-data-table__total" role="row">
              <strong role="cell">{t("TOPLAM")}</strong>
              {resolvedMetricKeys.map((key) => metricCell(totalRow, key))}
            </div>
          ) : null}
          <div className="mobile-data-table__body" role="rowgroup">
            {sortedRows.map((row, index) => (
              <button
                key={String(row.__row_id ?? `${primaryFor(row)}-${index}`)}
                type="button"
                className="mobile-data-table__row"
                role="row"
                onClick={() => setSelectedRow(row)}
                aria-label={`${primaryFor(row)} ${t("detayları")}`}
              >
                <strong role="cell" title={primaryFor(row)}>{primaryFor(row)}</strong>
                {resolvedMetricKeys.map((key) => metricCell(row, key))}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {view === "summary" && selectedRow ? (
        <div className="executive-store-sheet-layer mobile-data-sheet-layer" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelectedRow(null);
        }}>
          <section className="executive-store-sheet mobile-data-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-data-sheet-title">
            <div className="executive-store-sheet__handle" aria-hidden="true" />
            <div className="executive-store-sheet__header">
              <div>
                <small>{ariaLabel.split(" · ")[0]}</small>
                <h3 id="mobile-data-sheet-title">{primaryFor(selectedRow)}</h3>
                <p>{t("Seçili dönem performansı")}</p>
              </div>
              <button type="button" aria-label={t("Kapat")} onClick={() => setSelectedRow(null)}><X size={20} /></button>
            </div>
            <div className="executive-store-sheet__cards mobile-data-sheet__cards">
              {featuredMetricKey ? (
                <article className="executive-store-detail-card executive-store-detail-card--sales">
                  <header>
                    <span>{labelFor(featuredMetricKey)}</span>
                    {summaryContextFor(selectedRow, featuredMetricKey) ? (
                      <b className={summaryContextFor(selectedRow, featuredMetricKey)?.tone}>{summaryContextFor(selectedRow, featuredMetricKey)?.value}</b>
                    ) : null}
                  </header>
                  <strong className={toneFor(selectedRow, featuredMetricKey)}>{valueFor(selectedRow, featuredMetricKey)}</strong>
                  {featuredFactKeys.length || expectedFor(selectedRow, featuredMetricKey) || (featuredExpectedKey && expectedFor(selectedRow, featuredExpectedKey)) ? (
                    <div className="executive-store-detail-card__facts">
                      {featuredFactKeys.map((key) => (
                        <div key={key}><span>{labelFor(key)}</span><b className={toneFor(selectedRow, key)}>{valueFor(selectedRow, key)}</b></div>
                      ))}
                      {expectedFor(selectedRow, featuredMetricKey) ? <div><span>{t("Olması gereken")}</span><b>{expectedFor(selectedRow, featuredMetricKey)}</b></div> : null}
                      {featuredExpectedKey && expectedFor(selectedRow, featuredExpectedKey) ? <div><span>{t("Olması gereken")}</span><b>{expectedFor(selectedRow, featuredExpectedKey)}</b></div> : null}
                    </div>
                  ) : null}
                </article>
              ) : null}
              {secondaryMetricKeys.map((key, index) => {
                const comparisonKey = comparisonCompanionFor(key);
                const inlineCompanions = companionKeysFor(key).filter((companionKey) => companionKey !== comparisonKey);
                const isWide = inlineCompanions.length > 2;
                return (
                  <article key={key} className={`executive-store-detail-card ${detailCardClassFor(key, index)}${isWide ? " executive-store-detail-card--wide" : ""}`}>
                    <header>
                      <span>{labelFor(key)}</span>
                      {comparisonKey ? <b className={toneFor(selectedRow, comparisonKey)}>{valueFor(selectedRow, comparisonKey)}</b> : null}
                    </header>
                    <strong className={toneFor(selectedRow, key)}>{valueFor(selectedRow, key)}</strong>
                    {expectedFor(selectedRow, key) ? <div className="executive-store-detail-card__inline"><span>{t("Olması gereken")}</span><b>{expectedFor(selectedRow, key)}</b></div> : null}
                    {isWide ? (
                      <div className="executive-store-detail-card__facts">
                        {inlineCompanions.map((companionKey) => (
                          <div key={companionKey}><span>{labelFor(companionKey)}</span><b className={toneFor(selectedRow, companionKey)}>{valueFor(selectedRow, companionKey)}</b></div>
                        ))}
                      </div>
                    ) : inlineCompanions.map((companionKey) => (
                      <div key={companionKey} className="executive-store-detail-card__inline"><span>{labelFor(companionKey)}</span><b className={toneFor(selectedRow, companionKey)}>{valueFor(selectedRow, companionKey)}</b></div>
                    ))}
                  </article>
                );
              })}
              {productDetailColumns.length ? (
                <article className="executive-store-detail-card executive-store-detail-card--product mobile-data-detail-card--remaining">
                  <header><span>{t("Sepet ve ürün")}</span></header>
                  <div className="executive-store-detail-card__facts">
                    {productDetailColumns.map((column) => (
                      <div key={column.key}><span>{column.label}</span><b className={toneFor(selectedRow, column.key)}>{valueFor(selectedRow, column.key)}</b></div>
                    ))}
                  </div>
                </article>
              ) : null}
              {otherDetailColumns.length ? (
                <article className="executive-store-detail-card executive-store-detail-card--product mobile-data-detail-card--remaining">
                  <header><span>{t("Diğer Bilgiler")}</span></header>
                  <div className="executive-store-detail-card__facts">
                    {otherDetailColumns.map((column) => (
                      <div key={column.key}><span>{column.label}</span><b className={toneFor(selectedRow, column.key)}>{valueFor(selectedRow, column.key)}</b></div>
                    ))}
                  </div>
                </article>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function ProfessionalDataGrid({
  columns: sourceColumns,
  rows,
  totalRow,
  compact,
  ariaLabel,
  totalRowPosition = "top",
  pinnedColumns = 1,
  preserveGroups = false,
  onSortChange,
  linkColumnKey,
  onLinkClick,
  mobilePrimaryKeys,
  mobileMetricKeys,
  mobileExplorer = true,
  columnPicker = false,
  defaultHidden = [],
  originalReportLayout = false,
}: {
  columns: ProfessionalGridColumn[];
  rows: ProfessionalGridRow[];
  totalRow?: ProfessionalGridRow;
  compact: boolean;
  ariaLabel: string;
  totalRowPosition?: "top" | "bottom";
  pinnedColumns?: number;
  preserveGroups?: boolean;
  onSortChange?: (sort: GridSortState) => void;
  linkColumnKey?: string;
  onLinkClick?: (row: ProfessionalGridRow) => void;
  mobilePrimaryKeys?: string[];
  mobileMetricKeys?: string[];
  mobileExplorer?: boolean;
  columnPicker?: boolean;
  defaultHidden?: string[];
  originalReportLayout?: boolean;
}) {
  const gridLanguage = useContext(LanguageContext);
  const columns = useMemo(() => screenMetricColumns(sourceColumns, gridLanguage), [sourceColumns, gridLanguage]);
  const desktopStandard = useDesktopInformation() && !originalReportLayout;
  const [standardHidden,setStandardHidden] = useState<Set<string>>(()=>new Set(defaultHidden));
  const [standardSearch, setStandardSearch] = useState("");
  const standardVisible = desktopStandard && columnPicker ? columns.filter(c=>!standardHidden.has(c.key)) : columns;
  const displayColumns = desktopStandard ? standardStack(standardVisible) : standardVisible;
  const gridLocaleText = useGridLocale(gridLanguage);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridApiRef = useRef<GridApi<ProfessionalGridRow> | null>(null);
  const [mobileLandscapeFullscreen, setMobileLandscapeFullscreen] = useState(false);
  const initialSortRef = useRef<GridSortState>(gridSortRegistry.get(ariaLabel) ?? null);
  const [sharedSort, setSharedSort] = useState<GridSortState>(() => initialSortRef.current);
  const storageKey = `srr360-mobile-table-view:${ariaLabel}`;
  const [mobileView, setMobileView] = useState<MobileTableView>(() =>
    window.sessionStorage.getItem(storageKey) === "detail" ? "detail" : "summary",
  );
  const columnSignature = JSON.stringify(displayColumns);
  useEffect(() => {
    exportVisibleColumns.set(ariaLabel, standardVisible.map(column => column.key));
    return () => { exportVisibleColumns.delete(ariaLabel); };
  }, [ariaLabel, columnSignature]);
  const fitEveryColumn = compact && mobileLandscapeFullscreen;
  const columnDefs = useMemo<ColDef<ProfessionalGridRow>[]>(
    () => [
      ...displayColumns.map<ColDef<ProfessionalGridRow>>((column, index) => ({
        field: column.key,
        headerName: column.label + (column.comparisonKey ? "\n" + standardCopy(gridLanguage).change : ""),
        headerComponent: column.expectedHeader || column.comparisonKey ? (params:{api:GridApi<ProfessionalGridRow>}) => {
          const sorted=params.api.getColumnState().find(c=>c.sort);
          const choose=(key:string)=>recordSort({key,direction:sorted?.colId===key&&sorted.sort==="desc"?"asc":"desc"});
          return <div className="standard-grid-heading"><button type="button" aria-label={column.label} aria-pressed={sorted?.colId===column.key} onClick={event=>{event.stopPropagation();choose(column.key);}}>{column.label}</button>{column.expectedHeader ? <span className="store-overview-target-reference">{column.expectedHeader}</span> : <button type="button" aria-label={column.comparisonLabel} aria-pressed={sorted?.colId===column.comparisonKey} onClick={event=>{event.stopPropagation();choose(column.comparisonKey!);}}>{standardCopy(gridLanguage).change}</button>}</div>;
        } : undefined,
        headerTooltip: column.headerTooltip,
        wrapHeaderText: column.wrapHeaderText,
        sort: initialSortRef.current?.key === column.key ? initialSortRef.current.direction : undefined,
        minWidth: fitEveryColumn ? (index < pinnedColumns ? 72 : 34) : column.minWidth ?? (index === 0 ? 160 : 76),
        flex: column.flex ?? (index === 0 ? 1.6 : 1),
        pinned: index < pinnedColumns ? "left" : undefined,
        lockPinned: index < pinnedColumns,
        wrapText: desktopStandard ? false : column.wrap,
        autoHeight: desktopStandard ? false : column.wrap,
        cellRenderer:
          column.key === linkColumnKey && onLinkClick
            ? (params: { data?: ProfessionalGridRow; value?: unknown }) => (
                <button
                  type="button"
                  className="srr-grid-cell-link"
                  title={String(params.value ?? "")}
                  onClick={() => params.data && onLinkClick(params.data)}
                >
                  {String(params.value ?? "")}
                </button>
              )
            : desktopStandard && column.wrap
              ? (params: { value?: unknown }) => <TableFullText text={String(params.value ?? "")} label={column.label} language={gridLanguage} />
            : column.progressComparison
              ? (params: { data?: ProfessionalGridRow; value?: unknown }) => {
                  const expectedValue = params.data?.[`__expected_${column.key}`];
                  const actualNumber = parseGridNumber(params.data?.[`__sort_${column.key}`] ?? params.value);
                  const expectedNumber = parseGridNumber(expectedValue);
                  const comparisonTone = actualNumber == null || expectedNumber == null
                    ? ""
                    : actualNumber >= expectedNumber
                      ? " metric-positive"
                      : " metric-negative";
                  return (
                    <span className="srr-grid-progress-comparison">
                      <strong className={comparisonTone.trim()}>{String(params.value ?? "")}</strong>
                      {expectedNumber == null || desktopStandard && column.expectedHeader ? null : (
                        <>
                          <span aria-hidden="true">|</span>
                          <small>{oneDecimal.format(expectedNumber)}%</small>
                        </>
                      )}
                    </span>
                  );
                }
            : column.comparisonKey
              ? (params: {data?: ProfessionalGridRow;value?: unknown}) => {
                  const value=String(params.data?.[column.comparisonKey!]??"—");
                  return <span className="standard-grid-pair"><span>{String(params.value??"—")}</span><span title={column.comparisonLabel} className={value.startsWith("+")?"metric-positive":value.startsWith("-")?"metric-negative":""}>{value}</span></span>;
                }
              : desktopStandard && column.key === "scope" ? (params: { data?: ProfessionalGridRow; value?: unknown }) => repeatedSubtotalScope(params.data, column.key) ? "" : String(params.value ?? "") : undefined,
        tooltipValueGetter: (params) => String(column.statusTone ? params.data?.[`__tooltip_${column.key}`] ?? params.value ?? "" : params.value ?? ""),
        headerClass: column.band ? `srr-grid-header--${column.band}` : undefined,
        cellClass: (params) => {
          const classes = [column.align === "left" || index === 0 ? "srr-grid-cell--left" : column.align === "center" ? "srr-grid-cell--center" : "srr-grid-cell--numeric"];
          if (column.band) classes.push(`srr-grid-band--${column.band}`);
          if (column.wrap) classes.push("srr-grid-cell--wrap");
          if (column.cellClassKey && params.data?.[column.cellClassKey]) classes.push(String(params.data[column.cellClassKey]));
          if (column.tone) {
            const value = String(params.value ?? "").trim();
            if (value.startsWith("+")) classes.push("metric-positive");
            if (value.startsWith("-")) classes.push("metric-negative");
          }
          if (column.dangerAtOrAbove != null) {
            const raw = params.data?.[`__sort_${column.key}`] ?? params.value;
            const parsed = parseGridNumber(raw);
            if (parsed != null && parsed >= column.dangerAtOrAbove) classes.push("srr-grid-cell--danger");
          }
          if (column.statusTone) {
            const status = String(params.data?.[`__status_${column.key}`] ?? "").trim();
            if (status) classes.push("product-status-cell", `product-status-cell--${status}`);
          }
          return classes.join(" ");
        },
        filter: true,
        sortable: column.sortable ?? true,
        resizable: true,
        comparator: column.sortType === "text" || (!column.sortType && column.align !== "right" && (!column.band || column.band === "label"))
          ? undefined
          : (valueA, valueB, nodeA, nodeB, isDescending) => compareGridValues(
              nodeA.data?.[`__sort_${column.key}`] ?? valueA,
              nodeB.data?.[`__sort_${column.key}`] ?? valueB,
              column.sortType ?? "number",
              isDescending,
            ),
      })),
      // Retain hidden companion fields so their sort keys/history still work.
      ...(desktopStandard ? standardVisible.filter(c=>!displayColumns.some(d=>d.key===c.key)).map<ColDef<ProfessionalGridRow>>(column=>({field:column.key,headerName:column.label,hide:true,sort:initialSortRef.current?.key===column.key?initialSortRef.current.direction:undefined,sortable:true,comparator:(a:unknown,b:unknown,_a:unknown,_b:unknown,descending:boolean)=>compareGridValues(a,b,"number",descending)})) : []),
    ],
    [columnSignature, fitEveryColumn, linkColumnKey, onLinkClick, pinnedColumns],
  );
  const recordSort = useCallback((nextSort: GridSortState) => {
    setSharedSort(nextSort);
    if (nextSort) gridSortRegistry.set(ariaLabel, nextSort);
    else gridSortRegistry.delete(ariaLabel);
    const api = gridApiRef.current;
    if (api && !api.isDestroyed()) {
      api.applyColumnState({
        defaultState: { sort: null },
        state: nextSort ? [{ colId: nextSort.key, sort: nextSort.direction }] : [],
      });
    } else {
      onSortChange?.(nextSort);
    }
  }, [ariaLabel, onSortChange]);
  useEffect(() => {
    const element = containerRef.current;
    const section = element?.closest<HTMLElement>(".data-report-section");
    if (!element || !section) return;
    const fullscreenDocument = document as WebkitFullscreenDocument;
    const syncMode = () => {
      const nativeElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement;
      const fullscreen = nativeElement === section || section.classList.contains("is-pseudo-fullscreen");
      const mobile = Boolean(element.closest(".app-shell.mobile-ready"));
      const landscape = window.matchMedia("(orientation: landscape)").matches;
      setMobileLandscapeFullscreen(mobile && fullscreen && landscape);
    };
    const classObserver = new MutationObserver(syncMode);
    classObserver.observe(section, { attributes: true, attributeFilter: ["class"] });
    document.addEventListener("fullscreenchange", syncMode);
    document.addEventListener("webkitfullscreenchange", syncMode);
    window.addEventListener("resize", syncMode);
    window.addEventListener("orientationchange", syncMode);
    syncMode();
    return () => {
      classObserver.disconnect();
      document.removeEventListener("fullscreenchange", syncMode);
      document.removeEventListener("webkitfullscreenchange", syncMode);
      window.removeEventListener("resize", syncMode);
      window.removeEventListener("orientationchange", syncMode);
    };
  }, []);
  const fit = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || !containerRef.current?.clientWidth) return;
    window.requestAnimationFrame(() => {
      if (!api.isDestroyed()) api.sizeColumnsToFit();
    });
  }, []);
  useEffect(() => {
    fit();
  }, [columnDefs, compact, fit, rows]);
  useEffect(() => {
    window.sessionStorage.setItem(storageKey, mobileView);
  }, [mobileView, storageKey]);
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [fit]);
  const onGridReady = (event: GridReadyEvent<ProfessionalGridRow>) => {
    gridApiRef.current = event.api;
    fit();
  };
  const groupOrder = useMemo(
    () => Array.from(new Set(rows.map((row) => String(row.__group ?? "")).filter(Boolean))),
    [rows],
  );
  const postSortRows = useCallback((params: PostSortRowsParams<ProfessionalGridRow>) => {
    if (!preserveGroups || !groupOrder.length) return;
    const grouped = new Map<string, { detail: typeof params.nodes; subtotal: typeof params.nodes }>();
    groupOrder.forEach((group) => grouped.set(group, { detail: [], subtotal: [] }));
    const ungrouped: typeof params.nodes = [];
    params.nodes.forEach((node) => {
      const group = String(node.data?.__group ?? "");
      const bucket = grouped.get(group);
      if (!bucket) {
        ungrouped.push(node);
      } else if (node.data?.__kind === "subtotal") {
        bucket.subtotal.push(node);
      } else {
        bucket.detail.push(node);
      }
    });
    params.nodes.splice(0, params.nodes.length, ...groupOrder.flatMap((group) => {
      const bucket = grouped.get(group);
      return bucket ? [...bucket.detail, ...bucket.subtotal] : [];
    }), ...ungrouped);
  }, [groupOrder, preserveGroups]);
  const rowHeight = desktopStandard ? 36 : fitEveryColumn ? 30 : compact ? 38 : 44;
  const headerHeight = desktopStandard ? 48 : fitEveryColumn ? 36 : 44;
  const visibleRows = Math.max(5, Math.min(rows.length + (totalRow ? 1 : 0), 10));
  const height = headerHeight + visibleRows * rowHeight + 2;
  const captureVisibleRows = useCallback((api: GridApi<ProfessionalGridRow>) => {
    const rowIds: string[] = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      const rowId = node.data?.__row_id;
      if (rowId != null) rowIds.push(String(rowId));
    });
    gridVisibleRowsRegistry.set(ariaLabel, rowIds);
  }, [ariaLabel]);
  return (
    <>
      {desktopStandard && columnPicker ? <div className="report-toolbar"><input type="search" aria-label={translate(gridLanguage,"Kod veya değer ara...")} placeholder={translate(gridLanguage,"Kod veya değer ara...")} value={standardSearch} onChange={event=>setStandardSearch(event.target.value)} /><TableColumnPicker columns={columns} hidden={standardHidden} onChange={setStandardHidden} language={gridLanguage} locked={columns.slice(0,pinnedColumns).map(c=>c.key)}/><ReportFullscreenButton /></div> : null}
      {mobileExplorer ? (
        <MobileDataExplorer
          columns={columns}
          rows={rows}
          totalRow={totalRow}
          ariaLabel={ariaLabel}
          view={mobileView}
          onView={setMobileView}
          primaryKeys={mobilePrimaryKeys}
          metricKeys={mobileMetricKeys}
          sort={sharedSort}
          onSort={recordSort}
          preserveGroups={preserveGroups}
        />
      ) : null}
      <div ref={containerRef} className={`professional-grid${originalReportLayout ? " friday-report-grid" : ""}${compact ? " is-compact" : ""}${fitEveryColumn ? " is-mobile-full-fit" : ""}${mobileExplorer && mobileView === "summary" ? " is-mobile-summary-hidden" : ""}`} style={{ height }} role="region" aria-label={ariaLabel}>
      <AgGridReact<ProfessionalGridRow>
        theme={srrGridTheme}
        getLocaleText={gridLocaleText}
        columnDefs={columnDefs}
        rowData={rows}
        quickFilterText={desktopStandard && columnPicker ? standardSearch : undefined}
        pinnedTopRowData={totalRow && (totalRowPosition === "top" || desktopStandard && columnPicker) ? [totalRow] : undefined}
        pinnedBottomRowData={totalRow && totalRowPosition === "bottom" && !(desktopStandard && columnPicker) ? [totalRow] : undefined}
        defaultColDef={{ suppressHeaderMenuButton: true, sortingOrder: ["desc", "asc"] }}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        animateRows={false}
        ensureDomOrder
        enableCellTextSelection
        suppressCellFocus={false}
        getRowClass={(params) => params.data?.__kind === "subtotal" ? "srr-report-subtotal" : undefined}
        postSortRows={preserveGroups ? postSortRows : undefined}
        onGridReady={onGridReady}
        onModelUpdated={(event) => captureVisibleRows(event.api)}
        onFilterChanged={(event) => captureVisibleRows(event.api)}
        onSortChanged={(event) => {
          const sortedColumn = event.api.getColumnState().find((column) => column.sort === "asc" || column.sort === "desc");
          const nextSort: GridSortState = sortedColumn?.sort ? { key: sortedColumn.colId, direction: sortedColumn.sort } : null;
          setSharedSort(nextSort);
          if (nextSort) gridSortRegistry.set(ariaLabel, nextSort);
          else gridSortRegistry.delete(ariaLabel);
          captureVisibleRows(event.api);
          onSortChange?.(nextSort);
          if (desktopStandard) event.api.refreshHeader();
        }}
      />
      </div>
      <DesktopTableNavigation target={containerRef} label={ariaLabel} language={gridLanguage} />
    </>
  );
}

// The Reports tab retains its 23 October presentation independently of the
// later table standard used by the analysis pages.
function OriginalReportGrid(props: Parameters<typeof ProfessionalDataGrid>[0]) {
  return <ProfessionalDataGrid {...props} originalReportLayout />;
}

function parseGridNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value ?? "").trim();
  if (!text || text === "—" || text === "-") return null;
  const token = text.match(/[-+]?\d[\d.\s]*(?:,\d+)?/)?.[0];
  if (!token) return null;
  const parsed = Number(token.replace(/\s/g, "").replace(/\./g, "").replace(",", ".").replace(/^\+/, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function parseGridDate(value: unknown) {
  const text = String(value ?? "").trim();
  if (!text || text === "—" || text === "-" || /satış yok/i.test(text)) return null;
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const display = text.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (display) return Date.UTC(Number(display[3]), Number(display[2]) - 1, Number(display[1]));
  return null;
}

function compareGridValues(valueA: unknown, valueB: unknown, type: "number" | "date" | "text", isDescending = false) {
  if (type === "text") return numericLabelCollator.compare(String(valueA ?? ""), String(valueB ?? ""));
  const a = type === "date" ? parseGridDate(valueA) : parseGridNumber(valueA);
  const b = type === "date" ? parseGridDate(valueB) : parseGridNumber(valueB);
  if (a === null) return b === null ? 0 : isDescending ? -1 : 1;
  if (b === null) return isDescending ? 1 : -1;
  return a - b;
}

function sortRowsForExport<T>(rows: T[], sort: GridSortState, valueFor: (row: T, key: string) => number | string | null) {
  if (!sort) return rows;
  const direction = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const a = valueFor(left, sort.key);
    const b = valueFor(right, sort.key);
    if (a == null) return b == null ? 0 : 1;
    if (b == null) return -1;
    const compared = typeof a === "number" && typeof b === "number" ? a - b : numericLabelCollator.compare(String(a), String(b));
    return compared * direction;
  });
}

function rowsFromGridView<T>(
  rows: T[],
  gridTitle: string,
  rowId: (row: T) => string,
  valueFor: (row: T, key: string) => number | string | null,
) {
  const visibleRowIds = gridVisibleRowsRegistry.get(gridTitle);
  if (visibleRowIds) {
    const rowsById = new Map<string, T[]>();
    for (const row of rows) rowsById.set(rowId(row), [...(rowsById.get(rowId(row)) ?? []), row]);
    return visibleRowIds.flatMap((id) => {
      const matches = rowsById.get(id);
      const row = matches?.shift();
      return row ? [row] : [];
    });
  }
  return sortRowsForExport(rows, gridSortRegistry.get(gridTitle) ?? null, valueFor);
}

type WebkitFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};

type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function ReportFullscreenButton() {
  const t = useT();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [active, setActive] = useState(false);

  const reportSection = useCallback(
    () => buttonRef.current?.closest<HTMLElement>(".data-report-section") ?? null,
    [],
  );

  const leavePseudoFullscreen = useCallback(() => {
    reportSection()?.classList.remove("is-pseudo-fullscreen");
    if (!document.querySelector(".data-report-section.is-pseudo-fullscreen")) {
      document.body.classList.remove("report-fullscreen-open");
    }
    setActive(false);
  }, [reportSection]);

  useEffect(() => {
    const fullscreenDocument = document as WebkitFullscreenDocument;
    const syncState = () => {
      const section = reportSection();
      const nativeElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement;
      setActive(Boolean(section && (nativeElement === section || section.classList.contains("is-pseudo-fullscreen"))));
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && reportSection()?.classList.contains("is-pseudo-fullscreen")) {
        leavePseudoFullscreen();
      }
    };
    document.addEventListener("fullscreenchange", syncState);
    document.addEventListener("webkitfullscreenchange", syncState);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("fullscreenchange", syncState);
      document.removeEventListener("webkitfullscreenchange", syncState);
      window.removeEventListener("keydown", closeOnEscape);
      const section = reportSection();
      section?.classList.remove("is-pseudo-fullscreen");
      if (!document.querySelector(".data-report-section.is-pseudo-fullscreen")) {
        document.body.classList.remove("report-fullscreen-open");
      }
    };
  }, [leavePseudoFullscreen, reportSection]);

  const toggleFullscreen = async () => {
    const section = reportSection() as WebkitFullscreenElement | null;
    if (!section) return;
    const fullscreenDocument = document as WebkitFullscreenDocument;
    const nativeElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement;
    if (nativeElement) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else await Promise.resolve(fullscreenDocument.webkitExitFullscreen?.());
      return;
    }
    if (section.classList.contains("is-pseudo-fullscreen")) {
      leavePseudoFullscreen();
      return;
    }
    try {
      if (section.requestFullscreen) await section.requestFullscreen();
      else if (section.webkitRequestFullscreen) await Promise.resolve(section.webkitRequestFullscreen());
      else throw new Error("Fullscreen API is unavailable");
    } catch {
      section.classList.add("is-pseudo-fullscreen");
      document.body.classList.add("report-fullscreen-open");
      setActive(true);
    }
  };

  return (
    <button ref={buttonRef} type="button" className={`report-fullscreen-button${active ? " active" : ""}`} aria-pressed={active} onClick={() => void toggleFullscreen()}>
      ⛶ {t(active ? "Tam Ekrandan Çık" : "Tam Ekran")}
    </button>
  );
}

function ExecutiveMobileStoreSummary({
  rows,
  summaryTotals,
  asOfDate,
  fullMonthTarget,
  view,
  onView,
}: {
  rows: MetricRow[];
  summaryTotals?: {
    sales: number;
    target: number;
    previousSales: number;
    units: number;
    receipts: number;
    visitors: number;
  };
  asOfDate: string;
  fullMonthTarget: boolean;
  view: MobileTableView;
  onView: (view: MobileTableView) => void;
}) {
  const t = useT();
  const summaryRef = useRef<HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<MetricRow | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<GridSortState>({ key: "targetRate", direction: "desc" });
  const normalizedSearch = search.trim().toLocaleLowerCase("tr");
  const filteredRows = useMemo(
    () => rows.filter((row) =>
      !normalizedSearch || Object.values(row).some((value) =>
        String(value ?? "").toLocaleLowerCase("tr").includes(normalizedSearch),
      ),
    ),
    [normalizedSearch, rows],
  );
  const sortedRows = useMemo(() => sortMobileRows(
    filteredRows,
    sort,
    (row, key) => {
      const values: Record<string, string | number | null> = {
        label: row.label,
        sales: row.net_sales_uzs,
        targetRate: row.target_achievement_percent,
        cr: row.conversion_rate_percent,
        upt: row.upt,
      };
      return values[key] ?? null;
    },
  ), [filteredRows, sort]);
  const totalRow = useMemo(() => {
    const totals = summaryTotals ?? rows.reduce((result, row) => ({
      sales: result.sales + row.net_sales_uzs,
      target: result.target + (row.target_uzs ?? 0),
      previousSales: result.previousSales + row.previous_year_sales_uzs,
      units: result.units + row.net_units,
      receipts: result.receipts + row.receipts,
      visitors: result.visitors + (row.visitors ?? 0),
    }), { sales: 0, target: 0, previousSales: 0, units: 0, receipts: 0, visitors: 0 });
    return {
      ...totals,
      salesChange: totals.previousSales ? ((totals.sales - totals.previousSales) / Math.abs(totals.previousSales)) * 100 : null,
      targetRate: totals.target ? (totals.sales / totals.target) * 100 : null,
      cr: totals.visitors ? (totals.receipts / totals.visitors) * 100 : null,
      upt: totals.receipts ? totals.units / totals.receipts : 0,
    };
  }, [rows, summaryTotals]);
  const toggleSort = (key: string) => setSort((current) => nextMobileSort(current, key));
  const ariaSortFor = (key: string) => sort && sort.key === key ? (sort.direction === "desc" ? "descending" : "ascending") : "none";

  useEffect(() => {
    if (!selectedRow) return;
    document.body.classList.add("executive-store-sheet-open");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedRow(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("executive-store-sheet-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedRow]);

  useEffect(() => {
    if (selectedRow && !rows.some((row) => row.label === selectedRow.label)) setSelectedRow(null);
  }, [rows, selectedRow]);

  useEffect(() => {
    if (view === "detail") setSelectedRow(null);
  }, [view]);

  const percent = (value: number | null) => (value == null ? "—" : `${oneDecimal.format(value)}%`);
  const expectedTargetRate = rows.reduce((sum, row) => sum + (row.target_uzs ?? 0) * (row.target_progress_percent ?? targetProgressBenchmark(asOfDate, fullMonthTarget)), 0) / (rows.reduce((sum, row) => sum + (row.target_uzs ?? 0), 0) || 1);
  const salesChange = (row: MetricRow) =>
    row.revenue_change_percent == null
      ? t("Karşılaştırma yok")
      : `${row.revenue_change_percent >= 0 ? "+" : ""}${oneDecimal.format(row.revenue_change_percent)}%`;

  const changeView = useCallback((nextView: MobileTableView) => {
    if (nextView === view) return;
    const beforeTop = summaryRef.current?.getBoundingClientRect().top;
    onView(nextView);
    if (beforeTop == null) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const afterTop = summaryRef.current?.getBoundingClientRect().top;
        if (afterTop != null) window.scrollBy({ top: afterTop - beforeTop, behavior: "auto" });
      });
    });
  }, [onView, view]);

  return (
    <section ref={summaryRef} className={`executive-mobile-store-summary${view === "detail" ? " is-detail-view" : ""}`} aria-label={t("Mağaza Özeti")}>
      <div className="executive-mobile-store-summary__title">
        <span />
        <div className="executive-mobile-store-summary__title-copy">
          <h2>{t("Mağaza Özeti")}</h2>
          <small>
            {view === "summary"
              ? `${integer.format(sortedRows.length)} ${t("kayıt bulundu")} · ${t("Tüm detaylar için mağazaya dokunun")}`
              : t("Diğer sütunlar için yatay kaydırın")}
          </small>
        </div>
        <div className="executive-mobile-store-summary__view-switch" role="group" aria-label={t("Görünüm")}>
          <button type="button" className={view === "summary" ? "is-active" : ""} aria-pressed={view === "summary"} onClick={() => changeView("summary")}>{t("Özet")}</button>
          <button type="button" className={view === "detail" ? "is-active" : ""} aria-pressed={view === "detail"} onClick={() => changeView("detail")}>{t("Detay")}</button>
        </div>
      </div>
      {view === "summary" ? <div className="executive-mobile-store-summary__search">
        <label>
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={t("Kod veya değer ara...")}
            placeholder={t("Kod veya değer ara...")}
          />
          {search ? <button type="button" aria-label={t("Temizle")} onClick={() => setSearch("")}><X size={15} /></button> : null}
        </label>
      </div> : null}
      {view === "summary" ? <div className="executive-mobile-store-table" role="table" aria-label={t("Mağaza Özeti")}>
        <div className="executive-mobile-store-table__header" role="row">
          <span role="columnheader" aria-sort={ariaSortFor("label")}><button type="button" onClick={() => toggleSort("label")}>{t("Mağaza")}</button></span>
          <span role="columnheader" aria-sort={ariaSortFor("sales")}><button type="button" onClick={() => toggleSort("sales")}>{t("Satış")}</button></span>
          <span role="columnheader" aria-sort={ariaSortFor("targetRate")}><button type="button" onClick={() => toggleSort("targetRate")}>{t("Hedef %")}</button></span>
          <span role="columnheader" aria-sort={ariaSortFor("cr")}><button type="button" onClick={() => toggleSort("cr")}>CR</button></span>
          <span role="columnheader" aria-sort={ariaSortFor("upt")}><button type="button" onClick={() => toggleSort("upt")}>UPT</button></span>
        </div>
        <div className="executive-mobile-store-table__total" role="row">
          <strong role="cell">{t("TOPLAM")}</strong>
          <span role="cell" className="executive-mobile-store-table__sales">
            <b>{integer.format(totalRow.sales)}</b>
            <small className={totalRow.salesChange == null ? "" : totalRow.salesChange >= 0 ? "is-positive" : "is-negative"}>
              {totalRow.salesChange == null ? t("Karşılaştırma yok") : `${totalRow.salesChange >= 0 ? "+" : ""}${oneDecimal.format(totalRow.salesChange)}%`}
            </small>
          </span>
          <span role="cell" className="executive-mobile-store-table__target">
            <b className={totalRow.targetRate == null ? "" : totalRow.targetRate >= expectedTargetRate ? "is-positive" : "is-negative"}>{percent(totalRow.targetRate)}</b>
            <small>{oneDecimal.format(expectedTargetRate)}%</small>
          </span>
          <span role="cell">{percent(totalRow.cr)}</span>
          <span role="cell">{decimal.format(totalRow.upt)}</span>
        </div>
        <div className="executive-mobile-store-table__body" role="rowgroup">
          {sortedRows.map((row) => (
            <button
              key={`${row.brand ?? ""}-${row.label}`}
              type="button"
              className="executive-mobile-store-table__row"
              role="row"
              onClick={() => setSelectedRow(row)}
              aria-label={`${row.label} ${t("detayları")}`}
            >
              <strong role="cell" title={row.label}>{row.label}</strong>
              <span role="cell" className="executive-mobile-store-table__sales">
                <b title={`${integer.format(row.net_sales_uzs)}`}>{integer.format(row.net_sales_uzs)}</b>
                <small className={row.revenue_change_percent == null ? "" : row.revenue_change_percent >= 0 ? "is-positive" : "is-negative"}>
                  {salesChange(row)}
                </small>
              </span>
              <span role="cell" className="executive-mobile-store-table__target">
                <b className={row.target_achievement_percent == null ? "" : row.target_achievement_percent >= (row.target_progress_percent ?? expectedTargetRate) ? "is-positive" : "is-negative"}>
                  {percent(row.target_achievement_percent)}
                </b>
                <small>{oneDecimal.format(row.target_progress_percent ?? expectedTargetRate)}%</small>
              </span>
              <span role="cell">{percent(row.conversion_rate_percent)}</span>
              <span role="cell">{decimal.format(row.upt)}</span>
            </button>
          ))}
          {!filteredRows.length ? <div className="executive-mobile-store-table__empty">{t("Kayıt bulunamadı")}</div> : null}
        </div>
      </div> : null}

      {view === "summary" && selectedRow ? (
        <div className="executive-store-sheet-layer" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelectedRow(null);
        }}>
          <section className="executive-store-sheet" role="dialog" aria-modal="true" aria-labelledby="executive-store-sheet-title">
            <div className="executive-store-sheet__handle" aria-hidden="true" />
            <div className="executive-store-sheet__header">
              <div>
                <small>{t("Mağaza performansı")}</small>
                <h3 id="executive-store-sheet-title">{selectedRow.label}</h3>
                <p>{t("Seçili dönem performansı")}</p>
              </div>
              <button type="button" aria-label={t("Kapat")} onClick={() => setSelectedRow(null)}><X size={20} /></button>
            </div>
            <div className="executive-store-sheet__cards">
              <article className="executive-store-detail-card executive-store-detail-card--sales">
                <header>
                  <span>{t("Satış")}</span>
                  <b className={selectedRow.revenue_change_percent == null ? "" : selectedRow.revenue_change_percent >= 0 ? "is-positive" : "is-negative"}>{salesChange(selectedRow)}</b>
                </header>
                <strong>{integer.format(selectedRow.net_sales_uzs)}</strong>
                <div className="executive-store-detail-card__facts">
                  <div><span>{t("Hedef")}</span><b>{selectedRow.target_uzs == null ? "—" : `${integer.format(selectedRow.target_uzs)}`}</b></div>
                  <div><span>{t("Geçen Yıl")}</span><b>{selectedRow.previous_year_sales_uzs ? `${integer.format(selectedRow.previous_year_sales_uzs)}` : "—"}</b></div>
                  <div><span>{t("Hedef %")}</span><b>{percent(selectedRow.target_achievement_percent)}</b></div>
                  <div><span>{t("Olması gereken")}</span><b>{oneDecimal.format(selectedRow.target_progress_percent ?? expectedTargetRate)}%</b></div>
                </div>
              </article>

              <article className="executive-store-detail-card executive-store-detail-card--upt">
                <header><span>UPT</span><b className={selectedRow.upt_change_percent == null ? "" : selectedRow.upt_change_percent >= 0 ? "is-positive" : "is-negative"}>{selectedRow.upt_change_percent == null ? "—" : `${selectedRow.upt_change_percent >= 0 ? "+" : ""}${oneDecimal.format(selectedRow.upt_change_percent)}%`}</b></header>
                <strong>{decimal.format(selectedRow.upt)}</strong>
                <div className="executive-store-detail-card__inline"><span>{t("Geçen Yıl")}</span><b>{selectedRow.previous_year_upt ? decimal.format(selectedRow.previous_year_upt) : "—"}</b></div>
              </article>

              <article className="executive-store-detail-card executive-store-detail-card--cr">
                <header><span>CR</span></header>
                <strong>{percent(selectedRow.conversion_rate_percent)}</strong>
                <div className="executive-store-detail-card__inline"><span>{t("Ziyaretçi")}</span><b>{selectedRow.visitors == null ? "—" : integer.format(selectedRow.visitors)}</b></div>
                <div className="executive-store-detail-card__inline"><span>{t("Fiş Sayısı")}</span><b>{integer.format(selectedRow.receipts)}</b></div>
              </article>

              <article className="executive-store-detail-card executive-store-detail-card--product">
                <header><span>{t("Sepet ve ürün")}</span></header>
                <div className="executive-store-detail-card__facts">
                  <div><span>{t("Sepet Ort.")}</span><b>{integer.format(selectedRow.average_basket_uzs)}</b></div>
                  <div><span>{t("Birim Fiyat")}</span><b>{integer.format(selectedRow.average_unit_price_uzs)}</b></div>
                  <div><span>{t("Net Adet")}</span><b>{integer.format(selectedRow.net_units)}</b></div>
                  <div><span>{t("Ciro Payı")}</span><b>{oneDecimal.format(selectedRow.revenue_share_percent)}%</b></div>
                </div>
              </article>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

function ReportTable({
  title,
  firstColumn,
  rows,
  showAllocations = false,
  showComparisons = true,
  showWeekday = false,
  allocationTotals,
  targetProgress,
  mobileExplorer = true,
  storeOverviewView = false,
  executiveOverviewView = false,
  categoryOverviewView = false,
}: {
  title: string;
  firstColumn: string;
  rows: MetricRow[];
  showAllocations?: boolean;
  showComparisons?: boolean;
  showWeekday?: boolean;
  allocationTotals?: {
    target: number;
    visitors: number;
    sales?: number;
    units?: number;
    receipts?: number;
    previousUpt?: number;
  };
  targetProgress?: {
    asOfDate: string;
    fullMonthTarget: boolean;
  };
  mobileExplorer?: boolean;
  storeOverviewView?: boolean;
  executiveOverviewView?: boolean;
  categoryOverviewView?: boolean;
}) {
  const t = useT();
  const language = useContext(LanguageContext);
  const desktopInformation = useDesktopInformation();
  const [search, setSearch] = useState("");
  const focusedExecutiveView = executiveOverviewView && ["Marka Özeti", "Alışveriş Merkezi Özeti", "Günlük Satış", "Aylık Performans"].includes(title);
  const [compact, setCompact] = useState(true);
  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const optionalComparisons = desktopInformation && showComparisons && (storeOverviewView || executiveOverviewView || categoryOverviewView);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => storeOverviewView || optionalComparisons ? storeOverviewDefaultHiddenColumns() : new Set());
  const [overviewResetKey, setOverviewResetKey] = useState(0);
  const recordStoreOverviewView = useCallback((nextSort: MobileSortState, rowIds: string[]) => {
    const key = translate(language, title);
    if (nextSort) gridSortRegistry.set(key, nextSort);
    else gridSortRegistry.delete(key);
    gridVisibleRowsRegistry.set(key, rowIds);
  }, [language, title]);
  const rowTotal = rows.reduce(
    (result, row) => ({
      sales: result.sales + row.net_sales_uzs,
      units: result.units + row.net_units,
      receipts: result.receipts + row.receipts,
    }),
    { sales: 0, units: 0, receipts: 0 },
  );
  const total = allocationTotals
    ? {
        sales: allocationTotals.sales ?? rowTotal.sales,
        units: allocationTotals.units ?? rowTotal.units,
        receipts: allocationTotals.receipts ?? rowTotal.receipts,
      }
    : rowTotal;
  const totalUpt = total.receipts ? total.units / total.receipts : 0;
  const totalTarget = allocationTotals?.target ?? rows.reduce((value, row) => value + (row.target_uzs ?? 0), 0);
  const totalVisitors = allocationTotals?.visitors ?? rows.reduce((value, row) => value + (row.visitors ?? 0), 0);
  const totalPreviousSales = rows.reduce((value, row) => value + row.previous_year_sales_uzs, 0);
  const totalPreviousUpt = allocationTotals?.previousUpt ?? 0;
  const expectedTargetRate = (row: MetricRow) =>
    targetProgress ? metricTargetProgressBenchmark(firstColumn, row.label, targetProgress.asOfDate, targetProgress.fullMonthTarget, row.target_progress_percent) : null;
  const totalExpectedTargetRate = targetProgress && totalTarget > 0
    ? rows.reduce((value, row) => value + (row.target_uzs ?? 0) * (expectedTargetRate(row) ?? 100), 0) / totalTarget
    : null;
  const target = (value: number | null) => (value && value > 0 ? integer.format(value) : "—");
  const percent = (value: number | null) => (value === null ? "—" : `${oneDecimal.format(value)}%`);
  const filteredRows = rows.filter(
    (row) =>
      !search.trim() ||
      [firstColumn === "Ay" ? displayMonth(row.label, language) : firstColumn === "Tarih" ? displayDate(row.label) + " " + displayWeekday(row.label, language) : "", ...Object.values(row)].some((value) =>
        String(value ?? "")
          .toLocaleLowerCase("tr")
          .includes(search.trim().toLocaleLowerCase("tr")),
      ),
  );
  const columns = [
    { key: "label", label: t(firstColumn) },
    ...(showWeekday ? [{ key: "weekday", label: t("Gün") }] : []),
    ...(showAllocations ? [{ key: "target", label: t("Hedef UZS") }] : []),
    { key: "sales", label: t("Net Ciro UZS") },
    ...(showAllocations ? [{ key: "targetRate", label: t("Hedef %") }] : []),
    { key: "upt", label: "UPT" },
    ...(showAllocations
      ? [
          { key: "cr", label: "CR" },
          { key: "visitors", label: t("Ziyaretçi") },
        ]
      : []),
    { key: "receipts", label: t("Fiş Sayısı") },
    { key: "units", label: t("Net Adet") },
    { key: "basket", label: t("Sepet Ort.") },
    { key: "unitPrice", label: t("Birim Fiyat") },
    { key: "share", label: t("Ciro Payı") },
    ...(showComparisons
      ? [
          { key: "lySales", label: t("LY Ciro UZS") },
          { key: "salesChange", label: t("Ciro %") },
          { key: "lyUpt", label: t("LY UPT") },
          { key: "uptChange", label: "UPT %" },
        ]
      : []),
  ];
  if (optionalComparisons && !storeOverviewView) columns.push(...storeOverviewColumns(language).filter(c=>optionalLyKeys.includes(c.key)));
  const showExecutiveRemaining = executiveOverviewView && ["Marka", "Alışveriş Merkezi"].includes(firstColumn);
  const viewColumns = screenMetricColumns(executiveOverviewView && title === "Saatlik Satış" ? hourlySalesColumns(language) : storeOverviewView ? storeOverviewMenuColumns(language) : executiveOverviewView ? executiveOverviewColumns(columns, language, showExecutiveRemaining) : categoryOverviewView ? categoryOverviewColumns(columns, language, "gender") : columns, language);
  const visible = (key: string) => !hiddenColumns.has(key);
  const toggleColumn = (key: string) =>
    setHiddenColumns((current) => {
      if (desktopInformation) return standardToggle(current,key);
      const next = new Set(current);
      const keys = optionalComparisons ? storeOverviewToggleKeys(key) : [key];
      for (const field of keys) {
        if (current.has(key)) next.delete(field);
        else next.add(field);
      }
      return next;
    });
  const tone = (value: number | null) => (value == null ? "" : value >= 0 ? " metric-positive" : " metric-negative");
  const targetTone = (value: number | null) => (value == null ? "" : value >= 100 ? " metric-positive" : " metric-negative");
  const totalChange = totalPreviousSales ? ((total.sales - totalPreviousSales) / Math.abs(totalPreviousSales)) * 100 : null;
  const gridColumns: ProfessionalGridColumn[] = columns
    .filter((column) => visible(column.key))
    .map((column, index) => ({
      key: column.key,
      label: desktopInformationView() ? desktopColumnLabel(column.key, column.label, language) : column.label,
      minWidth: index === 0 ? 170 : column.key === "weekday" ? 105 : column.key === "targetRate" ? 112 : column.key.includes("Sales") || column.key === "sales" || column.key === "target" ? 118 : 76,
      flex: index === 0 ? 1.65 : column.key === "weekday" ? 0.9 : column.key === "targetRate" ? 1.05 : column.key.includes("Sales") || column.key === "sales" || column.key === "target" ? 1.2 : 0.8,
      align: index === 0 || column.key === "weekday" ? "left" : "right",
      sortType: index === 0 && (firstColumn === "Tarih" || firstColumn === "Ay") ? "date" : index === 0 || column.key === "weekday" ? "text" : "number",
      tone: column.key === "salesChange" || column.key === "uptChange",
      progressComparison: column.key === "targetRate" && Boolean(targetProgress),
      headerTooltip: column.key === "targetRate" && targetProgress ? t("Solda gerçekleşen hedef oranı, sağda seçili son tarihe kadar olması gereken oran gösterilir.") : desktopMetricHelp(column.key, language),
    }));
  const gridRows: ProfessionalGridRow[] = filteredRows.map((row) => ({
    ...(storeOverviewView || optionalComparisons ? storeOverviewOptionalDisplay(storeOverviewOptionalValues(row)) : {}),
    __row_id: row.label,
    label: firstColumn === "Tarih" ? displayDate(row.label) : firstColumn === "Ay" ? displayMonth(row.label, language) : row.label,
    __sort_label: firstColumn === "Tarih" || firstColumn === "Ay" ? row.label : null,
    weekday: showWeekday ? displayWeekday(row.label, language) : "—",
    target: target(row.target_uzs),
    sales: integer.format(row.net_sales_uzs),
    targetRate: percent(row.target_achievement_percent),
    __sort_targetRate: row.target_achievement_percent,
    __expected_targetRate: row.target_achievement_percent == null ? null : expectedTargetRate(row),
    upt: decimal.format(row.upt),
    cr: percent(row.conversion_rate_percent),
    visitors: row.visitors == null ? "—" : integer.format(row.visitors),
    receipts: integer.format(row.receipts),
    units: integer.format(row.net_units),
    basket: integer.format(row.average_basket_uzs),
    unitPrice: integer.format(row.average_unit_price_uzs),
    share: `${oneDecimal.format(row.revenue_share_percent)}%`,
    lySales: row.previous_year_sales_uzs ? integer.format(row.previous_year_sales_uzs) : "—",
    salesChange: row.revenue_change_percent === null ? "—" : `${row.revenue_change_percent >= 0 ? "+" : ""}${oneDecimal.format(row.revenue_change_percent)}%`,
    lyUpt: row.previous_year_upt ? decimal.format(row.previous_year_upt) : "—",
    uptChange: row.upt_change_percent == null ? "—" : `${row.upt_change_percent >= 0 ? "+" : ""}${oneDecimal.format(row.upt_change_percent)}%`,
  }));
  const gridTotal: ProfessionalGridRow = {
    ...(storeOverviewView || optionalComparisons ? storeOverviewOptionalDisplay(storeOverviewOptionalTotals(rows, total)) : {}),
    label: t("TOPLAM"),
    weekday: "—",
    target: target(totalTarget),
    sales: integer.format(total.sales),
    targetRate: totalTarget ? percent((total.sales / totalTarget) * 100) : "—",
    __sort_targetRate: totalTarget ? (total.sales / totalTarget) * 100 : null,
    __expected_targetRate: totalTarget ? totalExpectedTargetRate : null,
    upt: decimal.format(totalUpt),
    cr: totalVisitors ? percent((total.receipts / totalVisitors) * 100) : "—",
    visitors: integer.format(totalVisitors),
    receipts: integer.format(total.receipts),
    units: integer.format(total.units),
    basket: integer.format(total.receipts ? total.sales / total.receipts : 0),
    unitPrice: integer.format(total.units ? total.sales / total.units : 0),
    share: "100,0%",
    lySales: totalPreviousSales ? integer.format(totalPreviousSales) : "—",
    salesChange: totalChange == null ? "—" : `${totalChange >= 0 ? "+" : ""}${oneDecimal.format(totalChange)}%`,
    lyUpt: totalPreviousUpt ? decimal.format(totalPreviousUpt) : "—",
    uptChange: totalPreviousUpt ? `${totalUpt >= totalPreviousUpt ? "+" : ""}${oneDecimal.format(((totalUpt - totalPreviousUpt) / totalPreviousUpt) * 100)}%` : "—",
  };
  return (
    <ReportSection className="report-section data-report-section" collapsible={executiveOverviewView && ["Günlük Satış", "Saatlik Satış", "Aylık Performans"].includes(title)} heading={<div className="report-title">
        <span />
        <h2>{t(title)}</h2>
      </div>}>
      {!storeOverviewView && !focusedExecutiveView ? <>
      <div className="report-toolbar">
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`🔍 ${t("Kod veya değer ara...")}`} />
        <button type="button" className={`report-columns-button${viewPanelOpen ? " active" : ""}`} onClick={() => setViewPanelOpen((value) => !value)}>
          ☷ {t("Sütunları Göster/Gizle")}
        </button>
        {!executiveOverviewView && !categoryOverviewView ? <>
        {!storeOverviewView ? <>
        <button type="button" className={`report-fit-button${compact ? " active" : ""}`} aria-pressed={compact} onClick={() => setCompact((value) => !value)}>
          ↔ {t("Sütunları Sığdır")}
        </button>
        <button
          type="button"
          className="report-reset-button"
          onClick={() => {
            setSearch("");
            setHiddenColumns(new Set());
            setCompact(true);
            setViewPanelOpen(false);
            setOverviewResetKey(value => value + 1);
          }}
        >
          ↻ {t("Görünümü Sıfırla")}
        </button>
        </> : null}
        </> : null}
        <ReportFullscreenButton />
        <b>{integer.format(filteredRows.length)} {t("kayıt bulundu")}</b>
        {viewPanelOpen ? (
          <div className="report-view-panel">
            <strong>{t("Gösterilecek sütunlar")}</strong>
            {(desktopInformation ? standardMenu(viewColumns) : viewColumns).map((column) => (
              <label key={column.key}>
                <input type="checkbox" checked={visible(column.key)} onChange={() => toggleColumn(column.key)} /> {column.label}
              </label>
            ))}
          </div>
        ) : null}
      </div>
      <div className="mobile-table-hint" aria-hidden="true">
        <ArrowRight size={14} strokeWidth={2.2} />
        <span>{t("Diğer sütunlar için yatay kaydırın")}</span>
      </div>
      </> : null}
      {storeOverviewView ? (
        <StoreOverviewTable
          rows={gridRows}
          totalRow={gridTotal}
          targets={filteredRows}
          totalTarget={totalTarget}
          totalSales={total.sales}
          language={language}
          hiddenColumns={hiddenColumns}
          compact={compact}
          targetProgress={targetProgress}
          expectedTarget={totalExpectedTargetRate}
          initialSort={gridSortRegistry.get(t(title)) ?? null}
          resetKey={overviewResetKey}
          ariaLabel={t(title)}
          legacyExportColumns={[...columns.map(column => column.key), ...storeOverviewOptionalKeys()]}
          parseNumber={parseGridNumber}
          onViewChange={recordStoreOverviewView}
          search={search}
          onSearchChange={setSearch}
          storeCount={rows.length}
          sourceRows={filteredRows}
          fullscreenControl={<ReportFullscreenButton />}
          totalPreviousSales={totalPreviousSales}
        />
      ) : executiveOverviewView && title === "Saatlik Satış" ? (
        <HourlySalesTable
          rows={gridRows} totalRow={gridTotal} sourceRows={rows} language={language} hiddenColumns={hiddenColumns}
          ariaLabel={t(title)} currentSort={() => gridSortRegistry.get(t(title)) ?? null}
          parseNumber={parseGridNumber} onViewChange={recordStoreOverviewView}
        />
      ) : focusedExecutiveView ? (
        <FocusedExecutiveOverviewTable
          kind={firstColumn === "Marka" ? "brand" : firstColumn === "Alışveriş Merkezi" ? "mall" : firstColumn === "Tarih" ? "daily" : "monthly"}
          rows={gridRows.map((row,index) => ({...storeOverviewOptionalDisplay(storeOverviewOptionalValues(filteredRows[index])),...row}))} totalRow={{...storeOverviewOptionalDisplay(storeOverviewOptionalTotals(rows,total)),...gridTotal}} columns={columns} language={language} hiddenColumns={hiddenColumns}
          expectedTarget={totalExpectedTargetRate} expectedPerRow={firstColumn === "Ay"}
          singleMonthLabel={firstColumn === "Ay" && rows.length === 1 && filteredRows.length === 1 ? String(gridRows[0].label) : undefined}
          remainderSources={showExecutiveRemaining ? filteredRows : undefined}
          remainderTotal={showExecutiveRemaining ? { target: totalTarget, sales: total.sales } : undefined}
          targetProgress={targetProgress}
          ariaLabel={t(title)} currentSort={() => gridSortRegistry.get(t(title)) ?? null}
          parseNumber={parseGridNumber} onViewChange={recordStoreOverviewView}
          sourceRows={filteredRows} search={search} onSearchChange={setSearch}
          fullscreenControl={<ReportFullscreenButton />}
        />
      ) : executiveOverviewView ? (
        <ExecutiveOverviewTable
          rows={gridRows} totalRow={gridTotal} columns={columns} language={language} hiddenColumns={hiddenColumns}
          expectedTarget={totalExpectedTargetRate} expectedPerRow={firstColumn === "Ay"}
          singleMonthLabel={firstColumn === "Ay" && rows.length === 1 && filteredRows.length === 1 ? String(gridRows[0].label) : undefined}
          remainderSources={showExecutiveRemaining ? filteredRows : undefined}
          remainderTotal={showExecutiveRemaining ? { target: totalTarget, sales: total.sales } : undefined}
          targetProgress={targetProgress}
          ariaLabel={t(title)} currentSort={() => gridSortRegistry.get(t(title)) ?? null}
          parseNumber={parseGridNumber} onViewChange={recordStoreOverviewView}
        >
          <ProfessionalDataGrid
            columns={gridColumns} rows={gridRows} totalRow={gridTotal} compact={compact} ariaLabel={t(title)}
            mobilePrimaryKeys={["label"]}
            mobileMetricKeys={showAllocations ? ["sales", "targetRate", "upt", "cr", "units"] : ["sales", "upt", "cr", "units"]}
            mobileExplorer={mobileExplorer}
          />
        </ExecutiveOverviewTable>
      ) : categoryOverviewView ? (
        <FocusedStoreAnalysisTable kind="gender" rows={gridRows.map((row,index) => ({...storeOverviewOptionalDisplay(storeOverviewOptionalValues(filteredRows[index])),...row}))} totalRow={{...storeOverviewOptionalDisplay(storeOverviewOptionalTotals(rows,total)),...gridTotal}} columns={columns} language={language} hiddenColumns={hiddenColumns}
          ariaLabel={t(title)} currentSort={() => gridSortRegistry.get(t(title)) ?? null} parseNumber={parseGridNumber} onViewChange={recordStoreOverviewView}
          sourceRows={rows} search={search} onSearchChange={setSearch} fullscreenControl={<ReportFullscreenButton />}>
        <CategoryOverviewTable
          rows={gridRows} totalRow={gridTotal} columns={columns} language={language} hiddenColumns={hiddenColumns}
          variant="gender" singleLabel={singleIdentityLabel(rows.length, gridRows)} ariaLabel={t(title)} currentSort={() => gridSortRegistry.get(t(title)) ?? null}
          parseNumber={parseGridNumber} onViewChange={recordStoreOverviewView}
        >
          <ProfessionalDataGrid
            columns={gridColumns} rows={gridRows} totalRow={gridTotal} compact={compact} ariaLabel={t(title)}
            mobilePrimaryKeys={["label"]}
            mobileMetricKeys={showAllocations ? ["sales", "targetRate", "upt", "cr", "units"] : ["sales", "upt", "cr", "units"]}
            mobileExplorer={mobileExplorer}
          />
        </CategoryOverviewTable>
        </FocusedStoreAnalysisTable>
      ) : (
      <ProfessionalDataGrid
        columns={gridColumns}
        rows={gridRows}
        totalRow={gridTotal}
        compact={compact}
        ariaLabel={t(title)}
        mobilePrimaryKeys={["label"]}
        mobileMetricKeys={showAllocations ? ["sales", "targetRate", "upt", "cr", "units"] : ["sales", "upt", "cr", "units"]}
        mobileExplorer={mobileExplorer}
      />
      )}
    </ReportSection>
  );
}

function buildActionStoreRows(rows: MetricRow[]): StoreActionRow[] {
  const candidates = rows.filter((row): row is MetricRow & { brand: string } => Boolean(row.brand));
  const reasons = new Map<string, StoreActionReason[]>();
  const keyFor = (row: MetricRow & { brand: string }) => `${row.brand}\u001f${row.label}`;
  const addReason = (row: MetricRow & { brand: string }, reason: StoreActionReason) => {
    const key = keyFor(row);
    const current = reasons.get(key) ?? [];
    if (!current.some((item) => item.kind === reason.kind)) current.push(reason);
    reasons.set(key, current);
  };
  const rankLowest = (
    source: (MetricRow & { brand: string })[],
    value: (row: MetricRow & { brand: string }) => number,
    count: number,
    kind: StoreActionReasonKind,
  ) => {
    [...source]
      .sort((left, right) => value(left) - value(right) || left.label.localeCompare(right.label, "tr"))
      .slice(0, count)
      .forEach((row, index) => addReason(row, { kind, rank: index + 1 }));
  };
  const crEligible = candidates.filter((row) => row.conversion_rate_percent != null);
  const uptEligible = candidates.filter((row) => row.receipts > 0);
  const uptDropEligible = uptEligible.filter((row) => row.previous_year_upt > 0 && (row.upt_change_percent ?? 0) < 0);
  const salesDropEligible = candidates.filter((row) => row.previous_year_sales_uzs > 0 && Number.isFinite(row.revenue_change_percent) && (row.revenue_change_percent ?? 0) < 0);

  rankLowest(crEligible, (row) => row.conversion_rate_percent ?? Number.POSITIVE_INFINITY, 3, "company-cr");
  rankLowest(uptEligible, (row) => row.upt, 3, "company-upt");
  rankLowest(uptDropEligible, (row) => row.upt_change_percent ?? 0, 3, "company-upt-drop");
  rankLowest(salesDropEligible, (row) => row.revenue_change_percent ?? 0, 3, "company-sales-drop");
  rankLowest(candidates.filter((row) => Number.isFinite(row.net_sales_uzs)), (row) => row.net_sales_uzs, 3, "company-sales");

  return candidates
    .flatMap((row) => {
      const rowReasons = reasons.get(keyFor(row)) ?? [];
      if (!rowReasons.length) return [];
      return [{ ...row, reasons: rowReasons }];
    })
    .sort(
      (left, right) =>
        right.reasons.length - left.reasons.length ||
        (left.target_achievement_percent ?? Number.POSITIVE_INFINITY) - (right.target_achievement_percent ?? Number.POSITIVE_INFINITY) ||
        (left.revenue_change_percent ?? Number.POSITIVE_INFINITY) - (right.revenue_change_percent ?? Number.POSITIVE_INFINITY) ||
        left.label.localeCompare(right.label, "tr"),
    );
}

function actionReasonText(reason: StoreActionReason, t: (value: string) => string) {
  switch (reason.kind) {
    case "company-cr":
      return `${t("Şirket CR")} #${reason.rank}`;
    case "company-upt":
      return `${t("Şirket UPT")} #${reason.rank}`;
    case "company-upt-drop":
      return `${t("Şirket UPT düşüş")} #${reason.rank}`;
    case "company-sales-drop":
      return `${t("Şirket satış düşüşü")} #${reason.rank}`;
    case "company-sales":
      return `${t("Şirket en düşük satış")} #${reason.rank}`;
  }
}

function actionStoreRowId(row: Pick<StoreActionRow, "brand" | "label">) {
  return `${row.brand}\u001f${row.label}`;
}

function actionStoreDisplayRow(row:StoreActionRow,asOfDate:string,fullMonthTarget:boolean,expected:number,reason:string):ProfessionalGridRow {
  const remainder=storeTargetRemainder(row.target_uzs,row.net_sales_uzs,asOfDate,fullMonthTarget);
  const pct=(value:number|null)=>value==null?"—":`${oneDecimal.format(value)}%`;
  const signed=(value:number|null)=>value==null?"—":`${value>=0?"+":""}${oneDecimal.format(value)}%`;
  return {...storeOverviewOptionalDisplay(storeOverviewOptionalValues(row)),...focusedStoreBasketDisplay([row]),
    __row_id:actionStoreRowId(row),store:row.label,target:row.target_uzs&&row.target_uzs>0?integer.format(row.target_uzs):"—",
    sales:integer.format(row.net_sales_uzs),targetRate:pct(row.target_achievement_percent),__sort_targetRate:row.target_achievement_percent,
    __expected_targetRate:row.target_achievement_percent==null?null:row.target_progress_percent??expected,
    remaining:remainder.remaining==null?"—":integer.format(remainder.remaining),daily:remainder.daily==null?"—":integer.format(Math.round(remainder.daily)),
    lySales:row.previous_year_sales_uzs?integer.format(row.previous_year_sales_uzs):"—",salesChange:signed(row.revenue_change_percent),
    cr:pct(row.conversion_rate_percent),upt:decimal.format(row.upt),lyUpt:row.previous_year_upt?decimal.format(row.previous_year_upt):"—",uptChange:signed(row.upt_change_percent),
    basket:integer.format(row.average_basket_uzs),unitPrice:integer.format(row.average_unit_price_uzs),
    visitors:row.visitors==null?"—":integer.format(row.visitors),receipts:integer.format(row.receipts),units:integer.format(row.net_units),reason};
}

function ActionStoresTable({ rows, asOfDate, fullMonthTarget }: { rows: MetricRow[]; asOfDate: string; fullMonthTarget: boolean }) {
  const t = useT();
  const language = useContext(LanguageContext);
  const [search, setSearch] = useState("");
  const [compact, setCompact] = useState(false);
  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const actionRows = useMemo(() => buildActionStoreRows(rows), [rows]);
  const expectedAchievementPercent = rows.reduce((sum, row) => sum + (row.target_uzs ?? 0) * (row.target_progress_percent ?? targetProgressBenchmark(asOfDate, fullMonthTarget)), 0) / (rows.reduce((sum, row) => sum + (row.target_uzs ?? 0), 0) || 1);
  const reasonText = (row: StoreActionRow) => row.reasons.map((reason) => actionReasonText(reason, t)).join(" • ");
  const filteredRows = actionRows.filter((row) => {
    const needle = search.trim().toLocaleLowerCase("tr");
    if (!needle) return true;
    return [
      row.label,
      row.target_uzs,
      row.net_sales_uzs,
      row.target_achievement_percent,
      row.previous_year_sales_uzs,
      row.revenue_change_percent,
      row.conversion_rate_percent,
      row.previous_year_upt,
      row.upt_change_percent,
      reasonText(row),
    ].some((value) => String(value ?? "").toLocaleLowerCase("tr").includes(needle));
  });
  const labels=new Map(storeOverviewColumns(language).map(c=>[c.key,c.label]));
  labels.set("daily",storeOverviewCopy(language)[5]);labels.set("lyBasket","LY "+labels.get("basket"));labels.set("basketChange","LY "+labels.get("basket")+" "+focusedChangeLabel(language));
  const columns=focusedStoreColumns(language).flatMap(c=>c.key==="label"?[{key:"store",label:t("Mağaza Adı")}]:c.key==="share"?[{key:"reason",label:t("Aksiyon Nedeni")}]:c.fields.map(key=>({key,label:labels.get(key)!})));
  const visible = (key: string) => !hiddenColumns.has(key);
  const toggleColumn = (key: string) =>
    setHiddenColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const gridColumns: ProfessionalGridColumn[] = columns
    .filter((column) => visible(column.key))
    .map((column, index) => ({
      key: column.key,
      label: desktopInformationView() ? desktopColumnLabel(column.key, column.label, language) : column.label,
      minWidth:
        column.key === "store" ? 190
          : column.key === "reason" ? desktopInformationView() ? 260 : 370
            : column.key === "target" || column.key === "sales" || column.key === "lySales" ? 125
              : column.key === "criteria" ? 78
              : column.key === "targetRate" ? 112
              : 92,
      flex: column.key === "reason" ? 2.8 : column.key === "store" ? 1.5 : 0.9,
      align: index === 0 || column.key === "reason" ? "left" : "right",
      sortType: index === 0 || column.key === "reason" ? "text" : "number",
      wrap: column.key === "reason",
      progressComparison: column.key === "targetRate",
      expectedHeader: desktopInformationView() && column.key === "targetRate" && actionRows.every(row => (row.target_progress_percent ?? expectedAchievementPercent) === expectedAchievementPercent) ? `${storeOverviewCopy(language)[18]}: %${oneDecimal.format(expectedAchievementPercent)}` : undefined,
      headerTooltip: column.key === "targetRate" ? storeOverviewCopy(language)[18] + ": %" + oneDecimal.format(expectedAchievementPercent) : undefined,
    }));
  const gridRows=filteredRows.map(row=>actionStoreDisplayRow(row,asOfDate,fullMonthTarget,expectedAchievementPercent,reasonText(row)));

  return (
    <ReportSection className="report-section data-report-section action-stores-standard-section" collapsible heading={<div className="report-title">
        <span /><div className="table-scope-heading"><h2>{t("Aksiyon Gerektiren Mağazalar")}</h2><TableScopeInfo title={t("Aksiyon Gerektiren Mağazalar")} language={language} text={t("Şirket genelinde CR, UPT ve satışı en düşük; LY’ye göre UPT ve satış değişimi en fazla düşen 3’er mağaza.")} /></div>
      </div>}>
      {actionRows.length ? (
        <ActionStoresPresentation rows={gridRows} columns={gridColumns} language={language} parseNumber={parseGridNumber} ariaLabel={t("Aksiyon Gerektiren Mağazalar")} toolbar={
          <div className="report-toolbar">
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`🔍 ${t("Kod veya değer ara...")}`} />
            <button type="button" className={`report-columns-button${viewPanelOpen ? " active" : ""}`} onClick={() => setViewPanelOpen((value) => !value)}>
              ☷ {t("Sütunları Göster/Gizle")}
            </button>
            <button type="button" className={`report-fit-button${compact ? " active" : ""}`} aria-pressed={compact} onClick={() => setCompact((value) => !value)}>
              ↔ {t("Sütunları Sığdır")}
            </button>
            <button
              type="button"
              className="report-reset-button"
              onClick={() => {
                setSearch("");
                setHiddenColumns(new Set());
                setCompact(false);
                setViewPanelOpen(false);
              }}
            >
              ↻ {t("Görünümü Sıfırla")}
            </button>
            <ReportFullscreenButton />
            <b>{integer.format(filteredRows.length)} {t("kayıt bulundu")}</b>
            {viewPanelOpen ? (
              <div className="report-view-panel">
                <strong>{t("Gösterilecek sütunlar")}</strong>
                {(desktopInformationView() ? standardMenu(columns) : columns).map((column) => (
                  <label key={column.key}>
                    <input type="checkbox" checked={visible(column.key)} onChange={() => desktopInformationView() ? setHiddenColumns(current=>standardToggle(current,column.key)) : toggleColumn(column.key)} /> {screenMetricColumnLabel(column.key, desktopInformationView() ? desktopColumnLabel(column.key,column.label,language) : column.label, language)}
                  </label>
                ))}
              </div>
            ) : null}
          </div>}>
          <ProfessionalDataGrid
            columns={desktopInformationView() ? standardReportOrder(gridColumns) : gridColumns}
            rows={gridRows}
            compact={compact}
            ariaLabel={t("Aksiyon Gerektiren Mağazalar")}
            mobilePrimaryKeys={["store"]}
            mobileMetricKeys={["sales", "targetRate", "upt", "cr"]}
          />
        </ActionStoresPresentation>
      ) : (
        <div className="action-stores-empty"><CheckCircle2 size={18} aria-hidden="true" /> {t("Aksiyon gerektiren mağaza bulunmuyor.")}</div>
      )}
    </ReportSection>
  );
}

function StoreDetailTable({
  title,
  firstColumn,
  rows: originalRows,
  variant,
  categoryOverviewView = false,
  categoryScope,
  collapsible = false,
  availability,
}: {
  title: string;
  firstColumn: string;
  rows: MetricRow[];
  variant: "stock" | "weekday";
  categoryOverviewView?: boolean;
  categoryScope?: StoreCategoryScope;
  collapsible?: boolean;
  availability?: {loading:boolean;error?:string};
}) {
  const t = useT();
  const language = useContext(LanguageContext);
  const [localCategoryByStore, setLocalCategoryByStore] = useState(false);
  const monthGrouping = useMonthGrouping(), categoryByStore = monthGrouping?.byStore ?? localCategoryByStore, setCategoryByStore = monthGrouping?.setByStore ?? setLocalCategoryByStore;
  const categoryView = useStoreCategoryRows(originalRows, categoryScope, categoryByStore && !monthGrouping?.enabled, language);
  const rows = categoryView.rows;
  const withStore = !!categoryScope && categoryByStore && !monthGrouping?.enabled;
  const categoryRegistryKey = translate(language, title) + (withStore ? ` · ${categoryView.copy.toggle}` : "");
  const recordCategoryView = useCallback((nextSort: MobileSortState, rowIds: string[]) => {
    const key = categoryRegistryKey;
    if (nextSort) gridSortRegistry.set(key, nextSort);
    else gridSortRegistry.delete(key);
    gridVisibleRowsRegistry.set(key, rowIds);
  }, [categoryRegistryKey]);
  const [search, setSearch] = useState("");
  const [compact, setCompact] = useState(true);
  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => desktopDetailHiddenColumns(variant));
  useEffect(() => categoryScope ? registerStoreCategoryExport(categoryScope, {
    rows, byStore: withStore, loading: categoryView.loading, error: categoryView.error, search, registryKey: categoryRegistryKey,
  }) : undefined, [categoryScope?.params, categoryScope?.releaseId, rows, withStore, categoryView.loading, categoryView.error, search, categoryRegistryKey]);
  const percent = (value: number | null) => (value == null ? "—" : `${oneDecimal.format(value)}%`);
  const signedPercent = (value: number | null) => (value == null ? "—" : `${value >= 0 ? "+" : ""}${oneDecimal.format(value)}%`);
  const totalSales = rows.reduce((sum, row) => sum + row.net_sales_uzs, 0);
  const totalUsd = rows.reduce((sum, row) => sum + row.net_sales_usd, 0);
  const totalUnits = rows.reduce((sum, row) => sum + row.net_units, 0);
  const totalReceipts = rows.reduce((sum, row) => sum + row.receipts, 0);
  const totalPreviousSales = rows.reduce((sum, row) => sum + row.previous_year_sales_uzs, 0);
  const totalPreviousUnits = rows.reduce((sum, row) => sum + row.previous_year_units, 0);
  const totalStock = rows.reduce((sum, row) => sum + (row.stock_units ?? 0), 0);
  const totalVisitors = rows.reduce((sum, row) => sum + (row.visitors ?? 0), 0);
  const totalDays = rows.reduce((sum, row) => sum + (row.day_count ?? 0), 0);
  const totalUpt = totalReceipts ? totalUnits / totalReceipts : 0;
  const weekdayComparison = weekdayLyUptTotals(rows, totalUpt);
  const totalSalesChange = totalPreviousSales ? ((totalSales - totalPreviousSales) / Math.abs(totalPreviousSales)) * 100 : null;
  const totalUnitsChange = totalPreviousUnits ? ((totalUnits - totalPreviousUnits) / Math.abs(totalPreviousUnits)) * 100 : null;
  const tone = (value: number | null) => (value == null ? "" : value >= 0 ? " metric-positive" : " metric-negative");
  type StoreDetailColumn = {
    key: string;
    label: string;
    cell: (row: MetricRow) => string;
    total: string;
    className?: (row: MetricRow) => string;
    totalClassName?: string;
  };
  const stockColumns: StoreDetailColumn[] = [
    { key: "label", label: t(firstColumn), cell: (row: MetricRow) => row.label, total: t("TOPLAM") },
    { key: "sales", label: t("Net Ciro UZS"), cell: (row: MetricRow) => integer.format(row.net_sales_uzs), total: integer.format(totalSales) },
    { key: "units", label: t("Net Adet"), cell: (row: MetricRow) => integer.format(row.net_units), total: integer.format(totalUnits) },
    { key: "unitPrice", label: t("Birim Fiyat"), cell: (row: MetricRow) => integer.format(row.average_unit_price_uzs), total: integer.format(totalUnits ? totalSales / totalUnits : 0) },
    { key: "share", label: t("Ciro Payı %"), cell: (row: MetricRow) => percent(row.revenue_share_percent), total: "100,0%" },
    { key: "stock", label: t("Stok"), cell: (row: MetricRow) => row.stock_units == null ? "—" : integer.format(row.stock_units), total: integer.format(totalStock) },
    { key: "stockShare", label: t("Stok Payı %"), cell: (row: MetricRow) => percent(row.stock_share_percent), total: totalStock ? "100,0%" : "—" },
    { key: "lySales", label: t("LY Ciro UZS"), cell: (row: MetricRow) => row.previous_year_sales_uzs ? integer.format(row.previous_year_sales_uzs) : "—", total: totalPreviousSales ? integer.format(totalPreviousSales) : "—" },
    { key: "salesChange", label: t("Ciro %"), cell: (row: MetricRow) => signedPercent(row.revenue_change_percent), total: signedPercent(totalSalesChange), className: (row: MetricRow) => tone(row.revenue_change_percent), totalClassName: tone(totalSalesChange) },
    { key: "lyUnits", label: t("LY Adet"), cell: (row: MetricRow) => row.previous_year_units ? integer.format(row.previous_year_units) : "—", total: totalPreviousUnits ? integer.format(totalPreviousUnits) : "—" },
    { key: "unitsChange", label: t("Adet %"), cell: (row: MetricRow) => signedPercent(row.previous_year_units ? ((row.net_units - row.previous_year_units) / Math.abs(row.previous_year_units)) * 100 : null), total: signedPercent(totalUnitsChange), className: (row: MetricRow) => tone(row.previous_year_units ? ((row.net_units - row.previous_year_units) / Math.abs(row.previous_year_units)) * 100 : null), totalClassName: tone(totalUnitsChange) },
  ];
  const weekdayColumns: StoreDetailColumn[] = [
    { key: "label", label: t(firstColumn), cell: (row: MetricRow) => row.label, total: t("TOPLAM") },
    { key: "days", label: t("Gün Sayısı"), cell: (row: MetricRow) => integer.format(row.day_count ?? 0), total: integer.format(totalDays) },
    { key: "sales", label: t("Net Ciro UZS"), cell: (row: MetricRow) => integer.format(row.net_sales_uzs), total: integer.format(totalSales) },
    { key: "average", label: t("Ortalama Ciro"), cell: (row: MetricRow) => integer.format(row.average_sales_uzs ?? 0), total: integer.format(totalDays ? totalSales / totalDays : 0) },
    { key: "usd", label: t("Net Ciro USD"), cell: (row: MetricRow) => integer.format(row.net_sales_usd), total: integer.format(totalUsd) },
    { key: "share", label: t("Ciro Payı %"), cell: (row: MetricRow) => percent(row.revenue_share_percent), total: "100,0%" },
    { key: "upt", label: "UPT", cell: (row: MetricRow) => decimal.format(row.upt), total: decimal.format(totalUpt) },
    { key: "cr", label: "CR", cell: (row: MetricRow) => percent(row.conversion_rate_percent), total: totalVisitors ? percent((totalReceipts / totalVisitors) * 100) : "—" },
    { key: "visitors", label: t("Ziyaretçi"), cell: (row: MetricRow) => row.visitors == null ? "—" : integer.format(row.visitors), total: totalVisitors ? integer.format(totalVisitors) : "—" },
    { key: "receipts", label: t("Fiş Sayısı"), cell: (row: MetricRow) => integer.format(row.receipts), total: integer.format(totalReceipts) },
    { key: "units", label: t("Net Adet"), cell: (row: MetricRow) => integer.format(row.net_units), total: integer.format(totalUnits) },
    { key: "basket", label: t("Sepet Ort."), cell: (row: MetricRow) => integer.format(row.average_basket_uzs), total: integer.format(totalReceipts ? totalSales / totalReceipts : 0) },
  ];
  const columns = variant === "stock" ? stockColumns : weekdayColumns;
  if (withStore) columns.unshift({ key: "store", label: t("Mağaza"), cell: row => row.store ?? "—", total: t("TOPLAM") });
  if (withStore) columns.find(column => column.key === "label")!.total = "—";
  if (desktopInformationView()) {
    const optional = variant === "stock" ? ["lyUnitPrice","unitPriceChange"] : optionalLyKeys;
    const optionalTotals = storeOverviewOptionalDisplay(storeOverviewOptionalTotals(rows,{sales:totalSales,units:totalUnits,receipts:totalReceipts}));
    const approved = storeOverviewColumns(language);
    for (const key of optional) columns.push({key,label:approved.find(c=>c.key===key)!.label,cell:row=>String(storeOverviewOptionalDisplay(storeOverviewOptionalValues(row))[key]),total:String(optionalTotals[key])});
    if (variant === "weekday") {
      columns.push({key:"lyUpt",label:"LY UPT",cell:row=>row.previous_year_receipts != null && row.previous_year_receipts > 0 ? decimal.format(row.previous_year_upt) : "—",total:weekdayComparison.lyUpt == null ? "—" : decimal.format(weekdayComparison.lyUpt)});
      columns.push({key:"uptChange",label:t("vs LY UPT"),cell:row=>signedPercent(row.upt_change_percent),total:signedPercent(weekdayComparison.uptChange)});
      columns.push(...stockColumns.filter(c=>["lySales","salesChange"].includes(c.key)));
      columns.push({key:"unitPrice",label:t("Birim Fiyat"),cell:row=>integer.format(row.average_unit_price_uzs),total:integer.format(totalUnits?totalSales/totalUnits:0)});
    }
  }
  const visible = (key: string) => !hiddenColumns.has(key);
  const filteredRows = rows.filter((row) => !search.trim() || Object.values(row).some((value) => String(value ?? "").toLocaleLowerCase("tr").includes(search.trim().toLocaleLowerCase("tr"))));
  const toggleColumn = (key: string) => setHiddenColumns((current) => {
    if (desktopInformationView()) return standardToggle(current,key);
    const next=new Set(current);if(next.has(key))next.delete(key);else next.add(key);return next;
  });
  const gridColumns: ProfessionalGridColumn[] = columns.filter((column) => visible(column.key)).map((column, index) => ({
    key: column.key,
    label: desktopInformationView() ? desktopColumnLabel(column.key, column.label, language) : column.label,
    headerTooltip: desktopMetricHelp(column.key, language),
    minWidth: index === 0 ? 178 : column.key === "sales" || column.key === "lySales" ? 120 : 82,
    flex: index === 0 ? 1.6 : column.key === "sales" || column.key === "lySales" ? 1.2 : .85,
    align: index === 0 || (withStore && column.key === "label") ? "left" : "right",
    tone: column.key === "salesChange" || column.key === "unitsChange",
  }));
  const gridRows: ProfessionalGridRow[] = filteredRows.map((row) => ({
    __row_id: withStore ? storeCategoryRowId(row) : row.label,
    ...Object.fromEntries(columns.map((column) => [column.key, column.cell(row)])),
  }));
  const gridTotal: ProfessionalGridRow = Object.fromEntries(columns.map((column) => [column.key, column.total]));
  const categoryColumns = categoryOverviewColumns(columns, language, variant);
  const dataGrid = <ProfessionalDataGrid
    columns={gridColumns}
    rows={gridRows}
    totalRow={gridTotal}
    compact={compact}
    ariaLabel={categoryRegistryKey}
    mobilePrimaryKeys={withStore ? ["store", "label"] : ["label"]}
    mobileMetricKeys={variant === "stock" ? ["sales", "units", "stock"] : ["sales", "average", "upt", "cr", "units"]}
  />;
  return (
    <ReportSection className="report-section data-report-section" collapsible={collapsible} heading={<div className="report-title"><span /><div className="table-scope-heading"><h2>{t(title)}</h2>{categoryOverviewView && variant === "stock" ? <TableScopeInfo title={t(title)} language={language} text={withStore ? categoryView.copy.scope : t("Ciro payı seçili döneme; stok payı güncel stok adedine aittir.")} /> : null}</div>{categoryScope ? <label className="store-category-toggle"><input type="checkbox" checked={categoryByStore} onChange={event => { setCategoryByStore(event.target.checked); setSearch(""); }} />{categoryView.copy.toggle}</label> : null}</div>}>
      {availability?.loading ? <p className="report-caption" role="status">{t("Yükleniyor…")}</p> : availability?.error ? <p className="report-caption" role="alert">{t(availability.error)}</p> : <>
      {variant === "stock" ? <p className={categoryOverviewView ? "store-overview-sr-only" : "report-caption"}>{t("Ciro payı seçili döneme; stok payı güncel stok adedine aittir.")}</p> : null}
      <div className="report-toolbar">
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`🔍 ${t("Kod veya değer ara...")}`} />
        <button type="button" className={`report-columns-button${viewPanelOpen ? " active" : ""}`} onClick={() => setViewPanelOpen((value) => !value)}>☷ {t("Sütunları Göster/Gizle")}</button>
        {!categoryOverviewView ? <>
        <button type="button" className={`report-fit-button${compact ? " active" : ""}`} aria-pressed={compact} onClick={() => setCompact((value) => !value)}>↔ {t("Sütunları Sığdır")}</button>
        <button type="button" className="report-reset-button" onClick={() => { setSearch(""); setHiddenColumns(new Set()); setCompact(true); setViewPanelOpen(false); }}>↻ {t("Görünümü Sıfırla")}</button>
        </> : null}
        <ReportFullscreenButton />
        <b>{categoryView.loading ? t("Yükleniyor…") : `${integer.format(filteredRows.length)} ${t("kayıt bulundu")}`}</b>
        {viewPanelOpen ? <div className="report-view-panel"><strong>{t("Gösterilecek sütunlar")}</strong>{(desktopInformationView() ? standardMenu<{key:string;label:string}>(categoryOverviewView ? categoryColumns : columns) : columns).map((column) => <label key={column.key}><input type="checkbox" checked={visible(column.key)} onChange={() => toggleColumn(column.key)} /> {screenMetricColumnLabel(column.key, column.label, language)}</label>)}</div> : null}
      </div>
      {categoryView.loading ? <p className="report-caption" role="status">{categoryView.copy.loading}</p> : categoryView.error ? <p className="report-caption" role="alert">{categoryView.error}</p> : categoryOverviewView ? <FocusedStoreAnalysisTable key={withStore ? "store-category" : "category"} kind={variant}
        rows={gridRows} totalRow={gridTotal} columns={columns} language={language} hiddenColumns={hiddenColumns}
        ariaLabel={categoryRegistryKey} currentSort={() => gridSortRegistry.get(categoryRegistryKey) ?? null} parseNumber={parseGridNumber} onViewChange={recordCategoryView}
        sourceRows={rows} search={search} onSearchChange={setSearch} fullscreenControl={<ReportFullscreenButton />}>
      <CategoryOverviewTable key={withStore ? "store-category" : "category"}
        rows={gridRows} totalRow={gridTotal} columns={columns} language={language} hiddenColumns={hiddenColumns}
        variant={variant}
        ariaLabel={categoryRegistryKey} currentSort={() => gridSortRegistry.get(categoryRegistryKey) ?? null}
        parseNumber={parseGridNumber} onViewChange={recordCategoryView}
      >{dataGrid}</CategoryOverviewTable></FocusedStoreAnalysisTable> : dataGrid}
      </>}
    </ReportSection>
  );
}

function ExecutiveWeekdayTable({scope}:{scope:StoreCategoryScope}) {
  const language=useContext(LanguageContext),t=useT();
  const data=useExecutiveWeekdays<MetricRow>(scope);
  return <StoreAnalysisPresentationProvider scope={scope}>
    <StoreBreakdownSection kind="weekday" scope={scope} language={language} firstColumn={t("Gün")} aggregateRows={data.rows} disabled={data.loading||!!data.error}>
      <StoreDetailTable title="Haftanın Günleri Performansı" firstColumn="Gün" rows={data.rows} variant="weekday" categoryOverviewView collapsible availability={data} />
    </StoreBreakdownSection>
  </StoreAnalysisPresentationProvider>;
}

function AdviserTable({
  rows,
  total,
  asOfDate,
  fullMonthTarget,
}: {
  rows: AdviserRow[];
  total: KpiValues;
  asOfDate: string;
  fullMonthTarget: boolean;
}) {
  const t = useT();
  const language = useContext(LanguageContext);
  const [search, setSearch] = useState("");
  const [compact] = useState(true);
  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const recordAdviserView = useCallback((nextSort: MobileSortState, rowIds: string[]) => {
    const key = translate(language, "Satış Danışmanı Performansı");
    if (nextSort) gridSortRegistry.set(key, nextSort);
    else gridSortRegistry.delete(key);
    gridVisibleRowsRegistry.set(key, rowIds);
  }, [language]);
  const totalSales = total.net_sales_uzs;
  const totalTarget = rows.reduce((value, row) => value + (row.target_uzs ?? 0), 0);
  const totalUnits = total.net_units;
  const totalReceipts = total.receipts;
  const expectedAchievementPercent = rows.reduce((sum, row) => sum + (row.target_uzs ?? 0) * (row.target_progress_percent ?? targetProgressBenchmark(asOfDate, fullMonthTarget)), 0) / (totalTarget || 1);
  const targetText = (value: number | null) => (value === null ? t("Hedef Yok") : integer.format(value));
  const percentText = (value: number | null) => (value === null ? t("Hedef Yok") : `${oneDecimal.format(value)}%`);
  const columns = [
    ["store", "Mağaza"],
    ["name", "Satış Danışmanı"],
    ["target", "Hedef UZS"],
    ["sales", "Ciro UZS"],
    ["achievement", "Hedef %"],
    ["remaining", "Kalan Hedef UZS"],
    ["share", "Ciro Payı"],
    ["receipts", "Fiş Sayısı"],
    ["units", "Net Adet"],
    ["upt", "UPT"],
    ["basket", "Sepet Ort."],
    ["unit", "Birim Fiyat"],
  ] as const;
  const overviewColumns = adviserOverviewColumns(columns.map(([key, label]) => ({ key, label: t(label) })), language);
  const visible = (key: string) => !hiddenColumns.has(key);
  const toggleColumn = (key: string) =>
    setHiddenColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const term = search.trim().toLocaleLowerCase("tr");
  const filteredRows = rows.filter((row) => !term || `${row.store} ${row.name}`.toLocaleLowerCase("tr").includes(term));
  const gridColumns: ProfessionalGridColumn[] = columns.filter(([key]) => visible(key)).map(([key, label], index) => ({
    key,
    label: t(label),
    minWidth: index < 2 ? 150 : key === "target" || key === "sales" || key === "remaining" ? 118 : 82,
    flex: index < 2 ? 1.35 : 1,
    align: index < 2 ? "left" : "right",
    progressComparison: key === "achievement",
    headerTooltip: key === "achievement" ? t("Solda gerçekleşen hedef oranı, sağda seçili son tarihe kadar olması gereken oran gösterilir.") : undefined,
  }));
  const gridRows: ProfessionalGridRow[] = filteredRows.map((row) => ({
    __row_id: `${row.store}\u001f${row.name}`,
    __daily_required: formatAdviserDailyRequired(row.target_uzs, row.net_sales_uzs, asOfDate, fullMonthTarget),
    store: row.store,
    name: row.name,
    target: targetText(row.target_uzs),
    sales: integer.format(row.net_sales_uzs),
    achievement: percentText(row.target_achievement_percent),
    __sort_achievement: row.target_achievement_percent,
    __expected_achievement: row.target_uzs === null ? null : row.target_progress_percent,
    remaining: targetText(row.remaining_target_uzs),
    share: `${oneDecimal.format(row.revenue_share_percent)}%`,
    receipts: integer.format(row.receipts),
    units: integer.format(row.net_units),
    upt: decimal.format(row.upt),
    basket: integer.format(row.average_basket_uzs),
    unit: integer.format(row.average_unit_price_uzs),
  }));
  const gridTotal: ProfessionalGridRow = {
    __daily_required: formatAdviserDailyRequired(totalTarget, totalSales, asOfDate, fullMonthTarget),
    store: t("TOPLAM"), name: "—", target: integer.format(totalTarget), sales: integer.format(totalSales),
    achievement: totalTarget ? `${oneDecimal.format((totalSales / totalTarget) * 100)}%` : t("Hedef Yok"),
    __sort_achievement: totalTarget ? (totalSales / totalTarget) * 100 : null,
    __expected_achievement: totalTarget ? expectedAchievementPercent : null,
    remaining: totalTarget ? integer.format(Math.max(totalTarget - totalSales, 0)) : t("Hedef Yok"),
    share: "100,0%", receipts: integer.format(totalReceipts), units: integer.format(totalUnits),
    upt: decimal.format(totalReceipts ? totalUnits / totalReceipts : 0),
    basket: integer.format(totalReceipts ? totalSales / totalReceipts : 0),
    unit: integer.format(totalUnits ? totalSales / totalUnits : 0),
  };
  return (
    <section className="report-section adviser-section data-report-section">
      <div className="report-title">
        <span />
        <div>
          <div className="table-scope-heading"><h2>{t("Satış Danışmanı Performansı")}</h2><TableScopeInfo title={t("Satış Danışmanı Performansı")} language={language} text={t("Hedefler OX’taki ilgili ayın kişisel hedeflerinden alınır; gerçekleşme dashboarddaki doğrulanmış net satışla hesaplanır. Ciro payı danışmanın kendi mağazasına göredir. Aynı fişte birden fazla danışman varsa fiş her danışmanda sayılır; toplamda tek sayılır.")} /></div>
          <p className="store-overview-sr-only">{t("Hedefler OX’taki ilgili ayın kişisel hedeflerinden alınır; gerçekleşme dashboarddaki doğrulanmış net satışla hesaplanır. Ciro payı danışmanın kendi mağazasına göredir. Aynı fişte birden fazla danışman varsa fiş her danışmanda sayılır; toplamda tek sayılır.")}</p>
        </div>
      </div>
      <div className="report-toolbar">
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`🔍 ${t("Kod veya değer ara...")}`} />
        <button type="button" className={`report-columns-button${viewPanelOpen ? " active" : ""}`} onClick={() => setViewPanelOpen((value) => !value)}>
          ☷ {t("Sütunları Göster/Gizle")}
        </button>
        <ReportFullscreenButton />
        <b>{integer.format(filteredRows.length)} {t("kayıt bulundu")}</b>
        {viewPanelOpen ? (
          <div className="report-view-panel">
            <strong>{t("Gösterilecek sütunlar")}</strong>
            {overviewColumns.map(({ key, label }) => (
              <label key={key}>
                <input type="checkbox" checked={visible(key)} onChange={() => toggleColumn(key)} /> {label}
              </label>
            ))}
          </div>
        ) : null}
      </div>
      <FocusedStoreAnalysisTable kind="adviser" rows={gridRows} totalRow={gridTotal} columns={overviewColumns} language={language} hiddenColumns={hiddenColumns}
        expectedTarget={totalTarget ? expectedAchievementPercent : null} ariaLabel={t("Satış Danışmanı Performansı")} currentSort={() => gridSortRegistry.get(t("Satış Danışmanı Performansı")) ?? null}
        parseNumber={parseGridNumber} onViewChange={recordAdviserView} search={search} onSearchChange={setSearch} fullscreenControl={<ReportFullscreenButton />}>
      <AdviserOverviewTable
        rows={gridRows} totalRow={gridTotal} columns={overviewColumns} language={language} hiddenColumns={hiddenColumns}
        expectedTarget={totalTarget ? expectedAchievementPercent : null}
        ariaLabel={t("Satış Danışmanı Performansı")} currentSort={() => gridSortRegistry.get(t("Satış Danışmanı Performansı")) ?? null}
        parseNumber={parseGridNumber} onViewChange={recordAdviserView}
      >
      <ProfessionalDataGrid
        columns={visible("remaining") ? [...gridColumns, { key: "__daily_required", label: storeOverviewCopy(language)[5], minWidth: 118, align: "right", sortable: false }] : gridColumns}
        rows={gridRows}
        totalRow={gridTotal}
        compact={compact}
        ariaLabel={t("Satış Danışmanı Performansı")}
        mobilePrimaryKeys={["name", "store"]}
        mobileMetricKeys={["sales", "achievement", "upt", "receipts", "units"]}
      />
      </AdviserOverviewTable>
      </FocusedStoreAnalysisTable>
    </section>
  );
}

type ProductStockStatus = "new" | "on-track" | "risk" | "no-sales" | "out-of-stock";
type ProductStatusFilter = "all" | "risk" | "no-sales";

function productStockStatus(row: ProductPerformanceRow, asOfDate?: string): ProductStockStatus | null {
  if (["new", "on-track", "risk", "no-sales", "out-of-stock"].includes(row.status)) return row.status as ProductStockStatus;
  if (!asOfDate || row.season.trim().toLocaleUpperCase("tr").replaceAll("Ş", "S") !== "2026 KIS") return null;
  if (row.stock_units <= 0) return "out-of-stock";
  const asOf = Date.parse(asOfDate + "T00:00:00Z");
  const firstImport = row.first_import ? Date.parse(row.first_import.slice(0, 10) + "T00:00:00Z") : Number.NaN;
  if (Number.isFinite(asOf) && Number.isFinite(firstImport) && Math.floor((asOf - firstImport) / 86_400_000) < 7) return "new";
  if (row.daily_sales <= 0 || row.stock_days === null) return "no-sales";
  const projectedEnd = asOf + Math.ceil(row.stock_days) * 86_400_000;
  if (projectedEnd > Date.parse("2027-02-28T00:00:00Z")) return "risk";
  return "on-track";
}

function productStockStatusLabel(status: ProductStockStatus | null) {
  if (status === "new") return "Yeni";
  if (status === "on-track") return "Sezonda Biter";
  if (status === "risk") return "Riskli";
  if (status === "no-sales") return "Satış Yok";
  if (status === "out-of-stock") return "Stok Bitti";
  return "—";
}

function productStockStatusDescription(status: ProductStockStatus | null) {
  if (status === "new") return "İlk importtan sonra henüz 7 gün dolmadı.";
  if (status === "on-track") return "Bu satış hızıyla stok sezon bitmeden tükenir.";
  if (status === "risk") return "Bu satış hızıyla 28.02.2027 tarihinde stok kalır.";
  if (status === "no-sales") return "Yeni ürün süresi geçti; stok var ancak satış hızı sıfır.";
  if (status === "out-of-stock") return "Güncel stok sıfırdır.";
  return "Durum, 2026 Kış sezon sonu 28.02.2027’ye göre hesaplanır.";
}

function productStockStatusRank(status: ProductStockStatus | null) {
  if (status === "risk") return 1;
  if (status === "no-sales") return 2;
  if (status === "new") return 3;
  if (status === "on-track") return 4;
  if (status === "out-of-stock") return 5;
  return 99;
}

function productRowsWithStatus(rows: ProductPerformanceRow[], asOfDate: string) {
  return rows.map((row) => ({ ...row, status: productStockStatus(row, asOfDate) ?? "" }));
}

function ProductPerformanceTable({
  title,
  firstColumn,
  rows,
  loadPage,
  statusFilter,
  onStatusFilterChange,
  asOfDate,
}: {
  title: string;
  firstColumn: string;
  rows: ProductPerformanceRow[];
  loadPage: (offset: number, limit: number, search: string, sort: GridSortState, statusFilter: ProductStatusFilter) => Promise<ProductTablePage>;
  statusFilter: ProductStatusFilter;
  onStatusFilterChange: (value: ProductStatusFilter) => void;
  asOfDate: string;
}) {
  const t = useT();
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [compact, setCompact] = useState(true);
  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageRows, setPageRows] = useState(rows.slice(0, 500));
  const [totalRows, setTotalRows] = useState(rows[0]?.total_rows ?? rows.length);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(false);
  const activeSort = useRef<GridSortState>(gridSortRegistry.get(t(title)) ?? null);
  const requestSequence = useRef(0);
  const pageSize = 500;
  useEffect(() => {
    setSearch("");
    setAppliedSearch("");
    gridSearchRegistry.delete(t(title));
    setPage(1);
    setPageRows(statusFilter === "all" ? rows.slice(0, pageSize) : []);
    setTotalRows(statusFilter === "all" ? rows[0]?.total_rows ?? rows.length : 0);
    setPageError(false);
    if (activeSort.current || statusFilter !== "all") void showPage(1, "", activeSort.current);
  }, [rows, statusFilter]);
  const visibleTotals = pageRows.reduce(
    (value, row) => ({
      imported: value.imported + row.import_units,
      sales: value.sales + row.sales_units,
      stock: value.stock + row.stock_units,
      revenue: value.revenue + row.average_unit_price_uzs * row.sales_units,
      gross: value.gross + row.price_uzs * row.sales_units,
    }),
    { imported: 0, sales: 0, stock: 0, revenue: 0, gross: 0 },
  );
  const first = pageRows[0] ?? rows[0];
  const totals = first
    ? {
        imported: first.overall_import_units,
        sales: first.overall_sales_units,
        stock: first.overall_stock_units,
        revenue: first.overall_revenue_uzs,
        gross: first.overall_gross_uzs,
      }
    : visibleTotals;
  const totalDiscount = totals.gross > 0 ? (1 - totals.revenue / totals.gross) * 100 : null;
  const nullable = (value: number | null, suffix = "") => (value === null ? "—" : `${decimal.format(value)}${suffix}`);
  const columns = [
    ["label", firstColumn],
    ["firstImport", "İlk İmport"],
    ["lastImport", "Son İmport"],
    ["import", "İmport Adet"],
    ["sales", "Adet"],
    ["stock", "Stok"],
    ["sellthrough", "Sell-through"],
    ["daily", "Günlük Satış"],
    ["stockDays", "Stok Günü"],
    ["last", "Son Satış"],
    ["status", "Durum"],
    ["price", "Fiyat"],
    ["unit", "Birim Fiyat"],
    ["discount", "İndirim %"],
  ] as const;
  const visible = (key: string) => !hiddenColumns.has(key);
  const toggleColumn = (key: string) =>
    setHiddenColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPage = Math.min(page, pageCount);
  async function showPage(nextPage: number, nextSearch = appliedSearch, nextSort = activeSort.current) {
    const boundedPage = Math.max(1, nextPage);
    const requestId = ++requestSequence.current;
    setPageLoading(true);
    setPageError(false);
    try {
      const result = await loadPage((boundedPage - 1) * pageSize, pageSize, nextSearch, nextSort, statusFilter);
      if (requestId !== requestSequence.current) return;
      setPageRows(result.rows);
      setTotalRows(result.total_rows);
      setPage(boundedPage);
      setAppliedSearch(nextSearch);
      if (nextSearch) gridSearchRegistry.set(t(title), nextSearch);
      else gridSearchRegistry.delete(t(title));
    } catch {
      if (requestId === requestSequence.current) setPageError(true);
    } finally {
      if (requestId === requestSequence.current) setPageLoading(false);
    }
  }
  const handleSortChange = (nextSort: GridSortState) => {
    const previous = activeSort.current;
    if (previous?.key === nextSort?.key && previous?.direction === nextSort?.direction) return;
    activeSort.current = nextSort;
    void showPage(1, appliedSearch, nextSort);
  };
  const gridColumns: ProfessionalGridColumn[] = columns.filter(([key]) => visible(key)).map(([key, label], index) => ({
    key,
    label: t(label),
    minWidth: index === 0 ? 165 : ["firstImport", "lastImport", "last"].includes(key) ? 104 : key === "status" ? 76 : 78,
    flex: index === 0 ? 1.6 : .85,
    align: index === 0 ? "left" : key === "status" ? "center" : "right",
    sortType: ["firstImport", "lastImport", "last"].includes(key) ? "date" : index === 0 ? "text" : "number",
    screenMetric: key === "sales" ? "units" : undefined,
    statusTone: key === "status",
    headerTooltip: key === "status" ? t("Durum, 2026 Kış sezon sonu 28.02.2027’ye göre hesaplanır.") : undefined,
    sortable: true,
  }));
  const gridRows: ProfessionalGridRow[] = pageRows.map((row) => ({
    __row_id: row.label,
    label: row.label, category: row.category, season: row.season,
    import: integer.format(row.import_units),
    firstImport: row.first_import ? displayDate(row.first_import) : "—",
    lastImport: row.last_import ? displayDate(row.last_import) : "—",
    sales: integer.format(row.sales_units), stock: integer.format(row.stock_units),
    sellthrough: nullable(row.sellthrough_percent, "%"), price: integer.format(row.price_uzs), unit: integer.format(row.average_unit_price_uzs),
    discount: nullable(row.discount_percent, "%"), daily: decimal.format(row.daily_sales),
    stockDays: row.stock_days === null ? "—" : integer.format(row.stock_days),
    first: row.first_sale ? displayDate(row.first_sale) : t("Satış Yok"),
    last: row.last_sale ? displayDate(row.last_sale) : t("Satış Yok"),
    status: t(productStockStatusLabel(productStockStatus(row, asOfDate))),
    __status_status: productStockStatus(row, asOfDate),
    __tooltip_status: t(productStockStatusDescription(productStockStatus(row, asOfDate))),
    __sort_import: row.import_units,
    __sort_firstImport: row.first_import,
    __sort_lastImport: row.last_import,
    __sort_sales: row.sales_units,
    __sort_stock: row.stock_units,
    __sort_sellthrough: row.sellthrough_percent,
    __sort_price: row.price_uzs,
    __sort_unit: row.average_unit_price_uzs,
    __sort_discount: row.discount_percent,
    __sort_daily: row.daily_sales,
    __sort_stockDays: row.stock_days,
    __sort_first: row.first_sale,
    __sort_last: row.last_sale,
    __sort_status: productStockStatusRank(productStockStatus(row, asOfDate)),
  }));
  const gridTotal: ProfessionalGridRow = {
    label: t("TOPLAM"), category: "—", season: "—", import: integer.format(totals.imported), firstImport: "—", lastImport: "—", sales: integer.format(totals.sales),
    stock: integer.format(totals.stock), sellthrough: nullable(totals.sales + totals.stock ? (totals.sales / (totals.sales + totals.stock)) * 100 : null, "%"),
    price: "—", unit: integer.format(totals.sales ? totals.revenue / totals.sales : 0), discount: nullable(totalDiscount, "%"),
    daily: "—", stockDays: "—", first: "—", last: "—", status: "—",
  };
  return (
    <section className="report-section product-section data-report-section">
      <div className="report-title">
        <span />
        <div>
          <h2>{t(title)}</h2>
          <p>{t("Satışı olmayan ürünler indirim ortalamasına katılmaz.")}</p>
        </div>
      </div>
      <div className="report-toolbar">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void showPage(1, search.trim());
          }}
          placeholder={`🔍 ${t("Kod veya değer ara...")}`}
        />
        <button type="button" disabled={pageLoading} onClick={() => void showPage(1, search.trim())}>
          {t("Ara")}
        </button>
        <button type="button" className={`report-columns-button${viewPanelOpen ? " active" : ""}`} onClick={() => setViewPanelOpen((value) => !value)}>
          ☷ {t("Sütunları Göster/Gizle")}
        </button>
        <button type="button" className={`report-fit-button${compact ? " active" : ""}`} aria-pressed={compact} onClick={() => setCompact((value) => !value)}>
          ↔ {t("Sütunları Sığdır")}
        </button>
        <button
          type="button"
          onClick={() => {
            setSearch("");
            setAppliedSearch("");
            setHiddenColumns(new Set());
            setCompact(true);
            setViewPanelOpen(false);
            if (statusFilter === "all") void showPage(1, "");
            else onStatusFilterChange("all");
          }}
        >
          ↻ {t("Görünümü Sıfırla")}
        </button>
        <ReportFullscreenButton />
        <label className="product-status-inline-filter">
          <span>{t("Durum")}</span>
          <select value={statusFilter} disabled={pageLoading} onChange={(event) => onStatusFilterChange(event.target.value as ProductStatusFilter)}>
            <option value="all">{t("Tümü")}</option>
            <option value="risk">{t("Riskli")}</option>
            <option value="no-sales">{t("Satış Yok")}</option>
          </select>
        </label>
        <details className="product-status-help product-status-help--inline">
          <summary aria-label={t("Durumlar ne demek?")} title={t("Durumlar ne demek?")}><CircleHelp aria-hidden="true" size={16} /></summary>
          <div>
            {(["new", "on-track", "risk", "no-sales", "out-of-stock"] as ProductStockStatus[]).map((status) => (
              <p key={status}>
                <strong className={"product-status-help__" + status}>{t(productStockStatusLabel(status))}</strong>
                <span>{t(productStockStatusDescription(status))}</span>
              </p>
            ))}
          </div>
        </details>
        <button type="button" disabled={pageLoading || currentPage <= 1} onClick={() => void showPage(currentPage - 1)}>
          ‹ {t("Önceki")}
        </button>
        <button type="button" disabled={pageLoading || currentPage >= pageCount} onClick={() => void showPage(currentPage + 1)}>
          {t("Sonraki")} ›
        </button>
        <b>
          {integer.format(totalRows)} {t("kayıt bulundu")}
        </b>
        {viewPanelOpen ? (
          <div className="report-view-panel">
            <strong>{t("Gösterilecek sütunlar")}</strong>
            {columns.map(([key, label]) => (
              <label key={key}>
                <input type="checkbox" checked={visible(key)} onChange={() => toggleColumn(key)} /> {t(label)}
              </label>
            ))}
          </div>
        ) : null}
      </div>
      {pageError ? <div className="inline-notice error">{t("Veri açılamadı.")}</div> : null}
      <ProfessionalDataGrid
        columns={gridColumns}
        rows={gridRows}
        totalRow={gridTotal}
        compact={compact}
        onSortChange={handleSortChange}
        ariaLabel={t(title)}
        mobilePrimaryKeys={["label"]}
        mobileMetricKeys={["sales", "stock", "price"]}
      />
      <div className="table-pagination">
        <span>
          {t("Sayfa")} {currentPage} / {pageCount}
        </span>
      </div>
    </section>
  );
}

function ProductDimensionTable({ title, firstColumn, rows, showColorName = false }: { title: string; firstColumn: string; rows: ProductDimensionRow[]; showColorName?: boolean }) {
  const t = useT();
  const [search, setSearch] = useState("");
  const visibleTotals = rows.reduce(
    (value, row) => ({
      imported: value.imported + row.import_units,
      sales: value.sales + row.sales_units,
      stock: value.stock + row.stock_units,
    }),
    { imported: 0, sales: 0, stock: 0 },
  );
  const first = rows[0];
  const totals = first
    ? {
        imported: first.overall_import_units,
        sales: first.overall_sales_units,
        stock: first.overall_stock_units,
      }
    : visibleTotals;
  const totalRows = first?.total_rows ?? rows.length;
  const term = search.trim().toLocaleLowerCase("tr");
  const filteredRows = rows.filter((row) => !term || `${row.label} ${row.color_name}`.toLocaleLowerCase("tr").includes(term));
  const gridColumns: ProfessionalGridColumn[] = [
    { key: "label", label: t(firstColumn), minWidth: 170, flex: 1.7, align: "left" },
    ...(showColorName ? [{ key: "colorName", label: t("Renk Adı"), minWidth: 130, flex: 1.25, align: "left" as const }] : []),
    { key: "import", label: t("İmport"), minWidth: 90, align: "right" },
    { key: "sales", label: t("Adet"), screenMetric: "units", minWidth: 90, align: "right" },
    { key: "stock", label: t("Stok"), minWidth: 90, align: "right" },
    { key: "sellthrough", label: "Sellthrough", minWidth: 105, align: "right" },
  ];
  const gridRows: ProfessionalGridRow[] = filteredRows.map((row) => ({
    __row_id: row.label,
    label: row.label,
    colorName: row.color_name || t("Bilgi Yok"),
    import: integer.format(row.import_units),
    sales: integer.format(row.sales_units),
    stock: integer.format(row.stock_units),
    sellthrough: row.sales_units + row.stock_units ? `${oneDecimal.format((row.sales_units / (row.sales_units + row.stock_units)) * 100)}%` : "—",
  }));
  const gridTotal: ProfessionalGridRow = {
    label: t("TOPLAM"), colorName: "—", import: integer.format(totals.imported), sales: integer.format(totals.sales), stock: integer.format(totals.stock),
    sellthrough: totals.sales + totals.stock ? `${oneDecimal.format((totals.sales / (totals.sales + totals.stock)) * 100)}%` : "—",
  };
  return (
    <section className="report-section product-dimension-section data-report-section">
      <div className="report-title">
        <span />
        <h2>{t(title)}</h2>
      </div>
      <div className="report-toolbar report-toolbar--search-only">
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`🔍 ${t("Kod veya değer ara...")}`} />
        <b>
          {integer.format(filteredRows.length)} / {integer.format(totalRows)} {t("kayıt")}
        </b>
      </div>
      <ProfessionalDataGrid
        columns={gridColumns}
        rows={gridRows}
        totalRow={gridTotal}
        compact
        ariaLabel={t(title)}
        mobilePrimaryKeys={showColorName ? ["label", "colorName"] : ["label"]}
        mobileMetricKeys={["sales", "stock"]}
      />
    </section>
  );
}

type StorePerformanceAggregate = {
  target: number;
  sales: number;
  visitors: number;
  receipts: number;
  units: number;
  previousSales: number;
  previousReceipts: number;
  previousUnits: number;
};

function storePerformanceAggregate(rows: BrandedStoreMetric[]): StorePerformanceAggregate {
  return rows.reduce<StorePerformanceAggregate>(
    (total, row) => {
      total.target += row.target_uzs ?? 0;
      total.sales += row.net_sales_uzs;
      total.visitors += row.visitors ?? 0;
      total.receipts += row.receipts;
      total.units += row.net_units;
      total.previousSales += row.previous_year_sales_uzs;
      total.previousUnits += row.previous_year_units;
      total.previousReceipts += row.previous_year_receipts;
      return total;
    },
    { target: 0, sales: 0, visitors: 0, receipts: 0, units: 0, previousSales: 0, previousReceipts: 0, previousUnits: 0 },
  );
}

function performancePercent(value: number | null) {
  return value === null || !Number.isFinite(value) ? "—" : `${oneDecimal.format(value)}%`;
}

function performanceChange(current: number, previous: number) {
  const value = ratio(current, previous);
  return value === null ? "—" : `${value >= 0 ? "+" : ""}${oneDecimal.format(value)}%`;
}

function storePerformanceSortValue(row: BrandedStoreMetric, key: string): number | string | null {
  const values = storePerformanceAggregate([row]);
  const currentUpt = values.receipts ? values.units / values.receipts : 0;
  const currentAtv = values.receipts ? values.sales / values.receipts : 0;
  const currentAvp = values.units ? values.sales / values.units : 0;
  const previousUpt = values.previousReceipts ? values.previousUnits / values.previousReceipts : 0;
  const previousAtv = values.previousReceipts ? values.previousSales / values.previousReceipts : 0;
  const previousAvp = values.previousUnits ? values.previousSales / values.previousUnits : 0;
  const numeric: Record<string, number | null> = {
    target: values.target,
    sales: values.sales,
    visitors: values.visitors,
    receipts: values.receipts,
    units: values.units,
    conversion: values.visitors ? values.receipts / values.visitors : null,
    upt: currentUpt,
    atv: currentAtv,
    avp: currentAvp,
    previousSales: values.previousSales,
    salesChange: values.previousSales ? (values.sales - values.previousSales) / Math.abs(values.previousSales) : null,
    previousReceipts: values.previousReceipts,
    receiptsChange: values.previousReceipts ? (values.receipts - values.previousReceipts) / Math.abs(values.previousReceipts) : null,
    previousUnits: values.previousUnits,
    unitsChange: values.previousUnits ? (values.units - values.previousUnits) / Math.abs(values.previousUnits) : null,
    previousUpt,
    uptChange: previousUpt ? (currentUpt - previousUpt) / Math.abs(previousUpt) : null,
    previousAtv,
    atvChange: previousAtv ? (currentAtv - previousAtv) / Math.abs(previousAtv) : null,
    previousAvp,
    avpChange: previousAvp ? (currentAvp - previousAvp) / Math.abs(previousAvp) : null,
  };
  if (key === "brand") return row.brand;
  if (key === "store") return row.label;
  return Object.prototype.hasOwnProperty.call(numeric, key) ? numeric[key] : null;
}

function sortStorePerformanceRows(rows: BrandedStoreMetric[], sort: GridSortState) {
  const direction = sort?.direction === "asc" ? 1 : -1;
  const key = sort?.key ?? "sales";
  return [...rows].sort((left, right) => {
    const a = storePerformanceSortValue(left, key);
    const b = storePerformanceSortValue(right, key);
    if (a == null) return b == null ? left.label.localeCompare(right.label, "tr") : 1;
    if (b == null) return -1;
    const compared = typeof a === "number" && typeof b === "number" ? a - b : numericLabelCollator.compare(String(a), String(b));
    return compared ? compared * direction : left.label.localeCompare(right.label, "tr");
  });
}

export async function downloadStorePerformanceWorkbook(report: StorePerformanceReport, brands: string[], malls: string[], stores: string[], language: LanguageCode, sorts: StorePerformanceSorts) {
  const t = (value: string) => translate(language, value);
  const excelDelta = (current: number, previous: number) => previous ? (current - previous) / Math.abs(previous) : null;
  const currentYear = report.period.end.slice(0, 4);
  const previousYear = report.previous_year_period.end.slice(0, 4);
  const grouped = new Map<string, BrandedStoreMetric[]>();
  report.rows.forEach((row) => grouped.set(row.brand, [...(grouped.get(row.brand) ?? []), row]));
  const groups = Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b, "tr"));
  const valueRow = (brand: string, store: string, values: StorePerformanceAggregate, view: StorePerformanceView): WorkbookCell[] => {
    const currentUpt = values.receipts ? values.units / values.receipts : 0;
    const currentAtv = values.receipts ? values.sales / values.receipts : 0;
    const currentAvp = values.units ? values.sales / values.units : 0;
    const previousUpt = values.previousReceipts ? values.previousUnits / values.previousReceipts : 0;
    const previousAtv = values.previousReceipts ? values.previousSales / values.previousReceipts : 0;
    const previousAvp = values.previousUnits ? values.previousSales / values.previousUnits : 0;
    const conversion = values.visitors ? values.receipts / values.visitors : null;
    if (view === "current") return [brand, store, values.target, values.sales, values.visitors, values.receipts, values.units, conversion, currentUpt, currentAtv, currentAvp];
    if (view === "summary") return [brand, store, values.previousSales, values.sales, excelDelta(values.sales, values.previousSales), previousUpt, currentUpt, excelDelta(currentUpt, previousUpt), values.visitors, values.receipts, conversion];
    return [
      brand, store,
      values.previousSales, values.sales, excelDelta(values.sales, values.previousSales),
      values.previousReceipts, values.receipts, excelDelta(values.receipts, values.previousReceipts),
      values.previousUnits, values.units, excelDelta(values.units, values.previousUnits),
      previousUpt, currentUpt, excelDelta(currentUpt, previousUpt),
      previousAtv, currentAtv, excelDelta(currentAtv, previousAtv),
      previousAvp, currentAvp, excelDelta(currentAvp, previousAvp),
      values.visitors, conversion,
    ];
  };
  const dataRows = (view: StorePerformanceView) => {
    const rows: WorkbookCell[][] = [];
    const rowKinds: WorkbookRowKind[] = ["header"];
    groups.forEach(([brand, brandRows]) => {
      sortStorePerformanceRows(brandRows, sorts[view])
        .forEach((row) => {
          rows.push(valueRow(brand, row.label, storePerformanceAggregate([row]), view));
          rowKinds.push("detail");
        });
      rows.push(valueRow("", `${t("Toplam")} ${brand}`, storePerformanceAggregate(brandRows), view));
      rowKinds.push("subtotal");
    });
    rows.push(valueRow(t("TOPLAM"), t("Genel Toplam"), storePerformanceAggregate(report.rows), view));
    rowKinds.push("total");
    return { rows, rowKinds };
  };
  const currentHeader: WorkbookCell[] = [t("Marka"), t("Mağaza"), `${t("Mağaza Hedef")}`, `${t("Satış")}`, t("Ziyaretçi"), t("Müşteri"), t("Satış Adet"), "CR %", "UPT", "ATV", "AVP"];
  const comparisonHeader: WorkbookCell[] = [
    t("Marka"), t("Mağaza"), `${t("Satış")} ${previousYear}`, `${t("Satış")} ${currentYear}`, t("vs LY Satış"),
    `${t("Müşteri")} ${previousYear}`, `${t("Müşteri")} ${currentYear}`, t("vs LY Müşteri"),
    `${t("Satış Adet")} ${previousYear}`, `${t("Satış Adet")} ${currentYear}`, t("vs LY Adet"),
    `UPT ${previousYear}`, `UPT ${currentYear}`, t("vs LY UPT"),
    `ATV ${previousYear}`, `ATV ${currentYear}`, t("vs LY ATV"),
    `AVP ${previousYear}`, `AVP ${currentYear}`, t("vs LY AVP"),
    `${t("Ziyaretçi")} ${currentYear}`, `CR ${currentYear}`,
  ];
  const summaryHeader: WorkbookCell[] = [t("Marka"), t("Mağaza"), `${t("Satış")} ${previousYear}`, `${t("Satış")} ${currentYear}`, t("vs LY Satış"), `UPT ${previousYear}`, `UPT ${currentYear}`, t("vs LY UPT"), `${t("Ziyaretçi")} ${currentYear}`, `${t("Müşteri")} ${currentYear}`, `CR ${currentYear}`];
  const selection = (values: string[]) => values.length ? values.join(", ") : t("Tümü");
  const reportSubtitle = `${t("Dönem")}: ${displayDate(report.period.start)} – ${displayDate(report.period.end)}  |  ${t("Marka")}: ${selection(brands)}  |  ${t("Alışveriş Merkezi")}: ${selection(malls)}  |  ${t("Mağaza")}: ${selection(stores)}`;
  const current = dataRows("current");
  const comparison = dataRows("comparison");
  const summary = dataRows("summary");
  const sheets: WorkbookSheet[] = [
    {
      name: t("Güncel").toLocaleUpperCase(language === "tr" ? "tr-TR" : undefined),
      rows: [currentHeader, ...current.rows],
      rowKinds: current.rowKinds,
      integers: [3, 4, 5, 6, 7, 10, 11],
      decimals: [9],
      percentages: [8],
      widths: [21, 25, 16, 14, 12, 11, 13, 9, 8, 12, 12],
      brandColumn: 1,
      theme: "report",
      totalStyle: "bold",
      tabColor: "FF0F8C83",
      freezeColumns: 2,
    },
    {
      name: t("VS LY"),
      rows: [comparisonHeader, ...comparison.rows],
      rowKinds: comparison.rowKinds,
      integers: [3, 4, 6, 7, 9, 10, 15, 16, 18, 19, 21],
      decimals: [12, 13],
      percentages: [5, 8, 11, 14, 17, 20, 22],
      widths: [23, 25, 14, 14, 11, 12, 12, 14, 13, 13, 11, 9, 9, 11, 13, 13, 11, 13, 13, 11, 12, 9],
      brandColumn: 1,
      colorScaleColumns: [5],
      colorScaleColors: ["FFFF0000", "FFFFFFFF", "FF92D050"],
      colorScaleThresholds: [-0.2, 0, 0.2],
      theme: "report",
      totalStyle: "bold",
      tabColor: "FFF4B183",
      freezeColumns: 2,
    },
    {
      name: t("Özet").toLocaleUpperCase(language === "tr" ? "tr-TR" : undefined),
      rows: [summaryHeader, ...summary.rows],
      rowKinds: summary.rowKinds,
      integers: [3, 4, 9, 10],
      decimals: [6, 7],
      percentages: [5, 8, 11],
      widths: [23, 25, 14, 14, 11, 9, 9, 11, 12, 12, 9],
      brandColumn: 1,
      colorScaleColumns: [5],
      colorScaleColors: ["FFFF0000", "FFFFFFFF", "FF92D050"],
      colorScaleThresholds: [-0.2, 0, 0.2],
      theme: "report",
      totalStyle: "yellow",
      tabColor: "FFFFFF00",
      freezeColumns: 2,
    },
    {
      name: t("Rapor Bilgisi"),
      title: t("Rapor Bilgisi"),
      subtitle: t("Dosyanın kapsamı ve veri paketi bilgileri"),
      theme: "info",
      autoFilter: false,
      widths: [24, 56],
      tabColor: "FF153F55",
      rows: [
        [t("Alan"), t("Değer")],
        [t("Rapor"), t("Mağaza Performansı")],
        [t("Dönem"), `${displayDate(report.period.start)} – ${displayDate(report.period.end)}`],
        [t("Geçen Yıl Dönemi"), `${displayDate(report.previous_year_period.start)} – ${displayDate(report.previous_year_period.end)}`],
        [t("Marka"), selection(brands)],
        [t("Alışveriş Merkezi"), selection(malls)],
        [t("Mağaza"), selection(stores)],
        [t("Veri Paketi"), report.release_id],
      ],
    },
  ];
  await downloadWorkbook(sheets, `magaza_performans_raporlari_${report.period.end}_${language}.xlsx`, {
    reportKind: "store-performance",
    title: t("Mağaza Performansı"),
    subject: reportSubtitle,
  });
}

function performanceGridRow(brand: string, store: string, values: StorePerformanceAggregate, view: StorePerformanceView, kind?: "subtotal", group = brand): ProfessionalGridRow {
  const currentUpt = values.receipts ? values.units / values.receipts : 0;
  const currentAtv = values.receipts ? values.sales / values.receipts : 0;
  const currentAvp = values.units ? values.sales / values.units : 0;
  const previousUpt = values.previousReceipts ? values.previousUnits / values.previousReceipts : 0;
  const previousAtv = values.previousReceipts ? values.previousSales / values.previousReceipts : 0;
  const previousAvp = values.previousUnits ? values.previousSales / values.previousUnits : 0;
  const base: ProfessionalGridRow = { brand, store, __kind: kind ?? "detail", __group: group };
  if (view === "current") return {
    ...base,
    target: integer.format(values.target), sales: integer.format(values.sales), visitors: integer.format(values.visitors),
    receipts: integer.format(values.receipts), units: integer.format(values.units),
    conversion: performancePercent(values.visitors ? (values.receipts / values.visitors) * 100 : null),
    upt: decimal.format(currentUpt), atv: integer.format(currentAtv), avp: integer.format(currentAvp),
  };
  if (view === "summary") return {
    ...base,
    previousSales: integer.format(values.previousSales), sales: integer.format(values.sales), salesChange: performanceChange(values.sales, values.previousSales),
    previousUpt: decimal.format(previousUpt), upt: decimal.format(currentUpt), uptChange: performanceChange(currentUpt, previousUpt),
    visitors: integer.format(values.visitors), receipts: integer.format(values.receipts),
    conversion: performancePercent(values.visitors ? (values.receipts / values.visitors) * 100 : null),
  };
  return {
    ...base,
    previousSales: integer.format(values.previousSales), sales: integer.format(values.sales), salesChange: performanceChange(values.sales, values.previousSales),
    previousReceipts: integer.format(values.previousReceipts), receipts: integer.format(values.receipts), receiptsChange: performanceChange(values.receipts, values.previousReceipts),
    previousUnits: integer.format(values.previousUnits), units: integer.format(values.units), unitsChange: performanceChange(values.units, values.previousUnits),
    previousUpt: decimal.format(previousUpt), upt: decimal.format(currentUpt), uptChange: performanceChange(currentUpt, previousUpt),
    previousAtv: integer.format(previousAtv), atv: integer.format(currentAtv), atvChange: performanceChange(currentAtv, previousAtv),
    previousAvp: integer.format(previousAvp), avp: integer.format(currentAvp), avpChange: performanceChange(currentAvp, previousAvp),
    visitors: integer.format(values.visitors), conversion: performancePercent(values.visitors ? (values.receipts / values.visitors) * 100 : null),
  };
}

function StorePerformanceReports({ report, view, brands, malls, stores, brandOptions, mallOptions, storeOptions, start, end, minDate, maxDate, loading, exporting, onView, onBrands, onMalls, onStores, onStart, onEnd, onApply, onExport }: {
  report: StorePerformanceReport | null;
  view: StorePerformanceView;
  brands: string[];
  malls: string[];
  stores: string[];
  brandOptions: string[];
  mallOptions: string[];
  storeOptions: string[];
  start: string;
  end: string;
  minDate?: string;
  maxDate?: string;
  loading: boolean;
  exporting: boolean;
  onView: (view: StorePerformanceView) => void;
  onBrands: (values: string[]) => void;
  onMalls: (values: string[]) => void;
  onStores: (values: string[]) => void;
  onStart: (value: string) => void;
  onEnd: (value: string) => void;
  onApply: () => void;
  onExport: (sorts: StorePerformanceSorts) => void;
}) {
  const t = useT();
  const [sortStates, setSortStates] = useState<StorePerformanceSorts>({ current: null, comparison: null, summary: null });
  const currentYear = report?.period.end.slice(0, 4) ?? end.slice(0, 4);
  const previousYear = report?.previous_year_period.end.slice(0, 4) ?? String(Number(currentYear) - 1);
  const columns = useMemo<ProfessionalGridColumn[]>(() => {
    const first: ProfessionalGridColumn[] = [
      { key: "brand", label: t("Marka"), minWidth: 112, align: "left", band: "label" },
      { key: "store", label: t("Mağaza"), minWidth: 180, flex: 1.5, align: "left", band: "label" },
    ];
    if (view === "current") return [...first,
      { key: "target", label: `${t("Mağaza Hedef")}`, minWidth: 126, band: "context" }, { key: "sales", label: `${t("Satış")}`, minWidth: 126, band: "current" },
      { key: "visitors", label: t("Ziyaretçi"), minWidth: 92, band: "current" }, { key: "receipts", label: t("Müşteri"), minWidth: 92, band: "current" },
      { key: "units", label: t("Satış Adet"), minWidth: 96, band: "current" }, { key: "conversion", label: "CR %", minWidth: 78, band: "current" },
      { key: "upt", label: "UPT", minWidth: 74, band: "current" }, { key: "atv", label: "ATV", minWidth: 102, band: "current" }, { key: "avp", label: "AVP", minWidth: 102, band: "current" },
    ];
    if (view === "summary") return [...first,
      { key: "previousSales", label: `${t("Satış")} ${previousYear}`, minWidth: 128, band: "previous" }, { key: "sales", label: `${t("Satış")} ${currentYear}`, minWidth: 128, band: "current" },
      { key: "salesChange", label: t("vs LY Satış"), minWidth: 98, tone: true, band: "variance" }, { key: "previousUpt", label: `UPT ${previousYear}`, minWidth: 88, band: "previous" },
      { key: "upt", label: `UPT ${currentYear}`, minWidth: 88, band: "current" }, { key: "uptChange", label: t("vs LY UPT"), minWidth: 96, tone: true, band: "variance" },
      { key: "visitors", label: `${t("Ziyaretçi")} ${currentYear}`, minWidth: 104, band: "current" }, { key: "receipts", label: `${t("Müşteri")} ${currentYear}`, minWidth: 102, band: "current" },
      { key: "conversion", label: `CR ${currentYear}`, minWidth: 88, band: "current" },
    ];
    return [...first,
      { key: "previousSales", label: `${t("Satış")} ${previousYear}`, minWidth: 128, band: "previous" }, { key: "sales", label: `${t("Satış")} ${currentYear}`, minWidth: 128, band: "current" }, { key: "salesChange", label: t("vs LY Satış"), minWidth: 98, tone: true, band: "variance" },
      { key: "previousReceipts", label: `${t("Müşteri")} ${previousYear}`, minWidth: 100, band: "previous" }, { key: "receipts", label: `${t("Müşteri")} ${currentYear}`, minWidth: 100, band: "current" }, { key: "receiptsChange", label: t("vs LY Müşteri"), minWidth: 112, tone: true, band: "variance" },
      { key: "previousUnits", label: `${t("Satış Adet")} ${previousYear}`, minWidth: 108, band: "previous" }, { key: "units", label: `${t("Satış Adet")} ${currentYear}`, minWidth: 108, band: "current" }, { key: "unitsChange", label: t("vs LY Adet"), minWidth: 96, tone: true, band: "variance" },
      { key: "previousUpt", label: `UPT ${previousYear}`, minWidth: 86, band: "previous" }, { key: "upt", label: `UPT ${currentYear}`, minWidth: 86, band: "current" }, { key: "uptChange", label: t("vs LY UPT"), minWidth: 94, tone: true, band: "variance" },
      { key: "previousAtv", label: `ATV ${previousYear}`, minWidth: 104, band: "previous" }, { key: "atv", label: `ATV ${currentYear}`, minWidth: 104, band: "current" }, { key: "atvChange", label: t("vs LY ATV"), minWidth: 94, tone: true, band: "variance" },
      { key: "previousAvp", label: `AVP ${previousYear}`, minWidth: 104, band: "previous" }, { key: "avp", label: `AVP ${currentYear}`, minWidth: 104, band: "current" }, { key: "avpChange", label: t("vs LY AVP"), minWidth: 94, tone: true, band: "variance" },
      { key: "visitors", label: `${t("Ziyaretçi")} ${currentYear}`, minWidth: 106, band: "current" }, { key: "conversion", label: `CR ${currentYear}`, minWidth: 86, band: "current" },
    ];
  }, [currentYear, previousYear, t, view]);
  const rows = useMemo(() => {
    if (!report) return [];
    const byBrand = new Map<string, BrandedStoreMetric[]>();
    report.rows.forEach((row) => byBrand.set(row.brand, [...(byBrand.get(row.brand) ?? []), row]));
    return Array.from(byBrand.entries()).sort(([a], [b]) => a.localeCompare(b, "tr")).flatMap(([brand, brandRows]) => {
      const detail = [...brandRows].sort((a, b) => b.net_sales_uzs - a.net_sales_uzs || a.label.localeCompare(b.label, "tr"))
        .map((row) => performanceGridRow(brand, row.label, storePerformanceAggregate([row]), view));
      return [...detail, performanceGridRow("", `${t("Toplam")} ${brand}`, storePerformanceAggregate(brandRows), view, "subtotal", brand)];
    });
  }, [report, t, view]);
  const totalRow = useMemo(() => report ? performanceGridRow(t("TOPLAM"), t("Genel Toplam"), storePerformanceAggregate(report.rows), view) : undefined, [report, t, view]);
  return (
    <section className="private-reports">
      <div className="private-report-filter-card">
        <div className="private-report-filter-heading"><div><span>▦</span><div><strong>{t("Rapor Filtreleri")}</strong><small>{t("Marka, AVM, mağaza ve tarih seçiminize göre tüm toplamlar yeniden hesaplanır.")}</small></div></div><b>{report ? t(`${integer.format(report.rows.length)} ${t("mağaza")}`) : "—"}</b></div>
        <div className="private-report-filter-grid">
          <MainMultiSelect label="Marka" values={brands} options={brandOptions} disabled={loading} groupName="private-report-filter" onChange={onBrands} />
          <MainMultiSelect label="AVM" values={malls} options={mallOptions} disabled={loading} groupName="private-report-filter" onChange={onMalls} />
          <div className="private-report-filter--store"><MainMultiSelect label="Mağaza" values={stores} options={storeOptions} disabled={loading} groupName="private-report-filter" onChange={onStores} /></div>
          <label><span>{t("Başlangıç")}</span><input type="date" value={start} min={minDate} max={end} onChange={(event) => onStart(event.target.value)} /></label>
          <label><span>{t("Bitiş")}</span><input type="date" value={end} min={start} max={maxDate} onChange={(event) => onEnd(event.target.value)} /></label>
          <div className="private-report-filter-actions">
            <button type="button" className="is-primary" disabled={loading || !start || !end || start > end} onClick={onApply}>{loading ? t("Yükleniyor…") : `✓ ${t("Raporu Güncelle")}`}</button>
            <button type="button" className="is-export" disabled={loading || exporting || !report} onClick={() => onExport(sortStates)}><Download aria-hidden="true" size={15} />{exporting ? t("Excel hazırlanıyor…") : t("Raporları Excel’e İndir")}</button>
          </div>
        </div>
      </div>
      <div className="private-report-view-tabs" role="tablist" aria-label={t("Rapor görünümleri")}>
        <button type="button" className={view === "current" ? "active" : ""} onClick={() => onView("current")}>{t("Güncel")}</button>
        <button type="button" className={view === "comparison" ? "active" : ""} onClick={() => onView("comparison")}>{t("vs LY")}</button>
        <button type="button" className={view === "summary" ? "active" : ""} onClick={() => onView("summary")}>{t("Özet")}</button>
      </div>
      <section className="report-section data-report-section private-report-table">
        <div className="report-title"><span /><div><h2>{view === "current" ? t("Güncel Mağaza Performansı") : view === "comparison" ? t("Geçen Yıl Karşılaştırması") : t("Karşılaştırma Özeti")}</h2><p>{report ? `${displayDate(report.period.start)} – ${displayDate(report.period.end)}${view === "current" ? "" : ` • LY: ${displayDate(report.previous_year_period.start)} – ${displayDate(report.previous_year_period.end)}`}` : t("Rapor hazırlanıyor…")}</p></div></div>
        <ProfessionalDataGrid
          originalReportLayout
          columns={columns}
          rows={rows}
          totalRow={totalRow}
          totalRowPosition="bottom"
          pinnedColumns={2}
          preserveGroups
          onSortChange={(sort) => setSortStates((current) => ({ ...current, [view]: sort }))}
          compact
          ariaLabel={`${t("Mağaza performans raporu")} · ${view}`}
          mobilePrimaryKeys={["brand", "store"]}
          mobileMetricKeys={view === "current"
            ? ["sales", "conversion", "upt", "units", "atv", "avp"]
            : view === "comparison"
              ? ["sales", "receipts", "units", "upt", "atv", "avp", "conversion"]
              : ["sales", "upt", "conversion"]}
        />
      </section>
    </section>
  );
}

type ShowroomFilterState = {
  stores: string[];
  mainCategories: string[];
  subcategories: string[];
  genders: string[];
  skus: string[];
  skuColors: string[];
};
type ShowroomNode = {
  key: string;
  label: string;
  level: number;
  firstImport: string;
  lastImport: string;
  currentStock: number;
  inventoryKeys: Set<string>;
  firstSale: string;
  lastSale: string;
  months: Record<string, number>;
  children: Map<string, ShowroomNode>;
};
type ShowroomGridRow = ShowroomNode & { isExpanded: boolean };
const emptyShowroomFilters: ShowroomFilterState = {
  stores: [],
  mainCategories: [],
  subcategories: [],
  genders: [],
  skus: [],
  skuColors: [],
};

function showroomText(value: unknown) {
  const clean = String(value ?? "").trim();
  const normalized = clean.toLocaleLowerCase("tr-TR");
  return ["bilgi yok", "unknown", "no information", "нет данных", "ma’lumot yo‘q"].includes(normalized) ? "" : clean;
}

export async function downloadShowroomWorkbook(
  report: ShowroomReport,
  rows: ShowroomLeafRow[],
  months: string[],
  brands: string[],
  filters: ShowroomFilterState,
  language: LanguageCode,
) {
  const tr = (text: string) => translate(language, text);
  const selection = (values: string[]) => values.length ? values.join(", ") : tr("Tümü");
  const monthTitle = (value: string) => new Intl.DateTimeFormat(language === "tr" ? "tr-TR" : language, { month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${value}-01T12:00:00Z`));
  type ExportRow = {
    main_category: string;
    subcategory: string;
    gender: string;
    sku: string;
    color_code: string;
    color_name: string;
    size: string;
    first_import: string;
    last_import: string;
    current_stock: number;
    first_sale: string;
    last_sale: string;
    months: Record<string, number>;
    inventoryKeys: Set<string>;
  };
  const grouped = new Map<string, ExportRow>();
  rows.forEach((row) => {
    const key = [row.sku, row.color_code, row.color_name, row.size]
      .map((value) => showroomText(value).toLocaleUpperCase("tr-TR"))
      .join("\u001f");
    const current = grouped.get(key) ?? {
      main_category: row.main_category,
      subcategory: row.subcategory,
      gender: row.gender,
      sku: row.sku,
      color_code: row.color_code,
      color_name: row.color_name,
      size: row.size,
      first_import: row.first_import,
      last_import: row.last_import,
      current_stock: 0,
      first_sale: row.first_sale,
      last_sale: row.last_sale,
      months: {},
      inventoryKeys: new Set<string>(),
    };
    if (!showroomText(current.main_category) && showroomText(row.main_category)) current.main_category = row.main_category;
    if (!showroomText(current.subcategory) && showroomText(row.subcategory)) current.subcategory = row.subcategory;
    if (!showroomText(current.gender) && showroomText(row.gender)) current.gender = row.gender;
    current.months[row.month] = (current.months[row.month] ?? 0) + row.quantity;
    if (!current.first_import || (row.first_import && row.first_import < current.first_import)) current.first_import = row.first_import;
    if (!current.last_import || row.last_import > current.last_import) current.last_import = row.last_import;
    const inventoryKey = [row.brand, row.store, row.sku, row.color_code, row.color_name, row.size]
      .map((value) => showroomText(value).toLocaleUpperCase("tr-TR"))
      .join("\u001f");
    if (!current.inventoryKeys.has(inventoryKey)) {
      current.inventoryKeys.add(inventoryKey);
      current.current_stock += Number(row.current_stock) || 0;
    }
    if (!current.first_sale || row.first_sale < current.first_sale) current.first_sale = row.first_sale;
    if (!current.last_sale || row.last_sale > current.last_sale) current.last_sale = row.last_sale;
    grouped.set(key, current);
  });
  const details = Array.from(grouped.values()).sort((left, right) =>
    [left.main_category, left.sku, left.color_code, left.color_name, left.size].join("|").localeCompare(
      [right.main_category, right.sku, right.color_code, right.color_name, right.size].join("|"),
      "tr",
      { numeric: true },
    ),
  );
  const header: WorkbookCell[] = [
    tr("Ana Kategori"), tr("Alt Kategori"), tr("Cinsiyet"), "SKU", tr("Renk"), tr("Beden"),
    tr("İlk İmport"), tr("Son İmport"), tr("Güncel Stok"), tr("İlk Satış"), tr("Son Satış"),
    ...months.map(monthTitle), tr("Toplam Adet"),
  ];
  const detailRows: WorkbookCell[][] = details.map((row) => {
    const color = [showroomText(row.color_code), showroomText(row.color_name)].filter(Boolean).join(" · ");
    const quantities = months.map((month) => row.months[month] ?? 0);
    return [
      row.main_category, row.subcategory, row.gender, row.sku, color, showroomText(row.size),
      displayDate(row.first_import), displayDate(row.last_import), row.current_stock,
      displayDate(row.first_sale), displayDate(row.last_sale), ...quantities, quantities.reduce((total, value) => total + value, 0),
    ];
  });
  const monthTotals = months.map((month) => details.reduce((total, row) => total + (row.months[month] ?? 0), 0));
  const grandTotal = monthTotals.reduce((total, value) => total + value, 0);
  const sourceTotal = rows.reduce((total, row) => total + row.quantity, 0);
  if (Math.abs(grandTotal - sourceTotal) > 0.000001) throw new Error(tr("Showroom toplamları doğrulanamadı."));
  const totalStock = details.reduce((total, row) => total + row.current_stock, 0);
  const totalRow: WorkbookCell[] = [tr("TOPLAM"), "", "", "", "", "", "", "", totalStock, "", "", ...monthTotals, grandTotal];
  const quantityColumns = [9, ...Array.from({ length: months.length + 1 }, (_, index) => 12 + index)];
  const showroomSubtitle = `${tr("Dönem")}: ${displayDate(report.period.start)} – ${displayDate(report.period.end)}  |  ${tr("Marka")}: ${selection(brands)}  |  ${tr("Mağaza")}: ${selection(filters.stores)}`;
  const sheets: WorkbookSheet[] = [
    {
      name: tr("Ürün Hiyerarşisi").slice(0, 31),
      title: tr("Showroom Ürün Planlama"),
      subtitle: showroomSubtitle,
      rows: [header, ...detailRows, totalRow],
      rowKinds: ["header", ...detailRows.map(() => "detail" as const), "total"],
      integers: quantityColumns,
      widths: [20, 20, 13, 18, 22, 12, 13, 13, 14, 13, 13, ...months.map(() => 12), 14],
      dates: [7, 8, 10, 11],
      theme: "report",
      totalStyle: "yellow",
      tabColor: "FF0F8C83",
      freezeColumns: 2,
    },
    {
      name: tr("Rapor Bilgisi"),
      title: tr("Rapor Bilgisi"),
      subtitle: tr("Dosyanın kapsamı ve veri paketi bilgileri"),
      theme: "info",
      autoFilter: false,
      widths: [25, 70],
      tabColor: "FF153F55",
      rows: [
        [tr("Alan"), tr("Değer")],
        [tr("Rapor"), tr("Showroom Ürün Planlama")],
        [tr("Dönem"), `${displayDate(report.period.start)} – ${displayDate(report.period.end)}`],
        [tr("Marka"), selection(brands)],
        [tr("Mağaza"), selection(filters.stores)],
        [tr("Ana Kategori"), selection(filters.mainCategories)],
        [tr("Alt Kategori"), selection(filters.subcategories)],
        [tr("Cinsiyet"), selection(filters.genders)],
        ["SKU", selection(filters.skus)],
        [tr("Renk"), selection(filters.skuColors)],
        [tr("Filtrelenen satır"), rows.length],
        [tr("Veri Paketi"), report.release_id],
      ],
    },
  ];
  await downloadWorkbook(sheets, `showroom_urun_planlama_${report.period.start}_${report.period.end}_${language}.xlsx`, {
    title: tr("Showroom Ürün Planlama"),
    subject: showroomSubtitle,
  });
}

function ShowroomReportView({ report, selectedBrands, onChangeBrands, navigationState, onNavigationChange }: { report: ShowroomReport; selectedBrands: string[]; onChangeBrands: () => void; navigationState?: ShowroomNavigation; onNavigationChange?: (next: ShowroomNavigation) => void }) {
  const language = useContext(LanguageContext);
  const desktop = useDesktopInformation();
  const showroomLocaleText = useGridLocale(language);
  const t = useT();
  const [localFilters, setLocalFilters] = useState<ShowroomFilterState>(navigationState?.filters ?? emptyShowroomFilters);
  const [search, setSearch] = useState(navigationState?.search ?? "");
  const navigationReportIdentity = `${report.release_id}:${report.period.start}:${report.period.end}:${selectedBrands.join("|")}`;
  const previousNavigationReport = useRef(navigationReportIdentity);
  useEffect(() => { onNavigationChange?.({ filters: localFilters, search }); }, [localFilters, search, onNavigationChange]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedPath, setSelectedPath] = useState("");
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(()=>showroomDefaultHidden(report.period.start,report.period.end,desktopInformationView()));
  const [viewPanelOpen, setViewPanelOpen] = useState(false);
  const [hideZeroMonths, setHideZeroMonths] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showroomGridApi, setShowroomGridApi] = useState<GridApi<ShowroomGridRow> | null>(null);
  useEffect(() => {
    if (previousNavigationReport.current !== navigationReportIdentity) {
      setLocalFilters(emptyShowroomFilters);
      setSearch("");
    }
    previousNavigationReport.current = navigationReportIdentity;
    setExpanded(new Set());
    setSelectedPath("");
    setHiddenColumns(showroomDefaultHidden(report.period.start,report.period.end,desktopInformationView()));
    setViewPanelOpen(false);
    setHideZeroMonths(false);
    setExporting(false);
    setShowroomGridApi(null);
  }, [report.release_id, report.period.start, report.period.end, selectedBrands.join("|")]);
  const rowMatchesFilters = (row: ShowroomLeafRow, filters: ShowroomFilterState, skip?: keyof ShowroomFilterState) => {
    if (skip !== "stores" && filters.stores.length && !filters.stores.includes(row.store)) return false;
    if (skip !== "mainCategories" && filters.mainCategories.length && !filters.mainCategories.includes(row.main_category)) return false;
    if (skip !== "subcategories" && filters.subcategories.length && !filters.subcategories.includes(row.subcategory)) return false;
    if (skip !== "genders" && filters.genders.length && !filters.genders.includes(row.gender)) return false;
    if (skip !== "skus" && filters.skus.length && !filters.skus.includes(row.sku)) return false;
    if (skip !== "skuColors" && filters.skuColors.length && !filters.skuColors.includes(showroomColorKey(row))) return false;
    return true;
  };
  const options = useMemo(() => {
    const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b, "tr"));
    const valuesFor = (key: keyof ShowroomFilterState, value: (row: ShowroomLeafRow) => string) => unique(report.rows.filter((row) => rowMatchesFilters(row, localFilters, key)).map(value));
    return {
      stores: valuesFor("stores", (row) => row.store),
      mainCategories: valuesFor("mainCategories", (row) => row.main_category),
      subcategories: valuesFor("subcategories", (row) => row.subcategory),
      genders: valuesFor("genders", (row) => row.gender),
      skus: valuesFor("skus", (row) => row.sku),
      skuColors: valuesFor("skuColors", showroomColorKey),
    };
  }, [report, localFilters]);
  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr");
    return report.rows.filter((row) => {
      if (!rowMatchesFilters(row, localFilters)) return false;
      return !term || [row.main_category, row.sku, row.product_name, row.color_code, row.color_name, row.size].join(" ").toLocaleLowerCase("tr").includes(term);
    });
  }, [report, localFilters, search]);
  const months = useMemo(() => {
    const values: string[] = [];
    const cursor = new Date(`${report.period.start.slice(0, 7)}-01T00:00:00Z`);
    const finish = report.period.end.slice(0, 7);
    while (cursor.toISOString().slice(0, 7) <= finish) {
      values.push(cursor.toISOString().slice(0, 7));
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    return values;
  }, [report.period.start, report.period.end]);
  const displayedMonths = useMemo(() => (hideZeroMonths ? months.filter((month) => filteredRows.some((row) => row.month === month && row.quantity !== 0)) : months), [months, filteredRows, hideZeroMonths]);
  const roots = useMemo(() => {
    const result = new Map<string, ShowroomNode>();
    const nodeFor = (map: Map<string, ShowroomNode>, key: string, label: string, level: number) => {
      let node = map.get(key);
      if (!node) {
        node = {
          key,
          label,
          level,
          firstImport: "",
          lastImport: "",
          currentStock: 0,
          inventoryKeys: new Set<string>(),
          firstSale: "",
          lastSale: "",
          months: {},
          children: new Map(),
        };
        map.set(key, node);
      }
      return node;
    };
    filteredRows.forEach((row) => {
      const productName = showroomText(row.product_name);
      const colorCode = showroomText(row.color_code);
      const colorName = showroomText(row.color_name);
      const size = showroomText(row.size);
      const skuLabel = productName && productName !== row.sku ? `${row.sku} · ${productName}` : row.sku;
      const colorLabel = [colorCode, colorName].filter(Boolean).join(" · ") || "—";
      const inventoryKey = [row.brand, row.store, row.sku, row.color_code, row.color_name, row.size]
        .map((value) => showroomText(value).toLocaleUpperCase("tr-TR"))
        .join("\u001f");
      const levels = [
        [row.main_category, row.main_category],
        [row.sku, skuLabel],
        [`${colorCode}|${colorName}`, colorLabel],
        [size || "—", size || "—"],
      ];
      let map = result;
      let path = "";
      levels.forEach(([part, label], level) => {
        path = `${path}/${part}`;
        const node = nodeFor(map, path, label, level);
        node.months[row.month] = (node.months[row.month] ?? 0) + row.quantity;
        if (!node.firstImport || (row.first_import && row.first_import < node.firstImport)) node.firstImport = row.first_import;
        if (!node.lastImport || row.last_import > node.lastImport) node.lastImport = row.last_import;
        if (!node.inventoryKeys.has(inventoryKey)) {
          node.inventoryKeys.add(inventoryKey);
          node.currentStock += Number(row.current_stock) || 0;
        }
        if (!node.firstSale || row.first_sale < node.firstSale) node.firstSale = row.first_sale;
        if (!node.lastSale || row.last_sale > node.lastSale) node.lastSale = row.last_sale;
        map = node.children;
      });
    });
    return result;
  }, [filteredRows, language]);
  const visibleRows = useMemo(() => {
    const result: ShowroomNode[] = [];
    const walk = (map: Map<string, ShowroomNode>) =>
      Array.from(map.values())
        .sort((a, b) => a.label.localeCompare(b.label, "tr"))
        .forEach((node) => {
          result.push(node);
          if (expanded.has(node.key)) walk(node.children);
        });
    walk(roots);
    return result;
  }, [roots, expanded]);
  const allExpandableKeys = useMemo(() => {
    const keys: string[] = [];
    const walk = (map: Map<string, ShowroomNode>) =>
      map.forEach((node) => {
        if (node.children.size) keys.push(node.key);
        walk(node.children);
      });
    walk(roots);
    return keys;
  }, [roots]);
  const totalQuantity = filteredRows.reduce((total, row) => total + row.quantity, 0);
  const activeSkuCount = new Set(filteredRows.map((row) => row.sku)).size;
  const firstSale = filteredRows
    .map((row) => row.first_sale)
    .filter(Boolean)
    .sort()[0];
  const lastSale = filteredRows
    .map((row) => row.last_sale)
    .filter(Boolean)
    .sort()
    .at(-1);
  const filtered = Object.values(localFilters).some((values) => values.length) || !!search.trim();
  const setFilter = (key: keyof ShowroomFilterState, values: string[]) => setLocalFilters((current) => ({ ...current, [key]: values }));
  const toggle = (key: string) => {
    setSelectedPath(key);
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleColumn = (key: string) =>
    setHiddenColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const monthLabel = (value: string) => {
    const [year, month] = value.split("-");
    const names = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
    return `${names[Number(month) - 1]} ${year}`;
  };
  const exportShowroom = async () => {
    if (exporting || !filteredRows.length) return;
    setExporting(true);
    try {
      await downloadShowroomWorkbook(report, filteredRows, displayedMonths, selectedBrands, localFilters, language);
    } finally {
      setExporting(false);
    }
  };
  const showroomGridRows = useMemo<ShowroomGridRow[]>(
    () => visibleRows.map((node) => ({ ...node, isExpanded: expanded.has(node.key) })),
    [expanded, visibleRows],
  );
  const showroomColumnDefs = useMemo<ColDef<ShowroomGridRow>[]>(() => {
    const tr = (text: string) => translate(language, text);
    const columns: ColDef<ShowroomGridRow>[] = [
      {
        colId: "hierarchy",
        headerName: tr("Ürün Hiyerarşisi"),
        field: "label",
        pinned: "left",
        lockPinned: true,
        minWidth: desktop ? 300 : 230,
        width: desktop ? 380 : 320,
        cellClass: "showroom-grid__hierarchy-cell",
        cellRenderer: ({ data }: { data?: ShowroomGridRow }) => {
          if (!data) return null;
          return (
            <div className={`showroom-grid__hierarchy showroom-grid__hierarchy--level-${data.level}`} title={desktop ? data.label : undefined}>
              <button
                type="button"
                className="showroom-grid__tree-toggle"
                disabled={!data.children.size}
                aria-label={data.children.size ? (data.isExpanded ? tr("Kapat") : tr("Aç")) : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(data.key);
                }}
              >
                {data.children.size ? (data.isExpanded ? "−" : "+") : "·"}
              </button>
              {data.level > 0 ? <small className={`showroom-grid__level-tag showroom-grid__level-tag--${data.level}`}>{["", "SKU", tr("Renk"), tr("Beden")][data.level]}</small> : null}
              {desktop ? <DesktopShowroomLabel label={data.label} sku={data.level === 1} /> : <span>{data.label}</span>}
            </div>
          );
        },
      },
    ];
    if (!hiddenColumns.has("firstImport")) {
      columns.push({ colId: "firstImport", headerName: tr("İlk İmport"), field: "firstImport", minWidth: 112, width: 124, valueFormatter: ({ value }) => displayDate(String(value ?? "")) });
    }
    if (!hiddenColumns.has("lastImport")) {
      columns.push({ colId: "lastImport", headerName: tr("Son İmport"), field: "lastImport", minWidth: 112, width: 124, valueFormatter: ({ value }) => displayDate(String(value ?? "")) });
    }
    if (!hiddenColumns.has("stock")) {
      columns.push({
        colId: "stock",
        headerName: tr("Güncel Stok"),
        headerTooltip: standardCopy(language).stockScope,
        field: "currentStock",
        minWidth: 110,
        width: 122,
        type: "numericColumn",
        cellClass: "showroom-grid__number showroom-grid__stock",
        valueFormatter: ({ value }) => integer.format(Number(value ?? 0)),
      });
    }
    if (!hiddenColumns.has("first")) {
      columns.push({ colId: "first", headerName: tr("İlk Satış"), field: "firstSale", minWidth: 112, width: 124, valueFormatter: ({ value }) => displayDate(String(value ?? "")) });
    }
    if (!hiddenColumns.has("last")) {
      columns.push({ colId: "last", headerName: tr("Son Satış"), field: "lastSale", minWidth: 112, width: 124, valueFormatter: ({ value }) => displayDate(String(value ?? "")) });
    }
    displayedMonths
      .filter((month) => !hiddenColumns.has(`month:${month}`))
      .forEach((month) => {
        columns.push({
          colId: `month:${month}`,
          headerName: monthLabel(month),
          minWidth: 92,
          width: 106,
          type: "numericColumn",
          cellClass: "showroom-grid__number",
          valueGetter: ({ data }) => data?.months[month] ?? 0,
          valueFormatter: ({ value }) => integer.format(Number(value ?? 0)),
        });
      });
    if (!hiddenColumns.has("total")) {
      columns.push({
        colId: "total",
        headerName: tr("Toplam Adet"),
        pinned: "right",
        lockPinned: true,
        minWidth: 112,
        width: 126,
        type: "numericColumn",
        cellClass: "showroom-grid__total",
        valueGetter: ({ data }) => Object.values(data?.months ?? {}).reduce((total, value) => total + value, 0),
        valueFormatter: ({ value }) => integer.format(Number(value ?? 0)),
      });
    }
    return desktopInformationView() ? columns.map(column => column.pinned ? column : { ...column, flex: column.colId?.startsWith("month:") ? 1.15 : 1 }) : columns;
  }, [displayedMonths, hiddenColumns, language, desktop]);
  return (
    <>
      <section className="showroom-brand-strip">
        <span className="showroom-brand-strip__icon"><Tag aria-hidden="true" size={18} strokeWidth={2.1} /></span>
        <div>
          <small>{t("Seçili Markalar")}</small>
          <strong>{selectedBrands.join(", ")}</strong>
        </div>
        <button type="button" onClick={onChangeBrands}>
          {t("Markaları Değiştir")}
        </button>
      </section>
      <section className="showroom-filter-panel">
        <div className="showroom-filter-heading">
          <div>
            <p className="eyebrow">{t("Showroom filtreleri")}</p>
            <h2>{t("Ürün planlama kapsamı")}</h2>
          </div>
          <button
            type="button"
            disabled={!filtered}
            onClick={() => {
              setLocalFilters(emptyShowroomFilters);
              setSearch("");
            }}
          >
            {t("Filtreleri Temizle")}
          </button>
        </div>
        <div className="showroom-filter-grid">
          {(
            [
              ["stores", "Mağaza"],
              ["mainCategories", "Ana Kategori"],
              ["subcategories", "Alt Kategori"],
              ["genders", "Cinsiyet"],
              ["skus", "SKU"],
              ["skuColors", "SKU + Renk"],
            ] as [keyof ShowroomFilterState, string][]
          ).map(([key, label]) => (
            <MainMultiSelect
              key={key}
              label={label}
              values={localFilters[key]}
              options={options[key]}
              disabled={false}
              groupName={`showroom-${key}`}
              onChange={(values) => setFilter(key, values)}
            />
          ))}
        </div>
      </section>
      <section className="showroom-summary">
        <article>
          <span className="showroom-summary__icon"><ShoppingBasket aria-hidden="true" size={18} /></span>
          <span>{t("Toplam Adet")}</span>
          <strong>{integer.format(totalQuantity)}</strong>
        </article>
        <article>
          <span className="showroom-summary__icon"><PackageCheck aria-hidden="true" size={18} /></span>
          <span>{t("Aktif SKU")}</span>
          <strong>{integer.format(activeSkuCount)}</strong>
        </article>
        <article>
          <span className="showroom-summary__icon"><ReceiptText aria-hidden="true" size={18} /></span>
          <span>{t("Satış Yapılan Gün")}</span>
          <strong>{filtered ? "—" : integer.format(report.summary.sales_days)}</strong>
        </article>
        <article>
          <span className="showroom-summary__icon"><TrendingUp aria-hidden="true" size={18} /></span>
          <span>{t("İlk / Son Satış")}</span>
          <strong>
            {firstSale ? displayDate(firstSale) : "—"} · {lastSale ? displayDate(lastSale) : "—"}
          </strong>
        </article>
      </section>
      <section className="report-section showroom-section">
        <div className="report-title">
          <span />
          <div>
            <h2>{t("Ürün Hiyerarşisi")}</h2>
            <p>{t("Ana Kategori → SKU → Renk → Beden seviyelerinde aylık adetler.")}</p>
          </div>
          <small>{integer.format(filteredRows.length)} {t("kayıt")}</small>
        </div>
        <div className="showroom-toolbar">
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("Showroom tablosunda ara…")} />
          <button type="button" onClick={() => setExpanded(new Set(allExpandableKeys))}>
            {t("Tümünü Aç")}
          </button>
          <button
            type="button"
            onClick={() => {
              setExpanded(new Set());
              setSelectedPath("");
            }}
          >
            {t("Tümünü Kapat")}
          </button>
          <button type="button" className={viewPanelOpen ? "active" : ""} onClick={() => setViewPanelOpen((value) => !value)}>
            ☷ {t("Sütunları Göster/Gizle")}
          </button>
          <button type="button" className="report-fit-button" onClick={() => showroomGridApi?.sizeColumnsToFit()}>
            ↔ {t("Sütunları Sığdır")}
          </button>
          <button type="button" onClick={(event) => void event.currentTarget.closest(".showroom-section")?.requestFullscreen()}>
            <Maximize2 aria-hidden="true" size={14} /> {t("Tam Ekran")}
          </button>
          {viewPanelOpen ? (
            <div className="showroom-view-panel">
              <strong>{t("Gösterilecek sütunlar")}</strong>
              {([["firstImport", "İlk İmport"], ["lastImport", "Son İmport"], ["stock", "Güncel Stok"], ["first", "İlk Satış"], ["last", "Son Satış"], ...months.map((month) => [`month:${month}`, monthLabel(month)]), ["total", "Toplam Adet"]] as [string, string][]).map(([key, label]) => (
                <label key={key}>
                  <input type="checkbox" checked={!hiddenColumns.has(key)} onChange={() => toggleColumn(key)} /> {t(label)}
                </label>
              ))}
              <label>
                <input type="checkbox" checked={hideZeroMonths} onChange={(event) => setHideZeroMonths(event.target.checked)} /> {t("Sıfır satışlı ayları gizle")}
              </label>
              <button
                type="button"
                onClick={() => {
                  setHiddenColumns(new Set());
                  setHideZeroMonths(false);
                  showroomGridApi?.resetColumnState();
                  window.setTimeout(() => showroomGridApi?.sizeColumnsToFit(), 0);
                }}
              >
                ↺ {t("Görünümü Sıfırla")}
              </button>
            </div>
          ) : null}
        </div>
        {desktopInformationView() ? <p className="showroom-stock-scope">{standardCopy(language).stockScope}</p> : null}
        <div className="showroom-crumb">
          {t("Açık hiyerarşi")}:{" "}
          {selectedPath
            ? selectedPath
                .split("/")
                .filter(Boolean)
                .map((part) => part.replace("|", " / "))
                .join(" › ")
            : t("Henüz seçim yapılmadı")}
        </div>
        <div className="showroom-grid" aria-label={t("Ürün Hiyerarşisi")}>
          <AgGridReact<ShowroomGridRow>
            theme={srrGridTheme}
            getLocaleText={showroomLocaleText}
            rowData={showroomGridRows}
            columnDefs={showroomColumnDefs}
            defaultColDef={{ resizable: true, sortable: false, suppressHeaderMenuButton: true }}
            getRowId={({ data }) => data.key}
            headerHeight={desktopInformationView() ? 48 : 44}
            rowHeight={desktopInformationView() ? 36 : 38}
            animateRows={false}
            ensureDomOrder
            enableCellTextSelection
            onGridSizeChanged={event => { if (desktopInformationView()) event.api.sizeColumnsToFit(); }}
            onGridReady={(event: GridReadyEvent<ShowroomGridRow>) => {
              setShowroomGridApi(event.api);
              window.setTimeout(() => event.api.sizeColumnsToFit(), 0);
            }}
            onRowClicked={(event) => setSelectedPath(event.data?.key ?? "")}
            getRowClass={(params) =>
              `showroom-grid__row showroom-grid__row--level-${params.data?.level ?? 0}${selectedPath === params.data?.key ? " showroom-grid__row--selected" : ""}`
            }
            overlayNoRowsTemplate={`<span>${t("Kayıt bulunamadı")}</span>`}
          />
        </div>
      </section>
      <section className="showroom-export-footer">
        <div>
          <span className="showroom-export-footer__icon"><FileSpreadsheet aria-hidden="true" size={19} /></span>
          <span><strong>{t("Showroom’u Excel’e İndir")}</strong><small>{t("Seçili kapsamı Excel dosyası olarak indirin.")}</small></span>
        </div>
        <button type="button" disabled={exporting || !filteredRows.length} onClick={() => void exportShowroom()}>
          <Download aria-hidden="true" size={16} /> {exporting ? t("Excel hazırlanıyor…") : t("Showroom’u Excel’e İndir")}
        </button>
      </section>
    </>
  );
}

const excelText = {
  tr: {
    info: "Rapor Bilgisi",
    categories: "Kategori Kırılımı",
    genders: "Cinsiyet Dağılımı",
    weekdays: "Haftanın Günleri",
    subcategories: "Alt Kategori Kırılımı",
    advisers: "Satış Danışmanları",
    label: "Kırılım",
    sales: "Net Ciro UZS",
    receipts: "Fiş Sayısı",
    units: "Net Adet",
    upt: "UPT",
    basket: "Sepet Ortalaması",
    unitPrice: "Birim Fiyat",
    share: "Ciro Payı %",
    lySales: "Geçen Yıl Ciro UZS",
    change: "Ciro Değişimi %",
    store: "Mağaza",
    adviser: "Satış Danışmanı",
    target: "Hedef UZS",
    achievement: "Hedef %",
    remaining: "Kalan Hedef UZS",
    period: "Dönem",
    filters: "Uygulanan Filtreler",
    noTarget: "Hedef Yok",
  },
  en: {
    info: "Report Info",
    categories: "Category Breakdown",
    genders: "Gender Distribution",
    weekdays: "Weekdays",
    subcategories: "Subcategory Breakdown",
    advisers: "Sales Advisers",
    label: "Breakdown",
    sales: "Net Revenue UZS",
    receipts: "Receipts",
    units: "Net Units",
    upt: "UPT",
    basket: "Average Basket",
    unitPrice: "Unit Price",
    share: "Revenue Share %",
    lySales: "Last Year Revenue UZS",
    change: "Revenue Change %",
    store: "Store",
    adviser: "Sales Adviser",
    target: "Target UZS",
    achievement: "Target %",
    remaining: "Remaining Target UZS",
    period: "Period",
    filters: "Applied Filters",
    noTarget: "No Target",
  },
  ru: {
    info: "Об отчёте",
    categories: "Категории",
    genders: "Распределение по полу",
    weekdays: "Дни недели",
    subcategories: "Подкатегории",
    advisers: "Консультанты",
    label: "Разбивка",
    sales: "Выручка (нетто), UZS",
    receipts: "Количество чеков",
    units: "Количество (нетто)",
    upt: "UPT",
    basket: "Средний чек",
    unitPrice: "Цена за единицу",
    share: "Доля выручки %",
    lySales: "Выручка прошлого года UZS",
    change: "Изменение выручки %",
    store: "Магазин",
    adviser: "Продавец-консультант",
    target: "План, UZS",
    achievement: "Выполнение плана, %",
    remaining: "Остаток плана, UZS",
    period: "Период",
    filters: "Применённые фильтры",
    noTarget: "План не задан",
  },
  uz: {
    info: "Hisobot haqida",
    categories: "Kategoriya kesimi",
    genders: "Jins bo‘yicha taqsimot",
    weekdays: "Hafta kunlari",
    subcategories: "Quyi kategoriya",
    advisers: "Savdo maslahatchilari",
    label: "Kesim",
    sales: "Sof tushum, UZS",
    receipts: "Cheklar soni",
    units: "Sof sotuv, dona",
    upt: "UPT",
    basket: "O‘rtacha chek",
    unitPrice: "Bir dona narxi",
    share: "Tushum ulushi %",
    lySales: "O‘tgan yil tushumi UZS",
    change: "Tushum o‘zgarishi %",
    store: "Do‘kon",
    adviser: "Sotuvchi-maslahatchi",
    target: "Reja, UZS",
    achievement: "Reja bajarilishi, %",
    remaining: "Qolgan reja, UZS",
    period: "Davr",
    filters: "Qo‘llangan filtrlar",
    noTarget: "Reja belgilanmagan",
  },
} as const;

function metricExportValue(row: MetricRow, key: string): number | string | null {
  const values: Record<string, number | string | null> = {
    ...storeOverviewOptionalValues(row),
    label: row.label,
    target: row.target_uzs,
    sales: row.net_sales_uzs,
    targetRate: row.target_achievement_percent,
    upt: row.upt,
    cr: row.conversion_rate_percent,
    visitors: row.visitors,
    receipts: row.receipts,
    units: row.net_units,
    basket: row.average_basket_uzs,
    unitPrice: row.average_unit_price_uzs,
    share: row.revenue_share_percent,
    lySales: row.previous_year_sales_uzs,
    salesChange: row.revenue_change_percent,
    lyUpt: row.previous_year_upt,
    uptChange: row.upt_change_percent,
    days: row.day_count,
    average: row.average_sales_uzs,
    usd: row.net_sales_usd,
    stock: row.stock_units,
    stockShare: row.stock_share_percent,
    lyUnits: row.previous_year_units,
    unitsChange: row.previous_year_units ? ((row.net_units - row.previous_year_units) / Math.abs(row.previous_year_units)) * 100 : null,
  };
  return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null;
}

function sortedMetricExportRows(rows: MetricRow[], gridTitle: string) {
  return rowsFromGridView(rows, gridTitle, (row) => row.label, metricExportValue);
}

function adviserExportValue(row: AdviserRow, key: string): number | string | null {
  const values: Record<string, number | string | null> = {
    store: row.store,
    name: row.name,
    target: row.target_uzs,
    sales: row.net_sales_uzs,
    achievement: row.target_achievement_percent,
    remaining: row.remaining_target_uzs,
    share: row.revenue_share_percent,
    receipts: row.receipts,
    units: row.net_units,
    upt: row.upt,
    basket: row.average_basket_uzs,
    unit: row.average_unit_price_uzs,
  };
  return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null;
}

function metricExcelSheet(name: string, title: string, firstColumn: string, rows: MetricRow[], language: LanguageCode, subtitle: string, options: {
  kind?: "stock" | "weekday";
  allocations?: boolean;
  comparisons?: boolean;
  allocationTotals?: { target: number; visitors: number; sales?: number; units?: number; receipts?: number; previousUpt?: number };
  targetProgress?: { asOfDate: string; fullMonthTarget: boolean };
} = {}): WorkbookSheet {
  const t = (value: string) => translate(language, value);
  const sum = (key: keyof MetricRow) => rows.reduce((total, row) => total + (typeof row[key] === "number" ? row[key] as number : 0), 0);
  const sales = options.allocationTotals?.sales ?? sum("net_sales_uzs");
  const units = options.allocationTotals?.units ?? sum("net_units");
  const receipts = options.allocationTotals?.receipts ?? sum("receipts");
  const visitors = options.allocationTotals?.visitors ?? sum("visitors");
  const target = options.allocationTotals?.target ?? sum("target_uzs");
  const previousSales = sum("previous_year_sales_uzs"), previousUnits = sum("previous_year_units");
  const previousUpt = options.allocationTotals?.previousUpt ?? 0;
  const upt = receipts ? units / receipts : 0, stock = sum("stock_units"), days = sum("day_count");
  const totals: Record<string, WorkbookCell> = {
    label: t("TOPLAM"), weekday: "—", sales, units, receipts, visitors,
    target: target > 0 ? target : null, targetRate: target ? sales / target * 100 : null,
    upt, cr: visitors ? receipts / visitors * 100 : null,
    basket: receipts ? sales / receipts : 0, unitPrice: units ? sales / units : 0,
    share: 100, lySales: previousSales || null, salesChange: ratio(sales, previousSales),
    lyUpt: previousUpt || null, uptChange: previousUpt ? (upt - previousUpt) / previousUpt * 100 : null,
    stock, stockShare: stock ? 100 : null, lyUnits: previousUnits || null,
    unitsChange: ratio(units, previousUnits), days, average: days ? sales / days : 0, usd: sum("net_sales_usd"),
  };
  const labels: Record<string, string> = { label: firstColumn, weekday: "Gün", target: "Hedef UZS", sales: "Net Ciro UZS", targetRate: "Hedef %", upt: "UPT", cr: "CR", visitors: "Ziyaretçi", receipts: "Fiş Sayısı", units: "Net Adet", basket: "Sepet Ort.", unitPrice: "Birim Fiyat", share: "Ciro Payı", lySales: "LY Ciro UZS", salesChange: "Ciro %", lyUpt: "LY UPT", uptChange: "UPT %", stock: "Stok", stockShare: "Stok Payı %", lyUnits: "LY Adet", unitsChange: "Adet %", days: "Gün Sayısı", average: "Ortalama Ciro", usd: "Net Ciro USD" };
  const baseKeys = options.kind === "stock"
    ? ["label", "sales", "units", "unitPrice", "share", "stock", "stockShare", "lySales", "salesChange", "lyUnits", "unitsChange"]
    : options.kind === "weekday"
      ? ["label", "days", "sales", "average", "usd", "share", "upt", "cr", "visitors", "receipts", "units", "basket", ...(desktopInformationView() ? ["lyUpt", "uptChange"] : [])]
      : ["label", ...(firstColumn === "Tarih" ? ["weekday"] : []), ...(options.allocations ? ["target"] : []), "sales", ...(options.allocations ? ["targetRate"] : []), "upt", ...(options.allocations ? ["cr", "visitors"] : []), "receipts", "units", "basket", "unitPrice", "share", ...(options.comparisons !== false ? ["lySales", "salesChange", "lyUpt", "uptChange"] : [])];
  const optionalKeys = storeOverviewOptionalKeys().filter(key => !baseKeys.includes(key) && exportVisibleColumns.get(title)?.includes(key));
  const keys = [...baseKeys, ...optionalKeys];
  if (options.kind === "weekday" && desktopInformationView()) Object.assign(totals, weekdayLyUptTotals(rows, upt));
  if (optionalKeys.length) {
    const optional=storeOverviewOptionalTotals(rows, {sales,units,receipts});
    Object.assign(totals,Object.fromEntries(optionalKeys.map(key=>[key,optional[key as keyof typeof optional]])));
  }
  const optionalLabels = new Map(storeOverviewColumns(language).filter(column => optionalKeys.includes(column.key)).map(column => [column.key, column.label]));
  const percentages = new Set(["targetRate", "cr", "share", "stockShare", "salesChange", "unitsChange", "uptChange", "receiptsChange", "unitPriceChange"]);
  const columns: ExportColumn[] = keys.map(key => ({ key, label: optionalLabels.get(key) ?? t(labels[key]), format: key === "label" ? firstColumn === "Tarih" ? "dates" : undefined : key === "weekday" ? undefined : percentages.has(key) ? "percentagePoints" : key === "upt" || key === "lyUpt" ? "decimals" : "integers" }));
  const exportedRows = sortedMetricExportRows(rows, title);
  const records = exportedRows.map(row => Object.fromEntries(keys.map(key => [key,
    key === "weekday" ? displayWeekday(row.label, language) : key === "label" && firstColumn === "Ay" ? displayMonth(row.label, language) : metricExportValue(row, key),
  ])));
  const expectation = (row: MetricRow) => {
    const p = options.targetProgress!;
    const expected = metricTargetProgressBenchmark(firstColumn, row.label, p.asOfDate, p.fullMonthTarget, row.target_progress_percent);
    return { expected, expectedLabel: `${oneDecimal.format(expected)}%` };
  };
  const expected = options.targetProgress ? rows.reduce((total, row) => total + (row.target_uzs ?? 0) * expectation(row).expected, 0) / (target || 1) : 0;
  return tableExcelSheet({ name, title, subtitle: firstColumn === "Saat" ? `${subtitle} | ${t("İş günü: 06:00–06:00")}` : subtitle,
    theme: "standard", tabColor: "FF0F8C83", freezeColumns: firstColumn === "Tarih" ? 2 : 1,
    progressComparisons: options.targetProgress && options.allocations ? [{ column: keys.indexOf("targetRate") + 1, expected, expectedLabel: `${oneDecimal.format(expected)}%`, includeTotal: true, rowExpectations: [null, { expected, expectedLabel: `${oneDecimal.format(expected)}%` }, ...exportedRows.map(expectation)] }] : undefined,
  }, columns, records, totals);
}

export async function downloadExecutiveWorkbook(summary: ExecutiveSummary, report: ExecutiveTables, filters: Filters, language: LanguageCode) {
  const t = (value: string) => translate(language, value);
  const labels: Record<keyof Filters, string> = { brands: "Marka", stores: "Mağaza", malls: "Alışveriş Merkezi", main_categories: "Ana Kategori", subcategories: "Alt Kategori", genders: "Cinsiyet", seasons: "Sezon", skus: "SKU" };
  const filterText = (Object.entries(filters) as [keyof Filters, string[]][]).filter(([, values]) => values.length)
    .map(([key, values]) => `${t(labels[key])}: ${values.join(", ")}`).join(" | ") || t("Filtre yok");
  const actionRows = rowsFromGridView(buildActionStoreRows(report.stores), t("Aksiyon Gerektiren Mağazalar"), actionStoreRowId,
    (row, key) => key === "store" ? row.label : key === "criteria" ? row.reasons.length : key === "reason" ? row.reasons.map(reason => actionReasonText(reason, t)).join("; ") : metricExportValue(row, key));
  const { downloadExecutiveExcel } = await import("./executiveExcel");
  const params = report.breakdown_scope ?? reportParams(report.period.start, report.period.end, summary.full_month_target, filters, summary.ly_comparison ?? "same_date").toString();
  const breakdowns = readStoreBreakdownExports({ params, releaseId: report.release_id }, ["daily", "weekday", "hourly", "monthly"], language);
  const [weekdayResponse, presentationResponse] = await Promise.all([fetch(`/api/v1/store/report?${params}`), fetch(`/api/v1/store/presentation?${params}`)]);
  if (!weekdayResponse.ok || !presentationResponse.ok) throw new Error(t("Veri açılamadı."));
  const [weekdayReport, presentation] = await Promise.all([weekdayResponse.json() as Promise<StoreReport>, presentationResponse.json()]);
  const weekdayTitle = t("Haftanın Günleri Performansı");
  const weekdayNotes: [string,string][] = [[weekdayTitle + " · " + t("Görünüm"), t(breakdowns.weekday ? "Mağaza Bazlı" : "Gün Bazlı")]];
  if (breakdowns.weekday?.search) weekdayNotes.push([weekdayTitle + " · " + t("Arama"), breakdowns.weekday.search]);
  await downloadExecutiveExcel({ language, summary, filterText, translate: t, breakdowns, monthViews: readMonthViews({params,releaseId:report.release_id},["stores","malls","weekday","daily","hourly"],language), actionSources:report.stores,
    weekday: { title: weekdayTitle, notes: weekdayNotes, input: {language,summary,report:weekdayReport,presentation,filterText,translate:t,breakdowns:breakdowns.weekday?{weekday:breakdowns.weekday}:undefined} },
    report: { ...report,
      stores: sortedMetricExportRows(report.stores, t("Mağaza Özeti")),
      brands: sortedMetricExportRows(report.brands, t("Marka Özeti")),
      malls: sortedMetricExportRows(report.malls, t("Alışveriş Merkezi Özeti")),
      daily: sortedMetricExportRows(report.daily, t("Günlük Satış")),
      hourly: sortedMetricExportRows(report.hourly, t("Saatlik Satış")),
      monthly: sortedMetricExportRows(report.monthly, t("Aylık Performans")),
    },
    actions: actionRows.map(row => ({ label: row.label, brand:row.brand??"", target: row.target_uzs, sales: row.net_sales_uzs,
      targetRate: row.target_achievement_percent, lySales: row.previous_year_sales_uzs || null, salesChange: row.revenue_change_percent,
      cr: row.conversion_rate_percent, visitors: row.visitors, receipts: row.receipts, lyUpt: row.previous_year_upt || null,
      upt: row.upt, uptChange: row.upt_change_percent, reason: row.reasons.map(reason => actionReasonText(reason, t)).join(" • ") })),
  }, `yonetici_ozeti_${summary.period.end}_${language}.xlsx`);
}

export async function downloadStoreWorkbook(report: StoreReport, summary: ExecutiveSummary, filters: Filters, language: string) {
  const code = (language.slice(0, 2).toLowerCase() in excelText ? language.slice(0, 2).toLowerCase() : "tr") as keyof typeof excelText;
  const t = (value: string) => translate(code, value);
  const filterLabels: Record<keyof Filters, string> = { brands: "Marka", stores: "Mağaza", malls: "Alışveriş Merkezi", main_categories: "Ana Kategori", subcategories: "Alt Kategori", genders: "Cinsiyet", seasons: "Sezon", skus: "SKU" };
  const filterText = (Object.entries(filters) as [keyof Filters, string[]][]).filter(([, values]) => values.length).map(([key, values]) => `${t(filterLabels[key])}: ${values.join(", ")}`).join(" | ") || t("Filtre yok");
  const params = report.category_scope ?? reportParams(report.period.start, report.period.end, summary.full_month_target, filters, summary.ly_comparison ?? "same_date").toString();
  const breakdowns = readStoreBreakdownExports({ params, releaseId: report.release_id }, ["gender", "subcategory"], code);
  const categoryView = report.category_scope ? readStoreCategoryExport<MetricRow>({ params: report.category_scope, releaseId: report.release_id }) : undefined;
  const response = await fetch(`/api/v1/store/presentation?${params}`);
  if (!response.ok) throw new Error(t("Mağaza ekranının ek alanları açılamadı."));
  const presentation = await response.json();
  const { downloadStoreAnalysisExcel } = await import("./storeAnalysisExcel");
  await downloadStoreAnalysisExcel({ report, summary, presentation, language: code, filterText, translate: t, breakdowns, monthViews: readMonthViews({params,releaseId:report.release_id},["category","gender","subcategory"],code),
    categoryRows: categoryView?.byStore ? categoryView.rows : report.categories,
    byStore: categoryView?.byStore ?? false, categorySearch: categoryView?.search ?? "",
  }, `magaza_analizi_${summary.period.end}_${code}.xlsx`);
}

export async function downloadSessionWorkbook(rows: AdminSession[], start: string, end: string, selectedUser: string, status: SessionStatusFilter, language: LanguageCode) {
  const t = (value: string) => translate(language, value);
  const selectedUserLabel = selectedUser === "all"
    ? t("Tüm kullanıcılar")
    : rows.find((item) => item.username === selectedUser)?.display_name || selectedUser;
  const statusLabel = status === "active" ? t("Aktif oturumlar") : status === "completed" ? t("Tamamlanmış oturumlar") : t("Tüm durumlar");
  const rangeLabel = `${displayDate(start)} – ${displayDate(end)}`;
  const averageDuration = rows.length ? rows.reduce((total, item) => total + item.duration_seconds, 0) / rows.length : 0;
  const subtitle = `${t("Dönem")}: ${rangeLabel}  |  ${t("Kullanıcı")}: ${selectedUserLabel}  |  ${t("Durum")}: ${statusLabel}`;
  const sheets: WorkbookSheet[] = [
    {
      name: t("Kullanıcı Oturumları"),
      title: t("Kullanıcı Oturumları"),
      subtitle,
      rows: [
        [t("Kullanıcı"), t("Kullanıcı adı"), t("Giriş zamanı"), t("Son görülme / Çıkış"), t("Oturum süresi"), t("Durum")],
        ...rows.map((item) => [
          item.display_name || item.username,
          item.username,
          item.login_at,
          item.logout_at || item.last_seen_at,
          item.duration_seconds,
          t(item.online ? "Çevrimiçi" : item.logout_at ? "Oturum kapandı" : "Bağlantı sona erdi"),
        ]),
      ],
      rowKinds: ["header", ...rows.map(() => "detail" as const)],
      dateTimes: [3, 4],
      durations: [5],
      widths: [25, 20, 20, 20, 17, 19],
      theme: "standard",
      tabColor: "FF0F8C83",
      freezeColumns: 2,
    },
    {
      name: t("Rapor Bilgisi"),
      title: t("Rapor Bilgisi"),
      subtitle: t("Dosyanın kapsamı ve veri paketi bilgileri"),
      theme: "info",
      autoFilter: false,
      widths: [26, 72],
      tabColor: "FF153F55",
      rows: [
        [t("Alan"), t("Değer")],
        [t("Rapor"), t("Kullanıcı Oturumları")],
        [t("Dönem"), rangeLabel],
        [t("Kullanıcı"), selectedUserLabel],
        [t("Durum"), statusLabel],
        [t("Oturum"), rows.length],
        [t("Aktif kullanıcı"), new Set(rows.filter((item) => item.online).map((item) => item.username)).size],
        [t("Ortalama süre"), displaySessionDuration(averageDuration, language)],
      ],
    },
  ];
  await downloadWorkbook(sheets, `kullanici_oturumlari_${tashkentDate(new Date())}_${language}.xlsx`, {
    title: t("Kullanıcı Oturumları"),
    subject: subtitle,
  });
}

function AdminPage({ section, health, users, sessions, brands, loading, error, onSection, onRefresh, onSaveUser }: { section: AdminSection; health: AdminHealth | null; users: AdminUsersResponse | null; sessions: AdminSessionsResponse | null; brands: string[]; loading: boolean; error: string | null; onSection: (section: AdminSection) => void; onRefresh: () => void; onSaveUser: (draft: AdminUserDraft, editing: boolean) => Promise<void> }) {
  const t = useT();
  const language = useContext(LanguageContext);
  const telegram = useTelegramCodes(language, section === "users");
  const [botHistoryRevision, setBotHistoryRevision] = useState(0);
  const emptyDraft: AdminUserDraft = { username: "", display_name: "", role: "viewer", allowed_brands: [], pin: "", active: true };
  const [draft, setDraft] = useState<AdminUserDraft>(emptyDraft);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedSummaryDate, setSelectedSummaryDate] = useState("");
  const [historySummary, setHistorySummary] = useState<AdminNightHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [sessionStart, setSessionStart] = useState(() => sessionRangeDate(29));
  const [sessionEnd, setSessionEnd] = useState(() => sessionRangeDate(0));
  const [sessionUser, setSessionUser] = useState("all");
  const [sessionStatus, setSessionStatus] = useState<SessionStatusFilter>("all");
  const latestSummaryDate = health?.night_summary?.sales_date || health?.expected_data_date || "";
  const availableSummaryDates = useMemo(() => health?.available_summary_dates?.length
    ? health.available_summary_dates
    : latestSummaryDate
      ? [latestSummaryDate]
      : [], [health?.available_summary_dates, latestSummaryDate]);
  const summaryDate = selectedSummaryDate || latestSummaryDate;
  const showingHistory = Boolean(summaryDate && summaryDate !== latestSummaryDate);
  const displayedNightSummary = showingHistory
    ? historySummary?.selected_data_date === summaryDate ? historySummary.summary : null
    : health?.night_summary ?? null;
  const displayedSourceCreatedAt = showingHistory
    ? historySummary?.selected_data_date === summaryDate ? historySummary.source_created_at : undefined
    : health?.source_created_at;

  useEffect(() => {
    if (!latestSummaryDate) return;
    setSelectedSummaryDate((current) => current && availableSummaryDates.includes(current) ? current : latestSummaryDate);
  }, [latestSummaryDate, health?.data_release_id]);

  async function changeSummaryDate(nextDate: string) {
    if (!nextDate) return;
    setSelectedSummaryDate(nextDate);
    setHistoryError(null);
    if (nextDate === latestSummaryDate) {
      setHistorySummary(null);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/v1/admin/health/history?date=${encodeURIComponent(nextDate)}`);
      if (!response.ok) throw new Error(t("Seçilen tarihin doğrulanmış paketi bulunamadı."));
      setHistorySummary((await response.json()) as AdminNightHistory);
    } catch (caught) {
      setHistorySummary(null);
      setHistoryError(caught instanceof Error ? caught.message : t("Seçilen tarihin doğrulanmış paketi bulunamadı."));
    } finally {
      setHistoryLoading(false);
    }
  }
  const uptime = health ? `${Math.floor(health.uptime_seconds / 86400)} ${t("gün")} ${Math.floor((health.uptime_seconds % 86400) / 3600)} ${t("saat")} ${Math.floor((health.uptime_seconds % 3600) / 60)} ${t("dk")}` : "—";
  const rowEntries = health?.rows && typeof health.rows === "object" ? Object.entries(health.rows) : [];
  const expectedDataDate = health?.expected_data_date || expectedCompletedDataDate();
  const numericRow = (key: string) => {
    const value = health?.rows && typeof health.rows === "object" ? health.rows[key] : 0;
    return typeof value === "number" ? value : Number(value ?? 0) || 0;
  };
  const expectedPackageDate = expectedNightPackageDate();
  const sourceCreatedAt = health?.source_created_at ? new Date(health.source_created_at) : null;
  const activePackageDate = sourceCreatedAt && !Number.isNaN(sourceCreatedAt.getTime()) ? tashkentDate(sourceCreatedAt) : "";
  const dataAlerts = health ? [
    !health.night_summary || health.night_summary.sales_date !== expectedDataDate ? "Satış verisi güncel değil" : null,
    !health.night_summary || health.night_summary.visitors_date !== expectedDataDate || health.night_summary.visitors <= 0 ? "Ziyaretçi verisi eksik" : null,
    !health.night_summary || activePackageDate !== expectedPackageDate || health.night_summary.stock_total <= 0 || numericRow("inventory") <= 0 || numericRow("prices") <= 0 ? "Stok ve fiyat verisi güncel değil" : null,
    numericRow("targets") <= 0 ? "Hedefler aktarılmamış" : null,
    !health.data_release_id || health.release_status !== "verified" || activePackageDate !== expectedPackageDate ? "Platform yayını geride kalmış" : null,
  ].filter((value): value is string => Boolean(value)) : [];
  const hasCriticalDataIssue = dataAlerts.length > 0;
  const hasServiceError = health?.services.some((service) => service.status === "error") ?? false;
  const hasServiceWarning = health?.services.some((service) => service.status === "warning") ?? false;
  const healthStatus = !health
    ? "unknown"
    : hasCriticalDataIssue || hasServiceError || health.status === "error"
      ? "error"
      : hasServiceWarning || health.status === "warning"
        ? "warning"
        : "healthy";
  const HealthStatusIcon = healthStatus === "healthy" ? CheckCircle2 : healthStatus === "warning" ? AlertTriangle : XCircle;
  const healthTitle = !health
    ? "Sistem durumu yükleniyor"
    : dataAlerts.length
      ? "Veri güncellemesi kontrol edilmeli"
      : hasServiceError || hasServiceWarning
        ? "Sistem bağlantılarından biri kontrol edilmeli"
        : "Tüm veriler güncel";
  const allStoreSales = displayedNightSummary?.all_store_sales_uzs ?? displayedNightSummary?.net_sales_uzs ?? 0;
  const salesExcludingKarshi = displayedNightSummary?.sales_excluding_karshi_outlet_uzs ?? displayedNightSummary?.net_sales_uzs ?? 0;
  const karshiOutletSales = displayedNightSummary?.avva_karshi_outlet_sales_uzs ?? Math.max(0, allStoreSales - salesExcludingKarshi);
  const receiptsExcludingKarshi = displayedNightSummary?.receipts_excluding_karshi_outlet ?? displayedNightSummary?.receipts ?? 0;
  const netUnitsExcludingKarshi = displayedNightSummary?.net_units_excluding_karshi_outlet ?? displayedNightSummary?.net_units ?? 0;
  const currentSalesExcludingKarshi = health?.night_summary?.sales_excluding_karshi_outlet_uzs ?? health?.night_summary?.net_sales_uzs ?? 0;
  const oldestSummaryDate = availableSummaryDates.at(-1) || latestSummaryDate;
  const newestSummaryDate = availableSummaryDates.at(0) || latestSummaryDate;
  const dataStatusRows = [
    {
      label: "Satış",
      value: displayDate(health?.night_summary?.sales_date),
      detail: `${integer.format(currentSalesExcludingKarshi)} · ${t("AVVA Karshi Outlet hariç")}`,
      status: health?.night_summary?.sales_date === expectedDataDate ? "healthy" : "error",
    },
    {
      label: "Ziyaretçi",
      value: displayDate(health?.night_summary?.visitors_date),
      detail: `${integer.format(health?.night_summary?.visitors ?? 0)} · ${t("son tamamlanan gün")}`,
      status: health?.night_summary?.visitors_date === expectedDataDate ? "healthy" : "error",
    },
    {
      label: "Stok ve fiyat",
      value: integer.format(health?.night_summary?.stock_total ?? 0),
      detail: `${integer.format(numericRow("inventory"))} ${t("stok satırı")} · ${integer.format(numericRow("prices"))} ${t("fiyat satırı")}`,
      status: (health?.night_summary?.stock_total ?? 0) > 0 && numericRow("prices") > 0 ? "healthy" : "error",
    },
    {
      label: "Mağaza hedefleri",
      value: integer.format(numericRow("targets")),
      detail: t("Doğrulanmış paket kayıtları"),
      status: numericRow("targets") > 0 ? "healthy" : "error",
    },
    {
      label: "Satış danışmanı hedefleri",
      value: integer.format(numericRow("salesperson_targets")),
      detail: t("Doğrulanmış paket kayıtları"),
      status: numericRow("salesperson_targets") > 0 ? "healthy" : "neutral",
    },
    {
      label: "Manuel import",
      value: displayDate(health?.night_summary?.import_date),
      detail: t("Gece yenilemesini bekletmez"),
      status: "neutral",
    },
  ];
  const rowLabels: Record<string, string> = {
    daily_import: "Günlük import",
    inventory: "Stok kayıtları",
    prices: "Fiyat kayıtları",
    product_daily: "Ürün günlük kayıtları",
    sales: "Satış kayıtları",
    salesperson_targets: "Satış danışmanı hedefleri",
    seasons: "Sezon kayıtları",
    targets: "Hedef kayıtları",
    visitors: "Ziyaretçi kayıtları",
  };
  const sessionUsers = useMemo(() => Array.from(new Map((sessions?.sessions ?? []).map((item) => [item.username, item.display_name || item.username])).entries())
    .sort(([left], [right]) => left.localeCompare(right, "tr")), [sessions]);
  const visibleSessions = useMemo(() => filterSessions(sessions?.sessions ?? [], sessionStart, sessionEnd, sessionUser, sessionStatus), [sessions, sessionEnd, sessionStart, sessionStatus, sessionUser]);
  const visibleActiveUsers = new Set(visibleSessions.filter((item) => item.online).map((item) => item.username)).size;
  const visibleAverageDuration = averageSessionDuration(visibleSessions);
  const startCreate = () => {
    setDraft(emptyDraft);
    setEditing(false);
    setEditorOpen(true);
  };
  const startEdit = (current: AdminUser) => {
    setDraft({ username: current.username, display_name: current.display_name, role: current.role === "admin" ? "admin" : "viewer", allowed_brands: current.allowed_brands, pin: "", active: current.active });
    setEditing(true);
    setEditorOpen(true);
  };
  return (
    <section className="admin-page">
      <div className="admin-toolbar">
        <div className="admin-tabs" role="tablist" aria-label={t("Yönetim alanı")}>
          <button type="button" className={section === "health" ? "active" : ""} onClick={() => onSection("health")}>
            {t("Sistem Sağlığı")}
          </button>
          <button type="button" className={section === "users" ? "active" : ""} onClick={() => onSection("users")}>
            {t("Kullanıcı Yönetimi")}
          </button>
          <button type="button" className={section === "sessions" ? "active" : ""} onClick={() => onSection("sessions")}>
            {t("Kullanıcı Oturumları")}
          </button>
          <button type="button" className={section === "bot-report-history" ? "active" : ""} onClick={() => onSection("bot-report-history")}>
            {language === "ru" ? "История отчётов бота" : language === "uz" ? "Bot hisobotlari tarixi" : "Bot Rapor Geçmişi"}
          </button>
        </div>
        <button type="button" className="admin-refresh" disabled={loading} onClick={() => section === "bot-report-history" ? setBotHistoryRevision(value => value + 1) : onRefresh()}>
          {loading ? t("Yenileniyor…") : `↻ ${t("Yenile")}`}
        </button>
      </div>
      {error ? (
        <div className="notice notice--error">
          <strong>{t("Yönetim bilgileri açılamadı.")}</strong>
          <span>{t(error)}</span>
        </div>
      ) : null}
      {section === "bot-report-history" ? <BotReportHistory language={language} revision={botHistoryRevision} /> : section === "health" ? (
        <>
          <div className={`admin-health-banner status-${healthStatus}`} role="status" aria-live="polite">
            <HealthStatusIcon aria-hidden="true" />
            <div>
              <strong>{t(healthTitle)}</strong>
              {dataAlerts.length ? (
                <ul className="admin-data-alerts">
                  {dataAlerts.map((alert) => <li key={alert}>{t(alert)}</li>)}
                </ul>
              ) : null}
            </div>
          </div>
          <div className="admin-health-grid">
            <article className={`status-card status-${healthStatus}`}>
              <span>{t("Genel Durum")}</span>
              <strong className={`status-text status-${healthStatus}`}>{t(healthStatus === "healthy" ? "Sağlıklı" : healthStatus === "warning" ? "Kontrol gerekli" : healthStatus === "error" ? "Kritik hata" : "Bekleniyor")}</strong>
              <small>{t("Tüm temel servislerin anlık durumu")}</small>
            </article>
            <article className={`status-card status-${health?.release_status === "verified" ? "healthy" : "warning"}`}>
              <span>{t("Veri Paketi")}</span>
              <strong>{health?.data_release_id || "—"}</strong>
              <small>{t("Durum")}: {t(health?.release_status || "—")}</small>
            </article>
            <article>
              <span>{t("Kaynak Oluşturma")}</span>
              <strong>{health?.source_created_at ? new Date(health.source_created_at).toLocaleString("tr-TR") : "—"}</strong>
              <small>{t("Doğrulanmış paketin kaynak zamanı")}</small>
            </article>
            <article>
              <span>{t("API Çalışma Süresi")}</span>
              <strong>{uptime}</strong>
              <small>{t("Sürüm")}: {health?.api_release_id || "—"}</small>
            </article>
          </div>
          <article className="nightly-summary-panel">
            <div className="nightly-summary-panel__head">
              <div>
                <p className="eyebrow">{t("SON GECE YENİLEMESİ")}</p>
                <h2>{t("Kısa veri özeti")}</h2>
                <span>{t(showingHistory ? "Seçilen doğrulanmış gece paketi" : "Güncel doğrulanmış gece paketi")}</span>
              </div>
              <div className="nightly-summary-panel__actions">
                <label className="summary-date-picker">
                  <CalendarDays aria-hidden="true" />
                  <span>{t("Özet tarihi")}</span>
                  <input
                    type="date"
                    value={summaryDate}
                    min={oldestSummaryDate}
                    max={newestSummaryDate}
                    disabled={!latestSummaryDate || historyLoading}
                    onChange={(event) => void changeSummaryDate(event.target.value)}
                    aria-label={t("Özet tarihi")}
                  />
                </label>
                <span className={`summary-sync-badge status-${historyLoading ? "warning" : displayedNightSummary ? "healthy" : "error"}`}>
                  {historyLoading
                    ? t("Geçmiş özet yükleniyor…")
                    : displayedNightSummary
                      ? t(showingHistory ? "Geçmiş paket" : "Paketle eşleşiyor")
                      : t("Özet okunamadı")}
                </span>
              </div>
            </div>
            {historyError ? <p className="summary-history-error" role="alert">{t(historyError)}</p> : null}
            <div className="nightly-summary-grid">
              <div>
                <span className="summary-icon summary-icon--sales"><TrendingUp aria-hidden="true" /></span>
                <div>
                  <small>{t("AVVA Karshi Outlet hariç satış")}</small>
                  <strong>{displayedNightSummary ? `${integer.format(salesExcludingKarshi)}` : "—"}</strong>
                  <span>{t("Tüm mağazalar")}: {integer.format(allStoreSales)}  · {t("Fark")}: {integer.format(karshiOutletSales)}</span>
                  <span>{displayDate(displayedNightSummary?.sales_date)} · {integer.format(receiptsExcludingKarshi)} {t("fiş")} · {integer.format(netUnitsExcludingKarshi)} {t("net ürün")}</span>
                </div>
              </div>
              <div>
                <span className="summary-icon summary-icon--stock"><Database aria-hidden="true" /></span>
                <div>
                  <small>{t("Güncel stok")}</small>
                  <strong>{displayedNightSummary ? integer.format(displayedNightSummary.stock_total) : "—"}</strong>
                  <span>{displayDate(displayedSourceCreatedAt?.slice(0, 10))} · {t("Stok")} {integer.format(displayedNightSummary?.stock ?? 0)} · {t("Postpone")} {integer.format(displayedNightSummary?.postpone ?? 0)} · {t("Transfer Out")} {integer.format(displayedNightSummary?.transfer_out ?? 0)}</span>
                </div>
              </div>
              <div>
                <span className="summary-icon summary-icon--visitors"><UsersRound aria-hidden="true" /></span>
                <div>
                  <small>{t("Ziyaretçi")}</small>
                  <strong>{displayedNightSummary ? integer.format(displayedNightSummary.visitors) : "—"}</strong>
                  <span>{displayDate(displayedNightSummary?.visitors_date)} · {t("son tamamlanan gün")}</span>
                </div>
              </div>
              <div>
                <span className="summary-icon summary-icon--import"><PackageCheck aria-hidden="true" /></span>
                <div>
                  <small>{t("Import")}</small>
                  <strong>{displayDate(displayedNightSummary?.import_date)}</strong>
                  <span>{t("Manuel kaynak · paket tarihi")}</span>
                </div>
              </div>
            </div>
          </article>
          <div className="admin-two-column">
            <article className="admin-panel">
              <div className="admin-panel__head">
                <div>
                  <p className="eyebrow">{t("SERVİSLER")}</p>
                  <h2>{t("Bağlantı Durumu")}</h2>
                </div>
                <span className={`health-dot status-${healthStatus}`} />
              </div>
              <div className="service-list">
                {health?.services.map((service) => (
                  <div key={service.name}>
                    <span className={`health-dot status-${service.status}`} />
                    <strong>{t(service.name)}</strong>
                    <span>{t(service.detail)}</span>
                    <b>{t(service.status === "healthy" ? "Çalışıyor" : service.status === "warning" ? "Kontrol" : "Hata")}</b>
                  </div>
                )) ?? <p>{t("Servis bilgileri yükleniyor…")}</p>}
              </div>
            </article>
            <article className="admin-panel">
              <div className="admin-panel__head">
                <div>
                  <p className="eyebrow">{t("VERİ PAKETİ")}</p>
                  <h2>{t("Veri Durumu")}</h2>
                </div>
                <span>{t("Beklenen tarih")}: {displayDate(expectedDataDate)}</span>
              </div>
              <div className="data-status-list">
                {dataStatusRows.map((row) => (
                  <div key={row.label}>
                    <span className={`health-dot status-${row.status}`} />
                    <div>
                      <strong>{t(row.label)}</strong>
                      <small>{row.detail}</small>
                    </div>
                    <b>{row.value}</b>
                  </div>
                ))}
              </div>
              <details className="release-row-details">
                <summary>{t("Teknik satır sayılarını göster")}</summary>
                <div className="release-row-list">
                  {rowEntries.length ? (
                    rowEntries.map(([name, value]) => (
                      <div key={name}>
                        <span>{t(rowLabels[name] ?? name.replaceAll("_", " "))}</span>
                        <strong>{typeof value === "number" ? integer.format(value) : String(value ?? "—")}</strong>
                      </div>
                    ))
                  ) : (
                    <p>{t("Paket satır bilgisi bekleniyor.")}</p>
                  )}
                </div>
              </details>
            </article>
          </div>
        </>
      ) : section === "sessions" ? (
        <>
          <div className="admin-safety-note admin-session-note">
            <span>ⓘ</span>
            <div>
              <strong>{t("Oturum geçmişi")}</strong>
              <p>{t("Yalnızca giriş, son görülme, çıkış ve yaklaşık süre kaydedilir. Sayfa, filtre veya işlem ayrıntıları izlenmez.")} {t("Kayıtlar 90 gün saklanır.")}</p>
            </div>
          </div>
          <div className="admin-session-summary">
            <article>
              <span className="health-dot status-healthy" />
              <div><small>{t("Aktif kullanıcı")}</small><strong>{visibleActiveUsers}</strong></div>
            </article>
            <article>
              <span className="session-summary-icon">≡</span>
              <div><small>{t("Oturum")}</small><strong>{visibleSessions.length}</strong></div>
            </article>
            <article>
              <span className="session-summary-icon">◷</span>
              <div><small>{t("Ortalama süre")}</small><strong>{displaySessionDuration(visibleAverageDuration, language)}</strong></div>
            </article>
          </div>
          <section className="report-section admin-sessions-section">
            <div className="report-title admin-session-title">
              <span />
              <div>
                <h2>{t("Kullanıcı Oturumları")}</h2>
                <p>{t("Giriş zamanı")}, {t("Son görülme / Çıkış")} ve {t("Oturum süresi").toLocaleLowerCase(language)}</p>
              </div>
              <div className="admin-session-filters">
                <label>
                  <span>{t("Başlangıç")}</span>
                  <input type="date" value={sessionStart} min={sessionRangeDate(89)} max={sessionEnd} onChange={(event) => setSessionStart(event.target.value)} />
                </label>
                <label>
                  <span>{t("Bitiş")}</span>
                  <input type="date" value={sessionEnd} min={sessionStart} max={sessionRangeDate(0)} onChange={(event) => setSessionEnd(event.target.value)} />
                </label>
                <label>
                  <span>{t("Kullanıcı")}</span>
                  <select value={sessionUser} onChange={(event) => setSessionUser(event.target.value)}>
                    <option value="all">{t("Tüm kullanıcılar")}</option>
                    {sessionUsers.map(([username, displayName]) => <option key={username} value={username}>{displayName} · {username}</option>)}
                  </select>
                </label>
                <label>
                  <span>{t("Durum")}</span>
                  <select value={sessionStatus} onChange={(event) => setSessionStatus(event.target.value as SessionStatusFilter)}>
                    <option value="all">{t("Tüm durumlar")}</option>
                    <option value="active">{t("Aktif oturumlar")}</option>
                    <option value="completed">{t("Tamamlanmış oturumlar")}</option>
                  </select>
                </label>
                <button type="button" className="admin-session-export" disabled={!visibleSessions.length || !sessionStart || !sessionEnd || sessionStart > sessionEnd} onClick={() => void downloadSessionWorkbook(visibleSessions, sessionStart, sessionEnd, sessionUser, sessionStatus, language)}>
                  <Download aria-hidden="true" size={15} />
                  {t("Kullanıcı Oturumlarını Excel’e İndir")}
                </button>
              </div>
            </div>
            <div className="table-scroll admin-sessions-table-scroll">
              <table className="admin-users-table admin-sessions-table">
                <thead>
                  <tr>
                    <th>{t("Kullanıcı")}</th>
                    <th>{t("Giriş zamanı")}</th>
                    <th>{t("Son görülme / Çıkış")}</th>
                    <th>{t("Oturum süresi")}</th>
                    <th>{t("Durum")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSessions.map((item, index) => (
                    <tr key={`${item.username}-${item.login_at}-${index}`}>
                      <td><strong>{item.display_name || item.username}</strong><small>@{item.username}</small></td>
                      <td>{displaySessionDate(item.login_at, language)}</td>
                      <td>{displaySessionDate(item.logout_at || item.last_seen_at, language)}</td>
                      <td>{displaySessionDuration(item.duration_seconds, language)}</td>
                      <td><span className={`user-status ${item.online ? "active" : "closed"}`}>{t(item.online ? "Çevrimiçi" : item.logout_at ? "Oturum kapandı" : "Bağlantı sona erdi")}</span></td>
                    </tr>
                  ))}
                  {!visibleSessions.length ? <tr><td className="admin-session-empty" colSpan={5}>{t("Henüz oturum kaydı yok")}</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <>
          <div className="admin-safety-note">
            <span>ⓘ</span>
            <div>
              <strong>{t(users?.writes_enabled ? "Kullanıcı işlemleri güvenli biçimde kaydedilir." : "Canlı kullanıcılar korunuyor.")}</strong>
              <p>{t(users?.writes_enabled ? "Mevcut PIN'ler gösterilmez. Düzenleme sırasında PIN alanını boş bırakırsanız PIN değişmez; yeni bir PIN yazarsanız güvenli biçimde yenilenir." : "Kullanıcı ekleme, PIN değiştirme ve yetki düzenleme şu anda salt okunur durumdadır.")}</p>
            </div>
          </div>
          <section className="report-section admin-users-section">
            <div className="report-title">
              <span />
              <div>
                <h2>{t("Kullanıcılar")}</h2>
                <p>{users?.users.length ?? 0} {t("kullanıcı ve erişim kapsamı")}</p>
              </div>
              <div className="admin-user-actions">
                <b>{t(users?.writes_enabled ? "Düzenleme açık" : "Salt okunur")}</b>
                {users?.writes_enabled ? <button type="button" onClick={startCreate}>+ {t("Yeni kullanıcı")}</button> : null}
              </div>
            </div>
            {editorOpen && users?.writes_enabled ? (
              <form
                className="admin-user-editor"
                onSubmit={(event) => {
                  event.preventDefault();
                  void onSaveUser(draft, editing).then(() => setEditorOpen(false));
                }}
              >
                <label>
                  {t("Kullanıcı adı")}
                  <input value={draft.username} disabled={editing || loading} required pattern="[a-zA-Z0-9_.-]+" onChange={(event) => setDraft((current) => ({ ...current, username: event.target.value }))} />
                </label>
                <label>
                  {t("Ad Soyad")}
                  <input value={draft.display_name} disabled={loading} required onChange={(event) => setDraft((current) => ({ ...current, display_name: event.target.value }))} />
                </label>
                <label>
                  {t("Rol")}
                  <select value={draft.role} disabled={loading} onChange={(event) => setDraft((current) => ({ ...current, role: event.target.value as "admin" | "viewer", allowed_brands: event.target.value === "admin" ? [] : current.allowed_brands }))}>
                    <option value="viewer">{t("Kullanıcı")}</option>
                    <option value="admin">{t("Yönetici")}</option>
                  </select>
                </label>
                <label>
                  PIN {editing ? `(${t("değiştirmek istemiyorsanız boş bırakın")})` : ""}
                  <input type="password" inputMode="numeric" minLength={editing ? undefined : 4} maxLength={12} required={!editing} value={draft.pin} disabled={loading} onChange={(event) => setDraft((current) => ({ ...current, pin: event.target.value.replace(/\D/g, "") }))} />
                </label>
                <label className="admin-user-editor__brands">
                  {t("Marka Yetkisi")}
                  <select multiple value={draft.allowed_brands} disabled={loading || draft.role === "admin"} onChange={(event) => setDraft((current) => ({ ...current, allowed_brands: selectedValues(event.target) }))}>
                    {brands.map((brand) => <option key={brand} value={brand}>{brand}</option>)}
                  </select>
                </label>
                <label className="admin-user-editor__active">
                  <input type="checkbox" checked={draft.active} disabled={loading} onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))} />
                  {t("Aktif")}
                </label>
                {editing ? <TelegramCodeEditor username={draft.username} model={telegram} disabled={loading || !draft.active || draft.username === "aydind-demo"} /> : null}
                <div>
                  <button type="button" disabled={loading} onClick={() => setEditorOpen(false)}>{t("İptal")}</button>
                  <button type="submit" disabled={loading}>{loading ? t("Kaydediliyor…") : t("Kaydet")}</button>
                </div>
              </form>
            ) : null}
            <div className="table-scroll admin-users-table-scroll">
              <table className="admin-users-table">
                <colgroup>
                  <col className="admin-col-username" />
                  <col className="admin-col-name" />
                  <col className="admin-col-role" />
                  <col className="admin-col-brands" />
                  <col className="admin-col-showroom" />
                  <col className="admin-col-status" />
                  <col data-telegram-code />
                  {users?.writes_enabled ? <col className="admin-col-action" /> : null}
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("Kullanıcı")}</th>
                    <th>{t("Ad Soyad")}</th>
                    <th>{t("Rol")}</th>
                    <th>{t("Marka Yetkisi")}</th>
                    <th>{t("Showroom")}</th>
                    <th>{t("Durum")}</th>
                    <th data-telegram-code>{telegram.copy.column}</th>
                    {users?.writes_enabled ? <th>{t("İşlem")}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {users?.users.map((user) => (
                    <tr key={user.username}>
                      <td>
                        <strong>{user.username}</strong>
                      </td>
                      <td>{user.display_name || "—"}</td>
                      <td>{t(user.role === "admin" ? "Yönetici" : "Kullanıcı")}</td>
                      <td>{user.role === "admin" || !user.allowed_brands.length ? t("Tüm markalar") : user.allowed_brands.join(", ")}</td>
                      <td>{t(user.showroom_access ? "Erişim var" : "Erişim yok")}</td>
                      <td>
                        <span className={`user-status ${user.active ? "active" : "inactive"}`}>{t(user.active ? "Aktif" : "Pasif")}</span>
                      </td>
                      <TelegramCodeCell username={user.username} model={telegram} />
                      {users?.writes_enabled ? <td><button type="button" onClick={() => startEdit(user)}>{t("Düzenle")}</button></td> : null}
                    </tr>
                  )) ?? null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </section>
  );
}

const productExcelText = {
  tr: {
    info: "Rapor Bilgisi",
    products: "Ürün Performansı",
    skuColors: "SKU Renk",
    sizes: "Beden Performansı",
    colors: "Renk Performansı",
    period: "Dönem",
    filters: "Uygulanan Filtreler",
    sku: "SKU",
    skuColor: "SKU + Renk",
    category: "Kategori",
    season: "Sezon",
    imported: "İmport Adet",
    firstImport: "İlk İmport",
    lastImport: "Son İmport",
    sales: "Satılan Adet",
    stock: "Stok",
    sellthrough: "Sell-through %",
    price: "Fiyat",
    unitPrice: "Birim Fiyat",
    discount: "İndirim %",
    dailySales: "Günlük Satış",
    stockDays: "Stok Günü",
    firstSale: "İlk Satış",
    lastSale: "Son Satış",
    status: "Durum",
    size: "Beden",
    colorCode: "Renk Kodu",
    colorName: "Renk Adı",
  },
  en: {
    info: "Report Info",
    products: "Product Performance",
    skuColors: "SKU Colour",
    sizes: "Size Performance",
    colors: "Colour Performance",
    period: "Period",
    filters: "Applied Filters",
    sku: "SKU",
    skuColor: "SKU + Colour",
    category: "Category",
    season: "Season",
    imported: "Import",
    firstImport: "First Import",
    lastImport: "Last Import",
    sales: "Sales",
    stock: "Stock",
    sellthrough: "Sell-through %",
    price: "Price",
    unitPrice: "Unit Price",
    discount: "Discount %",
    dailySales: "Daily Sales",
    stockDays: "Stock Days",
    firstSale: "First Sale",
    lastSale: "Last Sale",
    status: "Status",
    size: "Size",
    colorCode: "Colour Code",
    colorName: "Colour Name",
  },
  ru: {
    info: "Об отчёте",
    products: "Показатели товаров",
    skuColors: "SKU и цвет",
    sizes: "Показатели по размерам",
    colors: "Показатели по цветам",
    period: "Период",
    filters: "Применённые фильтры",
    sku: "SKU",
    skuColor: "SKU + Цвет",
    category: "Категория",
    season: "Сезон",
    imported: "Поступило, шт.",
    firstImport: "Первое поступление",
    lastImport: "Последнее поступление",
    sales: "Продано, шт.",
    stock: "Остаток",
    sellthrough: "Доля проданного, %",
    price: "Цена",
    unitPrice: "Цена за единицу",
    discount: "Скидка %",
    dailySales: "Продажи в день",
    stockDays: "На сколько дней хватит",
    firstSale: "Первая продажа",
    lastSale: "Последняя продажа",
    status: "Состояние",
    size: "Размер",
    colorCode: "Код цвета",
    colorName: "Название цвета",
  },
  uz: {
    info: "Hisobot haqida",
    products: "Mahsulotlar ko‘rsatkichlari",
    skuColors: "SKU va rang",
    sizes: "O‘lchamlar bo‘yicha ko‘rsatkichlar",
    colors: "Ranglar bo‘yicha ko‘rsatkichlar",
    period: "Davr",
    filters: "Qo‘llangan filtrlar",
    sku: "SKU",
    skuColor: "SKU + Rang",
    category: "Toifa",
    season: "Mavsum",
    imported: "Kirim, dona",
    firstImport: "Birinchi kirim",
    lastImport: "Oxirgi kirim",
    sales: "Sotilgan, dona",
    stock: "Qoldiq",
    sellthrough: "Sotilish darajasi, %",
    price: "Narx",
    unitPrice: "Bir dona narxi",
    discount: "Chegirma %",
    dailySales: "Kunlik savdo",
    stockDays: "Qoldiq necha kunga yetadi",
    firstSale: "Birinchi savdo",
    lastSale: "Oxirgi savdo",
    status: "Holat",
    size: "O‘lcham",
    colorCode: "Rang kodi",
    colorName: "Rang nomi",
  },
} as const;

function productPerformanceExportValue(row: ProductPerformanceRow, key: string): number | string | null {
  const values: Record<string, number | string | null> = {
    label: row.label,
    category: row.category,
    subcategory: row.subcategory ?? "",
    season: row.season,
    import: row.import_units,
    firstImport: row.first_import,
    lastImport: row.last_import,
    sales: row.sales_units,
    stock: row.stock_units,
    sellthrough: row.sellthrough_percent,
    price: row.price_uzs,
    unit: row.average_unit_price_uzs,
    discount: row.discount_percent,
    daily: row.daily_sales,
    stockDays: row.stock_days,
    first: row.first_sale,
    last: row.last_sale,
    stale: row.days_without_sale,
    status: productStockStatusRank(productStockStatus(row)),
  };
  return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null;
}

function productDimensionExportValue(row: ProductDimensionRow, key: string): number | string | null {
  const values: Record<string, number | string | null> = {
    label: row.label,
    colorName: row.color_name,
    import: row.import_units,
    sales: row.sales_units,
    stock: row.stock_units,
    sellthrough: row.sales_units + row.stock_units ? row.sales_units / (row.sales_units + row.stock_units) : null,
  };
  return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null;
}

export async function downloadProductWorkbook(report: ProductReport, filters: Filters, language: keyof typeof productExcelText, view?: { productSearch: string; productStatus: string; colorSearch: string; colorStatus: string }, variants?: import("./productAnalysisExcel").ProductExcelVariants) {
  if (!variants) throw new Error(translate(language, "Beden detayları tamamlanamadı. Excel oluşturulmadı."));
  const filterLabels: Record<keyof Filters, string> = { brands: "Marka", stores: "Mağaza", malls: "Alışveriş Merkezi", main_categories: "Ana Kategori", subcategories: "Alt Kategori", genders: "Cinsiyet", seasons: "Sezon", skus: "SKU" };
  const filterText = Object.entries(filters).filter(([, values]) => values.length).map(([key, values]) => `${translate(language, filterLabels[key as keyof Filters])}: ${values.join(", ")}`).join(" | ") || "—";
  const products = sortRowsForExport(productRowsWithStatus(report.products, report.period.end), gridSortRegistry.get(translate(language, "Ürün Performansı")) ?? null, productPerformanceExportValue);
  const sku_colors = sortRowsForExport(productRowsWithStatus(report.sku_colors, report.period.end), gridSortRegistry.get(translate(language, "SKU + Renk Performansı")) ?? null, productPerformanceExportValue);
  const { downloadProductAnalysisExcel } = await import("./productAnalysisExcel");
  await downloadProductAnalysisExcel({ report: { ...report, products, sku_colors }, variants, language, filterText, view,
    translate: value => translate(language, value), statusLabel: row => translate(language, productStockStatusLabel(productStockStatus(row, report.period.end))),
  }, `urun_analizi_${report.period.end}_${language}.xlsx`);
}

// Keep unchanged report tables stable while the shell updates filters/loading.
const MemoReportTable = memo(ReportTable, equalReportProps);
const MemoStoreDetailTable = memo(StoreDetailTable, equalReportProps);
const MemoAdviserTable = memo(AdviserTable, equalReportProps);
const MemoExecutiveMobileStoreSummary = memo(ExecutiveMobileStoreSummary, equalReportProps);
const MemoActionStoresTable = memo(ActionStoresTable, equalReportProps);
const MemoExecutiveWeekdayTable = memo(ExecutiveWeekdayTable, equalReportProps);
const MemoExecutiveInsightPanel = memo(ExecutiveInsightPanel, equalReportProps);

export default function App() {
  const defaults = useMemo(fallbackPeriod, []);
  const [auth, setAuth] = useState<AuthUser | null | undefined>(undefined);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<PlatformStatus | null>(null);
  const [catalog, setCatalog] = useState<FilterCatalog | null>(null);
  const [summary, setSummary] = useState<ExecutiveSummary | null>(null);
  const [tables, setTables] = useState<ExecutiveTables | null>(null);
  const [storeReport, setStoreReport] = useState<StoreReport | null>(null);
  const [productReport, setProductReport] = useState<ProductReport | null>(null);
  const [productSection, setProductSection] = useState<"performance" | "sellthrough">("performance");
  const [initialColorPage, setInitialColorPage] = useState<InitialProductPage>();
  const productPagePrefetch = useMemo(() => new ProductPagePrefetch(), []);
  const filterCatalogSequence = useRef(0);
  const filterChangeSequence = useRef(0);
  const [showroomReport, setShowroomReport] = useState<ShowroomReport | null>(null);
  const [storePerformanceReport, setStorePerformanceReport] = useState<StorePerformanceReport | null>(null);
  const [reportKind, setReportKind] = useState<"store" | "campaign">("store");
  const [privateSection, setPrivateSection] = useState<PrivateWorkspaceSection>("reports");
  const [storePerformanceView, setStorePerformanceView] = useState<StorePerformanceView>("current");
  const [reportBrands, setReportBrands] = useState<string[]>([]);
  const [reportMalls, setReportMalls] = useState<string[]>([]);
  const [reportStores, setReportStores] = useState<string[]>([]);
  const [reportBrandOptions, setReportBrandOptions] = useState<string[]>([]);
  const [reportMallOptions, setReportMallOptions] = useState<string[]>([]);
  const [reportStoreOptions, setReportStoreOptions] = useState<string[]>([]);
  const [adminSection, setAdminSection] = useState<AdminSection>("health");
  const [adminHealth, setAdminHealth] = useState<AdminHealth | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUsersResponse | null>(null);
  const [adminSessions, setAdminSessions] = useState<AdminSessionsResponse | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [showroomBrandChoices, setShowroomBrandChoices] = useState<string[]>([]);
  const [showroomSelectedBrands, setShowroomSelectedBrands] = useState<string[]>([]);
  const [showroomOpen, setShowroomOpen] = useState(false);
  const [productSkuDraft, setProductSkuDraft] = useState("");
  const [productStatusFilter, setProductStatusFilter] = useState<ProductStatusFilter>("all");
  const [productColorStatusFilter, setProductColorStatusFilter] = useState<ProductStatusFilter>("all");
  const [productTableBusy, setProductTableBusy] = useState(false);
  const [productColorTableBusy, setProductColorTableBusy] = useState(false);
  const [globalSkuSearch, setGlobalSkuSearch] = useState("");
  const [skuSuggestions, setSkuSuggestions] = useState<string[]>([]);
  const [page, setPage] = useState<PageId>("executive");
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [period, setPeriod] = useState<PeriodId>("month");
  const [productPeriodMode, setProductPeriodMode] = useState<ProductPeriodMode>("lifecycle");
  const [productSelectedPeriod, setProductSelectedPeriod] = useState<PeriodId>("month");
  const [productSelectedRange, setProductSelectedRange] = useState<ProductDateRange>(defaults);
  const productRange = productAnalysisRange(productPeriodMode, productSelectedRange, catalog);
  const activePeriod = page === "product" ? productSelectedPeriod : period;
  const activeSelectedRange = page === "product" ? productSelectedRange : { start, end };
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [showroomStart, setShowroomStart] = useState(defaults.start);
  const [showroomEnd, setShowroomEnd] = useState(defaults.end);
  const [reportStart, setReportStart] = useState(defaults.start);
  const [reportEnd, setReportEnd] = useState(defaults.end);
  const [showroomPeriod, setShowroomPeriod] = useState<ShowroomPeriodId>("month");
  const [showroomMonthAnchor, setShowroomMonthAnchor] = useState<string | null>(null);
  const showroomMonthStripRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!desktopInformationView() || page !== "showroom" || privateSection !== "showroom" || !showroomOpen) return;
    const strip = showroomMonthStripRef.current?.lastElementChild as HTMLElement | null;
    const selected = strip?.querySelector<HTMLElement>(`[data-month="${showroomMonthAnchor ?? showroomEnd.slice(0, 7)}"]`);
    if (strip && selected) strip.scrollTo({ left: strip.scrollLeft + selected.getBoundingClientRect().left - strip.getBoundingClientRect().left - (strip.clientWidth - selected.offsetWidth) / 2, behavior: "auto" });
  }, [page, privateSection, showroomOpen, showroomStart, showroomEnd, showroomMonthAnchor, showroomReport, catalog?.min_date, catalog?.max_date]);
  const [fullMonthTarget, setFullMonthTarget] = useState(true);
  const [lyComparison, setLyComparison] = useState<LyComparison>("same_date");
  const [loading, setLoading] = useState(true);
  const reportSequence = useRef(0);
  const [exporting, setExporting] = useState(false);
  const productExportDisabled = productExportBlocked(loading, productTableBusy || productColorTableBusy, exporting, Boolean(productReport));
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<LanguageCode>("tr");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileQuickFilter, setMobileQuickFilter] = useState<ExecutiveMobileQuickFilterId | null>(null);
  const [mobileExecutiveSection, setMobileExecutiveSection] = useState<"overview" | "tables">("overview");
  const [executiveStoreTableView, setExecutiveStoreTableView] = useState<MobileTableView>(() =>
    window.sessionStorage.getItem("srr360-executive-store-table-view") === "detail" ? "detail" : "summary",
  );
  const [isCompactViewport, setIsCompactViewport] = useState(() =>
    window.matchMedia("(max-width: 900px), (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 600px)").matches,
  );
  const [isWideDesktop, setIsWideDesktop] = useState(() => window.matchMedia("(min-width: 1181px)").matches);
  const t = (text: string) => translate(language, text);

  /* Persistent navigation: URL/history only; existing report calculations stay below unchanged. */
  const navigationFlights = useMemo(() => new NavigationFlights(), []);
  const navigationBaseCatalog = useRef<FilterCatalog | null>(null);
  const navigationRestoring = useRef(false);
  const navigationSequence = useRef(0);
  const navigationActiveReport = useRef<string | null>(null);
  const navigationReplaceNext = useRef(false);
  const navigationPendingPath = useRef<string | null>(null);
  const navigationPop = useRef<() => void>(() => undefined);
  const [navigationReady, setNavigationReady] = useState(false);
  const [navigationReplay, setNavigationReplay] = useState(0);
  const [campaignNavigation, setCampaignNavigation] = useState<CampaignNavigation>(emptyCampaignNavigation);
  const [showroomNavigation, setShowroomNavigation] = useState<ShowroomNavigation>(emptyShowroomNavigation);
  const rememberCampaignNavigation = useCallback((next: CampaignNavigation, replace = false) => {
    setCampaignNavigation(previous => {
      if (JSON.stringify(previous) === JSON.stringify(next)) return previous;
      if (replace) navigationReplaceNext.current = true;
      return next;
    });
  }, []);
  const rememberShowroomNavigation = useCallback((next: ShowroomNavigation) => setShowroomNavigation(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next), []);
  const navigationState: NavigationState = {
    page, period: activePeriod, ...activeSelectedRange, fullMonthTarget, lyComparison, filters, productPeriodMode, language,
    privateSection, reportKind, reportView: storePerformanceView, adminSection,
    reportStart, reportEnd, reportBrands, reportMalls, reportStores,
    showroomStart, showroomEnd, showroomPeriod, showroomBrands: showroomOpen ? showroomSelectedBrands : showroomBrandChoices,
    showroomOpen, showroom: showroomNavigation, campaign: campaignNavigation,
    productStatus: productStatusFilter, colorStatus: productColorStatusFilter,
  };
  const navigationSignature = JSON.stringify(navigationState);
  useEffect(() => {
    if (!navigationReady || navigationRestoring.current || !auth) return;
    const next = JSON.parse(navigationSignature) as NavigationState;
    const path = navigationPath(next);
    const replace = navigationReplaceNext.current || navigationPendingPath.current === path;
    navigationReplaceNext.current = false;
    const changed = writeNavigation(window.history, window.location, next, replace);
    if (loading || adminLoading) { if (changed) navigationPendingPath.current = path; }
    else navigationPendingPath.current = null;
  }, [navigationSignature, navigationReady, auth, loading, adminLoading]);
  useEffect(() => {
    const onPop = () => navigationPop.current();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  navigationPop.current = () => {
    if (!auth || !navigationBaseCatalog.current) return;
    const base = navigationBaseCatalog.current;
    const next = readNavigation(window.location, base, periodRange("month", base.min_date, base.max_date), auth);
    void restoreNavigation(next, base, true).catch(caught => setError(caught instanceof Error ? caught.message : "Sayfa geri yüklenemedi."));
  };
  function loadPage(nextPage = page, nextStart = start, nextEnd = end, nextFullMonthTarget = fullMonthTarget, nextFilters = filters, productOverride?: { mode: ProductPeriodMode; selected: ProductDateRange; catalog?: FilterCatalog; colorStatus?: ProductStatusFilter }, catalogReady?: Promise<Filters>, nextLyComparison: LyComparison = lyComparison) {
    const override = productOverride ?? { mode: productPeriodMode, selected: productSelectedRange, catalog: catalog ?? undefined };
    const range = nextPage === "product" ? productAnalysisRange(override.mode, override.selected, override.catalog ?? catalog) : { start: nextStart, end: nextEnd };
    const key = nextPage + ":" + reportParams(range.start, range.end, nextFullMonthTarget, nextFilters, nextLyComparison);
    navigationActiveReport.current = key;
    return navigationFlights.run("page:" + key, () => loadPageData(nextPage, nextStart, nextEnd, nextFullMonthTarget, nextFilters, productOverride, key, catalogReady, nextLyComparison));
  }
  function fetchFilterCatalog(nextFilters: Filters, skuSearch = "") {
    return navigationFlights.run("filters:" + JSON.stringify([nextFilters, skuSearch]), () => fetchFilterCatalogData(nextFilters, skuSearch));
  }
  function loadStorePerformanceReport(nextStart = reportStart, nextEnd = reportEnd, nextBrands = reportBrands, nextMalls = reportMalls, nextStores = reportStores, availableBrands = reportBrandOptions.length ? reportBrandOptions : (catalog?.brands ?? []), nextLyComparison: LyComparison = lyComparison) {
    const key = "store-report:" + JSON.stringify([nextStart, nextEnd, nextBrands, nextMalls, nextStores, availableBrands, nextLyComparison]);
    navigationActiveReport.current = key;
    return navigationFlights.run(key, () => loadStorePerformanceReportData(nextStart, nextEnd, nextBrands, nextMalls, nextStores, availableBrands, key, nextLyComparison));
  }
  function loadShowroom(nextStart = showroomStart, nextEnd = showroomEnd, labels = showroomSelectedBrands, availableBrands = catalog?.brands ?? []) {
    const key = "showroom:" + JSON.stringify([nextStart, nextEnd, labels, availableBrands]);
    navigationActiveReport.current = key;
    return navigationFlights.run(key, () => loadShowroomData(nextStart, nextEnd, labels, availableBrands, key));
  }
  function loadAdmin(nextSection: AdminSection = adminSection) {
    return navigationFlights.run("admin:" + nextSection, () => loadAdminData(nextSection));
  }
  async function restoreNavigation(next: NavigationState, base: FilterCatalog, replay = false) {
    const sequence = ++navigationSequence.current;
    ++filterCatalogSequence.current;
    ++filterChangeSequence.current;
    const reuseReport = navigationReady && !loading && navigationReportKey(next, base) === navigationReportKey(navigationState, base);
    ++reportSequence.current;
    navigationActiveReport.current = null;
    navigationRestoring.current = true;
    setLoading(true);
    setMobileFiltersOpen(false);
    window.dispatchEvent(new Event("srr360:close-popovers"));
    try {
      const sameFilters = navigationReady && JSON.stringify(next.filters) === JSON.stringify(filters);
      const nextCatalog = sameFilters ? catalog ?? base : Object.values(next.filters).some(values => values.length) ? await fetchFilterCatalog(next.filters) : base;
      if (sequence !== navigationSequence.current) return;
      setCatalog(nextCatalog);
      setPage(next.page); setPeriod(next.period); setStart(next.start); setEnd(next.end);
      setFilters(next.filters); setFullMonthTarget(next.fullMonthTarget); setLyComparison(normalizeLyComparison(next.lyComparison)); setLanguage(next.language);
      setProductPeriodMode(next.productPeriodMode); setProductSelectedPeriod(next.period); setProductSelectedRange({ start: next.start, end: next.end });
      setProductStatusFilter(next.productStatus); setProductColorStatusFilter(next.colorStatus);
      setPrivateSection(next.privateSection); setReportKind(next.reportKind); setStorePerformanceView(next.reportView); setAdminSection(next.adminSection);
      setReportStart(next.reportStart); setReportEnd(next.reportEnd); setReportBrands(next.reportBrands); setReportMalls(next.reportMalls); setReportStores(next.reportStores);
      setReportBrandOptions(base.brands); setReportMallOptions(base.malls); setReportStoreOptions(base.stores);
      setShowroomStart(next.showroomStart); setShowroomEnd(next.showroomEnd); setShowroomPeriod(next.showroomPeriod); setShowroomMonthAnchor(null);
      setShowroomBrandChoices(next.showroomBrands); setShowroomSelectedBrands(next.showroomBrands); setShowroomOpen(next.showroomOpen);
      setCampaignNavigation(next.campaign); setShowroomNavigation(next.showroom);
      if (replay) setNavigationReplay(value => value + 1);
      if (reuseReport) { /* History-only change: no report request. */ }
      else if (next.page === "admin") await loadAdmin(next.adminSection);
      else if (next.page === "showroom") {
        if (next.privateSection === "showroom") {
          setShowroomReport(null);
          if (next.showroomOpen) await loadShowroom(next.showroomStart, next.showroomEnd, next.showroomBrands, base.brands);
        } else if (next.reportKind === "store") {
          if (next.reportBrands.length || next.reportMalls.length) {
            const options = await fetchFilterCatalog({ ...emptyFilters, brands: next.reportBrands, malls: next.reportMalls });
            if (sequence !== navigationSequence.current) return;
            setReportMallOptions(options.malls); setReportStoreOptions(options.stores);
          }
          await loadStorePerformanceReport(next.reportStart, next.reportEnd, next.reportBrands, next.reportMalls, next.reportStores, base.brands, normalizeLyComparison(next.lyComparison));
        }
        // CampaignReports owns its one cancellable report request; no parent duplicate.
      } else await loadPage(next.page, next.start, next.end, next.fullMonthTarget, next.filters, { mode: next.productPeriodMode, selected: { start: next.start, end: next.end }, catalog: nextCatalog, colorStatus: next.colorStatus }, undefined, normalizeLyComparison(next.lyComparison));
      if (sequence !== navigationSequence.current) return;
      writeNavigation(window.history, window.location, next, true);
    } finally {
      if (sequence === navigationSequence.current) {
        navigationRestoring.current = false;
        setLoading(false); setNavigationReady(true);
      }
    }
  }
  /* End persistent navigation. */
  useEffect(() => {
    if (page !== "product") productPagePrefetch.cancel();
  }, [page, productPagePrefetch]);
  useEffect(() => () => productPagePrefetch.cancel(), [productPagePrefetch]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    window.sessionStorage.setItem("srr360-executive-store-table-view", executiveStoreTableView);
  }, [executiveStoreTableView]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 900px), (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 600px)");
    const syncViewport = () => setIsCompactViewport(query.matches);
    syncViewport();
    query.addEventListener("change", syncViewport);
    return () => query.removeEventListener("change", syncViewport);
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(min-width: 1181px)");
    const syncViewport = () => setIsWideDesktop(query.matches);
    syncViewport();
    query.addEventListener("change", syncViewport);
    return () => query.removeEventListener("change", syncViewport);
  }, []);

  useEffect(() => {
    if (isCompactViewport && page === "showroom" && privateSection === "showroom") {
      setPrivateSection("reports");
      setError(null);
      if (!storePerformanceReport) void loadStorePerformanceReport();
    }
  }, [isCompactViewport, page, privateSection, storePerformanceReport]);

  useEffect(() => {
    if (!mobileFiltersOpen || page === "showroom") return;
    setMobileQuickFilter(null);
    document.body.classList.add("mobile-drawer-open");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileFiltersOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("mobile-drawer-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileFiltersOpen, page]);

  useEffect(() => {
    if (!isCompactViewport || page !== "executive") return;
    const syncMobileSection = () => {
      const tablesSection = document.getElementById("executive-mobile-tables");
      if (!tablesSection) return;
      setMobileExecutiveSection(tablesSection.getBoundingClientRect().top <= 120 ? "tables" : "overview");
    };
    syncMobileSection();
    window.addEventListener("scroll", syncMobileSection, { passive: true });
    return () => window.removeEventListener("scroll", syncMobileSection);
  }, [isCompactViewport, page]);

  useEffect(() => {
    const closeDrawerAfterRotation = () => setMobileFiltersOpen(false);
    window.addEventListener("orientationchange", closeDrawerAfterRotation);
    screen.orientation?.addEventListener("change", closeDrawerAfterRotation);
    return () => {
      window.removeEventListener("orientationchange", closeDrawerAfterRotation);
      screen.orientation?.removeEventListener("change", closeDrawerAfterRotation);
    };
  }, []);

  useEffect(() => {
    if (isCompactViewport && (page === "executive" || page === "store")) return;
    setMobileQuickFilter(null);
  }, [isCompactViewport, page]);

  function showroomBrandCatalog() {
    const brands = catalog?.brands ?? [];
    const hasInstreet = brands.includes("INSTREET");
    const hasReebok = brands.includes("REEBOK");
    return [...brands.filter((brand) => brand !== "INSTREET" && brand !== "REEBOK"), ...(hasInstreet || hasReebok ? ["INSTREET + REEBOK"] : [])].sort((a, b) => a.localeCompare(b, "tr"));
  }
  function showroomSourceBrands(labels: string[], available = catalog?.brands ?? []) {
    return Array.from(new Set(labels.flatMap((label) => (label === "INSTREET + REEBOK" ? ["INSTREET", "REEBOK"].filter((brand) => available.includes(brand)) : [label]))));
  }

  async function loadShowroomData(nextStart = showroomStart, nextEnd = showroomEnd, labels = showroomSelectedBrands, availableBrands = catalog?.brands ?? [], navigationRequestKey?: string) {
    const brands = showroomSourceBrands(labels, availableBrands);
    if (!brands.length) return;
    setLoading(true);
    setError(null);
    try {
      const params = reportParams(nextStart, nextEnd, false, {
        ...emptyFilters,
        brands,
      }, lyComparison);
      const response = await fetch(`/api/v1/showroom/report?${params}`);
      if (response.status === 401) setAuth(null);
      if (response.status === 403) throw new Error("Bu kullanıcı için Showroom erişimi bulunmuyor.");
      if (!response.ok) {
        const detail = await response.json().catch(() => null) as { message?: string } | null;
        throw new Error(detail?.message || `Showroom servisi ${response.status} yanıtı verdi.`);
      }
      const nextReport = (await response.json()) as ShowroomReport;
      if (navigationActiveReport.current === navigationRequestKey) setShowroomReport(nextReport);
    } catch (caught) {
      if (navigationActiveReport.current === navigationRequestKey) setError(caught instanceof Error ? caught.message : "Showroom açılamadı.");
    } finally {
      if (navigationActiveReport.current === navigationRequestKey) setLoading(false);
    }
  }

  async function loadStorePerformanceReportData(nextStart = reportStart, nextEnd = reportEnd, nextBrands = reportBrands, nextMalls = reportMalls, nextStores = reportStores, availableBrands = reportBrandOptions.length ? reportBrandOptions : (catalog?.brands ?? []), navigationRequestKey?: string, nextLyComparison: LyComparison = lyComparison) {
    const brands = nextBrands.length ? nextBrands : availableBrands;
    if (!brands.length) return;
    setLoading(true);
    setError(null);
    try {
      const params = reportParams(nextStart, nextEnd, true, { ...emptyFilters, brands: nextBrands, malls: nextMalls, stores: nextStores }, nextLyComparison);
      const response = await fetch(`/api/v1/reports/store-performance?${params}`);
      if (response.status === 401) setAuth(null);
      if (response.status === 403) throw new Error("Bu rapor alanı yalnızca yetkili kullanıcıya açıktır.");
      if (!response.ok) throw new Error(`Rapor servisi ${response.status} yanıtı verdi.`);
      const nextReport = (await response.json()) as StorePerformanceReport;
      if (normalizeLyComparison(nextReport.ly_comparison) !== nextLyComparison) throw new Error(t("Veri açılamadı."));
      if (navigationActiveReport.current === navigationRequestKey) setStorePerformanceReport(nextReport);
    } catch (caught) {
      if (navigationActiveReport.current === navigationRequestKey) setError(caught instanceof Error ? caught.message : "Mağaza performans raporu açılamadı.");
    } finally {
      if (navigationActiveReport.current === navigationRequestKey) setLoading(false);
    }
  }

  async function changeReportBrands(values: string[]) {
    const navigationRequest = ++navigationSequence.current;
    navigationActiveReport.current = null;
    setReportBrands(values);
    setLoading(true);
    setError(null);
    try {
      const brandCatalog = await fetchFilterCatalog({ ...emptyFilters, brands: values });
      if (navigationRequest !== navigationSequence.current) return;
      setReportMallOptions(brandCatalog.malls);
      const validMalls = reportMalls.filter((mall) => brandCatalog.malls.includes(mall));
      setReportMalls(validMalls);
      const nextCatalog = validMalls.length ? await fetchFilterCatalog({ ...emptyFilters, brands: values, malls: validMalls }) : brandCatalog;
      if (navigationRequest !== navigationSequence.current) return;
      setReportStoreOptions(nextCatalog.stores);
      const validStores = reportStores.filter((store) => nextCatalog.stores.includes(store));
      setReportStores(validStores);
      await loadStorePerformanceReport(reportStart, reportEnd, values, validMalls, validStores, reportBrandOptions.length ? reportBrandOptions : brandCatalog.brands);
    } catch (caught) {
      if (navigationRequest !== navigationSequence.current) return;
      setError(caught instanceof Error ? caught.message : "Rapor filtreleri güncellenemedi.");
      setLoading(false);
    }
  }

  async function changeReportMalls(values: string[]) {
    const navigationRequest = ++navigationSequence.current;
    navigationActiveReport.current = null;
    setReportMalls(values);
    setLoading(true);
    setError(null);
    try {
      const nextCatalog = await fetchFilterCatalog({ ...emptyFilters, brands: reportBrands, malls: values });
      if (navigationRequest !== navigationSequence.current) return;
      setReportStoreOptions(nextCatalog.stores);
      const validStores = reportStores.filter((store) => nextCatalog.stores.includes(store));
      setReportStores(validStores);
      await loadStorePerformanceReport(reportStart, reportEnd, reportBrands, values, validStores);
    } catch (caught) {
      if (navigationRequest !== navigationSequence.current) return;
      setError(caught instanceof Error ? caught.message : "Rapor filtreleri güncellenemedi.");
      setLoading(false);
    }
  }

  function changeReportStores(values: string[]) {
    setReportStores(values);
    void loadStorePerformanceReport(reportStart, reportEnd, reportBrands, reportMalls, values);
  }

  async function loadAdminData(nextSection: AdminSection = adminSection) {
    if (nextSection === "bot-report-history") {
      setAdminError(null);
      return;
    }
    setAdminLoading(true);
    setAdminError(null);
    try {
      const response = await fetch(`/api/v1/admin/${nextSection}${nextSection === "sessions" ? "?days=90" : ""}`);
      if (response.status === 401) setAuth(null);
      if (response.status === 403) throw new Error("Bu alan yalnızca yönetici hesabına açıktır.");
      if (!response.ok) throw new Error(`Yönetim servisi ${response.status} yanıtı verdi.`);
      if (nextSection === "health") {
        const nextHealth = (await response.json()) as AdminHealth;
        nextHealth.expected_data_date ||= expectedCompletedDataDate();
        const nightSummary = nextHealth.night_summary;
        if (nightSummary && nightSummary.sales_excluding_karshi_outlet_uzs === undefined && nightSummary.sales_date) {
          try {
            const params = reportParams(nightSummary.sales_date, nightSummary.sales_date, false, emptyFilters, lyComparison);
            const summaryResponse = await fetch(`/api/v1/executive/summary?${params}`);
            if (summaryResponse.ok) {
              const visibleSummary = (await summaryResponse.json()) as ExecutiveSummary;
              nightSummary.all_store_sales_uzs = nightSummary.net_sales_uzs;
              nightSummary.sales_excluding_karshi_outlet_uzs = visibleSummary.current.net_sales_uzs;
              nightSummary.avva_karshi_outlet_sales_uzs = Math.max(0, nightSummary.net_sales_uzs - visibleSummary.current.net_sales_uzs);
              nightSummary.receipts_excluding_karshi_outlet = visibleSummary.current.receipts;
              nightSummary.net_units_excluding_karshi_outlet = visibleSummary.current.net_units;
            }
          } catch {
            // The health screen still renders its verified package data if the compatibility lookup is unavailable.
          }
        }
        setAdminHealth(nextHealth);
      } else if (nextSection === "users") setAdminUsers((await response.json()) as AdminUsersResponse);
      else setAdminSessions((await response.json()) as AdminSessionsResponse);
    } catch (caught) {
      setAdminError(caught instanceof Error ? caught.message : "Yönetim bilgileri açılamadı.");
    } finally {
      setAdminLoading(false);
    }
  }

  async function saveAdminUser(draft: AdminUserDraft, editing: boolean) {
    setAdminLoading(true);
    setAdminError(null);
    try {
      const response = await fetch("/api/v1/admin/users", {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (response.status === 401) setAuth(null);
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(payload?.message || `Yönetim servisi ${response.status} yanıtı verdi.`);
      }
      await loadAdmin("users");
    } catch (caught) {
      setAdminError(caught instanceof Error ? caught.message : "Kullanıcı kaydedilemedi.");
      throw caught;
    } finally {
      setAdminLoading(false);
    }
  }

  function chooseAdminSection(nextSection: AdminSection) {
    setAdminSection(nextSection);
    if (nextSection === "bot-report-history") setAdminError(null);
    if ((nextSection === "health" && !adminHealth) || (nextSection === "users" && !adminUsers) || (nextSection === "sessions" && !adminSessions)) void loadAdmin(nextSection);
  }

  async function loadPageData(nextPage = page, nextStart = start, nextEnd = end, nextFullMonthTarget = fullMonthTarget, nextFilters = filters, productOverride?: { mode: ProductPeriodMode; selected: ProductDateRange; catalog?: FilterCatalog; colorStatus?: ProductStatusFilter }, navigationRequestKey?: string, catalogReady?: Promise<Filters>, nextLyComparison: LyComparison = lyComparison) {
    const requestId = ++reportSequence.current;
    let colorPage: InitialProductPage | undefined;
    setLoading(true);
    setError(null);
    try {
      const range = nextPage === "product"
        ? productAnalysisRange(productOverride?.mode ?? productPeriodMode, productOverride?.selected ?? productSelectedRange, productOverride?.catalog ?? catalog)
        : { start: nextStart, end: nextEnd };
      const params = reportParams(range.start, range.end, nextFullMonthTarget, nextFilters, nextLyComparison);
      const detailUrl = nextPage === "executive" ? "/api/v1/executive/tables" : nextPage === "store" ? "/api/v1/store/report" : "/api/v1/product/report";
      const detailParams = new URLSearchParams(params);
      if (nextPage === "product") detailParams.set("initial_only", "true");
      if (nextPage === "product") {
        const sort = gridSortRegistry.get(t("SKU + Renk Performansı")) ?? null;
        const status = productOverride?.colorStatus ?? productColorStatusFilter;
        const colorParams = new URLSearchParams(params);
        colorParams.set("table", "sku_colors"); colorParams.set("offset", "0"); colorParams.set("limit", "100");
        if (status !== "all") colorParams.set("status", status);
        if (sort) { colorParams.set("sort_by", sort.key); colorParams.set("sort_direction", sort.direction); }
        colorPage = productPagePrefetch.start(sort, status, async signal => {
          const response = await fetch(`/api/v1/product/table?${colorParams}`, { signal });
          if (response.status === 401) setAuth(null);
          if (!response.ok) throw new Error(t("Veri açılamadı."));
          return await response.json();
        });
      } else productPagePrefetch.cancel();
      const retainedTrend = nextPage === "store" && tables?.breakdown_scope === params.toString() ? tables : null;
      const [summaryResponse, detailResponse, storeTrendResponse, resolvedFilters] = await Promise.all([
        fetch(`/api/v1/executive/summary?${params}`),
        fetch(`${detailUrl}?${detailParams}`),
        nextPage === "store" && !retainedTrend ? fetch(`/api/v1/executive/tables?${params}&daily_only=true`) : Promise.resolve(null),
        catalogReady,
      ]);
      if (summaryResponse.status === 401 || detailResponse.status === 401 || storeTrendResponse?.status === 401) setAuth(null);
      if (!summaryResponse.ok || !detailResponse.ok || (storeTrendResponse && !storeTrendResponse.ok)) {
        const status = !summaryResponse.ok ? summaryResponse.status : !detailResponse.ok ? detailResponse.status : storeTrendResponse?.status;
        throw new Error(`Rapor servisi ${status} yanıtı verdi.`);
      }
      // A catalogue can change with a newly published release. Speculative
      // reads never commit if validation pruned another selected dimension.
      if (navigationActiveReport.current !== navigationRequestKey) return;
      if (resolvedFilters && reportParams(range.start, range.end, nextFullMonthTarget, resolvedFilters, nextLyComparison).toString() !== params.toString()) {
        setFilters(resolvedFilters);
        await loadPage(nextPage, nextStart, nextEnd, nextFullMonthTarget, resolvedFilters, productOverride, undefined, nextLyComparison);
        return;
      }
      const [nextSummary, nextDetail, fetchedTrend] = await Promise.all([
        summaryResponse.json() as Promise<ExecutiveSummary>,
        detailResponse.json(),
        storeTrendResponse ? storeTrendResponse.json() as Promise<ExecutiveTables> : Promise.resolve(null),
      ]);
      let nextTrend = fetchedTrend;
      if (retainedTrend) {
        if (canReuseStoreTrend(retainedTrend, params.toString(), nextSummary.release_id, nextSummary.applied_filters)) nextTrend = retainedTrend;
        else {
          const response = await fetch(`/api/v1/executive/tables?${params}&daily_only=true`);
          if (response.status === 401) setAuth(null);
          if (!response.ok) throw new Error(`Rapor servisi ${response.status} yanıtı verdi.`);
          nextTrend = await response.json() as ExecutiveTables;
        }
      }
      if (normalizeLyComparison(nextSummary.ly_comparison) !== nextLyComparison ||
          (nextPage !== "product" && normalizeLyComparison(nextDetail.ly_comparison) !== nextLyComparison) ||
          (nextTrend && normalizeLyComparison(nextTrend.ly_comparison) !== nextLyComparison) ||
          nextSummary.release_id !== nextDetail.release_id || (nextTrend && nextTrend.release_id !== nextSummary.release_id)) {
        throw new Error(t("Veri açılamadı."));
      }
      if (navigationActiveReport.current !== navigationRequestKey) { if (colorPage) productPagePrefetch.cancel(colorPage); return; }
      setSummary(nextSummary);
      if (nextPage === "executive") setTables({ ...nextDetail as ExecutiveTables, breakdown_scope: params.toString(), request_scope: JSON.stringify(nextSummary.applied_filters) });
      else if (nextPage === "store") {
        setStoreReport({ ...nextDetail as StoreReport, category_scope: params.toString() });
        if (nextTrend && nextTrend !== retainedTrend) setTables({ ...nextTrend, breakdown_scope: params.toString(), request_scope: JSON.stringify(nextSummary.applied_filters) });
      }
      else { setProductReport(nextDetail as ProductReport); setInitialColorPage(colorPage); }
    } catch (caught) {
      if (colorPage) productPagePrefetch.cancel(colorPage);
      if (navigationActiveReport.current === navigationRequestKey) setError(caught instanceof Error ? caught.message : "Rapor açılamadı.");
    } finally {
      if (navigationActiveReport.current === navigationRequestKey) setLoading(false);
    }
  }

  async function loadProductTablePage(table: "products" | "sku_colors", offset: number, limit: number, search: string, sort: GridSortState, statusFilter: ProductStatusFilter) {
    const params = reportParams(productRange.start, productRange.end, fullMonthTarget, filters, lyComparison);
    params.set("table", table);
    params.set("offset", String(offset));
    params.set("limit", String(limit));
    if (search) params.set("search", search);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (sort) {
      params.set("sort_by", sort.key);
      params.set("sort_direction", sort.direction);
    }
    const response = await fetch(`/api/v1/product/table?${params}`);
    if (response.status === 401) setAuth(null);
    if (!response.ok) throw new Error(`Ürün tablosu servisi ${response.status} yanıtı verdi.`);
    return (await response.json()) as ProductTablePage;
  }

  // The lower colour table uses the existing bounded, paginated query, not a
  // second full report. Its loading/search/status never blocks the main table.
  async function loadProductColorTablePage(offset: number, limit: number, search: string, sort: GridSortState, statusFilter: ProductStatusFilter, signal?: AbortSignal): Promise<ProductTablePage> {
    const params = reportParams(productRange.start, productRange.end, fullMonthTarget, filters, lyComparison);
    params.set("table", "sku_colors");
    params.set("offset", String(offset));
    params.set("limit", String(limit));
    if (search) params.set("search", search);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (sort) { params.set("sort_by", sort.key); params.set("sort_direction", sort.direction); }
    const response = await fetch(`/api/v1/product/table?${params}`, { signal });
    if (response.status === 401) setAuth(null);
    if (!response.ok) throw new Error(t("Veri açılamadı."));
    const result = await response.json() as ProductTablePage & { release_id: string };
    if (result.release_id !== productReport?.release_id) throw new Error(t("Veri paketi değişti. Sayfayı yenileyin."));
    return result;
  }

  async function loadProductVariantDetail(sku: string, signal: AbortSignal): Promise<ProductVariantDetail> {
    const params = reportParams(productRange.start, productRange.end, fullMonthTarget, { ...filters, skus: [sku] }, lyComparison);
    const response = await fetch(`/api/v1/product/detail?${params}`, { signal });
    if (response.status === 401) setAuth(null);
    if (!response.ok) throw new Error(t("Veri açılamadı."));
    return withProductImportDates((await response.json()) as ProductVariantDetail, params, signal, () => setAuth(null));
  }

  async function fetchFilterCatalogData(nextFilters: Filters, skuSearch = "") {
    const params = filterCatalogParams(nextFilters);
    if (skuSearch.trim().length >= 2) params.set("sku_search", skuSearch.trim());
    const response = await fetch(`/api/v1/executive/filters${params.size ? `?${params}` : ""}`);
    if (response.status === 401) {
      setAuth(null);
      throw new Error("Oturum süresi doldu.");
    }
    if (!response.ok) throw new Error("Filtre kataloğu alınamadı.");
    const result = (await response.json()) as FilterCatalog;
    return { ...result, genders: groupedGenderFilterOptions(result.genders) };
  }

  async function resolveFilterCatalog(requested: Filters, preserveKey?: FilterKey) {
    const sequence = ++filterCatalogSequence.current;
    let nextCatalog = await fetchFilterCatalog(requested);
    const resolved = { ...requested };
    let cleaned = false;
    for (const definition of filterDefinitions) {
      if (definition.key === preserveKey) continue;
      const available = new Set(nextCatalog[definition.key]);
      const valid = resolved[definition.key].filter((value) => available.has(value));
      if (valid.length !== resolved[definition.key].length) {
        resolved[definition.key] = valid;
        cleaned = true;
      }
    }
    if (cleaned) nextCatalog = await fetchFilterCatalog(resolved);
    if (sequence === filterCatalogSequence.current) setCatalog(nextCatalog);
    return resolved;
  }

  async function loadCatalogAndDashboard(user: AuthUser) {
    const baseCatalog = await fetchFilterCatalog(emptyFilters);
    navigationBaseCatalog.current = baseCatalog;
    const next = readNavigation(window.location, baseCatalog, periodRange("month", baseCatalog.min_date, baseCatalog.max_date), user);
    await restoreNavigation(next, baseCatalog);
  }

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([fetch("/api/v1/platform", { signal: controller.signal }).then((response) => response.json() as Promise<PlatformStatus>), fetch("/api/v1/auth/session", { signal: controller.signal })])
      .then(async ([status, sessionResponse]) => {
        setPlatform(status);
        if (sessionResponse.status === 401) {
          setAuth(null);
          setLoading(false);
          return;
        }
        if (!sessionResponse.ok) throw new Error("Oturum servisine ulaşılamadı.");
        const session = (await sessionResponse.json()) as AuthResponse;
        if (controller.signal.aborted) return;
        setAuth(session.user);
        await loadCatalogAndDashboard(session.user);
      })
      .catch((caught) => {
        if (!controller.signal.aborted) {
          const message = caught instanceof Error ? caught.message : "Bağlantı kurulamadı.";
          setAuth(null);
          setLoginError(message);
          setLoading(false);
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!auth) return;
    const heartbeat = () => {
      void fetch("/api/v1/auth/heartbeat", { method: "POST", keepalive: true }).then((response) => {
        if (response.status === 401) setAuth(null);
      }).catch(() => undefined);
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, 60000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") heartbeat();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [auth?.username]);

  useEffect(() => {
    const search = globalSkuSearch.trim();
    if (!auth || search.length < 2) {
      setSkuSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const params = filterCatalogParams(filters);
      params.set("sku_search", search);
      fetch(`/api/v1/executive/filters?${params}`, {
        signal: controller.signal,
      })
        .then((response) => (response.ok ? (response.json() as Promise<FilterCatalog>) : Promise.reject(new Error("SKU seçenekleri alınamadı."))))
        .then((result) => setSkuSuggestions(result.skus.filter((sku) => !filters.skus.includes(sku))))
        .catch((caught) => {
          if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "SKU seçenekleri alınamadı.");
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [auth, globalSkuSearch, filters]);

  async function login(username: string, pin: string, rememberMe: boolean) {
    setLoginBusy(true);
    setLoginError(null);
    try {
      const response = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, pin, remember_me: rememberMe }),
      });
      if (!response.ok) {
        setLoginError(response.status === 401 ? "Kullanıcı adı veya PIN hatalı." : "Giriş servisine şu anda ulaşılamıyor.");
        return;
      }
      const result = (await response.json()) as AuthResponse;
      setAuth(result.user);
      setError(null);
      await loadCatalogAndDashboard(result.user);
    } catch {
      setLoginError("Giriş servisine şu anda ulaşılamıyor.");
    } finally {
      setLoginBusy(false);
    }
  }

  async function logout() {
    setNavigationReady(false); ++navigationSequence.current;
    window.history.replaceState(null, "", "/yonetici-ozeti");
    await fetch("/api/v1/auth/logout", { method: "POST" }).catch(() => undefined);
    setAuth(null);
    setCatalog(null);
    setSummary(null);
    setTables(null);
    setStoreReport(null);
    setProductReport(null);
    setShowroomReport(null);
    setStorePerformanceReport(null);
    setPrivateSection("reports");
    setStorePerformanceView("current");
    setReportBrands([]);
    setReportMalls([]);
    setReportStores([]);
    setReportBrandOptions([]);
    setReportMallOptions([]);
    setReportStoreOptions([]);
    setShowroomBrandChoices([]);
    setShowroomSelectedBrands([]);
    setShowroomOpen(false);
    setAdminHealth(null);
    setAdminUsers(null);
    setAdminSessions(null);
    setAdminError(null);
    setAdminSection("health");
    setFilters(emptyFilters);
    setProductSkuDraft("");
    setGlobalSkuSearch("");
    setSkuSuggestions([]);
    setLoginError(null);
  }

  function choosePeriod(nextPeriod: PeriodId) {
    if (page === "product") {
      setProductSelectedPeriod(nextPeriod);
      setProductPeriodMode("selected");
      if (!catalog || nextPeriod === "custom") return;
      const range = periodRange(nextPeriod, catalog.min_date, catalog.max_date);
      setProductSelectedRange(range);
      void loadPage(page, start, end, fullMonthTarget, filters, { mode: "selected", selected: range });
      return;
    }
    setPeriod(nextPeriod);
    if (!catalog || nextPeriod === "custom") return;
    const range = periodRange(nextPeriod, catalog.min_date, catalog.max_date);
    setStart(range.start);
    setEnd(range.end);
    void loadPage(page, range.start, range.end, fullMonthTarget, filters);
  }
  function applyMobilePeriod(nextPeriod: PeriodId, nextStart: string, nextEnd: string) {
    if (nextPeriod !== "custom") {
      choosePeriod(nextPeriod);
      return;
    }
    if (page === "product") {
      const range = { start: nextStart, end: nextEnd };
      setProductSelectedPeriod("custom");
      setProductSelectedRange(range);
      setProductPeriodMode("selected");
      void loadPage(page, start, end, fullMonthTarget, filters, { mode: "selected", selected: range });
      return;
    }
    setPeriod("custom");
    setStart(nextStart);
    setEnd(nextEnd);
    void loadPage(page, nextStart, nextEnd, fullMonthTarget, filters);
  }
  function chooseShowroomPeriod(nextPeriod: ShowroomPeriodId) {
    setShowroomPeriod(nextPeriod);
    setShowroomMonthAnchor(null);
    if (!catalog || nextPeriod === "custom" || nextPeriod === "monthRange") return;
    const range = showroomPeriodRange(nextPeriod, catalog.min_date, catalog.max_date);
    setShowroomStart(range.start);
    setShowroomEnd(range.end);
    if (showroomOpen) void loadShowroom(range.start, range.end);
  }
  function chooseShowroomMonth(month: string) {
    if (!catalog) return;
    if (!showroomMonthAnchor) {
      setShowroomMonthAnchor(month);
      return;
    }
    const [first, last] = [showroomMonthAnchor, month].sort();
    const nextStart = `${first}-01` < catalog.min_date ? catalog.min_date : `${first}-01`;
    const nextEnd = monthEnd(last) > catalog.max_date ? catalog.max_date : monthEnd(last);
    setShowroomMonthAnchor(null);
    setShowroomPeriod("monthRange");
    setShowroomStart(nextStart);
    setShowroomEnd(nextEnd);
    if (showroomOpen) void loadShowroom(nextStart, nextEnd);
  }
  async function changeFilter(key: FilterKey, values: string[]) {
    const sequence = ++filterChangeSequence.current;
    const requested = { ...filters, [key]: values };
    setFilters(requested);
    setLoading(true);
    setError(null);
    try {
      if (canOverlapFilterCatalog(requested, key, catalog?.[key])) {
        const catalogReady = resolveFilterCatalog(requested, key);
        await loadPage(page, start, end, fullMonthTarget, requested, undefined, catalogReady);
      } else {
        const resolved = await resolveFilterCatalog(requested, key);
        if (sequence !== filterChangeSequence.current) return;
        setFilters(resolved);
        await loadPage(page, start, end, fullMonthTarget, resolved);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Filtreler güncellenemedi.");
      setLoading(false);
    }
  }
  async function clearFilters() {
    setFilters(emptyFilters);
    setProductSkuDraft("");
    setGlobalSkuSearch("");
    setSkuSuggestions([]);
    setLoading(true);
    setError(null);
    try {
      await resolveFilterCatalog(emptyFilters);
      await loadPage(page, start, end, fullMonthTarget, emptyFilters);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Filtreler temizlenemedi.");
      setLoading(false);
    }
  }
  async function addGlobalSkus(values: string[]) {
    const additions = values.map((value) => value.trim()).filter(Boolean);
    const skus = [...new Set([...filters.skus, ...additions])].sort((a, b) => a.localeCompare(b, "tr"));
    if (skus.length === filters.skus.length && skus.every((sku, index) => sku === filters.skus[index])) return;
    const requested = { ...filters, skus };
    setFilters(requested);
    setProductSkuDraft(requested.skus.join("\n"));
    setGlobalSkuSearch("");
    setSkuSuggestions([]);
    setLoading(true);
    setError(null);
    try {
      const resolved = await resolveFilterCatalog(requested);
      setFilters(resolved);
      await loadPage(page, start, end, fullMonthTarget, resolved);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "SKU filtresi uygulanamadı.");
      setLoading(false);
    }
  }
  async function addGlobalSku(value: string) {
    await addGlobalSkus([value]);
  }
  async function removeActiveFilter(key: keyof Filters, value?: string) {
    const values = value === undefined ? [] : filters[key].filter((item) => item !== value);
    if (key !== "skus") {
      await changeFilter(key, values);
      return;
    }
    const requested = { ...filters, skus: values };
    setProductSkuDraft(values.join("\n"));
    setFilters(requested);
    setLoading(true);
    setError(null);
    try {
      const resolved = await resolveFilterCatalog(requested);
      setFilters(resolved);
      await loadPage(page, start, end, fullMonthTarget, resolved);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "SKU filtresi kaldırılamadı.");
      setLoading(false);
    }
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void loadPage();
  }
  function submitShowroom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setShowroomMonthAnchor(null);
    if (showroomOpen) void loadShowroom(showroomStart, showroomEnd);
  }
  function changePage(nextPage: PageId) {
    if (!navigationReady || nextPage === page) return;
    ++navigationSequence.current; navigationRestoring.current = false;
    navigationActiveReport.current = null;
    window.dispatchEvent(new Event("srr360:close-popovers"));
    setMobileFiltersOpen(false);
    setPage(nextPage);
    resetReportScroll(nextPage);
    if (nextPage === "product") {
      const selected = { start, end };
      setProductPeriodMode("lifecycle");
      setProductSelectedPeriod(period);
      setProductSelectedRange(selected);
      void loadPage(nextPage, start, end, fullMonthTarget, filters, { mode: "lifecycle", selected });
      return;
    }
    if (nextPage === "showroom") {
      setLoading(false);
      setError(null);
      if (privateSection === "reports" && reportKind === "store" && !storePerformanceReport) void loadStorePerformanceReport();
      return;
    }
    if (nextPage === "admin") {
      setLoading(false);
      setError(null);
      void loadAdmin(adminSection);
      return;
    }
    void loadPage(nextPage);
  }
  function changePrivateSection(nextSection: PrivateWorkspaceSection) {
    if (nextSection === "showroom" && isCompactViewport) return;
    setPrivateSection(nextSection);
    setError(null);
    if (nextSection === "reports" && reportKind === "store" && !storePerformanceReport) void loadStorePerformanceReport();
  }
  function openReportsWorkspace() {
    const needsExplicitLoad = page === "showroom" || privateSection !== "reports";
    setPrivateSection("reports");
    setError(null);
    if (page !== "showroom") changePage("showroom");
    if (needsExplicitLoad && reportKind === "store" && !storePerformanceReport) void loadStorePerformanceReport();
  }
  function toggleShowroomBrand(brand: string) {
    setShowroomBrandChoices((current) => (current.includes(brand) ? current.filter((value) => value !== brand) : [...current, brand]));
  }
  function openShowroom() {
    if (!showroomBrandChoices.length) return;
    setShowroomSelectedBrands(showroomBrandChoices);
    setShowroomOpen(true);
    void loadShowroom(showroomStart, showroomEnd, showroomBrandChoices);
  }
  function changeShowroomBrands() {
    setShowroomOpen(false);
    setShowroomReport(null);
    setShowroomBrandChoices(showroomSelectedBrands);
    setError(null);
  }
  async function applyProductSkus() {
    const skus = parseProductSkuList(productSkuDraft);
    const requested = { ...filters, skus: Array.from(new Set([...filters.skus, ...skus])).sort((a, b) => a.localeCompare(b, "tr")) };
    setFilters(requested);
    setLoading(true);
    setError(null);
    try {
      const resolved = await resolveFilterCatalog(requested);
      setFilters(resolved);
      await loadPage("product", start, end, fullMonthTarget, resolved);
      setProductSkuDraft("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "SKU filtresi uygulanamadı.");
      setLoading(false);
    }
  }
  async function exportStoreReport() {
    if (!storeReport || !summary || exporting) return;
    setExporting(true);
    setError(null);
    try {
      await downloadStoreWorkbook(storeReport, summary, filters, language);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Excel dosyası hazırlanamadı.");
    } finally {
      setExporting(false);
    }
  }
  async function exportExecutiveReport() {
    if (!tables || !summary || exporting) return;
    setExporting(true);
    setError(null);
    try {
      await downloadExecutiveWorkbook(summary, tables, filters, language);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Excel dosyası hazırlanamadı.");
    } finally {
      setExporting(false);
    }
  }
  async function exportProductReport() {
    if (productExportDisabled || !productReport) return;
    setExporting(true);
    setError(null);
    try {
      const params = reportParams(productRange.start, productRange.end, fullMonthTarget, filters, lyComparison);
      params.set("export_all", "true");
      const productSearch = (gridSearchRegistry.get(t("Ürün Performansı")) ?? "").trim();
      const colorSearch = (gridSearchRegistry.get(t("SKU + Renk Performansı")) ?? "").trim();
      params.set("product_search", productSearch);
      params.set("product_status", productStatusFilter);
      const response = await fetch(`/api/v1/product/report?${params}`);
      if (!response.ok) throw new Error(`Excel veri servisi ${response.status} yanıtı verdi.`);
      let report = await response.json() as ProductReport;
      if (report.release_id !== productReport.release_id) throw new Error(t("Veri paketi değişti. Sayfayı yenileyin."));
      if (colorSearch || productColorStatusFilter !== "all") {
        const colors = await collectExportPages((offset, limit) => loadProductColorTablePage(offset, limit, colorSearch, gridSortRegistry.get(t("SKU + Renk Performansı")) ?? null, productColorStatusFilter));
        report = { ...report, sku_colors: colors };
      } else if (productSearch || productStatusFilter !== "all") {
        // Keep the independently unfiltered colour table complete. This cached
        // read occurs only on export, never on initial page/detail loading.
        const colorParams = new URLSearchParams(params);
        colorParams.set("product_search", ""); colorParams.set("product_status", "all");
        const colorsResponse = await fetch(`/api/v1/product/report?${colorParams}`);
        if (!colorsResponse.ok) throw new Error(`Excel veri servisi ${colorsResponse.status} yanıtı verdi.`);
        const colors = await colorsResponse.json() as ProductReport;
        if (colors.release_id !== report.release_id) throw new Error(t("Veri paketi değişti. Sayfayı yenileyin."));
        report = { ...report, sku_colors: colors.sku_colors };
      }
      // A single scoped read supplies all size identities. Never fetch one request per SKU,
      // distribute summary quantities across sizes, or fall back to an unfiltered workbook.
      let variants: import("./productAnalysisExcel").ProductExcelVariants = { release_id: report.release_id, period: report.period, variants: [], complete: true };
      if (report.sku_colors.length) {
        const variantParams = reportParams(report.period.start, report.period.end, fullMonthTarget, filters, lyComparison);
        const variantResponse = await fetch(`/api/v1/product/export-variants?${variantParams}`);
        if (!variantResponse.ok) throw new Error(`Excel beden veri servisi ${variantResponse.status} yanıtı verdi.`);
        variants = await variantResponse.json();
      }
      await downloadProductWorkbook(report, filters, language, { productSearch, productStatus: productStatusFilter, colorSearch, colorStatus: productColorStatusFilter }, variants);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Excel dosyası hazırlanamadı.");
    } finally {
      setExporting(false);
    }
  }
  async function exportStorePerformanceReports(sorts: StorePerformanceSorts) {
    if (!storePerformanceReport || exporting) return;
    setExporting(true);
    setError(null);
    try {
      await downloadStorePerformanceWorkbook(storePerformanceReport, reportBrands, reportMalls, reportStores, language, sorts);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Excel dosyası hazırlanamadı.");
    } finally {
      setExporting(false);
    }
  }
  const activeFilterCount = Object.values(filters).reduce((count, values) => count + values.length, 0);
  const activeFilterGroups = [...filterDefinitions, { key: "skus" as const, label: "SKU", icon: "#" }].filter((definition) => filters[definition.key].length);
  const showroomDesktopOnly = page === "showroom" && privateSection === "showroom" && !isCompactViewport;
  const useExecutiveLayout = page === "executive" || page === "store";
  const useCompactPageLayout = useExecutiveLayout
    || page === "product"
    || (page === "showroom" && (privateSection === "reports" || isWideDesktop));
  const pageTitle = page === "executive"
    ? "Yönetici Özeti"
    : page === "store"
      ? "Mağaza Analizi"
      : page === "product"
        ? "Ürün Analizi"
        : page === "showroom"
          ? privateSection === "reports"
            ? "Raporlar"
            : "Showroom Ürün Planlama"
          : "Yönetim";
  useBrowserTitle(Boolean(auth), t(pageTitle));
  const pageEyebrow = page === "executive"
    ? "SRR360 • SILK ROAD RETAIL"
    : page === "store"
      ? "SRR360 • MAĞAZA PERFORMANSI"
      : page === "product"
        ? "SRR360 • ÜRÜN PERFORMANSI"
        : page === "showroom"
          ? isCompactViewport ? "SRR360 • RAPORLAR" : "Showroom ve Raporlar"
          : "";
  const pageDescription = page === "executive" ? "Satış, hedef, stok ve ziyaretçi performansının tek görünümü." : page === "store" ? "Seçili mağazaların satış, müşteri ve danışman performansı." : page === "product" ? "SKU, renk ve beden bazında satış, stok ve ürün yaşam döngüsü." : page === "showroom" ? (privateSection === "reports" ? "Mağaza performansı, geçen yıl karşılaştırması ve yönetici özeti." : "Ürün, renk, beden ve aylık adet inceleme modeli.") : "Sistem sağlığı ve kullanıcı erişimlerinin güvenli yönetimi.";
  const visibleStart = page === "product" ? productRange.start : page === "showroom" ? (privateSection === "reports" ? reportStart : showroomStart) : start;
  const visibleEnd = page === "product" ? productRange.end : page === "showroom" ? (privateSection === "reports" ? reportEnd : showroomEnd) : end;
  const displayedPeriod = page === "product" && productPeriodMode === "lifecycle" ? "İlk importtan bugüne" : periods.find((choice) => choice.id === activePeriod)?.label ?? "Seçili dönem";
  function updateSelectedDate(key: "start" | "end", value: string) {
    if (page === "product") setProductSelectedRange((current) => ({ ...current, [key]: value }));
    else if (key === "start") setStart(value);
    else setEnd(value);
  }
  function chooseLyComparison(mode: LyComparison) {
    if (loading || mode === lyComparison) return;
    setLyComparison(mode);
    void loadPage(page, start, end, fullMonthTarget, filters, undefined, undefined, mode);
  }
  function chooseProductMode(mode: ProductPeriodMode) {
    setProductPeriodMode(mode);
    void loadPage("product", start, end, fullMonthTarget, filters, { mode, selected: productSelectedRange });
  }
  const showroomMonths = catalog ? monthKeysBetween(catalog.min_date, catalog.max_date) : [];
  const dailyTrendRows = useExecutiveLayout ? tables?.daily ?? [] : [];
  const dailyTrends = {
    sales: dailyTrendRows.map((row) => ({ label: row.label, value: row.net_sales_uzs })),
    upt: dailyTrendRows.map((row) => ({ label: row.label, value: row.upt })),
    conversion: dailyTrendRows.flatMap((row) => row.conversion_rate_percent == null ? [] : [{ label: row.label, value: row.conversion_rate_percent }]),
    units: dailyTrendRows.map((row) => ({ label: row.label, value: row.net_units })),
    receipts: dailyTrendRows.map((row) => ({ label: row.label, value: row.receipts })),
    basket: dailyTrendRows.map((row) => ({ label: row.label, value: row.average_basket_uzs })),
    price: dailyTrendRows.map((row) => ({ label: row.label, value: row.average_unit_price_uzs })),
  };
  const mobileSelectionLabel = (values: string[]) => values.length === 0
    ? t("Tümü")
    : values.length === 1
      ? values[0]
      : `${values.length} ${t("seçili")}`;
  const scrollToMobileExecutiveSection = (section: "overview" | "tables") => {
    setMobileExecutiveSection(section);
    document.getElementById(`executive-mobile-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (auth === undefined)
    return (
      <main className="session-loading">
        <span />
        {t("SRR360 güvenli oturumu kontrol ediliyor…")}
      </main>
    );
  if (auth === null) return <LoginScreen busy={loginBusy} error={loginError} language={language} onLanguage={setLanguage} onLogin={login} />;
  const userInitials =
    auth.display_name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((value) => value[0])
      .join("")
      .toUpperCase() || auth.username.slice(0, 2).toUpperCase();
  const shellPageClass = page === "showroom" && privateSection === "reports" ? "page-reports" : `page-${page}`;
  return (
    <LanguageContext.Provider value={language}>
      <div className={`app-shell desktop-workspace ${shellPageClass}${page === "store" ? " page-executive store-executive-layout" : ""}${page === "product" ? " page-executive product-executive-layout" : ""}${page === "showroom" && privateSection === "reports" ? " page-executive report-executive-layout" : ""}${page === "showroom" && privateSection === "showroom" && isWideDesktop ? " page-executive showroom-executive-layout" : ""}${page === "showroom" ? ` private-section-${privateSection}` : ""}${showroomDesktopOnly ? " showroom-desktop-only" : " mobile-ready"}${auth.demo_mode ? " demo-mode" : ""}`}>
        <aside className={`sidebar${mobileFiltersOpen ? " is-mobile-open" : ""}`} aria-label={t("Gezinme ve filtreler")}>
          {page !== "showroom" ? (
            <div className="mobile-drawer-heading">
              <div>
                <SlidersHorizontal aria-hidden="true" size={19} strokeWidth={2.2} />
                <span>
                  <strong>{t("Filtreler")}</strong>
                  <small>{activeFilterCount ? `${activeFilterCount} ${t("seçili")}` : t("Tüm veriler")}</small>
                </span>
              </div>
              <button type="button" aria-label={t("Kapat")} onClick={() => setMobileFiltersOpen(false)}>
                <X aria-hidden="true" size={20} strokeWidth={2.2} />
              </button>
            </div>
          ) : null}
          <div className="sidebar-brand">
            <span className="sidebar-brand__mark"><img src="/identity-20261104/system-lockup.png" alt="SRR360" width="108" height="36" /></span>
            <small>Silk Road Retail</small>
          </div>
          <div className="user-card">
            <span className="avatar">{userInitials}</span>
            <span>
              <strong>{auth.display_name || auth.username}</strong>
              <small>
                {t("Yetki")}: {auth.role === "admin" || !auth.allowed_brands.length ? t("Tüm markalar") : auth.allowed_brands.join(", ")}
              </small>
              {auth.demo_mode ? <em className="demo-account-badge">{t("DEMO VERİSİ")}</em> : null}
            </span>
            <div className="sidebar-profile-actions">
            {auth.role === "admin" ? (
              <button type="button" className={page === "admin" ? "active" : ""} aria-label={t("Yönetim")} title={`${t("Sistem Sağlığı")} • ${t("Kullanıcı Yönetimi")} • ${t("Kullanıcı Oturumları")}`} onClick={() => changePage("admin")}>
                ⚙
              </button>
            ) : null}
            <button className="sidebar-profile-logout" type="button" aria-label={t("Çıkış yap")} title={t("Çıkış yap")} onClick={() => void logout()}>
              <LogOut aria-hidden="true" size={16} />
            </button>
            </div>
          </div>
          <button className="logout" type="button" onClick={() => void logout()}>
            ↪ {t("Çıkış yap")}
          </button>
          <div className="sidebar-title">▦ &nbsp; {t("SAYFALAR")}</div>
          <nav className="page-nav" aria-label={t("Sayfalar")}>
            <button className={page === "executive" ? "active" : ""} onClick={() => changePage("executive")}>
              <span>{page === "executive" ? "●" : "○"}</span> {t("Yönetici özeti")}
            </button>
            <button className={page === "store" ? "active" : ""} onClick={() => changePage("store")}>
              <span>{page === "store" ? "●" : "○"}</span> {t("Mağaza analizi")}
            </button>
            <button className={page === "product" ? "active" : ""} onClick={() => changePage("product")}>
              <span>{page === "product" ? "●" : "○"}</span> {t("Ürün analizi")}
            </button>
            {auth.showroom_access ? (
              <button className={page === "showroom" ? "active" : ""} onClick={isCompactViewport ? openReportsWorkspace : () => changePage("showroom")}>
                <span>{page === "showroom" ? "●" : "○"}</span> {t(isCompactViewport ? "Raporlar" : "Showroom ve Raporlar")}
              </button>
            ) : null}
          </nav>
          {page !== "showroom" && page !== "admin" ? (
            <>
              <div className="sidebar-title filters-title">
                ☷ &nbsp; {t("FİLTRELER")} {activeFilterCount ? `(${activeFilterCount})` : ""}
              </div>
              <div className="sidebar-filter sidebar-period-filter">
                <span>▦ &nbsp; {t("Dönem")}</span>
                <select value={page === "product" && productPeriodMode === "lifecycle" ? "lifecycle" : activePeriod} disabled={loading} onChange={(event) => event.target.value === "lifecycle" ? chooseProductMode("lifecycle") : choosePeriod(event.target.value as PeriodId)}>
                  {page === "product" ? <option value="lifecycle">{t("İlk importtan bugüne")}</option> : null}
                  {periods.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {t(choice.label)}
                    </option>
                  ))}
                </select>
                {activePeriod === "custom" && (page !== "product" || productPeriodMode === "selected") ? (
                  <details className="sidebar-date-popover">
                    <summary>
                      <span aria-hidden="true">▣</span>
                      <b>{displayDate(activeSelectedRange.start)} – {displayDate(activeSelectedRange.end)}</b>
                      <i aria-hidden="true">⌄</i>
                    </summary>
                    <form onSubmit={submit}>
                      <label>
                        {t("Başlangıç")}
                        <input type="date" value={activeSelectedRange.start} min={catalog?.min_date} max={activeSelectedRange.end} onChange={(event) => updateSelectedDate("start", event.target.value)} />
                      </label>
                      <label>
                        {t("Bitiş")}
                        <input type="date" value={activeSelectedRange.end} min={activeSelectedRange.start} max={catalog?.max_date} onChange={(event) => updateSelectedDate("end", event.target.value)} />
                      </label>
                      <button type="submit" disabled={loading}>
                        {loading ? t("Yükleniyor…") : `✓ ${t("Uygula")}`}
                      </button>
                    </form>
                  </details>
                ) : null}
              </div>
              <button className="clear-filters" type="button" disabled={!activeFilterCount || loading} onClick={() => void clearFilters()}>
                {t("TÜM FİLTRELERİ TEMİZLE")}
              </button>
              <div className="filter-list">
                {filterDefinitions.map((definition) => (
                  <SidebarMultiFilter key={definition.key} definition={definition} values={filters[definition.key]} options={catalog?.[definition.key] ?? []} disabled={!catalog || loading} onChange={(values) => void changeFilter(definition.key, values)} />
                ))}
                <SidebarSkuFilter
                  values={filters.skus}
                  query={globalSkuSearch}
                  suggestions={skuSuggestions}
                  disabled={!catalog || loading}
                  onQuery={setGlobalSkuSearch}
                  onAdd={addGlobalSku}
                  onAddMany={addGlobalSkus}
                  onRemove={(sku) => removeActiveFilter("skus", sku)}
                  onClear={() => removeActiveFilter("skus")}
                />
              </div>
            </>
          ) : null}
        </aside>

        {page === "product" ? <nav className="product-nav-rail" aria-label={t("Sayfalar")}>
          <strong>SRR</strong>
          <button type="button" aria-label={t("Yönetici özeti")} title={t("Yönetici özeti")} onClick={() => changePage("executive")}><LayoutDashboard size={19} /></button>
          <button type="button" aria-label={t("Mağaza analizi")} title={t("Mağaza analizi")} onClick={() => changePage("store")}><Store size={19} /></button>
          <button type="button" aria-label={t("Ürün analizi")} title={t("Ürün analizi")} aria-current="page" onClick={() => changePage("product")}><PackageSearch size={19} /></button>
          {auth.showroom_access ? <button type="button" aria-label={t("Raporlar")} title={t("Raporlar")} onClick={openReportsWorkspace}><FileSpreadsheet size={19} /></button> : null}
          <button type="button" aria-label={t("Filtreleri aç")} title={t("Filtreler")} onClick={() => setMobileFiltersOpen(true)}><SlidersHorizontal size={19} /></button>
          {auth.role === "admin" ? <button type="button" aria-label={t("Yönetim")} title={t("Yönetim")} onClick={() => changePage("admin")}><Settings size={19} /></button> : null}
        </nav> : null}

        {page !== "showroom" ? <button type="button" className={`mobile-sidebar-backdrop${mobileFiltersOpen ? " is-visible" : ""}`} aria-label={t("Filtreleri kapat")} onClick={() => setMobileFiltersOpen(false)} /> : null}

        <main className="main-area">
          <header className="app-header">
            {showroomDesktopOnly ? (
              <>
                <div>
                  <strong>Silk Road Retail</strong>
                  <span>{t("Yönetim Platformu")}</span>
                </div>
                <select value={language} onChange={(event) => setLanguage(event.target.value as LanguageCode)} aria-label={t("Dil")}>
                  {(Object.keys(languageLabels) as LanguageCode[]).map((code) => (
                    <option key={code} value={code}>{languageLabels[code]}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <div className="app-header__identity">
                  <span className="app-header__mark">SRR<span>360</span></span>
                  <span className="app-header__copy">
                    <strong>Silk Road Retail</strong>
                    <small>{t(pageTitle)}</small>
                  </span>
                  {page === "executive" || page === "store" || page === "product" || (page === "showroom" && privateSection === "reports") ? (
                    <span className="app-header__executive-title">
                      <strong>{t(pageTitle)}</strong>
                      <small>{displayDate(catalog?.max_date)}</small>
                    </span>
                  ) : null}
                </div>
                <div className="app-header__actions">
                  {page !== "admin" && page !== "showroom" ? (
                    <button type="button" className="mobile-header-button mobile-filter-button" aria-label={t("Filtreleri aç")} aria-expanded={mobileFiltersOpen} onClick={() => setMobileFiltersOpen(true)}>
                      <SlidersHorizontal aria-hidden="true" size={19} strokeWidth={2.1} />
                      {activeFilterCount ? <b>{activeFilterCount}</b> : null}
                    </button>
                  ) : null}
                  <Popover.Root>
                    <Popover.Trigger asChild>
                      <button type="button" className="mobile-header-button mobile-language-button" aria-label={t("Dil")}>
                        <Languages aria-hidden="true" size={19} strokeWidth={2.1} />
                      </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content className="mobile-language-popover" side="bottom" align="end" sideOffset={7} collisionPadding={10}>
                        {(Object.keys(languageLabels) as LanguageCode[]).map((code) => (
                          <Popover.Close asChild key={code}>
                            <button type="button" className={language === code ? "is-selected" : ""} aria-pressed={language === code} onClick={() => setLanguage(code)}>
                              <span>{code.toUpperCase()}</span>
                              {language === code ? <Check aria-hidden="true" size={14} /> : null}
                            </button>
                          </Popover.Close>
                        ))}
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>
                  {auth.role === "admin" ? (
                    <button type="button" className={`mobile-header-button mobile-admin-button${page === "admin" ? " is-active" : ""}`} aria-label={t("Yönetim")} onClick={() => changePage("admin")}>
                      <Settings aria-hidden="true" size={19} strokeWidth={2.1} />
                    </button>
                  ) : null}
                  <button type="button" className="mobile-header-button" aria-label={t("Çıkış yap")} onClick={() => void logout()}>
                    <LogOut aria-hidden="true" size={19} strokeWidth={2.1} />
                  </button>
                  <select value={language} onChange={(event) => setLanguage(event.target.value as LanguageCode)} aria-label={t("Dil")}>
                    {(Object.keys(languageLabels) as LanguageCode[]).map((code) => (
                      <option key={code} value={code}>{languageLabels[code]}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </header>
          <div className="page-content">
            <section className={`page-hero${useCompactPageLayout ? " page-hero--executive" : " page-hero--simple"}`}>
              <div>
                {pageEyebrow ? <p className="eyebrow">{t(pageEyebrow)}</p> : null}
                <h1>{t(pageTitle)}</h1>
                {useCompactPageLayout ? null : <p>{`${t("Veri son tarihi")}: ${displayDate(catalog?.max_date)} • ${t("Seçili dönem")}: ${displayDate(visibleStart)} – ${displayDate(visibleEnd)}`}</p>}
              </div>
              {useCompactPageLayout ? (
                <div className="executive-hero-tools">
                  <div className="executive-hero-date">
                    <span>{t("Veri son tarihi")}</span>
                    <strong>{displayDate(catalog?.max_date)}</strong>
                  </div>
                  <select className="executive-hero-language" value={language} onChange={(event) => setLanguage(event.target.value as LanguageCode)} aria-label={t("Dil")}>
                    {(Object.keys(languageLabels) as LanguageCode[]).map((code) => (
                      <option key={code} value={code}>{languageLabels[code]}</option>
                    ))}
                  </select>
                </div>
              ) : null}
            </section>
            {auth.demo_mode ? (
              <section className="demo-safety-banner" aria-label={t("Tanıtım hesabı")}>
                <span className="demo-safety-banner__icon"><Database aria-hidden="true" size={18} strokeWidth={2.1} /></span>
                <span>
                  <strong>{t("Tanıtım hesabı")}</strong>
                  <small>{t("Bu oturum yalnızca sentetik veriler kullanır; gerçek şirket bilgileriyle bağlantısı yoktur.")}</small>
                </span>
                <b>{t("İzole demo verisi")}</b>
              </section>
            ) : null}
            {page === "showroom" && !isCompactViewport ? (
              <nav className="private-workspace-tabs" aria-label={t("Özel çalışma alanı")}>
                <button type="button" className={`private-workspace-tab private-workspace-tab--reports${privateSection === "reports" ? " active" : ""}`} onClick={() => changePrivateSection("reports")}><FileSpreadsheet aria-hidden="true" size={16} /> {t("Raporlar")}</button>
                <button type="button" className={`private-workspace-tab private-workspace-tab--showroom${privateSection === "showroom" ? " active" : ""}`} onClick={() => changePrivateSection("showroom")}><Boxes aria-hidden="true" size={16} />{t("Showroom")}</button>
              </nav>
            ) : null}
            {page !== "showroom" && page !== "admin" && page !== "product" && !useExecutiveLayout ? (
              <section className="period-summary" aria-label={t("Seçili dönem özeti")}>
                <span className="period-summary__tag">{t(displayedPeriod)}</span>
                <span>
                  {displayDate(visibleStart)} – {displayDate(visibleEnd)}
                </span>
                <span>
                  <b>{t("Geçen yıl")} · {lyComparisonLabel(summary?.ly_comparison ?? lyComparison, language)}:</b> {summary ? `${displayDate(summary.previous_year_period.start)} – ${displayDate(summary.previous_year_period.end)}` : "—"}
                </span>
                <span className="period-summary__filtered" aria-hidden="true">
                  <b>{t("Filtrelenen satır")}:</b> {integer.format(summary?.filtered_rows ?? 0)}
                </span>
              </section>
            ) : null}
            {page === "executive" || page === "store" || page === "product" ? (
              <section className={`executive-mobile-quick-filters${useExecutiveLayout ? " compact-period-quick-filters" : ""}`} aria-label={t("Hızlı filtreler")}>
                {!useExecutiveLayout ? <ExecutiveMobilePeriodFilter
                  open={mobileQuickFilter === "period"}
                  onOpenChange={(open) => setMobileQuickFilter(open ? "period" : null)}
                  value={t(displayedPeriod)}
                  period={activePeriod}
                  start={activeSelectedRange.start}
                  end={activeSelectedRange.end}
                  minDate={catalog?.min_date}
                  maxDate={catalog?.max_date}
                  disabled={!catalog || loading}
                  onApply={applyMobilePeriod}
                /> : null}
                <ExecutiveMobileMultiFilter
                  filterId="brands"
                  open={mobileQuickFilter === "brands"}
                  onOpenChange={(open) => setMobileQuickFilter(open ? "brands" : null)}
                  icon={<Tag aria-hidden="true" size={16} />}
                  label={t("Marka")}
                  summary={mobileSelectionLabel(filters.brands)}
                  values={filters.brands}
                  options={catalog?.brands ?? []}
                  disabled={!catalog || loading}
                  context={t("Marka seçimi AVM ve mağaza seçeneklerini daraltır.")}
                  onApply={(values) => void changeFilter("brands", values)}
                />
                <ExecutiveMobileMultiFilter
                  filterId="malls"
                  open={mobileQuickFilter === "malls"}
                  onOpenChange={(open) => setMobileQuickFilter(open ? "malls" : null)}
                  icon={<BuildingComplex aria-hidden="true" size={16} />}
                  label={t("AVM")}
                  summary={mobileSelectionLabel(filters.malls)}
                  values={filters.malls}
                  options={catalog?.malls ?? []}
                  disabled={!catalog || loading}
                  context={filters.brands.length ? t("Seçenekler seçili markaya göre gösteriliyor.") : t("Marka seçerseniz AVM listesi daralır.")}
                  onApply={(values) => void changeFilter("malls", values)}
                />
                <div className="executive-mobile-filter-cluster">
                  <ExecutiveMobileMultiFilter
                    filterId="stores"
                    open={mobileQuickFilter === "stores"}
                    onOpenChange={(open) => setMobileQuickFilter(open ? "stores" : null)}
                    icon={<Store aria-hidden="true" size={16} />}
                    label={t("Mağaza")}
                    summary={mobileSelectionLabel(filters.stores)}
                    values={filters.stores}
                    options={catalog?.stores ?? []}
                    disabled={!catalog || loading}
                    context={filters.brands.length || filters.malls.length ? t("Seçenekler marka ve AVM seçimine göre gösteriliyor.") : t("Marka veya AVM seçerseniz mağaza listesi daralır.")}
                    onApply={(values) => void changeFilter("stores", values)}
                  />
                  <button
                    type="button"
                    className="executive-mobile-filter-clear"
                    disabled={!activeFilterCount || loading}
                    aria-label={t("Tümünü Temizle")}
                    title={t("Tümünü Temizle")}
                    onClick={() => { setMobileQuickFilter(null); void clearFilters(); }}
                  >
                    <Broom aria-hidden="true" size={18} strokeWidth={2.1} />
                  </button>
                </div>
              </section>
            ) : null}
            {page === "admin" || (page === "showroom" && privateSection === "reports") ? null : page === "showroom" ? (
              <div className="showroom-period-stack">
                <section className="period-shortcuts showroom-period-shortcuts" aria-label={t("Showroom dönemi")}>
                  {showroomPeriods.map((choice) => (
                    <button type="button" key={choice.id} className={showroomPeriod === choice.id ? "active" : ""} disabled={loading && showroomPeriod !== choice.id} onClick={() => chooseShowroomPeriod(choice.id)}>
                      {t(choice.label)}
                    </button>
                  ))}
                </section>
                {showroomPeriod === "custom" ? (
                  <form className="period-form" onSubmit={submitShowroom}>
                    <label>
                      {t("Başlangıç")}
                      <input type="date" value={showroomStart} min={catalog?.min_date} max={showroomEnd} onChange={(event) => setShowroomStart(event.target.value)} />
                    </label>
                    <label>
                      {t("Bitiş")}
                      <input type="date" value={showroomEnd} min={showroomStart} max={catalog?.max_date} onChange={(event) => setShowroomEnd(event.target.value)} />
                    </label>
                    <span />
                    <button type="submit" disabled={loading}>
                      {loading ? t("Yükleniyor…") : t("Uygula")}
                    </button>
                  </form>
                ) : null}
                {showroomOpen ? (
                  <section className="showroom-month-strip" ref={showroomMonthStripRef}>
                    <div>
                      <strong>{t("Ay aralığı")}</strong>
                      <span>{t(showroomMonthAnchor ? "Bitiş ayını seçin" : "Başlangıç ve bitiş ayını sırayla seçin")}</span>
                    </div>
                    <div>
                      {showroomMonths.map((month) => {
                        const inRange = month >= showroomStart.slice(0, 7) && month <= showroomEnd.slice(0, 7);
                        return (
                          <button type="button" key={month} data-month={month} className={`${inRange ? "selected" : ""}${showroomMonthAnchor === month ? " anchor" : ""}`} onClick={() => chooseShowroomMonth(month)}>
                            {new Intl.DateTimeFormat("tr-TR", {
                              month: "short",
                              year: "2-digit",
                            }).format(new Date(`${month}-01T12:00:00`))}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ) : null}
              </div>
            ) : useExecutiveLayout ? (
              <CompactPeriodToolbar period={period} start={start} end={end} minDate={catalog?.min_date} maxDate={catalog?.max_date}
                options={periods.map(choice => ({ ...choice, label: t(choice.label) }))} language={language} disabled={!catalog || loading}
                fullMonthTarget={fullMonthTarget} targetDescription={t(fullMonthTarget ? "Hedef görünümü: seçili ayların tamamı" : "Hedef görünümü: seçili gün sayısına göre")}
                lyComparison={lyComparison} previousPeriod={summary?.previous_year_period} onPeriod={choosePeriod}
                onApply={(nextStart, nextEnd) => applyMobilePeriod("custom", nextStart, nextEnd)}
                onTarget={value => { setFullMonthTarget(value); void loadPage(page, start, end, value, filters); }} onComparison={chooseLyComparison}
                activeFilters={activeFilterGroups.length ? <GroupedActiveFilters
                  groups={activeFilterGroups.map(group => ({ key: group.key, label: t(group.label), values: filters[group.key] }))}
                  language={language} disabled={loading} onRemove={(key, value) => void removeActiveFilter(key, value)} onClearAll={() => void clearFilters()} /> : undefined} />
            ) : page === "product" ? (
              <ProductFiltersToolbar mode={productPeriodMode} period={activePeriod} range={productRange} selectedRange={productSelectedRange}
                minDate={catalog?.min_date} maxDate={catalog?.max_date} options={periods.map(choice => ({ ...choice, label: t(choice.label) }))}
                language={language} disabled={!catalog || loading} hasFilters={activeFilterCount > 0}
                onMode={chooseProductMode} onPeriod={value => choosePeriod(value as PeriodId)}
                onApplyDates={(nextStart, nextEnd) => applyMobilePeriod("custom", nextStart, nextEnd)}
                brand={<MainMultiSelect productToolbar="primary" label="Marka" values={filters.brands} options={catalog?.brands ?? []} disabled={!catalog || loading} groupName="product-analysis-filter" onChange={values => void changeFilter("brands", values)} />}
                season={<MainMultiSelect productToolbar="primary" label="Sezon" values={filters.seasons} options={catalog?.seasons ?? []} disabled={!catalog || loading} groupName="product-analysis-filter" onChange={values => void changeFilter("seasons", values)} />}
                sku={<MainSkuSelect productToolbar values={filters.skus} query={globalSkuSearch} suggestions={skuSuggestions} disabled={!catalog || loading} groupName="product-analysis-filter" onQuery={setGlobalSkuSearch} onAdd={sku => void addGlobalSku(sku)} onAddMany={skus => void addGlobalSkus(skus)} onRemove={sku => void removeActiveFilter("skus", sku)} onClear={() => void removeActiveFilter("skus")} />}
                secondary={activeFilterGroups.some(group => !["brands", "seasons", "skus"].includes(group.key)) ? activeFilterGroups.filter(group => !["brands", "seasons", "skus"].includes(group.key)).map(group => <MainMultiSelect key={group.key} productToolbar="secondary" countSelection={group.key === "stores" || group.key === "malls"} label={group.key === "malls" ? "AVM" : group.label} values={filters[group.key]} options={catalog?.[group.key as FilterKey] ?? []} disabled={!catalog || loading} groupName="product-analysis-filter" onChange={values => void changeFilter(group.key as FilterKey, values)} />) : undefined}
                skuDraft={productSkuDraft} onSkuDraft={setProductSkuDraft} onApplySkus={() => void applyProductSkus()} onReset={() => void clearFilters()} />
            ) : (
              <>
                {useCompactPageLayout ? (
                  <div className="executive-period-controls">
                    {useCompactPageLayout ? (
                      <section className="period-shortcuts" aria-label={t(`${pageTitle} dönemi`)}>
                        {periods.map((choice) => (
                          <button type="button" key={choice.id} className={activePeriod === choice.id ? "active" : ""} disabled={loading && activePeriod !== choice.id} onClick={() => choosePeriod(choice.id)}>
                            {t(choice.label)}
                          </button>
                        ))}
                      </section>
                    ) : null}
                  </div>
                ) : null}
                {activePeriod === "custom" ? (
                  <form className="period-form" onSubmit={submit}>
                    <label>
                      {t("Başlangıç")}
                      <input type="date" value={activeSelectedRange.start} min={catalog?.min_date} max={activeSelectedRange.end} onChange={(event) => updateSelectedDate("start", event.target.value)} />
                    </label>
                    <label>
                      {t("Bitiş")}
                      <input type="date" value={activeSelectedRange.end} min={activeSelectedRange.start} max={catalog?.max_date} onChange={(event) => updateSelectedDate("end", event.target.value)} />
                    </label>
                    <span />
                    <button type="submit" disabled={loading}>
                      {loading ? t("Yükleniyor…") : t("Uygula")}
                    </button>
                  </form>
                ) : null}
              </>
            )}
            {page !== "showroom" && page !== "admin" && page !== "product" && !useExecutiveLayout && activeFilterGroups.length ? (
              <section className="active-filter-bar" aria-label={t("Aktif filtreler")}>
                <div className="active-filter-bar__title">
                  <span>◎</span>
                  <strong>{t("Aktif Filtreler")}</strong>
                  <b>{activeFilterCount}</b>
                </div>
                <div className="active-filter-groups">
                  {activeFilterGroups.map((definition) => (
                    <div className="active-filter-group" key={definition.key}>
                      <span>{t(definition.label)}</span>
                      {filters[definition.key].map((value) => (
                        <button type="button" key={value} disabled={loading} title={`${value} filtresini kaldır`} onClick={() => void removeActiveFilter(definition.key, value)}>
                          {value} ×
                        </button>
                      ))}
                      <button className="active-filter-group__clear" type="button" disabled={loading} title={`${definition.label} filtresini temizle`} onClick={() => void removeActiveFilter(definition.key)}>
                        {t("Temizle")}
                      </button>
                    </div>
                  ))}
                </div>
                <button className="active-filter-bar__clear" type="button" disabled={loading} onClick={() => void clearFilters()}>
                  {t("Tümünü Temizle")}
                </button>
              </section>
            ) : null}
            {page !== "admin" && summary && !summary.target_and_visitor_scope_supported ? (
              <section className="notice notice--scope">
                <strong>{t("SKU filtresi etkin.")}</strong>
                <span>{t("Hedef ve ziyaretçi SKU bazında dağıtılamadığı için bu iki oran canlı SRR360 ile aynı şekilde gösterilmiyor.")}</span>
              </section>
            ) : null}
            {page === "product" && productReport && !productReport.inventory_scope_supported ? (
              <section className="notice notice--scope">
                <strong>{t("Mağaza bazlı stok dağılımı hazırlanıyor.")}</strong>
                <span>{t("Satış seçili mağazaya göre doğrudur; hatalı stok göstermemek için stok ve import bu kapsamda boş bırakılır.")}</span>
              </section>
            ) : null}
            {error ? (
              <section className="notice notice--error">
                <strong>{t("Veri açılamadı.")}</strong>
                <span>{t(error)}</span>
                <button type="button" onClick={() => (page === "showroom" ? (privateSection === "reports" ? void loadStorePerformanceReport() : void loadShowroom()) : void loadPage())}>
                  {t("Tekrar dene")}
                </button>
              </section>
            ) : null}
            <section className={`content${loading ? " content--loading" : ""}`} aria-busy={loading}>
              {page === "admin" ? (
                <AdminPage section={adminSection} health={adminHealth} users={adminUsers} sessions={adminSessions} brands={catalog?.brands ?? []} loading={adminLoading} error={adminError} onSection={chooseAdminSection} onRefresh={() => void loadAdmin(adminSection)} onSaveUser={saveAdminUser} />
              ) : page === "showroom" ? (
                privateSection === "reports" ? (
                  <>
                  <div className="campaign-report-navigation" aria-label={t("Rapor seçimi")}>
                    <button type="button" className={reportKind === "store" ? "active" : ""} onClick={() => setReportKind("store")}>{t("Mağaza Performansı")}</button>
                    <button type="button" className={reportKind === "campaign" ? "active" : ""} onClick={() => setReportKind("campaign")}>{t("Kampanya Kullanımı")}</button>
                  </div>
                  {reportKind === "campaign" ? <CampaignReports navigationReplay={navigationReplay} initialNavigation={campaignNavigation} onNavigationChange={rememberCampaignNavigation} BrandFilter={MainMultiSelect} brandOptions={catalog?.brands ?? []} Grid={OriginalReportGrid} language={language} t={t} /> :
                  <StorePerformanceReports
                    report={storePerformanceReport}
                    view={storePerformanceView}
                    brands={reportBrands}
                    malls={reportMalls}
                    stores={reportStores}
                    brandOptions={reportBrandOptions}
                    mallOptions={reportMallOptions}
                    storeOptions={reportStoreOptions}
                    start={reportStart}
                    end={reportEnd}
                    minDate={catalog?.min_date}
                    maxDate={catalog?.max_date}
                    loading={loading}
                    exporting={exporting}
                    onView={setStorePerformanceView}
                    onBrands={(values) => void changeReportBrands(values)}
                    onMalls={(values) => void changeReportMalls(values)}
                    onStores={changeReportStores}
                    onStart={setReportStart}
                    onEnd={setReportEnd}
                    onApply={() => void loadStorePerformanceReport()}
                    onExport={(sorts) => void exportStorePerformanceReports(sorts)}
                  />
                  }
                  </>
                ) : !showroomOpen ? (
                  <section className="showroom-gate-v2">
                    <div className="showroom-gate-v2__intro">
                      <span className="showroom-gate-v2__icon"><Boxes aria-hidden="true" size={24} strokeWidth={1.9} /></span>
                      <div>
                        <p className="eyebrow">{t("Showroom markaları")}</p>
                        <h2>{t("Markalarınızı Seçin")}</h2>
                        <p>{t("Bir veya birden fazla marka seçin. Ağır Showroom verisi yalnızca “Showroom’u Aç” düğmesine bastığınızda sunucudan alınır.")}</p>
                      </div>
                    </div>
                    <div className="showroom-brand-grid">
                      {showroomBrandCatalog().map((brand) => (
                        <button type="button" key={brand} className={showroomBrandChoices.includes(brand) ? "selected" : ""} onClick={() => toggleShowroomBrand(brand)}>
                          <span><Tag aria-hidden="true" size={16} strokeWidth={2} /> {brand}</span>
                          <i aria-hidden="true">{showroomBrandChoices.includes(brand) ? <Check size={15} strokeWidth={2.6} /> : null}</i>
                        </button>
                      ))}
                    </div>
                    <div className="showroom-gate-footer">
                      <div>
                        <small>{t("Seçili markalar")}</small>
                        <span>{showroomBrandChoices.length ? showroomBrandChoices.join(", ") : t("Henüz marka seçilmedi")}</span>
                      </div>
                      <button type="button" disabled={!showroomBrandChoices.length || loading} onClick={openShowroom}>
                        <Boxes aria-hidden="true" size={16} /> {loading ? t("Hazırlanıyor…") : t("Showroom’u Aç")}
                      </button>
                    </div>
                  </section>
                ) : showroomReport ? (
                  <ShowroomReportView key={navigationReplay} navigationState={showroomNavigation} onNavigationChange={rememberShowroomNavigation} report={showroomReport} selectedBrands={showroomSelectedBrands} onChangeBrands={changeShowroomBrands} />
                ) : (
                  <section className="showroom-loading">
                    <span />
                    {t("Seçili marka verileri hazırlanıyor…")}
                  </section>
                )
              ) : (
                <>
                  {page !== "product" ? (
                    <ExecutiveKpiPresentation enabled={page === "executive" || page === "store"}>
                    <div className={page === "executive" || page === "store" ? "kpi-grid executive-kpi-polished" : "kpi-grid"} id={page === "executive" ? "executive-mobile-overview" : undefined}>
                      {useExecutiveLayout ? (
                        <>
                      <SalesKpiCard current={summary?.current.net_sales_uzs ?? 0} previous={summary?.previous_year.net_sales_uzs ?? 0} target={summary?.target_and_visitor_scope_supported ? summary.target_uzs : 0} forecast={summary ? projectedMonthEnd(summary.current.net_sales_uzs, summary.period.start, summary.period.end, period) : null} trend={dailyTrends.sales} targetDetailsInInsight />
                      <SellthroughCard summer={summary?.season_sellthrough.summer ?? emptySeasonSellthrough} winter={summary?.season_sellthrough.winter ?? emptySeasonSellthrough} />
                      <DualKpiCard
                        className="kpi-dual-card--efficiency"
                        left={{
                          label: "UPT",
                          description: "UPT, net satılan ürün adedinin fiş sayısına bölünmesiyle hesaplanır.",
                          icon: "upt",
                          value: decimal.format(summary?.current.upt ?? 0),
                          detail: changeText(ratio(summary?.current.upt ?? 0, summary?.previous_year.upt ?? 0)),
                          previous: decimal.format(summary?.previous_year.upt ?? 0),
                        }}
                        right={{
                          label: "CR",
                          description: "CR, fiş sayısının ziyaretçi sayısına bölünmesiyle hesaplanır.",
                          icon: "conversion",
                          value: summary?.target_and_visitor_scope_supported && summary.current.conversion_rate_percent != null ? `${oneDecimal.format(summary.current.conversion_rate_percent)}%` : "—",
                          detail: summary?.target_and_visitor_scope_supported && summary.current.conversion_rate_percent != null && summary.previous_year.conversion_rate_percent != null ? changeText(ratio(summary.current.conversion_rate_percent, summary.previous_year.conversion_rate_percent)) : "Karşılaştırma yok",
                          previous: summary?.previous_year.conversion_rate_percent == null ? undefined : `${oneDecimal.format(summary.previous_year.conversion_rate_percent)}%`,
                          contextLabel: "Ziyaretçi",
                          contextValue: integer.format(summary?.current.visitors ?? 0),
                        }}
                      />
                      <DualKpiCard
                        className="kpi-dual-card--volume"
                        left={{
                          label: "Satılan Adet",
                          description: "Net satılan adet; iade ve değişim etkileri dahil ürün adedidir.",
                          icon: "units",
                          value: integer.format(summary?.current.net_units ?? 0),
                          detail: changeText(ratio(summary?.current.net_units ?? 0, summary?.previous_year.net_units ?? 0)),
                          previous: integer.format(summary?.previous_year.net_units ?? 0),
                        }}
                        right={{
                          label: "Fiş Sayısı",
                          description: "Fiş sayısı, seçili dönemdeki benzersiz satış fişlerinin sayısıdır.",
                          icon: "receipts",
                          value: integer.format(summary?.current.receipts ?? 0),
                          detail: changeText(ratio(summary?.current.receipts ?? 0, summary?.previous_year.receipts ?? 0)),
                          previous: integer.format(summary?.previous_year.receipts ?? 0),
                        }}
                      />
                      <VerticalKpiCard
                        top={{
                          label: "Sepet Ortalaması",
                          description: "Sepet ortalaması, net cironun fiş sayısına bölünmesiyle hesaplanır.",
                          icon: "basket",
                          value: integer.format(summary?.current.average_basket_uzs ?? 0),
                          detail: changeText(ratio(summary?.current.average_basket_uzs ?? 0, summary?.previous_year.average_basket_uzs ?? 0)),
                          previous: integer.format(summary?.previous_year.average_basket_uzs ?? 0),
                        }}
                        bottom={{
                          label: "Ortalama Fiyat",
                          description: "Ortalama birim fiyat, net cironun net satılan adede bölünmesiyle hesaplanır.",
                          icon: "price",
                          value: integer.format(summary?.current.average_unit_price_uzs ?? 0),
                          detail: changeText(ratio(summary?.current.average_unit_price_uzs ?? 0, summary?.previous_year.average_unit_price_uzs ?? 0)),
                          previous: integer.format(summary?.previous_year.average_unit_price_uzs ?? 0),
                        }}
                      />
                       <MemoExecutiveInsightPanel summary={summary} period={period} />
                        </>
                      ) : (
                        <>
                          <SalesKpiCard current={summary?.current.net_sales_uzs ?? 0} previous={summary?.previous_year.net_sales_uzs ?? 0} target={summary?.target_and_visitor_scope_supported ? summary.target_uzs : 0} forecast={summary ? projectedMonthEnd(summary.current.net_sales_uzs, summary.period.start, summary.period.end, period) : null} trend={dailyTrends.sales} />
                          <KpiCard label="UPT" description="UPT, net satılan ürün adedinin fiş sayısına bölünmesiyle hesaplanır." icon="upt" value={decimal.format(summary?.current.upt ?? 0)} detail={changeText(ratio(summary?.current.upt ?? 0, summary?.previous_year.upt ?? 0))} previous={decimal.format(summary?.previous_year.upt ?? 0)} trend={dailyTrends.upt} trendValueFormatter={(value) => decimal.format(value)} />
                          <ConversionKpiCard current={summary?.current.conversion_rate_percent ?? null} previous={summary?.previous_year.conversion_rate_percent ?? null} visitors={summary?.current.visitors ?? 0} supported={summary?.target_and_visitor_scope_supported ?? false} trend={dailyTrends.conversion} />
                          <SellthroughCard summer={summary?.season_sellthrough.summer ?? emptySeasonSellthrough} winter={summary?.season_sellthrough.winter ?? emptySeasonSellthrough} />
                          <KpiCard label="Satılan Adet" description="Net satılan adet; iade ve değişim etkileri dahil ürün adedidir." icon="units" value={integer.format(summary?.current.net_units ?? 0)} detail={changeText(ratio(summary?.current.net_units ?? 0, summary?.previous_year.net_units ?? 0))} previous={integer.format(summary?.previous_year.net_units ?? 0)} trend={dailyTrends.units} />
                          <KpiCard label="Fiş Sayısı" description="Fiş sayısı, seçili dönemdeki benzersiz satış fişlerinin sayısıdır." icon="receipts" value={integer.format(summary?.current.receipts ?? 0)} detail={changeText(ratio(summary?.current.receipts ?? 0, summary?.previous_year.receipts ?? 0))} previous={integer.format(summary?.previous_year.receipts ?? 0)} trend={dailyTrends.receipts} />
                          <KpiCard label="Sepet Ortalaması" description="Sepet ortalaması, net cironun fiş sayısına bölünmesiyle hesaplanır." icon="basket" value={integer.format(summary?.current.average_basket_uzs ?? 0)} detail={changeText(ratio(summary?.current.average_basket_uzs ?? 0, summary?.previous_year.average_basket_uzs ?? 0))} previous={integer.format(summary?.previous_year.average_basket_uzs ?? 0)} trend={dailyTrends.basket} trendValueFormatter={(value) => `${integer.format(value)}`} />
                          <KpiCard label="Ortalama Birim Fiyat" description="Ortalama birim fiyat, net cironun net satılan adede bölünmesiyle hesaplanır." icon="price" value={integer.format(summary?.current.average_unit_price_uzs ?? 0)} detail={changeText(ratio(summary?.current.average_unit_price_uzs ?? 0, summary?.previous_year.average_unit_price_uzs ?? 0))} previous={integer.format(summary?.previous_year.average_unit_price_uzs ?? 0)} trend={dailyTrends.price} trendValueFormatter={(value) => `${integer.format(value)}`} />
                        </>
                      )}
                    </div>
                    </ExecutiveKpiPresentation>
                  ) : null}
                  {page === "executive" ? (
                    <>
                      {tables ? (
                        <div className="executive-tables-stack" id="executive-mobile-tables">
                          <MonthBreakdownSection kind="stores" scope={{params:tables.breakdown_scope??reportParams(tables.period.start,tables.period.end,fullMonthTarget,filters,lyComparison).toString(),releaseId:tables.release_id}} language={language} firstColumn={t("Mağaza")}>
                          <MemoExecutiveMobileStoreSummary
                            rows={tables.stores}
                            summaryTotals={summary ? {
                              sales: summary.current.net_sales_uzs,
                              target: summary.target_and_visitor_scope_supported ? summary.target_uzs : 0,
                              previousSales: summary.previous_year.net_sales_uzs,
                              units: summary.current.net_units,
                              receipts: summary.current.receipts,
                              visitors: summary.target_and_visitor_scope_supported ? summary.current.visitors : 0,
                            } : undefined}
                            asOfDate={tables.period.end}
                            fullMonthTarget={summary?.full_month_target ?? true}
                            view={executiveStoreTableView}
                            onView={setExecutiveStoreTableView}
                          />
                          <div className={`executive-store-summary-desktop${executiveStoreTableView === "detail" ? " is-mobile-detail" : ""}`}>
                            <MemoReportTable
                              title="Mağaza Özeti"
                              firstColumn="Mağaza"
                              rows={tables.stores}
                              showAllocations
                              allocationTotals={{
                                target: summary?.target_uzs ?? 0,
                                visitors: summary?.current.visitors ?? 0,
                                previousUpt: summary?.previous_year.upt ?? 0,
                              }}
                              targetProgress={{ asOfDate: tables.period.end, fullMonthTarget: summary?.full_month_target ?? true }}
                              mobileExplorer={false}
                              storeOverviewView
                            />
                          </div>
                          </MonthBreakdownSection>
                          {overviewSummaryVisibility(filters,desktopInformationView()).brands ? (
                              <MemoReportTable
                                title="Marka Özeti"
                                executiveOverviewView
                                firstColumn="Marka"
                                rows={tables.brands}
                                showAllocations
                                allocationTotals={{
                                  target: summary?.target_uzs ?? 0,
                                  visitors: summary?.current.visitors ?? 0,
                                  previousUpt: summary?.previous_year.upt ?? 0,
                                }}
                                targetProgress={{ asOfDate: tables.period.end, fullMonthTarget: summary?.full_month_target ?? true }}
                              />
                          ) : null}
                          {overviewSummaryVisibility(filters,desktopInformationView()).malls ? (
                            <MonthBreakdownSection kind="malls" scope={{params:tables.breakdown_scope??reportParams(tables.period.start,tables.period.end,fullMonthTarget,filters,lyComparison).toString(),releaseId:tables.release_id}} language={language} firstColumn={t("Alışveriş Merkezi")}>
                              <MemoReportTable
                                title="Alışveriş Merkezi Özeti"
                                executiveOverviewView
                                firstColumn="Alışveriş Merkezi"
                                rows={tables.malls}
                                showAllocations
                                allocationTotals={{
                                  target: summary?.target_uzs ?? 0,
                                  visitors: summary?.current.visitors ?? 0,
                                  previousUpt: summary?.previous_year.upt ?? 0,
                                }}
                                targetProgress={{ asOfDate: tables.period.end, fullMonthTarget: summary?.full_month_target ?? true }}
                              />
                            </MonthBreakdownSection>
                          ) : null}
                          <StoreBreakdownSection kind="daily" scope={{ params: tables.breakdown_scope ?? reportParams(tables.period.start, tables.period.end, fullMonthTarget, filters, lyComparison).toString(), releaseId: tables.release_id }} language={language} firstColumn={t("Tarih")} aggregateRows={tables.daily} targetTotal={summary?.target_uzs}>
                          <MemoReportTable
                            title="Günlük Satış"
                            executiveOverviewView
                            firstColumn="Tarih"
                            rows={tables.daily}
                            showAllocations
                            showWeekday
                            allocationTotals={{
                              target: summary?.target_uzs ?? 0,
                              visitors: summary?.current.visitors ?? 0,
                              previousUpt: summary?.previous_year.upt ?? 0,
                            }}
                          />
                          </StoreBreakdownSection>
                          <MemoExecutiveWeekdayTable scope={{params: tables.breakdown_scope ?? reportParams(tables.period.start, tables.period.end, fullMonthTarget, filters, lyComparison).toString(),releaseId:tables.release_id}} />
                          <StoreBreakdownSection kind="hourly" scope={{ params: tables.breakdown_scope ?? reportParams(tables.period.start, tables.period.end, fullMonthTarget, filters, lyComparison).toString(), releaseId: tables.release_id }} language={language} firstColumn={t("Saat")} aggregateRows={tables.hourly}>
                          <MemoReportTable title="Saatlik Satış" executiveOverviewView firstColumn="Saat" rows={tables.hourly} showComparisons={false} />
                          </StoreBreakdownSection>
                          <StoreBreakdownSection kind="monthly" scope={{ params: tables.breakdown_scope ?? reportParams(tables.period.start, tables.period.end, fullMonthTarget, filters, lyComparison).toString(), releaseId: tables.release_id }} language={language} firstColumn={t("Ay")} aggregateRows={tables.monthly} targetTotal={summary?.target_uzs}>
                          <MemoReportTable
                            title="Aylık Performans"
                            executiveOverviewView
                            firstColumn="Ay"
                            rows={tables.monthly}
                            showAllocations
                            allocationTotals={{
                              target: summary?.target_uzs ?? 0,
                              visitors: summary?.current.visitors ?? 0,
                              previousUpt: summary?.previous_year.upt ?? 0,
                            }}
                            targetProgress={{ asOfDate: tables.period.end, fullMonthTarget: summary?.full_month_target ?? true }}
                          />
                          </StoreBreakdownSection>
                          <MemoActionStoresTable rows={tables.stores} asOfDate={tables.period.end} fullMonthTarget={summary?.full_month_target ?? true} />
                          <div className="executive-export-actions">
                            <button className="excel-button" type="button" disabled={exporting} onClick={() => void exportExecutiveReport()}>
                              {exporting ? t("Excel hazırlanıyor…") : `⚙ ${t("Yönetici Özetini Excel’e İndir")}`}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : page === "store" ? (
                    storeReport && summary ? (
                      <StoreAnalysisPresentationProvider scope={{ params: storeReport.category_scope ?? reportParams(storeReport.period.start, storeReport.period.end, fullMonthTarget, filters, lyComparison).toString(), releaseId: storeReport.release_id }}>
                        <MonthBreakdownSection kind="category" scope={{params:storeReport.category_scope??reportParams(storeReport.period.start,storeReport.period.end,fullMonthTarget,filters,lyComparison).toString(),releaseId:storeReport.release_id}} language={language} firstColumn={t("Ana Kategori")}>
                        <MemoStoreDetailTable title="Mağazanın Kategori Kırılımı" firstColumn="Ana Kategori" rows={storeReport.categories} variant="stock" categoryOverviewView categoryScope={{ params: storeReport.category_scope ?? reportParams(storeReport.period.start, storeReport.period.end, fullMonthTarget, filters, lyComparison).toString(), releaseId: storeReport.release_id }} />
                        </MonthBreakdownSection>
                        <StoreBreakdownSection kind="gender" scope={{ params: storeReport.category_scope ?? reportParams(storeReport.period.start, storeReport.period.end, fullMonthTarget, filters, lyComparison).toString(), releaseId: storeReport.release_id }} language={language} firstColumn={t("Cinsiyet")} aggregateRows={storeReport.genders}>
                        <MemoReportTable title="Cinsiyet Dağılımı" firstColumn="Cinsiyet" rows={storeReport.genders} allocationTotals={{ target: 0, visitors: summary.current.visitors, sales: summary.current.net_sales_uzs, units: summary.current.net_units, receipts: summary.current.receipts, previousUpt: summary.previous_year.upt }} categoryOverviewView />
                        </StoreBreakdownSection>
                        <StoreBreakdownSection kind="subcategory" scope={{ params: storeReport.category_scope ?? reportParams(storeReport.period.start, storeReport.period.end, fullMonthTarget, filters, lyComparison).toString(), releaseId: storeReport.release_id }} language={language} firstColumn={t("Alt Kategori")} aggregateRows={storeReport.subcategories}>
                        <MemoStoreDetailTable title="Mağazanın Alt Kategori Kırılımı" firstColumn="Alt Kategori" rows={storeReport.subcategories} variant="stock" categoryOverviewView />
                        </StoreBreakdownSection>
                        <MemoAdviserTable
                          rows={storeReport.advisers}
                          total={summary.current}
                          asOfDate={storeReport.period.end}
                          fullMonthTarget={summary.full_month_target}
                        />
                        <button className="excel-button" type="button" disabled={exporting} onClick={() => void exportStoreReport()}>
                          {exporting ? t("Excel hazırlanıyor…") : `⚙ ${t("Excel’e İndir Hazırla")}`}
                        </button>
                      </StoreAnalysisPresentationProvider>
                    ) : null
                  ) : productReport ? (
                    <>
                      <nav className="product-analysis-sections" role="tablist" aria-label={t("Ürün Analizi bölümleri")}>
                        <button id="product-performance-section-tab" type="button" role="tab" aria-selected={productSection === "performance"} aria-controls="product-performance-section" onClick={() => setProductSection("performance")}>{t("Ürün Performansı")}</button>
                        <button id="product-sellthrough-section-tab" type="button" role="tab" aria-selected={productSection === "sellthrough"} aria-controls="product-sellthrough-section" onClick={() => setProductSection("sellthrough")}>Sell-through</button>
                      </nav>
                      <section id="product-performance-section" role="tabpanel" aria-labelledby="product-performance-section-tab" hidden={productSection !== "performance"}>
                      <ProductAnalysisWorkspace
                        rows={productReport.products}
                        releaseId={productReport.release_id}
                        language={language}
                        period={productReport.period}
                        busy={loading}
                        statusFilter={productStatusFilter}
                        onStatusFilterChange={setProductStatusFilter}
                        onExportBusyChange={setProductTableBusy}
                        loadPage={(offset, limit, search, sort, statusFilter) => loadProductTablePage("products", offset, limit, search, sort, statusFilter)}
                        loadDetail={loadProductVariantDetail}
                        initialSort={gridSortRegistry.get(t("Ürün Performansı")) ?? null}
                        onViewChange={(search, sort) => {
                          const title = t("Ürün Performansı");
                          if (search) gridSearchRegistry.set(title, search); else gridSearchRegistry.delete(title);
                          if (sort) gridSortRegistry.set(title, sort); else gridSortRegistry.delete(title);
                        }}
                        t={t}
                        statusLabel={row => t(productStockStatusLabel(productStockStatus(row, productReport.period.end)))}
                        statusDescription={row => t(productStockStatusDescription(productStockStatus(row, productReport.period.end)))}
                      />
                      <details className="product-color-disclosure">
                        <summary><h2>{t("SKU + Renk Performansı")}</h2><ChevronDown size={18} aria-hidden="true" /></summary>
                      <ProductAnalysisWorkspace
                        tableKind="sku_colors"
                        initialPage={initialColorPage}
                        onInitialPageConsumed={page => setInitialColorPage(current => current === page ? undefined : current)}
                        rows={productReport.sku_colors}
                        releaseId={productReport.release_id}
                        language={language}
                        period={productReport.period}
                        busy={loading}
                        statusFilter={productColorStatusFilter}
                        onStatusFilterChange={setProductColorStatusFilter}
                        onExportBusyChange={setProductColorTableBusy}
                        loadPage={loadProductColorTablePage}
                        loadDetail={loadProductVariantDetail}
                        initialSort={gridSortRegistry.get(t("SKU + Renk Performansı")) ?? null}
                        onViewChange={(search, sort) => {
                          const title = t("SKU + Renk Performansı");
                          if (search) gridSearchRegistry.set(title, search); else gridSearchRegistry.delete(title);
                          if (sort) gridSortRegistry.set(title, sort); else gridSortRegistry.delete(title);
                        }}
                        t={t}
                        statusLabel={row => t(productStockStatusLabel(productStockStatus(row, productReport.period.end)))}
                        statusDescription={row => t(productStockStatusDescription(productStockStatus(row, productReport.period.end)))}
                      />
                      </details>
                      <div className="excel-actions">
                        <span className="excel-language-note">{languageLabels[language]}</span>
                        <button className="excel-button" type="button" disabled={productExportDisabled} onClick={() => void exportProductReport()}>
                          {exporting ? t("Excel hazırlanıyor…") : `⚙ ${t("Excel’e İndir Hazırla")}`}
                        </button>
                      </div>
                      </section>
                      <section id="product-sellthrough-section" role="tabpanel" aria-labelledby="product-sellthrough-section-tab" hidden={productSection !== "sellthrough"}>
                        <ProductSellthroughWorkspace active={productSection === "sellthrough"} busy={loading} scope={reportParams(productRange.start, productRange.end, fullMonthTarget, filters, lyComparison).toString()} releaseId={productReport.release_id} language={language} period={productReport.period} stockDate={summary?.source_freshness.stock ?? ""} loadDetail={loadProductVariantDetail} onUnauthorized={() => setAuth(null)} t={t} />
                      </section>
                    </>
                  ) : null}
                </>
              )}
            </section>
          </div>
        </main>
        {!showroomDesktopOnly ? (
          page === "executive" ? (
          <nav className="mobile-bottom-nav mobile-executive-nav" aria-label={t("Yönetici özeti bölümleri")}>
            <button type="button" className={mobileExecutiveSection === "overview" ? "active" : ""} onClick={() => scrollToMobileExecutiveSection("overview")}>
              <LayoutDashboard aria-hidden="true" size={21} strokeWidth={2.1} />
              <span>{t("Özet")}</span>
            </button>
            <button type="button" onClick={() => changePage("store")}>
              <Store aria-hidden="true" size={21} strokeWidth={2.1} />
              <span>{t("Mağaza")}</span>
            </button>
            <button type="button" className={mobileExecutiveSection === "tables" ? "active" : ""} onClick={() => scrollToMobileExecutiveSection("tables")}>
              <Table2 aria-hidden="true" size={21} strokeWidth={2.1} />
              <span>{t("Tablolar")}</span>
            </button>
            <button type="button" className={mobileFiltersOpen ? "active" : ""} onClick={() => setMobileFiltersOpen(true)}>
              <SlidersHorizontal aria-hidden="true" size={21} strokeWidth={2.1} />
              <span>{t("Filtreler")}</span>
            </button>
          </nav>
          ) : (
          <nav className={`mobile-bottom-nav${auth.showroom_access ? " has-reports" : ""}`} aria-label={t("Ana sayfalar")}>
            <button type="button" onClick={() => changePage("executive")}>
              <LayoutDashboard aria-hidden="true" size={21} strokeWidth={2.1} />
              <span>{t("Özet")}</span>
            </button>
            <button type="button" className={page === "store" ? "active" : ""} onClick={() => changePage("store")}>
              <Store aria-hidden="true" size={21} strokeWidth={2.1} />
              <span>{t("Mağaza")}</span>
            </button>
            <button type="button" className={page === "product" ? "active" : ""} onClick={() => changePage("product")}>
              <PackageSearch aria-hidden="true" size={21} strokeWidth={2.1} />
              <span>{t("Ürün")}</span>
            </button>
            {auth.showroom_access ? (
              <button type="button" className={page === "showroom" && privateSection === "reports" ? "active" : ""} onClick={openReportsWorkspace}>
                <FileSpreadsheet aria-hidden="true" size={21} strokeWidth={2.1} />
                <span>{t("Raporlar")}</span>
              </button>
            ) : null}
          </nav>
          )
        ) : null}
      </div>
    </LanguageContext.Provider>
  );
}
